import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import { hoyChile, fechaCL } from '../utils/fecha';
import {
  Boxes, CheckCircle2, Eye, AlertOctagon, Building2, ClipboardList, Receipt,
} from 'lucide-react';
import ListaPresupuestos from '../components/presupuestos/ListaPresupuestos';
import TabReportes from '../components/TabReportes';
import CatalogoItems from '../components/presupuestos/CatalogoItems';
import ConfiguracionSistema from '../components/presupuestos/ConfiguracionSistema';
import MapaComunidades from '../components/MapaComunidades';

const ALL_TABS = [
  { id: 'ascensores',     label: 'Comunidades',    roles: ['admin','superadmin','riesgos'] },
  { id: 'tecnicos',       label: 'Técnicos',       roles: ['admin','superadmin'] },
  { id: 'historial',      label: 'Historial',      roles: ['admin','superadmin'] },
  { id: 'rai',            label: 'Alertas RAI',    roles: ['admin','superadmin','riesgos'] },
  { id: 'pendientes',     label: 'Por aprobar',    roles: ['admin','superadmin'] },
  { id: 'notificaciones', label: 'Notificaciones', roles: ['admin','superadmin','central','riesgos'] },
  { id: 'mapa',           label: 'Mapa técnicos',  roles: ['admin','superadmin','riesgos'] },
  { id: 'asistencia',     label: 'Asistencia',     roles: ['admin','superadmin'] },
  { id: 'stats',          label: 'Dashboard global', roles: ['admin','superadmin','presupuestos','riesgos'] },
  { id: 'planificacion',  label: 'Planificación',  roles: ['admin','superadmin'] },
  { id: 'rendiciones',    label: 'Rendiciones',    roles: ['admin','superadmin','riesgos'] },
  { id: 'presupuestos',   label: 'Presupuestos',   roles: ['admin','superadmin','presupuestos','riesgos'] },
  { id: 'catalogo',       label: 'Catálogo',       roles: ['admin','superadmin','presupuestos','riesgos'] },
  { id: 'facturable',     label: 'Facturación',    roles: ['admin','superadmin','facturacion','riesgos'] },
  { id: 'control-presupuestos', label: 'Control Presup.', roles: ['admin','superadmin','riesgos'] },
  { id: 'historicos',     label: 'Históricos',     roles: ['admin','superadmin'] },
  { id: 'reportes',       label: 'Reportes',       roles: ['admin','superadmin'] },
  { id: 'trabajos',       label: 'Trabajos',       roles: ['admin','superadmin'] },
  { id: 'ordenes',        label: 'Órdenes',        roles: ['admin','superadmin','riesgos'] },
  { id: 'recordatorios',  label: 'Pendientes',     roles: ['admin','superadmin'] },
  { id: 'mantenciones',   label: 'Mantenciones',   roles: ['admin','superadmin'] },
  { id: 'inventario',     label: 'Inventario',     roles: ['admin','superadmin'] },
  { id: 'webhooks',       label: 'Webhooks',       roles: ['superadmin'] },
  { id: 'almacenamiento', label: 'Almacenamiento', roles: ['superadmin'] },
  { id: 'auditoria',      label: 'Auditoría',      roles: ['superadmin'] },
  { id: 'bitacoras',      label: 'Bitácoras',      roles: ['superadmin'] },
];

const TAB_GROUPS = [
  { id: 'operaciones', label: 'Operaciones', tabs: ['stats','historial','rai','pendientes','ordenes','recordatorios','inventario','trabajos','reportes','notificaciones'] },
  { id: 'equipo',      label: 'Equipo',      tabs: ['ascensores','mantenciones','tecnicos','mapa','asistencia','planificacion'] },
  { id: 'finanzas',    label: 'Finanzas',    tabs: ['rendiciones','presupuestos','control-presupuestos','catalogo','facturable'] },
  { id: 'sistema',     label: 'Sistema',     tabs: ['historicos','webhooks','almacenamiento','auditoria','bitacoras'] },
];

// ── Modales simples ────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, maxWidth = 520 }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  zIndex:1000, padding:16 }}>
      <div style={{ background:'#111714', borderRadius:16, padding:24,
                    width:'100%', maxWidth, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between',
                      alignItems:'center', marginBottom:20,
                      borderBottom:'1px solid rgba(255,255,255,0.08)', paddingBottom:16 }}>
          <h2 style={{ margin:0, fontSize:18, color:'#F4F7F5' }}>{title}</h2>
          <button onClick={onClose} style={{ background:'none', border:'none',
            fontSize:22, cursor:'pointer', color:'#8A9994' }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type='text', placeholder='' }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:'block', fontSize:13, fontWeight:600,
                      color:'#8A9994', marginBottom:5 }}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #2E4A40',
                 borderRadius:8, fontSize:14, fontFamily:'inherit',
                 background:'#0D1311', color:'#F4F7F5' }} />
    </div>
  );
}

function Dato({ label, valor }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label style={{ display:'block', fontSize:13, fontWeight:600,
                      color:'#8A9994', marginBottom:5 }}>{label}</label>
      <div style={{ padding:'10px 12px', border:'1.5px solid #2E4A40', borderRadius:8,
                    fontSize:14, background:'#0D1311',
                    color: valor ? '#F4F7F5' : '#6B7C74' }}>
        {valor || '—'}
      </div>
    </div>
  );
}

function PasswordActual({ valor }) {
  const [visible, setVisible] = React.useState(false);
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display:'block', fontSize:13, fontWeight:600,
                      color:'#8A9994', marginBottom:5 }}>Contraseña actual</label>
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <div style={{
          flex:1, padding:'10px 12px', border:'1.5px solid #2E4A40',
          borderRadius:8, fontSize:14, background:'#0D1311',
          fontFamily: visible ? 'inherit' : 'monospace',
          letterSpacing: visible ? 'normal' : '3px',
          color: visible ? '#F4F7F5' : '#6B7C74',
          userSelect: visible ? 'text' : 'none',
        }}>
          {visible ? valor : '••••••••'}
        </div>
        <button type="button" onClick={() => setVisible(v => !v)} style={{
          padding:'10px 14px', border:'1.5px solid #2E4A40', borderRadius:8,
          background:'#161C19', cursor:'pointer', fontSize:16, color:'#8A9994',
          flexShrink:0,
        }}>
          {visible ? 'Ocultar' : 'Mostrar'}
        </button>
      </div>
    </div>
  );
}

function Btn({ onClick, children, color='#0f6e56', outline=false, small=false }) {
  return (
    <button onClick={onClick} style={{
      background: outline ? '#161C19' : color,
      color: outline ? (color === '#888' ? '#C9D3CE' : color) : 'white',
      border: outline ? `1.5px solid rgba(255,255,255,0.12)` : `1.5px solid ${color}`,
      borderRadius:8, padding: small ? '5px 12px' : '9px 18px',
      fontSize: small ? 12 : 14, fontWeight:600, cursor:'pointer',
      whiteSpace:'nowrap',
    }}>{children}</button>
  );
}

// ── Tab Ascensores ─────────────────────────────────────────────────────────────
const EQUIPO_VACIO = { label: '', codigo: '', n_ascensor: '', torre: '', sheets_id: '' };

function TabAscensores({ soloLectura }) {
  const [ascensores, setAscensores]               = useState([]);
  const [q, setQ]                                 = useState('');
  const [expandidas, setExpandidas]               = useState(new Set());
  const [editandoComunidad, setEditandoComunidad] = useState(null);
  const [editandoAscensor, setEditandoAscensor]   = useState(null);
  const [nuevaComunidad, setNuevaComunidad]       = useState(null);
  const [nuevoEquipo, setNuevoEquipo]             = useState(null);
  const [preview, setPreview]                     = useState(null);
  const [loading, setLoading]                     = useState(false);
  const [verInactivas, setVerInactivas]           = useState(false);
  const [verComunidad, setVerComunidad]           = useState(null); // ficha de solo lectura
  const fileRef = React.useRef();

  const cargar = useCallback(async () => {
    const r = await api.get('/admin/ascensores');
    setAscensores(r.data);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // Agrupar por comunidad con filtro de búsqueda
  const { comunidades, totalInactivas } = useMemo(() => {
    const qL = q.toLowerCase();
    const map = {};
    ascensores.forEach(a => {
      if (!map[a.comunidad]) {
        map[a.comunidad] = {
          nombre: a.comunidad,
          nombre_cliente: a.nombre_cliente,
          direccion: a.direccion,
          comuna: a.comuna,
          ciudad: a.ciudad,
          lat: a.lat,
          lng: a.lng,
          emails_cliente: a.emails_cliente || [],
          activo: a.activo,
          envio_directo: a.envio_directo,
          dias_mantencion: a.dias_mantencion ?? 1,
          ascensores: [],
        };
      }
      map[a.comunidad].ascensores.push(a);
    });
    const todas = Object.values(map);
    const totalInactivas = todas.filter(c => c.ascensores.every(a => !a.activo)).length;
    return {
      totalInactivas,
      comunidades: todas
        .filter(c => {
          const inactiva = c.ascensores.every(a => !a.activo);
          return verInactivas ? inactiva : !inactiva;
        })
        .filter(c => !q ||
          c.nombre.toLowerCase().includes(qL) ||
          c.ascensores.some(a =>
            a.codigo?.toLowerCase().includes(qL) ||
            a.label?.toLowerCase().includes(qL)))
        .sort((a, b) => a.nombre.localeCompare(b.nombre)),
    };
  }, [ascensores, q, verInactivas]);

  const toggleExpand = (nombre) => setExpandidas(prev => {
    const next = new Set(prev);
    next.has(nombre) ? next.delete(nombre) : next.add(nombre);
    return next;
  });

  const guardarComunidad = async () => {
    try {
      await api.put(
        `/admin/ascensores/comunidad/${encodeURIComponent(editandoComunidad._nombre_original)}`,
        editandoComunidad
      );
      setEditandoComunidad(null);
      cargar();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const guardarAscensor = async () => {
    try {
      await api.put(`/admin/ascensores/${editandoAscensor.id}`, editandoAscensor);
      setEditandoAscensor(null);
      cargar();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const abrirNuevaComunidad = () => setNuevaComunidad({
    comunidad: '', direccion: '', comuna: '', ciudad: '',
    lat: '', lng: '', emails_cliente: [],
    activo: true, envio_directo: true,
    equipos: [{ ...EQUIPO_VACIO }],
  });

  const crearComunidad = async () => {
    if (!nuevaComunidad.comunidad.trim()) {
      alert('El nombre de la comunidad es requerido');
      return;
    }
    const equiposValidos = nuevaComunidad.equipos.filter(
      e => e.label.trim() && e.codigo.trim()
    );
    if (equiposValidos.length === 0) {
      alert('Agrega al menos un equipo con label y código');
      return;
    }
    try {
      const r = await api.post('/admin/ascensores/comunidad', {
        ...nuevaComunidad, equipos: equiposValidos,
      });
      alert(`✅ Comunidad creada con ${r.data.insertados} equipo(s)`);
      setNuevaComunidad(null);
      setExpandidas(prev => new Set([...prev, nuevaComunidad.comunidad.trim()]));
      cargar();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const crearEquipo = async () => {
    if (!nuevoEquipo?.label?.trim()) { alert('El label es requerido'); return; }
    if (!nuevoEquipo?.codigo?.trim()) { alert('El código es requerido'); return; }
    try {
      await api.post(`/admin/ascensores/comunidad/${encodeURIComponent(nuevoEquipo._comunidad)}/equipo`, nuevoEquipo);
      alert('✅ Equipo agregado correctamente');
      setNuevoEquipo(null);
      cargar();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const setEquipo = (idx, field, value) =>
    setNuevaComunidad(p => {
      const equipos = [...p.equipos];
      equipos[idx] = { ...equipos[idx], [field]: value };
      return { ...p, equipos };
    });

  const importar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('archivo', file);
    fd.append('preview', 'true');
    const r = await api.post('/admin/ascensores/importar', fd);
    setPreview({ ...r.data, file });
    e.target.value = '';
  };

  const confirmarImport = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('archivo', preview.file);
      const r = await api.post('/admin/ascensores/importar', fd);
      const { insertados, actualizados, eliminados } = r.data;
      let msg = `✅ ${insertados} nuevos, ${actualizados} actualizados`;
      if (eliminados > 0) msg += `, ${eliminados} desactivados (historial conservado)`;
      alert(msg);
      setPreview(null);
      cargar();
    } catch (e) {
      alert('❌ Error al importar: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  };

  const exportar = async () => {
    try {
      const r = await api.get('/admin/ascensores/exportar', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a   = document.createElement('a');
      a.href = url;
      a.download = `ascensores_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Error al exportar: ' + (e.response?.data?.error || e.message));
    }
  };

  const abrirEditarComunidad = (com) => setEditandoComunidad({
    _nombre_original: com.nombre,
    comunidad:        com.nombre,
    nombre_cliente:   com.nombre_cliente || '',
    direccion:        com.direccion || '',
    comuna:           com.comuna    || '',
    ciudad:           com.ciudad    || '',
    lat:              com.lat       ?? '',
    lng:              com.lng       ?? '',
    emails_cliente:   [...(com.emails_cliente || [])],
    activo:           com.activo !== false,
    envio_directo:    com.envio_directo !== false,
    dias_mantencion:  com.dias_mantencion ?? 1,
  });

  const estadoComunidad = (com) => {
    const total  = com.ascensores.length;
    const activos = com.ascensores.filter(a => a.activo).length;
    if (activos === 0)     return { label: 'Inactiva', bg: '#2D0F0F', color: '#EF4444' };
    if (activos < total)   return { label: `${activos}/${total} activos`, bg: '#1C1200', color: '#F59E0B' };
    return                        { label: 'Activa', bg: '#12382D', color: '#4CAF50' };
  };

  return (
    <div>
      {/* Barra de herramientas */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder="Buscar comunidad o código..."
          style={{ flex:1, minWidth:200, padding:'9px 12px', border:'1.5px solid #2E4A40',
                   borderRadius:8, fontSize:14, background:'#0D1311', color:'#F4F7F5' }} />
        <input type="file" accept=".xlsx" ref={fileRef} onChange={importar} style={{ display:'none' }} />
        {!soloLectura && <Btn onClick={abrirNuevaComunidad} color='#0f6e56'>➕ Nueva comunidad</Btn>}
        {totalInactivas > 0 && (
          <Btn
            onClick={() => setVerInactivas(v => !v)}
            color={verInactivas ? '#888' : '#7B2D2D'}
            outline={verInactivas}
          >
            {verInactivas ? '← Volver a activas' : `🔴 Inactivas (${totalInactivas})`}
          </Btn>
        )}
        {!soloLectura && <Btn onClick={() => fileRef.current.click()} outline>📥 Importar Excel</Btn>}
        <Btn onClick={exportar} outline color='#0f6e56'>📤 Exportar Excel</Btn>
      </div>

      {/* Preview de importación */}
      {preview && (
        <div style={{ background: preview.eliminados > 0 ? '#1C1200' : '#12382D',
                      border: `1.5px solid ${preview.eliminados > 0 ? '#F59E0B' : '#4CAF50'}`,
                      borderRadius:10, padding:16, marginBottom:16 }}>
          <p style={{ margin:'0 0 8px', fontWeight:600, color:'#F4F7F5' }}>Vista previa de importación</p>
          <p style={{ margin:'0 0 8px', fontSize:14, color:'#8A9994' }}>
            Total: <strong style={{ color:'#F4F7F5' }}>{preview.total}</strong> &nbsp;|&nbsp;
            <span style={{ color:'#4CAF50' }}>✅ {preview.nuevos} nuevos</span> &nbsp;|&nbsp;
            <span style={{ color:'#8A9994' }}>🔄 {preview.actualizados} actualizados</span>
            {preview.eliminados > 0 && (
              <>&nbsp;|&nbsp;<span style={{ color:'#F59E0B', fontWeight:700 }}>⚠ {preview.eliminados} se desactivarán</span></>
            )}
          </p>
          {preview.eliminados > 0 && (
            <div style={{ background:'#1C1200', borderRadius:8, padding:'8px 12px',
                          marginBottom:12, fontSize:12, color:'#F59E0B',
                          border:'1px solid rgba(245,158,11,0.3)' }}>
              Los registros que no aparecen en el Excel quedarán inactivos.
              Sus formularios y planificación anterior no se perderán.
            </div>
          )}
          <div style={{ display:'flex', gap:8 }}>
            <Btn onClick={confirmarImport} color='#0f6e56' disabled={loading}>
              {loading ? 'Importando...' : preview.eliminados > 0
                ? `Confirmar (desactiva ${preview.eliminados})` : 'Confirmar importación'}
            </Btn>
            <Btn onClick={() => setPreview(null)} outline color='#888'>Cancelar</Btn>
          </div>
        </div>
      )}

      {/* Lista de comunidades agrupadas */}
      {verInactivas && (
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12,
                      padding:'10px 14px', borderRadius:8,
                      background:'#2D0F0F', border:'1px solid rgba(239,68,68,0.3)' }}>
          <span style={{ color:'#EF4444', fontWeight:700, fontSize:13 }}>
            🔴 Mostrando {comunidades.length} comunidad{comunidades.length !== 1 ? 'es' : ''} inactiva{comunidades.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}
      {comunidades.length === 0 && (
        <p style={{ textAlign:'center', color:'#aaa', padding:32 }}>
          {verInactivas ? 'No hay comunidades inactivas' : 'Sin resultados'}
        </p>
      )}

      {comunidades.map(com => {
        const estado   = estadoComunidad(com);
        const abierta  = expandidas.has(com.nombre);
        return (
          <div key={com.nombre} style={{
            border: '1.5px solid rgba(255,255,255,0.07)', borderRadius: 12,
            marginBottom: 10, overflow: 'hidden',
          }}>
            {/* Cabecera de comunidad */}
            <div
              onClick={() => toggleExpand(com.nombre)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', cursor: 'pointer',
                background: abierta ? '#12382D' : '#161C19',
                userSelect: 'none',
              }}
            >
              <span style={{ fontSize: 16, color: '#4CAF50', minWidth: 16 }}>
                {abierta ? '▼' : '▶'}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#F4F7F5',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {com.nombre}
                </div>
                <div style={{ fontSize: 12, color: '#8A9994', marginTop: 2 }}>
                  {com.direccion}{com.ciudad ? ` · ${com.ciudad}` : ''}
                  {(com.emails_cliente || []).length > 0 &&
                    ` · ${com.emails_cliente.length} email(s)`}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span style={{ fontSize: 12, color: '#888', fontWeight: 600 }}>
                  {com.ascensores.length} 🛗
                </span>
                <span style={{ background: estado.bg, color: estado.color,
                               borderRadius: 6, padding: '2px 8px',
                               fontSize: 11, fontWeight: 700 }}>
                  {estado.label}
                </span>
                {soloLectura ? (
                  <Btn small outline onClick={e => { e.stopPropagation(); setVerComunidad(com); }}>
                    👁 Ver ficha
                  </Btn>
                ) : (
                  <>
                    <Btn small onClick={e => { e.stopPropagation(); abrirEditarComunidad(com); }}>
                      ✏ Editar
                    </Btn>
                    <Btn small onClick={e => { e.stopPropagation(); setNuevoEquipo({ _comunidad: com.nombre, label: '', codigo: '', n_ascensor: '', torre: '', sheets_id: '' }); }}>
                      ➕ Equipo
                    </Btn>
                  </>
                )}
              </div>
            </div>

            {/* Detalle de ascensores (expandido) */}
            {abierta && (
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse',
                                fontSize: 13, tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 110 }} />
                    <col />
                    <col style={{ width: 64 }} />
                    <col style={{ width: 64 }} />
                    <col style={{ width: 150 }} />
                    <col style={{ width: 44 }} />
                  </colgroup>
                  <thead>
                    <tr style={{ background: '#0f6e56' }}>
                      {['Código', 'Label', 'N°', 'Torre', 'ID Sheets', ''].map(h => (
                        <th key={h} style={{ padding: '7px 10px', textAlign: 'left',
                                             color: 'white', fontWeight: 600, fontSize: 12 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {com.ascensores.map((a, i) => (
                      <tr key={a.id} style={{ background: i % 2 === 0 ? '#161C19' : '#111714' }}>
                        <td style={{ padding: '7px 10px', fontWeight: 600,
                                     color: '#4CAF50', fontFamily: 'monospace', fontSize: 12,
                                     overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.codigo}
                        </td>
                        <td style={{ padding: '7px 10px', color: '#F4F7F5',
                                     overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.label}
                        </td>
                        <td style={{ padding: '7px 10px', textAlign: 'center', color: '#8A9994' }}>
                          {a.n_ascensor ?? '—'}
                        </td>
                        <td style={{ padding: '7px 10px', textAlign: 'center', color: '#8A9994' }}>
                          {a.torre || '—'}
                        </td>
                        <td style={{ padding: '7px 10px', color: '#6B7C74', fontSize: 12,
                                     overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.sheets_id || '—'}
                        </td>
                        <td style={{ padding: '7px 6px', textAlign: 'center' }}>
                          {!soloLectura && <Btn small onClick={() => setEditandoAscensor({ ...a })}>✏</Btn>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Modal ficha de comunidad — consulta sin edición (roles lectores) */}
      {verComunidad && (
        <Modal title={`Comunidad — ${verComunidad.nombre}`}
               onClose={() => setVerComunidad(null)}>
          <Dato label="Nombre del cliente" valor={verComunidad.nombre_cliente} />
          <Dato label="Dirección"
            valor={[verComunidad.direccion, verComunidad.comuna, verComunidad.ciudad]
              .filter(Boolean).join(' · ')} />

          {/* Correos a los que se envían los reportes de esta comunidad */}
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:600,
                            color:'#8A9994', marginBottom:5 }}>
              Correos de envío ({(verComunidad.emails_cliente || []).length})
            </label>
            {(verComunidad.emails_cliente || []).length === 0 ? (
              <div style={{ padding:'10px 12px', border:'1.5px solid #2E4A40', borderRadius:8,
                            fontSize:13, background:'#0D1311', color:'#6B7C74' }}>
                Sin correos configurados — los reportes no se envían al cliente
              </div>
            ) : (verComunidad.emails_cliente || []).map((email, idx) => (
              <div key={idx} style={{ display:'flex', alignItems:'center', gap:8,
                                      padding:'9px 12px', marginBottom:6,
                                      border:'1.5px solid #2E4A40', borderRadius:8,
                                      background:'#0D1311' }}>
                <span style={{ color:'#4CAF50', fontSize:13 }}>✉</span>
                <a href={`mailto:${email}`}
                   style={{ flex:1, fontSize:13, color:'#F4F7F5', textDecoration:'none',
                            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {email}
                </a>
              </div>
            ))}
          </div>

          <Dato label="Envío directo al cliente"
            valor={verComunidad.envio_directo !== false
              ? 'Sí — los reportes se envían apenas se firma el comprobante'
              : 'No — quedan pendientes de aprobación antes de enviarse'} />
          <Dato label="Días de mantenimiento por visita"
            valor={`${verComunidad.dias_mantencion ?? 1} día(s) hábiles consecutivos`} />
          <Dato label="Equipos" valor={`${verComunidad.ascensores.length} ascensor(es)`} />

          {verComunidad.lat && verComunidad.lng && (
            <div style={{ marginBottom:14 }}>
              <a href={`https://www.google.com/maps?q=${verComunidad.lat},${verComunidad.lng}`}
                target="_blank" rel="noreferrer"
                style={{ fontSize:13, color:'#0f6e56', fontWeight:600, textDecoration:'none' }}>
                📍 Ver en Google Maps ↗
              </a>
            </div>
          )}

          <div style={{ display:'flex', justifyContent:'flex-end' }}>
            <Btn outline color='#888' onClick={() => setVerComunidad(null)}>Cerrar</Btn>
          </div>
        </Modal>
      )}

      {/* Modal editar comunidad (campos compartidos) */}
      {editandoComunidad && (
        <Modal title={`Comunidad — ${editandoComunidad._nombre_original}`}
               onClose={() => setEditandoComunidad(null)}>
          <Input label="Nombre de comunidad" value={editandoComunidad.comunidad}
            onChange={v => setEditandoComunidad(p => ({ ...p, comunidad: v }))} />
          <Input label="Nombre del cliente (solo presupuestos)"
            value={editandoComunidad.nombre_cliente}
            onChange={v => setEditandoComunidad(p => ({ ...p, nombre_cliente: v }))} />
          <Input label="Dirección" value={editandoComunidad.direccion}
            onChange={v => setEditandoComunidad(p => ({ ...p, direccion: v }))} />
          <Input label="Comuna" value={editandoComunidad.comuna}
            onChange={v => setEditandoComunidad(p => ({ ...p, comuna: v }))} />
          <Input label="Ciudad" value={editandoComunidad.ciudad}
            onChange={v => setEditandoComunidad(p => ({ ...p, ciudad: v }))} />

          {/* Días de mantenimiento */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600,
                            color: '#8A9994', marginBottom: 5 }}>
              Días de mantenimiento por visita
            </label>
            <input
              type="number" min="1" max="30"
              value={editandoComunidad.dias_mantencion ?? 1}
              onChange={e => setEditandoComunidad(p => ({
                ...p, dias_mantencion: Math.max(1, parseInt(e.target.value) || 1)
              }))}
              style={{ width: 100, padding: '10px 12px', border: '1.5px solid #2E4A40',
                       borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
                       background: '#0D1311', color: '#F4F7F5' }}
            />
            <span style={{ marginLeft: 10, fontSize: 12, color: '#6B7C74' }}>
              días hábiles consecutivos por mantención
            </span>
          </div>

          {/* Coordenadas */}
          <div style={{ display:'flex', gap:10, marginBottom:14 }}>
            <div style={{ flex:1 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600,
                              color:'#8A9994', marginBottom:5 }}>Latitud</label>
              <input type="number" step="any" value={editandoComunidad.lat ?? ''}
                onChange={e => setEditandoComunidad(p => ({ ...p, lat: e.target.value || null }))}
                placeholder="-33.4567"
                style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #2E4A40',
                         borderRadius:8, fontSize:14, fontFamily:'inherit', boxSizing:'border-box',
                         background:'#0D1311', color:'#F4F7F5' }} />
            </div>
            <div style={{ flex:1 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600,
                              color:'#8A9994', marginBottom:5 }}>Longitud</label>
              <input type="number" step="any" value={editandoComunidad.lng ?? ''}
                onChange={e => setEditandoComunidad(p => ({ ...p, lng: e.target.value || null }))}
                placeholder="-70.6543"
                style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #2E4A40',
                         borderRadius:8, fontSize:14, fontFamily:'inherit', boxSizing:'border-box',
                         background:'#0D1311', color:'#F4F7F5' }} />
            </div>
          </div>
          {editandoComunidad.lat && editandoComunidad.lng && (
            <div style={{ marginBottom:14 }}>
              <a href={`https://www.google.com/maps?q=${editandoComunidad.lat},${editandoComunidad.lng}`}
                target="_blank" rel="noreferrer"
                style={{ fontSize:13, color:'#0f6e56', fontWeight:600, textDecoration:'none' }}>
                📍 Ver en Google Maps ↗
              </a>
            </div>
          )}

          {/* Emails */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13, fontWeight:600, color:'#8A9994',
                            display:'block', marginBottom:5 }}>Emails cliente</label>
            {(editandoComunidad.emails_cliente || []).map((email, idx) => (
              <div key={idx} style={{ display:'flex', gap:6, marginBottom:6 }}>
                <input type="email" value={email}
                  onChange={e => {
                    const arr = [...editandoComunidad.emails_cliente];
                    arr[idx] = e.target.value;
                    setEditandoComunidad(p => ({ ...p, emails_cliente: arr }));
                  }}
                  style={{ flex:1, padding:'8px 10px', border:'1.5px solid #2E4A40',
                           borderRadius:8, fontSize:13, background:'#0D1311', color:'#F4F7F5' }} />
                <button
                  onClick={() => setEditandoComunidad(p => ({
                    ...p, emails_cliente: p.emails_cliente.filter((_,i) => i !== idx)
                  }))}
                  style={{ background:'#2D0F0F', color:'#EF4444', border:'1px solid rgba(239,68,68,0.3)',
                           borderRadius:8, padding:'0 10px', fontSize:16,
                           cursor:'pointer', fontWeight:700 }}>×</button>
              </div>
            ))}
            <button
              onClick={() => setEditandoComunidad(p => ({
                ...p, emails_cliente: [...(p.emails_cliente || []), '']
              }))}
              style={{ width:'100%', padding:'8px', border:'1.5px dashed #0f6e56',
                       borderRadius:8, background:'#12382D', color:'#4CAF50',
                       fontSize:13, fontWeight:600, cursor:'pointer', marginTop:2 }}>
              + Agregar email
            </button>
          </div>

          {/* Activo / envío directo */}
          <div style={{ marginBottom:10 }}>
            <label style={{ display:'flex', alignItems:'center', gap:8,
                            fontSize:13, fontWeight:600, cursor:'pointer' }}>
              <input type="checkbox" checked={editandoComunidad.activo !== false}
                onChange={e => setEditandoComunidad(p => ({ ...p, activo: e.target.checked }))}
                style={{ width:16, height:16, accentColor:'#0f6e56' }} />
              Comunidad activa (aplica a todos los equipos)
            </label>
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'flex', alignItems:'center', gap:8,
                            fontSize:13, fontWeight:600, cursor:'pointer' }}>
              <input type="checkbox" checked={editandoComunidad.envio_directo !== false}
                onChange={e => setEditandoComunidad(p => ({ ...p, envio_directo: e.target.checked }))}
                style={{ width:16, height:16, accentColor:'#0f6e56' }} />
              Envío directo al cliente
            </label>
            <p style={{ margin:'4px 0 0 24px', fontSize:12, color:'#888' }}>
              Si está desactivado, los reportes quedan pendientes de aprobación antes de enviarse
            </p>
          </div>

          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn outline color='#888' onClick={() => setEditandoComunidad(null)}>Cancelar</Btn>
            <Btn onClick={guardarComunidad}>Guardar comunidad</Btn>
          </div>
        </Modal>
      )}

      {/* Modal nueva comunidad */}
      {nuevaComunidad && (
        <Modal title="Nueva comunidad" onClose={() => setNuevaComunidad(null)} maxWidth={820}>
          {/* Datos de la comunidad */}
          <p style={{ margin:'0 0 12px', fontWeight:700, fontSize:13, color:'#0f6e56' }}>
            Datos de la comunidad
          </p>
          <Input label="Nombre *" value={nuevaComunidad.comunidad}
            onChange={v => setNuevaComunidad(p => ({ ...p, comunidad: v }))} />
          <Input label="Dirección" value={nuevaComunidad.direccion}
            onChange={v => setNuevaComunidad(p => ({ ...p, direccion: v }))} />
          <div style={{ display:'flex', gap:10, marginBottom:0 }}>
            <div style={{ flex:1 }}>
              <Input label="Comuna" value={nuevaComunidad.comuna}
                onChange={v => setNuevaComunidad(p => ({ ...p, comuna: v }))} />
            </div>
            <div style={{ flex:1 }}>
              <Input label="Ciudad" value={nuevaComunidad.ciudad}
                onChange={v => setNuevaComunidad(p => ({ ...p, ciudad: v }))} />
            </div>
          </div>
          <div style={{ display:'flex', gap:10, marginBottom:14 }}>
            <div style={{ flex:1 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>Latitud</label>
              <input type="number" step="any" value={nuevaComunidad.lat}
                onChange={e => setNuevaComunidad(p => ({ ...p, lat: e.target.value }))}
                placeholder="-33.4567"
                style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #2E4A40',
                         borderRadius:8, fontSize:14, fontFamily:'inherit', boxSizing:'border-box',
                         background:'#0D1311', color:'#F4F7F5' }} />
            </div>
            <div style={{ flex:1 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>Longitud</label>
              <input type="number" step="any" value={nuevaComunidad.lng}
                onChange={e => setNuevaComunidad(p => ({ ...p, lng: e.target.value }))}
                placeholder="-70.6543"
                style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #2E4A40',
                         borderRadius:8, fontSize:14, fontFamily:'inherit', boxSizing:'border-box',
                         background:'#0D1311', color:'#F4F7F5' }} />
            </div>
          </div>
          {/* Emails */}
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13, fontWeight:600, color:'#8A9994', display:'block', marginBottom:5 }}>
              Emails cliente
            </label>
            {(nuevaComunidad.emails_cliente || []).map((email, idx) => (
              <div key={idx} style={{ display:'flex', gap:6, marginBottom:6 }}>
                <input type="email" value={email}
                  onChange={e => {
                    const arr = [...nuevaComunidad.emails_cliente];
                    arr[idx] = e.target.value;
                    setNuevaComunidad(p => ({ ...p, emails_cliente: arr }));
                  }}
                  style={{ flex:1, padding:'8px 10px', border:'1.5px solid #2E4A40', borderRadius:8, fontSize:13,
                           background:'#0D1311', color:'#F4F7F5' }} />
                <button onClick={() => setNuevaComunidad(p => ({
                    ...p, emails_cliente: p.emails_cliente.filter((_,i) => i !== idx)
                  }))}
                  style={{ background:'#2D0F0F', color:'#EF4444', border:'1px solid rgba(239,68,68,0.3)',
                           borderRadius:8, padding:'0 10px', fontSize:16, cursor:'pointer', fontWeight:700 }}>×</button>
              </div>
            ))}
            <button onClick={() => setNuevaComunidad(p => ({
                ...p, emails_cliente: [...(p.emails_cliente || []), '']
              }))}
              style={{ width:'100%', padding:'8px', border:'1.5px dashed #0f6e56', borderRadius:8,
                       background:'#12382D', color:'#4CAF50', fontSize:13, fontWeight:600, cursor:'pointer' }}>
              + Agregar email
            </button>
          </div>
          <div style={{ display:'flex', gap:16, marginBottom:16 }}>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, fontWeight:600, cursor:'pointer' }}>
              <input type="checkbox" checked={nuevaComunidad.activo}
                onChange={e => setNuevaComunidad(p => ({ ...p, activo: e.target.checked }))}
                style={{ width:15, height:15, accentColor:'#0f6e56' }} />
              Activa
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, fontWeight:600, cursor:'pointer' }}>
              <input type="checkbox" checked={nuevaComunidad.envio_directo}
                onChange={e => setNuevaComunidad(p => ({ ...p, envio_directo: e.target.checked }))}
                style={{ width:15, height:15, accentColor:'#0f6e56' }} />
              Envío directo
            </label>
          </div>

          {/* Equipos */}
          <div style={{ borderTop:'2px solid #1E2922', paddingTop:14, marginBottom:14 }}>
            <p style={{ margin:'0 0 10px', fontWeight:700, fontSize:13, color:'#0f6e56' }}>
              Equipos ({nuevaComunidad.equipos.length})
            </p>
            {/* Un solo grid — cabecera + filas comparten el mismo contexto de columnas */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 110px 58px 58px 130px 32px',
              gap: '4px 6px',
              alignItems: 'center',
            }}>
              {/* Cabecera */}
              {['Label *', 'Código *', 'N°', 'Torre', 'ID Sheets', ''].map(h => (
                <span key={h} style={{ fontSize: 11, fontWeight: 600, color: '#888',
                                       paddingBottom: 2 }}>{h}</span>
              ))}
              {/* Filas de equipos */}
              {nuevaComunidad.equipos.map((eq, idx) => (
                <React.Fragment key={idx}>
                  <input value={eq.label} onChange={e => setEquipo(idx, 'label', e.target.value)}
                    placeholder="Ascensor Torre A"
                    style={{ padding:'7px 9px', border:'1.5px solid #2E4A40', borderRadius:7,
                             fontSize:13, fontFamily:'inherit', minWidth:0, boxSizing:'border-box',
                             width:'100%', background:'#0D1311', color:'#F4F7F5' }} />
                  <input value={eq.codigo} onChange={e => setEquipo(idx, 'codigo', e.target.value.toUpperCase())}
                    placeholder="SLV-045"
                    style={{ padding:'7px 8px', border:'1.5px solid #2E4A40', borderRadius:7,
                             fontSize:12, fontFamily:'monospace', minWidth:0, boxSizing:'border-box',
                             width:'100%', background:'#0D1311', color:'#4CAF50' }} />
                  <input value={eq.n_ascensor} onChange={e => setEquipo(idx, 'n_ascensor', e.target.value)}
                    type="number" placeholder="1"
                    style={{ padding:'7px 4px', border:'1.5px solid #2E4A40', borderRadius:7,
                             fontSize:13, textAlign:'center', minWidth:0, boxSizing:'border-box',
                             width:'100%', background:'#0D1311', color:'#F4F7F5' }} />
                  <input value={eq.torre} onChange={e => setEquipo(idx, 'torre', e.target.value)}
                    placeholder="A"
                    style={{ padding:'7px 4px', border:'1.5px solid #2E4A40', borderRadius:7,
                             fontSize:13, textAlign:'center', minWidth:0, boxSizing:'border-box',
                             width:'100%', background:'#0D1311', color:'#F4F7F5' }} />
                  <input value={eq.sheets_id} onChange={e => setEquipo(idx, 'sheets_id', e.target.value)}
                    placeholder="1BxiM…"
                    style={{ padding:'7px 8px', border:'1.5px solid #2E4A40', borderRadius:7,
                             fontSize:12, fontFamily:'monospace', minWidth:0, boxSizing:'border-box',
                             width:'100%', background:'#0D1311', color:'#F4F7F5' }} />
                  <button onClick={() => setNuevaComunidad(p => ({
                      ...p, equipos: p.equipos.filter((_,i) => i !== idx)
                    }))}
                    disabled={nuevaComunidad.equipos.length === 1}
                    style={{ background: nuevaComunidad.equipos.length === 1 ? '#0B0F0D' : '#2D0F0F',
                             color: nuevaComunidad.equipos.length === 1 ? '#2E4A40' : '#EF4444',
                             border:'1px solid rgba(239,68,68,0.3)', borderRadius:7, width:32, height:34,
                             cursor: nuevaComunidad.equipos.length === 1 ? 'default' : 'pointer',
                             fontWeight:700, fontSize:15 }}>×</button>
                </React.Fragment>
              ))}
            </div>
            <button onClick={() => setNuevaComunidad(p => ({
                ...p, equipos: [...p.equipos, { ...EQUIPO_VACIO }]
              }))}
              style={{ width:'100%', padding:'8px', border:'1.5px dashed #0f6e56',
                       borderRadius:8, background:'#12382D', color:'#4CAF50',
                       fontSize:13, fontWeight:600, cursor:'pointer', marginTop:2 }}>
              + Agregar equipo
            </button>
          </div>

          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn outline color='#888' onClick={() => setNuevaComunidad(null)}>Cancelar</Btn>
            <Btn onClick={crearComunidad}>Crear comunidad</Btn>
          </div>
        </Modal>
      )}

      {/* Modal editar ascensor individual (campos específicos del equipo) */}
      {editandoAscensor && (
        <Modal title={`Equipo — ${editandoAscensor.label}`}
               onClose={() => setEditandoAscensor(null)}>
          <div style={{ marginBottom:14, padding:'10px 12px', background:'#0B0F0D',
                        borderRadius:8, fontSize:12, color:'#8A9994',
                        border:'1px solid rgba(255,255,255,0.06)' }}>
            <strong style={{ color:'#F4F7F5' }}>Código:</strong> {editandoAscensor.codigo} &nbsp;·&nbsp;
            <strong style={{ color:'#F4F7F5' }}>Comunidad:</strong> {editandoAscensor.comunidad}
          </div>
          <Input label="N° Ascensor" value={editandoAscensor.n_ascensor ?? ''}
            onChange={v => setEditandoAscensor(p => ({ ...p, n_ascensor: v }))} />
          <Input label="Torre" value={editandoAscensor.torre || ''}
            onChange={v => setEditandoAscensor(p => ({ ...p, torre: v }))} />
          <Input label="ID Google Sheets (bitácora)" value={editandoAscensor.sheets_id || ''}
            onChange={v => setEditandoAscensor(p => ({ ...p, sheets_id: v }))} />
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn outline color='#888' onClick={() => setEditandoAscensor(null)}>Cancelar</Btn>
            <Btn onClick={guardarAscensor}>Guardar equipo</Btn>
          </div>
        </Modal>
      )}

      {/* Modal nuevo equipo en comunidad existente */}
      {nuevoEquipo && (
        <Modal title={`Nuevo equipo — ${nuevoEquipo._comunidad}`}
               onClose={() => setNuevoEquipo(null)}>
          <Input label="Label *" value={nuevoEquipo.label}
            onChange={v => setNuevoEquipo(p => ({ ...p, label: v }))}
            placeholder="Ej: PARQUE REAL 3A" />
          <Input label="Código *" value={nuevoEquipo.codigo}
            onChange={v => setNuevoEquipo(p => ({ ...p, codigo: v.toUpperCase() }))}
            placeholder="Ej: PRE03" />
          <div style={{ display:'flex', gap:10 }}>
            <div style={{ flex:1 }}>
              <Input label="N° Ascensor" value={nuevoEquipo.n_ascensor}
                onChange={v => setNuevoEquipo(p => ({ ...p, n_ascensor: v }))}
                placeholder="Ej: 3" />
            </div>
            <div style={{ flex:1 }}>
              <Input label="Torre" value={nuevoEquipo.torre}
                onChange={v => setNuevoEquipo(p => ({ ...p, torre: v.toUpperCase() }))}
                placeholder="Ej: A" />
            </div>
          </div>
          <Input label="ID Google Sheets (bitácora)" value={nuevoEquipo.sheets_id}
            onChange={v => setNuevoEquipo(p => ({ ...p, sheets_id: v }))}
            placeholder="ID del spreadsheet" />
          <p style={{ fontSize:12, color:'#6B7C74', margin:'4px 0 16px' }}>
            El equipo hereda la dirección, coordenadas y emails de la comunidad.
          </p>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn outline color='#888' onClick={() => setNuevoEquipo(null)}>Cancelar</Btn>
            <Btn onClick={crearEquipo}>Agregar equipo</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Tab Técnicos ───────────────────────────────────────────────────────────────
function TabTecnicos() {
  const { tecnico: usuarioActual } = useAuth();
  const [tecnicos, setTecnicos] = useState([]);
  const [editando, setEditando] = useState(null);
  const [nuevo, setNuevo] = useState(false);
  const form0 = { nombre:'', email:'', username:'', password:'', rol:'tecnico', activo:true };
  const [form, setForm] = useState(form0);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');

  const mostrarToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const cargar = async () => {
    const r = await api.get('/admin/tecnicos');
    setTecnicos(r.data);
  };
  useEffect(() => { cargar(); }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]:v }));

  const guardarNuevo = async () => {
    setErr('');
    try {
      await api.post('/admin/tecnicos', form);
      setNuevo(false); setForm(form0); cargar();
      mostrarToast('Técnico creado correctamente');
    } catch (e) {
      setErr(e.response?.data?.error || 'Error');
    }
  };

  const guardarEdicion = async () => {
    setErr('');
    try {
      await api.put(`/admin/tecnicos/${editando.id}`, editando);
      setEditando(null); cargar();
      mostrarToast('Cambios guardados correctamente');
    } catch (e) {
      setErr(e.response?.data?.error || 'Error');
    }
  };

  const eliminarTecnico = async (t) => {
    const ok = window.confirm(
      `¿Eliminar a "${t.nombre}" del listado de técnicos?\n\n` +
      'Dejará de aparecer en la lista y no podrá iniciar sesión. ' +
      'Sus registros (comprobantes, rendiciones, etc.) NO se modifican.'
    );
    if (!ok) return;
    try {
      await api.delete(`/admin/tecnicos/${t.id}`);
      cargar();
      mostrarToast('Técnico eliminado del listado');
    } catch (e) {
      mostrarToast(e.response?.data?.error || 'Error al eliminar');
    }
  };

  return (
    <div>
      {toast && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          background: '#0f6e56', color: 'white', padding: '12px 24px',
          borderRadius: 12, fontSize: 14, fontWeight: 600,
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)', zIndex: 9999,
          animation: 'fadeIn 0.2s ease',
        }}>
          ✓ {toast}
        </div>
      )}
      <div style={{ marginBottom:16 }}>
        <Btn onClick={() => { setNuevo(true); setForm(form0); setErr(''); }}>
          + Agregar técnico
        </Btn>
      </div>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
        <thead>
          <tr style={{ background:'#0f6e56', color:'white' }}>
            {['Nombre','Usuario','Email','Rol','Estado',''].map(h => (
              <th key={h} style={{ padding:'10px 12px', textAlign:'left' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tecnicos.map((t,i) => (
            <tr key={t.id} style={{ background: i%2===0?'#161C19':'#111714' }}>
              <td style={{ padding:'8px 12px', fontWeight:600, color:'#F4F7F5' }}>{t.nombre}</td>
              <td style={{ padding:'8px 12px', color:'#4CAF50', fontWeight:600 }}>
                {t.username ? `@${t.username}` : <span style={{ color:'#2E4A40' }}>—</span>}
              </td>
              <td style={{ padding:'8px 12px', color:'#8A9994' }}>{t.email}</td>
              <td style={{ padding:'8px 12px' }}>
                <span style={{ background: t.rol==='admin'?'#12382D':'#0B0F0D',
                  color: t.rol==='admin'?'#4CAF50':'#8A9994',
                  borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:700 }}>
                  {t.rol}
                </span>
              </td>
              <td style={{ padding:'8px 12px' }}>
                <span style={{ background: t.activo?'#12382D':'#2D0F0F',
                  color: t.activo?'#4CAF50':'#EF4444',
                  borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:700 }}>
                  {t.activo ? 'Activo' : 'Inactivo'}
                </span>
              </td>
              <td style={{ padding:'8px 12px', whiteSpace:'nowrap' }}>
                <Btn small onClick={() => { setEditando({...t,password:''}); setErr(''); }}>
                  ✏ Editar
                </Btn>
                {usuarioActual?.id !== t.id && (
                  <button onClick={() => eliminarTecnico(t)}
                    style={{ marginLeft:6, padding:'4px 10px', border:'1px solid rgba(198,40,40,0.3)',
                             borderRadius:6, background:'#2D0F0F', color:'#EF4444',
                             cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'inherit' }}>
                    🗑 Eliminar
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {nuevo && (
        <Modal title="Nuevo técnico" onClose={() => setNuevo(false)}>
          {err && <div style={{ background:'#2D0F0F',color:'#EF4444',borderRadius:8,
                                padding:'8px 12px',marginBottom:12,fontSize:13 }}>{err}</div>}
          <Input label="Nombre completo" value={form.nombre} onChange={v => set('nombre',v)} />
          <Input label="Email" type="email" value={form.email} onChange={v => set('email',v)} />
          <Input label="Contraseña inicial" type="password" value={form.password} onChange={v => set('password',v)} />
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13, fontWeight:600, color:'#8A9994', display:'block', marginBottom:5 }}>Rol</label>
            <select value={form.rol} onChange={e => set('rol',e.target.value)}
              style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #2E4A40',
                       borderRadius:8, fontSize:14, background:'#0D1311', color:'#F4F7F5' }}>
              <option value="tecnico">Técnico</option>
              <option value="admin">Administrador</option>
              <option value="superadmin">Super Administrador</option>
              <option value="central">Central</option>
              <option value="presupuestos">Presupuestos</option>
              <option value="facturacion">Facturación</option>
              <option value="riesgos">Riesgos</option>
            </select>
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn outline color='#888' onClick={() => setNuevo(false)}>Cancelar</Btn>
            <Btn onClick={guardarNuevo}>Crear técnico</Btn>
          </div>
        </Modal>
      )}

      {editando && (
        <Modal title={`Editar — ${editando.nombre}`} onClose={() => setEditando(null)}>
          {err && <div style={{ background:'#2D0F0F',color:'#EF4444',borderRadius:8,
                                padding:'8px 12px',marginBottom:12,fontSize:13 }}>{err}</div>}
          <Input label="Nombre" value={editando.nombre}
            onChange={v => setEditando(p=>({...p,nombre:v}))} />
          <Input label="Email" type="email" value={editando.email || ''}
            onChange={v => setEditando(p=>({...p,email:v}))} />
          <Input label="Nombre de usuario (para login)" value={editando.username||''}
            onChange={v => setEditando(p=>({...p,username:v}))} />

          {/* Contraseña actual — solo visible para admin/superadmin */}
          {editando.password_texto && (
            <PasswordActual valor={editando.password_texto} />
          )}

          <Input label="Nueva contraseña (dejar vacío para no cambiar)" type="password"
            value={editando.password||''} onChange={v => setEditando(p=>({...p,password:v}))} />
          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:13,fontWeight:600,color:'#8A9994',display:'block',marginBottom:5 }}>Rol</label>
            <select value={editando.rol} onChange={e => setEditando(p=>({...p,rol:e.target.value}))}
              style={{ width:'100%',padding:'10px 12px',border:'1.5px solid #2E4A40',borderRadius:8,fontSize:14,
                       background:'#0D1311', color:'#F4F7F5' }}>
              <option value="tecnico">Técnico</option>
              <option value="admin">Administrador</option>
              <option value="superadmin">Super Administrador</option>
              <option value="central">Central</option>
              <option value="presupuestos">Presupuestos</option>
              <option value="facturacion">Facturación</option>
              <option value="riesgos">Riesgos</option>
            </select>
          </div>
          <label style={{ display:'flex',alignItems:'center',gap:8,fontSize:13,
                          fontWeight:600,cursor:'pointer',marginBottom:16 }}>
            <input type="checkbox" checked={editando.activo!==false}
              onChange={e => setEditando(p=>({...p,activo:e.target.checked}))}
              style={{ width:16,height:16,accentColor:'#0f6e56' }} />
            Usuario activo
          </label>
          <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
            <Btn outline color='#888' onClick={() => setEditando(null)}>Cancelar</Btn>
            <Btn onClick={guardarEdicion}>Guardar cambios</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Modal Reenvío de correo ────────────────────────────────────────────────────
function ModalReenviar({ id, onClose }) {
  const [cargando,  setCargando]  = useState(true);
  const [enviando,  setEnviando]  = useState(false);
  const [resultado, setResultado] = useState(null); // { ok, to } | null
  const [to,        setTo]        = useState('');
  const [cc,        setCc]        = useState('');
  const [info,      setInfo]      = useState(null);

  useEffect(() => {
    api.get(`/formularios/${id}`)
      .then(r => {
        const d = r.data;
        setInfo(d);
        setTo((d.emails_cliente || []).join(', '));
      })
      .catch(() => alert('Error al cargar el formulario'))
      .finally(() => setCargando(false));
  }, [id]);

  const parsear = str => str.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);

  const enviar = async () => {
    const listaTo = parsear(to);
    const listaCc = parsear(cc);
    if (listaTo.length === 0) { alert('Agrega al menos un destinatario en "Para".'); return; }
    setEnviando(true);
    try {
      await api.post(`/admin/formularios/${id}/reenviar`, { to: listaTo, cc: listaCc });
      setResultado({ ok: true, to: listaTo });
    } catch (e) {
      alert('Error al reenviar: ' + (e.response?.data?.error || e.message));
    }
    setEnviando(false);
  };

  const inputSt = {
    width: '100%', padding: '9px 11px', border: '1.5px solid #2E4A40',
    borderRadius: 8, fontSize: 13, boxSizing: 'border-box',
    background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit',
  };
  const labelSt = {
    display: 'block', fontSize: 11, fontWeight: 700, color: '#8A9994',
    textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 5,
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)',
                  zIndex:3000, display:'flex', alignItems:'center', justifyContent:'center',
                  padding:'16px' }}>
      <div style={{ background:'#111714', borderRadius:14, maxWidth:520, width:'100%',
                    border:'1px solid rgba(255,255,255,0.08)', overflow:'hidden' }}>

        {/* Header */}
        <div style={{ background:'#0f6e56', padding:'14px 20px',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ color:'white', fontWeight:700, fontSize:15 }}>Reenviar comprobante</div>
            {info && (
              <div style={{ color:'rgba(255,255,255,0.8)', fontSize:12, marginTop:2 }}>
                {info.folio} · {info.comunidad} · {info.tipo === 'mantenimiento' ? '🔧 Mantenimiento' : '📋 Visita'}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none',
            color:'white', fontSize:22, cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        <div style={{ padding:'20px' }}>
          {cargando ? (
            <p style={{ color:'#6B7C74', textAlign:'center', padding:20 }}>Cargando...</p>
          ) : resultado ? (
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <p style={{ fontSize:28, margin:'0 0 8px' }}>✅</p>
              <p style={{ fontWeight:700, color:'#4CAF50', fontSize:15, margin:'0 0 6px' }}>Correo enviado correctamente</p>
              <p style={{ color:'#8A9994', fontSize:13, margin:'0 0 20px' }}>
                Enviado a: {resultado.to.join(', ')}
              </p>
              <Btn onClick={onClose}>Cerrar</Btn>
            </div>
          ) : (
            <>
              <p style={{ color:'#8A9994', fontSize:13, margin:'0 0 16px', lineHeight:1.5 }}>
                Edita los destinatarios antes de reenviar. Separa múltiples correos con coma.
                Este envío <strong style={{ color:'#F4F7F5' }}>ignora el modo prueba</strong> y va directo a las direcciones indicadas.
              </p>

              <div style={{ marginBottom:14 }}>
                <label style={labelSt}>Para (cliente) *</label>
                <input
                  value={to}
                  onChange={e => setTo(e.target.value)}
                  placeholder="correo@cliente.cl, otro@cliente.cl"
                  style={inputSt}
                />
              </div>

              <div style={{ marginBottom:20 }}>
                <label style={labelSt}>CC (supervisores)</label>
                <input
                  value={cc}
                  onChange={e => setCc(e.target.value)}
                  placeholder="supervisor@solvert.cl"
                  style={inputSt}
                />
              </div>

              {/* Preview de destinatarios */}
              {(parsear(to).length > 0 || parsear(cc).length > 0) && (
                <div style={{ background:'#0D1311', border:'1px solid #2E4A40',
                              borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12 }}>
                  {parsear(to).length > 0 && (
                    <div style={{ marginBottom: parsear(cc).length > 0 ? 4 : 0 }}>
                      <span style={{ color:'#6B7C74' }}>Para: </span>
                      {parsear(to).map(e => (
                        <span key={e} style={{ background:'#12382D', color:'#4CAF50',
                          borderRadius:4, padding:'1px 6px', marginRight:4, marginBottom:2,
                          display:'inline-block' }}>{e}</span>
                      ))}
                    </div>
                  )}
                  {parsear(cc).length > 0 && (
                    <div>
                      <span style={{ color:'#6B7C74' }}>CC: </span>
                      {parsear(cc).map(e => (
                        <span key={e} style={{ background:'#0D1E2E', color:'#38BDF8',
                          borderRadius:4, padding:'1px 6px', marginRight:4, marginBottom:2,
                          display:'inline-block' }}>{e}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <Btn outline color='#888' onClick={onClose}>Cancelar</Btn>
                <Btn onClick={enviar} color='#0f6e56'>
                  {enviando ? 'Enviando...' : '📧 Enviar ahora'}
                </Btn>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Tab Historial ──────────────────────────────────────────────────────────────
function TabHistorial() {
  const [formularios, setFormularios] = useState([]);
  const [filtros, setFiltros] = useState({ tipo:'', desde:'', hasta:'', rai:'', q:'' });
  const [reenviarId, setReenviarId]  = useState(null);
  const setF = (k,v) => setFiltros(p=>({...p,[k]:v}));

  const cargar = async () => {
    const params = Object.fromEntries(Object.entries(filtros).filter(([,v])=>v));
    const r = await api.get('/admin/formularios', { params });
    setFormularios(r.data);
  };
  useEffect(() => { cargar(); }, []);

  const descargarPDF = async (id, folio) => {
    const r = await api.get(`/pdf/${id}`, { responseType:'blob' });
    const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${folio}.pdf`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 150);
  };

  return (
    <div>
      {reenviarId && <ModalReenviar id={reenviarId} onClose={() => setReenviarId(null)} />}

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
        <input value={filtros.q} onChange={e=>setF('q',e.target.value)}
          placeholder="Comunidad o folio..."
          style={{ flex:1,minWidth:160,padding:'8px 12px',border:'1.5px solid #2E4A40',borderRadius:8,fontSize:13,
                   background:'#0D1311', color:'#F4F7F5' }} />
        <select value={filtros.tipo} onChange={e=>setF('tipo',e.target.value)}
          style={{ padding:'8px 12px',border:'1.5px solid #2E4A40',borderRadius:8,fontSize:13,
                   background:'#0D1311', color:'#F4F7F5' }}>
          <option value="">Todos los tipos</option>
          <option value="mantenimiento">Mantenimiento</option>
          <option value="visita">Visita técnica</option>
        </select>
        <input type="date" value={filtros.desde} onChange={e=>setF('desde',e.target.value)}
          style={{ padding:'8px 12px',border:'1.5px solid #2E4A40',borderRadius:8,fontSize:13,
                   background:'#0D1311', color:'#F4F7F5' }} />
        <input type="date" value={filtros.hasta} onChange={e=>setF('hasta',e.target.value)}
          style={{ padding:'8px 12px',border:'1.5px solid #2E4A40',borderRadius:8,fontSize:13,
                   background:'#0D1311', color:'#F4F7F5' }} />
        <select value={filtros.rai} onChange={e=>setF('rai',e.target.value)}
          style={{ padding:'8px 12px',border:'1.5px solid #2E4A40',borderRadius:8,fontSize:13,
                   background:'#0D1311', color:'#F4F7F5' }}>
          <option value="">Con y sin RAI</option>
          <option value="true">Solo con RAI</option>
        </select>
        <Btn onClick={cargar}>Buscar</Btn>
      </div>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
          <thead>
            <tr style={{ background:'#0f6e56',color:'white' }}>
              {['Folio','Tipo','Fecha','Comunidad','Técnico','RAI','PDF',''].map(h=>(
                <th key={h} style={{ padding:'10px 12px',textAlign:'left',whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {formularios.map((f,i)=>(
              <tr key={f.id} style={{ background:i%2===0?'#161C19':'#111714' }}>
                <td style={{ padding:'8px 12px',fontWeight:600,fontFamily:'monospace',fontSize:12,color:'#4CAF50' }}>{f.folio}</td>
                <td style={{ padding:'8px 12px',color:'#F4F7F5' }}>{f.tipo==='mantenimiento'?'🔧 Mant.':'📋 Visita'}</td>
                <td style={{ padding:'8px 12px',whiteSpace:'nowrap',color:'#8A9994' }}>
                  {f.fecha_visita ? String(f.fecha_visita).slice(0,10) : ''}
                </td>
                <td style={{ padding:'8px 12px',color:'#F4F7F5' }}>{f.comunidad}</td>
                <td style={{ padding:'8px 12px',color:'#8A9994' }}>{f.tecnico_nombre}</td>
                <td style={{ padding:'8px 12px',textAlign:'center' }}>
                  {f.tiene_rai && <span style={{ color:'#EF4444',fontWeight:700 }}>⚠</span>}
                </td>
                <td style={{ padding:'8px 12px' }}>
                  {f.pdf_url
                    ? <a href={f.pdf_url} target="_blank" rel="noreferrer"
                        style={{ color:'#4CAF50',fontSize:12 }}>Drive ↗</a>
                    : <span style={{ color:'#6B7C74',fontSize:12 }}>—</span>}
                </td>
                <td style={{ padding:'8px 12px' }}>
                  <div style={{ display:'flex', gap:6 }}>
                    <Btn small onClick={()=>descargarPDF(f.id,f.folio)}>📄 PDF</Btn>
                    <Btn small color='#1565C0' onClick={() => setReenviarId(f.id)}>📧 Reenviar</Btn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {formularios.length===0 && (
          <p style={{ textAlign:'center',color:'#6B7C74',padding:32 }}>Sin resultados</p>
        )}
      </div>
    </div>
  );
}

// ── Tab RAI ────────────────────────────────────────────────────────────────────
function TabRAI({ soloLectura }) {
  const [alertas, setAlertas] = useState([]);

  const cargar = () => api.get('/admin/rai').then(r => setAlertas(r.data));
  useEffect(() => { cargar(); }, []);

  const resolver = async (id) => {
    if (!window.confirm('¿Confirmas que esta alerta RAI fue subsanada? Se eliminará del listado.')) return;
    await api.delete(`/admin/rai/${id}`);
    cargar();
  };

  return (
    <div>
      {alertas.length === 0 && (
        <div style={{ textAlign:'center',padding:48,color:'#aaa' }}>
          <div style={{ fontSize:48,marginBottom:12 }}>✅</div>
          <p>No hay alertas RAI registradas</p>
        </div>
      )}
      {alertas.map(a => (
        <div key={a.id} style={{ border:'1.5px solid rgba(239,68,68,0.3)',borderRadius:12,
                                  padding:16,marginBottom:12,background:'#1A0A0A' }}>
          <div style={{ display:'flex',justifyContent:'space-between',
                        alignItems:'flex-start',marginBottom:10 }}>
            <div>
              <div style={{ fontWeight:700,fontSize:15,color:'#F4F7F5' }}>{a.comunidad}</div>
              <div style={{ fontSize:13,color:'#8A9994' }}>
                Ascensor {a.n_ascensor} Torre {a.torre} &nbsp;|&nbsp;
                {String(a.fecha_visita||'').slice(0,10)} &nbsp;|&nbsp;
                {a.tecnico_nombre}
              </div>
              <div style={{ fontSize:12,color:'#6B7C74',marginTop:2 }}>Folio: {a.folio}</div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end' }}>
              <span style={{ background:'#2D0F0F',color:'#EF4444',borderRadius:8,
                padding:'4px 10px',fontSize:12,fontWeight:700,whiteSpace:'nowrap' }}>
                {(a.items_rai||[]).length} RAI
              </span>
              {!soloLectura && (
                <button onClick={() => resolver(a.id)}
                  style={{ background:'#0f6e56',color:'white',border:'none',
                           borderRadius:8,padding:'5px 12px',fontSize:12,
                           fontWeight:600,cursor:'pointer',whiteSpace:'nowrap' }}>
                  ✅ Subsanado
                </button>
              )}
            </div>
          </div>
          {(a.items_rai||[]).map((it,i)=>(
            <div key={i} style={{ background:'#0B0F0D',border:'1px solid rgba(239,68,68,0.2)',
                                   borderRadius:8,padding:'8px 12px',marginBottom:6 }}>
              <div style={{ fontSize:13,fontWeight:600,color:'#EF4444' }}>{it.descripcion}</div>
              {it.observacion && (
                <div style={{ fontSize:12,color:'#8A9994',marginTop:3 }}>↳ {it.observacion}</div>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}


// ── Tab Mapa ───────────────────────────────────────────────────────────────────
function TabMapa() {
  const [ubicaciones, setUbicaciones] = useState([]);
  const [tecnicoSel, setTecnicoSel]   = useState(null);
  const [recorrido, setRecorrido]     = useState([]);
  const [fecha, setFecha]             = useState(new Date().toISOString().slice(0,10));
  const [tecnicos, setTecnicos]       = useState([]);

  useEffect(() => {
    api.get('/ubicaciones/ultima').then(r => setUbicaciones(r.data)).catch(()=>{});
    api.get('/admin/tecnicos').then(r => setTecnicos(r.data)).catch(()=>{});
    const iv = setInterval(() => {
      api.get('/ubicaciones/ultima').then(r => setUbicaciones(r.data)).catch(()=>{});
    }, 30000); // actualiza cada 30 seg
    return () => clearInterval(iv);
  }, []);

  const verRecorrido = async (tecnico_id) => {
    setTecnicoSel(tecnico_id);
    const r = await api.get(`/ubicaciones/recorrido/${tecnico_id}`, { params: { fecha } });
    setRecorrido(r.data);
  };

  const formatHora = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString('es-CL', { hour:'2-digit', minute:'2-digit' });
  };

  const minutosDesde = (ts) => {
    if (!ts) return null;
    return Math.round((Date.now() - new Date(ts)) / 60000);
  };

  return (
    <div>
      <h3 style={{ fontSize:15, marginBottom:16, color:'#F4F7F5' }}>
        Ubicación actual de técnicos
        <span style={{ fontSize:12, color:'#6B7C74', fontWeight:400, marginLeft:8 }}>
          (actualiza cada 30 seg)
        </span>
      </h3>

      {/* Tarjetas de técnicos con última ubicación */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',
                    gap:12, marginBottom:24 }}>
        {tecnicos.filter(t => !['admin','superadmin'].includes(t.rol) && t.activo).map(t => {
          const ub = ubicaciones.find(u => Number(u.tecnico_id) === Number(t.id));
          const mins = ub ? minutosDesde(ub.created_at) : null;
          const activo = mins !== null && mins <= 60;
          return (
            <div key={t.id} style={{
              background: '#161C19', border: `1.5px solid ${activo ? '#0f6e56' : 'rgba(255,255,255,0.07)'}`,
              borderRadius:12, padding:14,
            }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <div style={{ fontWeight:600, fontSize:14, color:'#F4F7F5' }}>{t.nombre}</div>
                <span style={{
                  background: activo ? '#12382D' : '#0B0F0D',
                  color: activo ? '#4CAF50' : '#6B7C74',
                  borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700
                }}>
                  {activo ? '● En línea' : '○ Sin señal'}
                </span>
              </div>
              {ub ? (
                <>
                  <div style={{ fontSize:12, color:'#8A9994', marginBottom:4 }}>
                    Última actualización: hace {mins} min ({formatHora(ub.created_at)})
                  </div>
                  <div style={{ fontSize:11, color:'#6B7C74', marginBottom:10 }}>
                    {Number(ub.lat).toFixed(5)}, {Number(ub.lng).toFixed(5)}
                    {ub.precision_metros && ` · ±${ub.precision_metros}m`}
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <a
                      href={`https://www.google.com/maps?q=${ub.lat},${ub.lng}`}
                      target="_blank" rel="noreferrer"
                      style={{ flex:1, padding:'6px', background:'#12382D',
                               color:'#4CAF50', borderRadius:8, fontSize:12,
                               fontWeight:600, textAlign:'center',
                               textDecoration:'none', border:'1px solid rgba(76,175,80,0.2)' }}>
                      📍 Ver en Maps
                    </a>
                    <button
                      onClick={() => verRecorrido(t.id)}
                      style={{ flex:1, padding:'6px', background: tecnicoSel===t.id ? '#0f6e56' : '#0B0F0D',
                               color: tecnicoSel===t.id ? 'white' : '#8A9994',
                               border:'1px solid rgba(255,255,255,0.07)', borderRadius:8,
                               fontSize:12, fontWeight:600, cursor:'pointer' }}>
                      🗺 Recorrido
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ fontSize:12, color:'#6B7C74' }}>
                  Sin ubicación registrada hoy
                </div>
              )}
            </div>
          );
        })}
        {tecnicos.filter(t => !['admin','superadmin'].includes(t.rol) && t.activo).length === 0 && (
          <p style={{ color:'#6B7C74', fontSize:13 }}>No hay técnicos activos</p>
        )}
      </div>

      {/* Recorrido del día */}
      {tecnicoSel && (
        <div style={{ background:'#161C19', border:'1px solid rgba(255,255,255,0.07)',
                      borderRadius:12, padding:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16,
                        flexWrap:'wrap' }}>
            <h4 style={{ margin:0, fontSize:14, color:'#F4F7F5' }}>
              Recorrido del día — {tecnicos.find(t=>t.id===tecnicoSel)?.nombre}
            </h4>
            <input type="date" value={fecha}
              onChange={e => { setFecha(e.target.value); verRecorrido(tecnicoSel); }}
              style={{ padding:'6px 10px', border:'1.5px solid #2E4A40',
                       borderRadius:8, fontSize:13, background:'#0D1311', color:'#F4F7F5' }} />
            <span style={{ fontSize:12, color:'#6B7C74' }}>
              {recorrido.length} puntos registrados
            </span>
          </div>

          {recorrido.length === 0 ? (
            <p style={{ color:'#aaa', fontSize:13 }}>
              Sin registros de ubicación para esta fecha
            </p>
          ) : (
            <>
              {/* Link a Google Maps con el recorrido completo */}
              <a
                href={`https://www.google.com/maps/dir/${recorrido.map(p=>`${p.lat},${p.lng}`).join('/')}`}
                target="_blank" rel="noreferrer"
                style={{ display:'inline-block', marginBottom:16,
                         padding:'8px 16px', background:'#0f6e56', color:'white',
                         borderRadius:8, fontSize:13, fontWeight:600,
                         textDecoration:'none' }}>
                🗺 Ver recorrido completo en Google Maps
              </a>

              {/* Lista de puntos */}
              <div style={{ maxHeight:300, overflowY:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ background:'#0f6e56', color:'white' }}>
                      <th style={{ padding:'6px 10px', textAlign:'left' }}>#</th>
                      <th style={{ padding:'6px 10px', textAlign:'left' }}>Hora</th>
                      <th style={{ padding:'6px 10px', textAlign:'left' }}>Coordenadas</th>
                      <th style={{ padding:'6px 10px', textAlign:'left' }}>Precisión</th>
                      <th style={{ padding:'6px 10px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recorrido.map((p, i) => (
                      <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)',
                                           background: i%2===0?'#161C19':'#111714' }}>
                        <td style={{ padding:'6px 10px', color:'#6B7C74' }}>{i+1}</td>
                        <td style={{ padding:'6px 10px', fontWeight:500, color:'#F4F7F5' }}>
                          {formatHora(p.created_at)}
                        </td>
                        <td style={{ padding:'6px 10px', fontFamily:'monospace', fontSize:11, color:'#8A9994' }}>
                          {Number(p.lat).toFixed(5)}, {Number(p.lng).toFixed(5)}
                        </td>
                        <td style={{ padding:'6px 10px', color:'#6B7C74' }}>
                          {p.precision_metros ? `±${p.precision_metros}m` : '—'}
                        </td>
                        <td style={{ padding:'6px 10px' }}>
                          <a href={`https://www.google.com/maps?q=${p.lat},${p.lng}`}
                            target="_blank" rel="noreferrer"
                            style={{ color:'#4CAF50', fontSize:11 }}>
                            Maps ↗
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Emergencias Chart ─────────────────────────────────────────────────────────
function EmergenciasChart() {
  const hoy = hoyChile();
  const haceDias = (n) => { const d = new Date(hoy + 'T12:00:00'); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

  const [data, setData]       = useState(null);
  const [desde, setDesde]     = useState(haceDias(7));
  const [hasta, setHasta]     = useState(hoy);
  const [verFiltros, setVerFiltros] = useState(false);
  const [cargando, setCargando]     = useState(false);
  const [sel, setSel]               = useState(null);  // ascensor seleccionado
  const [detalle, setDetalle]       = useState(null);  // comprobantes de emergencia
  const [cargandoDet, setCargandoDet] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true); setSel(null); setDetalle(null);
    try { const r = await api.get('/admin/emergencias', { params: { desde, hasta } }); setData(r.data); }
    catch { /* noop */ }
    finally { setCargando(false); }
  }, [desde, hasta]);
  useEffect(() => { cargar(); }, [cargar]);

  const verDetalle = async (c) => {
    if (sel?.ascensor_id === c.ascensor_id) { setSel(null); setDetalle(null); return; }
    setSel(c); setDetalle(null); setCargandoDet(true);
    try { const r = await api.get('/admin/emergencias/detalle', { params: { ascensor_id: c.ascensor_id, desde, hasta } }); setDetalle(r.data || []); }
    catch { setDetalle([]); }
    finally { setCargandoDet(false); }
  };

  const rango = (n) => { setDesde(haceDias(n)); setHasta(hoy); };

  if (!data) return <div style={{ color:'#6B7C74',fontSize:13 }}>Cargando...</div>;

  const { porCliente, reiteradas } = data;
  const top = porCliente.slice(0, 10);
  const max = Math.max(...top.map(x => x.total_emergencias), 1);
  const btnR = { padding:'4px 10px', borderRadius:7, fontSize:12, cursor:'pointer',
                 border:'1px solid #2E4A40', background:'transparent', color:'#C9D3CE' };
  const inp  = { padding:'6px 8px', border:'1.5px solid #2E4A40', borderRadius:8, fontSize:13,
                 background:'#0D1311', color:'#F4F7F5', fontFamily:'inherit' };

  return (
    <div>
      {/* Encabezado con rango y filtros al clic */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12, flexWrap:'wrap' }}>
        <button onClick={() => setVerFiltros(v => !v)}
          style={{ ...btnR, color:'#38BDF8', borderColor:'#1E3A5F' }}>
          📅 {desde.split('-').reverse().join('/')} → {hasta.split('-').reverse().join('/')} {verFiltros ? '▲' : '▼'}
        </button>
        {cargando && <span style={{ color:'#6B7C74', fontSize:12 }}>Actualizando…</span>}
      </div>
      {verFiltros && (
        <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap', alignItems:'center',
                      background:'#0D1311', border:'1px solid #1C2922', borderRadius:10, padding:'10px 12px' }}>
          <input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={inp} />
          <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={inp} />
          <button onClick={() => rango(7)}  style={btnR}>7 días</button>
          <button onClick={() => rango(30)} style={btnR}>30 días</button>
          <button onClick={() => rango(90)} style={btnR}>90 días</button>
        </div>
      )}
      {reiteradas.length > 0 && (
        <div style={{ background:'#2D0F0F', border:'1.5px solid rgba(239,68,68,0.4)',
                      borderRadius:10, padding:14, marginBottom:16 }}>
          <p style={{ margin:'0 0 10px', fontWeight:700, color:'#EF4444', fontSize:14 }}>
            🚨 Emergencias reiteradas en los últimos 15 días
          </p>
          {reiteradas.map((r,i) => (
            <div key={i} style={{ background:'#0B0F0D', borderRadius:8, padding:'8px 12px',
                                   marginBottom:6, fontSize:13, border:'1px solid rgba(255,255,255,0.06)' }}>
              <strong style={{ color:'#F4F7F5' }}>{r.comunidad}</strong><span style={{ color:'#8A9994' }}> — Asc. {r.n_ascensor} Torre {r.torre}</span>
              <span style={{ color:'#EF4444', fontWeight:700, marginLeft:8 }}>
                {r.emergencias_15dias} emergencias
              </span>
              <span style={{ color:'#6B7C74', fontSize:11, marginLeft:8 }}>
                ({String(r.primera).slice(0,10)} → {String(r.ultima).slice(0,10)})
              </span>
            </div>
          ))}
        </div>
      )}

      {top.length === 0 ? (
        <p style={{ color:'#6B7C74', fontSize:13 }}>Sin emergencias en el período seleccionado.</p>
      ) : (
        <div style={{ background:'linear-gradient(180deg,#141A17 0%,#0D1311 100%)', borderRadius:14, padding:'20px 16px 14px',
                      border:'1px solid rgba(255,255,255,0.07)' }}>
          {/* Gráfico de columnas con degradado (clickable) */}
          <div style={{ display:'flex', alignItems:'flex-end', gap:12, height:210, overflowX:'auto', paddingBottom:4 }}>
            {top.map((c) => {
              const h = Math.max(10, Math.round((c.total_emergencias / max) * 150));
              const activo = sel?.ascensor_id === c.ascensor_id;
              return (
                <div key={c.ascensor_id} onClick={() => verDetalle(c)}
                  title={`${c.comunidad} — Asc. ${c.n_ascensor}${c.torre ? ` T${c.torre}` : ''}`}
                  style={{ display:'flex', flexDirection:'column', alignItems:'center', cursor:'pointer',
                           minWidth:48, flex:'0 0 auto', opacity: sel && !activo ? 0.6 : 1, transition:'opacity .2s' }}>
                  <div style={{ color:'#F59E0B', fontWeight:800, fontSize:14, marginBottom:5 }}>{c.total_emergencias}</div>
                  <div style={{ width:36, height:h, borderRadius:'9px 9px 0 0',
                                background:'linear-gradient(180deg,#FBBF24 0%,#F59E0B 45%,#EF4444 100%)',
                                boxShadow: activo ? '0 0 0 2px #fff, 0 6px 16px rgba(239,68,68,0.55)' : '0 3px 10px rgba(239,68,68,0.35)',
                                transition:'height .4s cubic-bezier(.2,.8,.2,1)' }} />
                  <div style={{ marginTop:7, fontSize:11, color: activo ? '#F4F7F5' : '#C9D3CE', fontWeight:700 }}>
                    A{c.n_ascensor}{c.torre ? `·${c.torre}` : ''}
                  </div>
                  <div style={{ fontSize:9, color:'#6B7C74', maxWidth:64, textAlign:'center', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                    {c.comunidad}
                  </div>
                </div>
              );
            })}
          </div>
          <p style={{ margin:'12px 0 0', fontSize:11, color:'#6B7C74', borderTop:'1px solid rgba(255,255,255,0.06)', paddingTop:10 }}>
            👆 Toca una columna para ver los comprobantes de esas emergencias y su resumen.
          </p>

          {/* Detalle de la emergencia seleccionada */}
          {sel && (
            <div style={{ marginTop:12, background:'#0B0F0D', border:'1px solid rgba(239,68,68,0.3)', borderRadius:12, padding:14 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, marginBottom:10, flexWrap:'wrap' }}>
                <strong style={{ color:'#F4F7F5', fontSize:14 }}>
                  {sel.comunidad} — Asc. {sel.n_ascensor}{sel.torre ? ` T${sel.torre}` : ''}
                  <span style={{ color:'#EF4444', marginLeft:8 }}>· {sel.total_emergencias} emergencia(s)</span>
                </strong>
                <button onClick={() => { setSel(null); setDetalle(null); }}
                  style={{ background:'none', border:'1px solid #2E4A40', color:'#8A9994', borderRadius:7, padding:'3px 10px', cursor:'pointer', fontSize:14 }}>×</button>
              </div>
              {cargandoDet ? <p style={{ color:'#6B7C74', fontSize:13, margin:0 }}>Cargando comprobantes…</p>
                : (detalle || []).length === 0 ? <p style={{ color:'#6B7C74', fontSize:13, margin:0 }}>Sin comprobantes en el período.</p>
                : (
                <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:320, overflowY:'auto' }}>
                  {detalle.map(d => (
                    <div key={d.id} style={{ borderTop:'1px solid #1C2922', paddingTop:8 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', gap:8, fontSize:12, flexWrap:'wrap' }}>
                        <span style={{ color:'#C9D3CE' }}>
                          {fechaCL(d.fecha_visita)}{d.hora_emergencia ? ` · ${d.hora_emergencia}` : ''}
                          {d.folio ? ` · ${d.folio}` : ''}{d.tecnico_nombre ? ` · ${d.tecnico_nombre}` : ''}
                        </span>
                        {d.pdf_url && <a href={d.pdf_url} target="_blank" rel="noreferrer" style={{ color:'#38BDF8', whiteSpace:'nowrap' }}>Ver PDF ↗</a>}
                      </div>
                      <div style={{ color:'#E6ECE9', fontSize:12, marginTop:3, whiteSpace:'pre-wrap' }}>{d.resumen_visita || '(sin resumen)'}</div>
                      {d.estado_final_equipo && <div style={{ fontSize:11, color:'#8A9994', marginTop:2 }}>Estado del equipo: {d.estado_final_equipo}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab Estadísticas ───────────────────────────────────────────────────────────
function TabStats({ rol }) {
  // esAdmin habilita el cambio de estado del equipo; veStats solo la lectura de indicadores
  const esAdmin = ['admin', 'superadmin'].includes(rol);
  const veStats = esAdmin || rol === 'riesgos';

  const [stats, setStats]               = useState(null);
  const [estados, setEstados]           = useState(null);
  const [comunidadesMapa, setComunidades] = useState(null);
  const [vistaDetalle, setVista]        = useState(null); // 'detenidos' | 'observacion'
  const [kpis, setKpis]                 = useState(null); // órdenes abiertas / sin facturar
  const [mant, setMant]                 = useState(null); // estado mantenciones del mes
  const [verMant, setVerMant]           = useState(false);
  const [verOrdenes, setVerOrdenes]     = useState(false); // panel de órdenes abiertas
  const [ordenes, setOrdenes]           = useState(null);  // lista de órdenes abiertas
  const [ordExp, setOrdExp]             = useState(null);  // orden expandida
  const [ordItems, setOrdItems]         = useState({});    // { ordenId: {items|manual|error} }

  const toggleOrdenes = async () => {
    const nv = !verOrdenes; setVerOrdenes(nv);
    if (nv && !ordenes) {
      try { const r = await api.get('/trabajos', { params: { estado: 'abiertas' } }); setOrdenes(r.data || []); }
      catch { setOrdenes([]); }
    }
  };
  const toggleOrdenItems = async (o) => {
    if (ordExp === o.id) { setOrdExp(null); return; }
    setOrdExp(o.id);
    if (ordItems[o.id]) return;
    if (!o.presupuesto_id) { setOrdItems(p => ({ ...p, [o.id]: { manual: true, descripcion: o.descripcion } })); return; }
    try { const r = await api.get(`/trabajos/${o.id}/items`); setOrdItems(p => ({ ...p, [o.id]: { items: r.data || [] } })); }
    catch { setOrdItems(p => ({ ...p, [o.id]: { items: [], error: true } })); }
  };

  // Modal cambio de estado remoto
  const [modalEstado, setModalEstado]   = useState(null); // { formularioId, label, comunidad }
  const [nuevoEstado, setNuevoEstado]   = useState('Operativo');
  const [motivo, setMotivo]             = useState('');
  const [guardandoEstado, setGuardando] = useState(false);
  const [errEstado, setErrEstado]       = useState('');

  const recargar = () => {
    api.get('/admin/estado-ascensores').then(r => setEstados(r.data)).catch(() => {});
  };

  useEffect(() => {
    recargar();
    api.get('/admin/comunidades-mapa').then(r => setComunidades(r.data)).catch(() => {});
    api.get('/trabajos/kpis').then(r => setKpis(r.data)).catch(() => {});
    api.get('/admin/estado-mantenciones').then(r => setMant(r.data)).catch(() => {});
    if (veStats) {
      api.get('/admin/stats').then(r => setStats(r.data)).catch(() => {});
    }
  }, []); // eslint-disable-line

  const formatFecha = (val) => {
    if (!val) return '—';
    return String(val).slice(0,10).split('-').reverse().join('/');
  };

  const abrirModalEstado = (a) => {
    setModalEstado({ ascensorId: a.id, label: a.codigo, comunidad: a.comunidad,
                     estadoActual: a.estado_final_equipo });
    setNuevoEstado('Operativo');
    setMotivo('');
    setErrEstado('');
  };

  const guardarCambioEstado = async () => {
    if (!motivo.trim() || motivo.trim().length < 5) {
      setErrEstado('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setGuardando(true);
    setErrEstado('');
    try {
      await api.put(`/admin/estado-ascensor/${modalEstado.ascensorId}`, {
        estado_final_equipo: nuevoEstado,
        motivo: motivo.trim(),
      });
      setModalEstado(null);
      recargar();
    } catch (e) {
      setErrEstado(e.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const TablaAscensores = ({ lista, accentColor }) => (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%',borderCollapse:'collapse',fontSize:13 }}>
        <thead>
          <tr style={{ background:'#0f6e56' }}>
            {['Equipo','Comunidad','Última visita','Técnico','Observaciones','PDF',
              ...(esAdmin ? ['Acción'] : [])].map(h => (
              <th key={h} style={{ padding:'8px 10px',textAlign:'left',
                                   color:'white',fontWeight:600,fontSize:12 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lista.map((a,i) => (
            <tr key={a.id} style={{ background:i%2===0?'#161C19':'#111714',
                                    borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
              <td style={{ padding:'8px 10px',fontWeight:600,whiteSpace:'nowrap',color:'#F4F7F5' }}>
                {a.codigo}{a.torre ? ` — T${a.torre}` : ''}
              </td>
              <td style={{ padding:'8px 10px',color:'#F4F7F5' }}>{a.comunidad}</td>
              <td style={{ padding:'8px 10px',whiteSpace:'nowrap',color:'#8A9994' }}>
                {formatFecha(a.fecha_visita)}
              </td>
              <td style={{ padding:'8px 10px',color:'#8A9994' }}>{a.tecnico_nombre || '—'}</td>
              <td style={{ padding:'8px 10px',color:'#6B7C74',fontSize:12,maxWidth:220 }}>
                {a.resumen_visita || a.observaciones_generales || '—'}
              </td>
              <td style={{ padding:'8px 10px' }}>
                {a.pdf_url
                  ? <a href={a.pdf_url} target="_blank" rel="noreferrer"
                      style={{ color:accentColor,fontSize:12,fontWeight:600 }}>Ver PDF ↗</a>
                  : <span style={{ color:'#6B7C74',fontSize:12 }}>—</span>}
              </td>
              {esAdmin && (
                <td style={{ padding:'8px 10px' }}>
                  <button onClick={() => abrirModalEstado(a)}
                    style={{ padding:'4px 10px', border:`1px solid ${accentColor}`,
                             borderRadius:6, background:'#161C19', color:accentColor,
                             cursor:'pointer', fontSize:12, fontWeight:600 }}>
                    Cambiar estado
                  </button>
                  {a.override_motivo && (
                    <div style={{ fontSize:10, color:'#6B7C74', marginTop:3,
                                  maxWidth:140, lineHeight:1.3 }}
                         title={`Cambiado por ${a.override_por}`}>
                      ⚙ override activo
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      {/* ── Dashboard estados ── */}
      {estados && (
        <div style={{ marginBottom:28 }}>
          <h3 className="dash-h">Estado de ascensores</h3>
          <div className="kpi-grid">
            {[
              { label:'Total equipos',  value:estados.resumen.total,       color:'#8A9994', vcolor:'#F4F7F5', icon:Boxes,        key:null },
              { label:'Operativos',     value:estados.resumen.operativos,  color:'#4CAF50', vcolor:'#F4F7F5', icon:CheckCircle2, key:null },
              { label:'En observación', value:estados.resumen.observacion, color:'#F59E0B', vcolor:'#F59E0B', icon:Eye,          key:'observacion' },
              { label:'Detenidos',      value:estados.resumen.detenidos,   color:'#EF4444', vcolor:'#EF4444', icon:AlertOctagon, key:'detenidos' },
            ].map(s => {
              const Icon = s.icon;
              const activo = vistaDetalle === s.key;
              const cls = 'kpi'
                + (s.key ? ' kpi--click' : '')
                + (activo ? ' kpi--active' : '')
                + (vistaDetalle && !activo ? ' kpi--dim' : '');
              return (
                <div key={s.label} className={cls}
                  onClick={() => s.key && setVista(vistaDetalle === s.key ? null : s.key)}>
                  <div className="kpi__head">
                    <span className="kpi__label">{s.label}</span>
                    <Icon size={17} color={s.color} strokeWidth={2} />
                  </div>
                  <div className="kpi__value" style={{ color:s.vcolor }}>{s.value}</div>
                  {s.key && s.value > 0 && (
                    <div className="kpi__hint">{activo ? 'Ocultar listado' : 'Ver listado'}</div>
                  )}
                </div>
              );
            })}
            {/* Indicador comunidades clientes */}
            {comunidadesMapa && (
              <div className="kpi">
                <div className="kpi__head">
                  <span className="kpi__label">Comunidades clientes</span>
                  <Building2 size={17} color="#38BDF8" strokeWidth={2} />
                </div>
                <div className="kpi__value" style={{ color:'#F4F7F5' }}>{comunidadesMapa.n_comunidades}</div>
              </div>
            )}
            {/* KPIs de órdenes (abiertas / completadas sin facturar) */}
            {kpis && (
              <div onClick={toggleOrdenes}
                className={'kpi kpi--click' + (verOrdenes ? ' kpi--active' : '')}>
                <div className="kpi__head">
                  <span className="kpi__label">Órdenes abiertas</span>
                  <ClipboardList size={17} color="#F59E0B" strokeWidth={2} />
                </div>
                <div className="kpi__value" style={{ color:'#F59E0B' }}>{kpis.ordenes_abiertas}</div>
                {kpis.ordenes_abiertas > 0 && (
                  <div className="kpi__hint">{verOrdenes ? 'Ocultar listado' : 'Ver listado'}</div>
                )}
              </div>
            )}
            {kpis && (
              <div className="kpi">
                <div className="kpi__head">
                  <span className="kpi__label">Completadas sin facturar</span>
                  <Receipt size={17} color="#E5533D" strokeWidth={2} />
                </div>
                <div className="kpi__value" style={{ color:'#E5533D' }}>{kpis.ordenes_sin_facturar}</div>
              </div>
            )}
          </div>

          {vistaDetalle === 'detenidos' && (
            <div className="reveal" style={{ background:'#2D0F0F',border:'1.5px solid rgba(239,68,68,0.3)',
                          borderRadius:12,padding:16,marginBottom:16 }}>
              <h4 style={{ color:'#EF4444',margin:'0 0 12px',fontSize:14 }}>
                Ascensores detenidos ({estados.detenidos.length})
              </h4>
              {estados.detenidos.length === 0
                ? <p style={{ color:'#6B7C74',fontSize:13 }}>No hay ascensores detenidos</p>
                : <TablaAscensores lista={estados.detenidos}
                    accentColor='#EF4444' />}
            </div>
          )}

          {vistaDetalle === 'observacion' && (
            <div className="reveal" style={{ background:'#1C1200',border:'1.5px solid rgba(245,158,11,0.3)',
                          borderRadius:12,padding:16,marginBottom:16 }}>
              <h4 style={{ color:'#F59E0B',margin:'0 0 12px',fontSize:14 }}>
                Ascensores en observación ({estados.observacion.length})
              </h4>
              {estados.observacion.length === 0
                ? <p style={{ color:'#6B7C74',fontSize:13 }}>No hay ascensores en observación</p>
                : <TablaAscensores lista={estados.observacion}
                    accentColor='#F59E0B' />}
            </div>
          )}

          {/* Listado de órdenes abiertas (clic en el tile), cada una expande su detalle */}
          {verOrdenes && (
            <div className="reveal" style={{ background:'#140F03', border:'1.5px solid rgba(245,158,11,0.25)', borderRadius:12, padding:14, marginBottom:16 }}>
              <h4 style={{ color:'#F59E0B', margin:'0 0 10px', fontSize:14 }}>Órdenes abiertas ({ordenes?.length ?? '…'})</h4>
              {!ordenes ? <p style={{ color:'#6B7C74', fontSize:13 }}>Cargando…</p>
                : ordenes.length === 0 ? <p style={{ color:'#6B7C74', fontSize:13 }}>No hay órdenes abiertas.</p>
                : (
                <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:420, overflowY:'auto' }}>
                  {ordenes.map(o => {
                    const EO = { pendiente:{ l:'Pendiente', c:'#8A9994' }, programado:{ l:'Programado', c:'#38BDF8' }, en_proceso:{ l:'En proceso', c:'#F59E0B' } }[o.estado] || { l:o.estado, c:'#8A9994' };
                    const abierto = ordExp === o.id; const info = ordItems[o.id];
                    return (
                      <div key={o.id} style={{ border:'1px solid #1C2922', borderRadius:8 }}>
                        <div onClick={() => toggleOrdenItems(o)} style={{ padding:'9px 12px', cursor:'pointer', display:'flex', justifyContent:'space-between', gap:8, alignItems:'baseline' }}>
                          <span style={{ color:'#F4F7F5', fontSize:13, fontWeight:600 }}>
                            {abierto ? '▾ ' : '▸ '}{o.nombre || o.numero_presupuesto || (o.descripcion ? o.descripcion.slice(0,40) : `Orden #${o.id}`)}
                            <span style={{ color:'#6B7C74', fontWeight:400 }}> · {o.comunidad || '—'}</span>
                          </span>
                          <span style={{ display:'flex', gap:8, alignItems:'center', whiteSpace:'nowrap' }}>
                            {o.tecnico_nombre && <span style={{ color:'#8A9994', fontSize:11 }}>{o.tecnico_nombre}</span>}
                            <span style={{ background:EO.c + '22', color:EO.c, borderRadius:4, padding:'2px 8px', fontSize:11, fontWeight:600 }}>{EO.l}</span>
                          </span>
                        </div>
                        {abierto && (
                          <div style={{ borderTop:'1px solid #1C2922', padding:'8px 12px', background:'#0D1311' }}>
                            {!info ? <p style={{ color:'#6B7C74', fontSize:12, margin:0 }}>Cargando…</p>
                              : info.manual ? <p style={{ color:'#C9D3CE', fontSize:12, margin:0 }}>{info.descripcion || 'Orden manual sin checklist.'}</p>
                              : info.error ? <p style={{ color:'#EF4444', fontSize:12, margin:0 }}>No se pudo cargar el detalle.</p>
                              : (info.items || []).length === 0 ? <p style={{ color:'#6B7C74', fontSize:12, margin:0 }}>Sin ítems.</p>
                              : (
                                <div style={{ display:'flex', flexDirection:'column', gap:3 }}>
                                  {info.items.map(it => (
                                    <div key={it.id} style={{ fontSize:12, color: it.hecho ? '#6B7C74' : '#C9D3CE' }}>
                                      <span style={{ color: it.hecho ? '#4CAF50' : '#6B7C74' }}>{it.hecho ? '✓' : '○'}</span> {it.descripcion}
                                      {it.cantidad ? <span style={{ color:'#6B7C74' }}> ×{Number(it.cantidad)}</span> : null}
                                      {it.comentario ? <div style={{ color:'#9FAAA5', marginLeft:16 }}>{it.comentario}</div> : null}
                                    </div>
                                  ))}
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Mantenciones pendientes del mes (desplegable, para no saturar) ── */}
      {mant && mant.resumen && (
        <div style={{ marginBottom:28 }}>
          <button onClick={() => setVerMant(v => !v)}
            style={{ display:'flex', alignItems:'center', gap:10, width:'100%', textAlign:'left',
                     background:'#161C19', border:'1px solid rgba(255,255,255,0.07)', borderRadius:12,
                     padding:'12px 16px', cursor:'pointer', color:'#F4F7F5', fontSize:14, fontWeight:600 }}>
            <span style={{ color: mant.resumen.pendientes > 0 ? '#F59E0B' : '#4CAF50', fontSize:20, fontWeight:800 }}>
              {mant.resumen.pendientes}
            </span>
            <span>Comunidades con mantención pendiente este mes</span>
            <span style={{ marginLeft:'auto', color:'#6B7C74', fontSize:12 }}>{verMant ? '▲ ocultar' : '▼ ver detalle'}</span>
          </button>
          {verMant && (
            <div className="reveal" style={{ border:'1px solid #1C2922', borderTop:'none', borderRadius:'0 0 12px 12px', maxHeight:260, overflowY:'auto' }}>
              {(mant.comunidades || []).filter(c => c.pendientes > 0).length === 0 ? (
                <p style={{ color:'#6B7C74', fontSize:13, padding:14, margin:0 }}>Todas las comunidades están al día este mes. 🎉</p>
              ) : (mant.comunidades || []).filter(c => c.pendientes > 0).map(c => (
                <div key={c.comunidad} style={{ display:'flex', justifyContent:'space-between', gap:10, padding:'8px 14px', borderTop:'1px solid #1C2922', fontSize:13 }}>
                  <span style={{ color:'#F4F7F5' }}>{c.comunidad}</span>
                  <span style={{ color:'#F59E0B', whiteSpace:'nowrap' }}>
                    {c.pendientes}/{c.n_ascensores} sin mantención
                    {c.ultimo_mant ? <span style={{ color:'#6B7C74' }}> · últ. {fechaCL(c.ultimo_mant)}</span> : null}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Mapa de comunidades ── */}
      {comunidadesMapa && comunidadesMapa.comunidades?.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h3 className="dash-h">Mapa de comunidades</h3>
          <MapaComunidades comunidades={comunidadesMapa.comunidades} />
        </div>
      )}

      {/* ── Emergencias por comunidad (admin, superadmin y riesgos) ── */}
      {veStats && stats && (
        <>
          <h3 className="dash-h">Emergencias por comunidad</h3>
          <EmergenciasChart />
        </>
      )}

      {/* ── Modal cambio de estado remoto (solo admin) ── */}
      {modalEstado && (
        <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',
                      display:'flex',alignItems:'center',justifyContent:'center',
                      zIndex:1000,padding:16 }}>
          <div style={{ background:'#111714',borderRadius:16,padding:28,
                        width:'100%',maxWidth:460,border:'1px solid rgba(255,255,255,0.07)' }}>
            <h3 style={{ margin:'0 0 4px',fontSize:17,color:'#F4F7F5' }}>Cambiar estado del equipo</h3>
            <p style={{ margin:'0 0 6px',fontSize:13,color:'#8A9994' }}>
              <strong style={{ color:'#F4F7F5' }}>{modalEstado.label}</strong> — {modalEstado.comunidad}
            </p>
            <p style={{ margin:'0 0 18px',fontSize:12,color:'#6B7C74' }}>
              Estado actual: <strong style={{ color:'#F4F7F5' }}>{modalEstado.estadoActual || 'Operativo'}</strong>
            </p>
            <div style={{ background:'#1C1200',border:'1px solid rgba(245,158,11,0.3)',borderRadius:8,
                          padding:'8px 12px',marginBottom:16,fontSize:12,color:'#F59E0B' }}>
              El comprobante original no se modifica. Este cambio se registra como override administrativo.
            </div>

            {errEstado && (
              <div style={{ background:'#2D0F0F',color:'#EF4444',borderRadius:8,
                            padding:'8px 12px',marginBottom:14,fontSize:13 }}>
                {errEstado}
              </div>
            )}

            <label style={{ display:'block',fontSize:13,fontWeight:600,
                            color:'#8A9994',marginBottom:6 }}>
              Nuevo estado
            </label>
            <select value={nuevoEstado} onChange={e => setNuevoEstado(e.target.value)}
              style={{ width:'100%',padding:'10px 12px',border:'1.5px solid #2E4A40',
                       borderRadius:8,fontSize:14,marginBottom:16,
                       background:'#0D1311',color:'#F4F7F5' }}>
              <option value="Operativo">Operativo</option>
              <option value="Operativo en observación">Operativo en observación</option>
              <option value="Fuera de servicio">Fuera de servicio</option>
            </select>

            <label style={{ display:'block',fontSize:13,fontWeight:600,
                            color:'#8A9994',marginBottom:6 }}>
              Motivo del cambio *
            </label>
            <textarea value={motivo} onChange={e => setMotivo(e.target.value)}
              rows={4} placeholder="Describe el motivo del cambio de estado…"
              style={{ width:'100%',padding:'10px 12px',border:'1.5px solid #2E4A40',
                       borderRadius:8,fontSize:14,resize:'vertical',
                       boxSizing:'border-box',marginBottom:20,
                       background:'#0D1311',color:'#F4F7F5',fontFamily:'inherit' }} />

            <div style={{ display:'flex',gap:10,justifyContent:'flex-end' }}>
              <button onClick={() => setModalEstado(null)}
                style={{ padding:'9px 20px',border:'1.5px solid rgba(255,255,255,0.12)',borderRadius:8,
                         background:'#161C19',color:'#C9D3CE',cursor:'pointer',fontSize:14 }}>
                Cancelar
              </button>
              <button onClick={guardarCambioEstado} disabled={guardandoEstado}
                style={{ padding:'9px 20px',background:'#0f6e56',color:'white',
                         border:'none',borderRadius:8,fontWeight:600,
                         cursor:guardandoEstado?'wait':'pointer',fontSize:14 }}>
                {guardandoEstado ? 'Guardando…' : 'Confirmar cambio'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Tab Notificaciones ────────────────────────────────────────────────────────
// Tipo de cada fila de `notificaciones` (filas antiguas sin `tipo` se deducen)
const TIPO_NOTIF_CFG = {
  emergencia: { label:'🚨 Emergencia', bg:'#2D0F0F', fg:'#EF4444' },
  mensaje:    { label:'💬 Mensaje',    bg:'#12382D', fg:'#4CAF50' },
  inventario: { label:'📦 Inventario', bg:'#2E2410', fg:'#E5A93D' },
  respuesta:  { label:'🚗 Respuesta',  bg:'#0D1E2E', fg:'#60A5FA' },
};
const tipoNotif = (n) => {
  if (TIPO_NOTIF_CFG[n.tipo]) return n.tipo;
  if (n.comunidad === 'MENSAJE') return 'mensaje';
  if (n.comunidad === 'INVENTARIO') return 'inventario';
  return 'emergencia';
};
const detalleNotif = (n, t) =>
  t === 'mensaje'    ? `${n.n_ascensor}: ${n.problema}` :
  t === 'inventario' ? n.problema :
                       `Asc. ${n.n_ascensor} · ${n.problema}`;

const PROBLEMAS_EMERG = [
  'Ascensor detenido',
  'Detenido entre pisos',
  'Puerta no abre / no cierra',
  'Ruido o vibración',
  'Falla de botonera',
];
const EMERG_ASC_CACHE_KEY = 'solvert_emerg_ascensores';
const EMERG_RECIENTES_KEY = 'solvert_emerg_recientes';
const EMERG_HOLD_MS = 800;
const normEmerg = (s) => String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

function TabNotificaciones({ rol }) {
  // central solo envía emergencias (sin pestaña Mensaje)
  const puedeMensajes = ['admin', 'superadmin', 'riesgos'].includes(rol);
  const [subTab, setSubTab]             = useState('emergencia');
  const [ascensores, setAscensores]     = useState(() => {
    try { return JSON.parse(localStorage.getItem(EMERG_ASC_CACHE_KEY)) || []; } catch { return []; }
  });
  const [tecnicos, setTecnicos]         = useState([]);
  const [historial, setHistorial]       = useState([]);
  const [ascensorSel, setAscensorSel]   = useState(null);
  const [problemaTipo, setProblemaTipo] = useState(null);
  const [detalle, setDetalle]           = useState('');
  const [personaEncerrada, setPersonaEncerrada] = useState(false);
  const [enviandoEmerg, setEnviandoEmerg] = useState(false);
  const [recientesIds, setRecientesIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(EMERG_RECIENTES_KEY)) || []; } catch { return []; }
  });
  const [holdPct, setHoldPct]           = useState(0);
  const holdTimer = useRef(null);
  const [destinatario, setDestinatario]   = useState('todos');
  const [destinatariosIds, setDestinatariosIds] = useState([]);
  const [titulo, setTitulo]               = useState('');
  const [cuerpo, setCuerpo]               = useState('');
  const [enviandoMsg, setEnviandoMsg]     = useState(false);
  const [msg, setMsg]                     = useState({ text:'', ok:true });

  useEffect(() => {
    // Se muestra el cache local al instante y se refresca en segundo plano
    api.get('/ascensores').then(r => {
      const data = r.data || [];
      setAscensores(data);
      try { localStorage.setItem(EMERG_ASC_CACHE_KEY, JSON.stringify(data)); } catch {}
    }).catch(() => {});
    api.get('/admin/tecnicos').then(r => setTecnicos(r.data || [])).catch(() => {});
    recargarHistorial();
  }, []);

  const recargarHistorial = () => {
    api.get('/push/historial').then(r => setHistorial(r.data || [])).catch(() => {});
  };

  const hora = () => new Date().toLocaleTimeString('es-CL', { hour:'2-digit', minute:'2-digit' });

  // Recientes: enviados desde este navegador + últimas emergencias del historial
  const recientes = useMemo(() => {
    const porId = new Map(ascensores.map(a => [a.id, a]));
    const out = [];
    const agregar = (a) => { if (a && !out.some(x => x.id === a.id)) out.push(a); };
    recientesIds.forEach(id => agregar(porId.get(id)));
    historial
      .filter(h => h.tipo === 'emergencia' || (h.tipo == null && h.comunidad !== 'MENSAJE'))
      .forEach(h => agregar(ascensores.find(a =>
        a.comunidad === h.comunidad && String(a.n_ascensor) === String(h.n_ascensor))));
    return out.slice(0, 6);
  }, [ascensores, recientesIds, historial]);

  const [busqueda, setBusqueda] = useState('');
  const resultados = useMemo(() => {
    const tokens = normEmerg(busqueda).split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];
    return ascensores.filter(a => {
      const t = normEmerg(`${a.comunidad} ${a.label} ${a.n_ascensor} torre ${a.torre ?? ''}`);
      return tokens.every(k => t.includes(k));
    }).slice(0, 40);
  }, [busqueda, ascensores]);

  const problemaFinal = [problemaTipo, detalle.trim()].filter(Boolean).join(' — ')
    || (personaEncerrada ? 'Ascensor detenido' : '');
  const listo = !!ascensorSel && !!problemaFinal;

  const seleccionarAscensor = (asc) => { setAscensorSel(asc); setBusqueda(''); };
  const resetEmergencia = () => {
    setAscensorSel(null); setProblemaTipo(null); setDetalle('');
    setPersonaEncerrada(false); setBusqueda(''); setHoldPct(0);
  };

  const enviarEmergencia = async () => {
    if (!listo || enviandoEmerg) return;
    setMsg({ text:'', ok:true });
    setEnviandoEmerg(true);
    try {
      const res = await api.post('/push/emergencia', {
        comunidad: ascensorSel.comunidad, n_ascensor: ascensorSel.n_ascensor,
        problema: problemaFinal, persona_encerrada: personaEncerrada,
      });
      const nuevos = [ascensorSel.id, ...recientesIds.filter(x => x !== ascensorSel.id)].slice(0, 6);
      setRecientesIds(nuevos);
      try { localStorage.setItem(EMERG_RECIENTES_KEY, JSON.stringify(nuevos)); } catch {}
      setMsg({ text:`✅ Alerta enviada a ${res.data.enviados} persona(s) · ${ascensorSel.comunidad} · Asc. ${ascensorSel.n_ascensor}`, ok:true });
      resetEmergencia();
      recargarHistorial();
    } catch(e) { setMsg({ text: e.response?.data?.error || 'Error', ok:false }); setHoldPct(0); }
    finally { setEnviandoEmerg(false); }
  };

  // Mantener presionado reemplaza el confirm()
  const holdStart = () => {
    if (!listo || enviandoEmerg) return;
    const t0 = Date.now();
    clearInterval(holdTimer.current);
    holdTimer.current = setInterval(() => {
      const pct = Math.min(100, ((Date.now() - t0) / EMERG_HOLD_MS) * 100);
      setHoldPct(pct);
      if (pct >= 100) { clearInterval(holdTimer.current); enviarEmergencia(); }
    }, 30);
  };
  const holdEnd = () => {
    clearInterval(holdTimer.current);
    if (!enviandoEmerg) setHoldPct(0);
  };
  useEffect(() => () => clearInterval(holdTimer.current), []);

  const enviarMensaje = async () => {
    setMsg({ text:'', ok:true });
    if (!titulo.trim()) { setMsg({ text:'Ingresa un título', ok:false }); return; }
    if (!cuerpo.trim()) { setMsg({ text:'Ingresa el mensaje', ok:false }); return; }
    const dest = destinatario === 'individual' ? destinatariosIds : destinatario;
    if (destinatario === 'individual' && destinatariosIds.length === 0) {
      setMsg({ text:'Selecciona al menos un destinatario', ok:false }); return;
    }
    setEnviandoMsg(true);
    try {
      const res = await api.post('/push/mensaje', { destinatarios:dest, titulo:titulo.trim(), cuerpo:cuerpo.trim() });
      setMsg({ text:`✅ Mensaje enviado a ${res.data.enviados} usuario(s)`, ok:true });
      setTitulo(''); setCuerpo(''); setDestinatariosIds([]);
      recargarHistorial();
    } catch(e) { setMsg({ text: e.response?.data?.error || 'Error', ok:false }); }
    finally { setEnviandoMsg(false); }
  };

  const FilaAsc = (a, keyP) => (
                    <div key={keyP + a.id} onClick={() => seleccionarAscensor(a)}
                      style={{ padding:'12px 14px', cursor:'pointer', borderBottom:'1px solid rgba(255,255,255,0.05)',
                        display:'flex', justifyContent:'space-between', alignItems:'center' }}
                      onMouseEnter={e => e.currentTarget.style.background='#1A2520'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                      <div>
                        <div style={{ fontSize:14, fontWeight:600, color:'#F4F7F5' }}>{a.comunidad}</div>
                        <div style={{ fontSize:12, color:'#6B7C74' }}>
                          Ascensor N° {a.n_ascensor}{a.torre ? ` · Torre ${a.torre}` : ''} · {a.label}
                        </div>
                      </div>
                      <span style={{ color:'#6B7C74', fontSize:18 }}>›</span>
                    </div>
                  );

  const btnSub = (id, label) => (
    <button key={id} onClick={() => { setSubTab(id); setMsg({text:'',ok:true}); }}
      style={{ padding:'8px 16px', borderRadius:8, cursor:'pointer', fontSize:13, border:'none',
        fontWeight: subTab===id?700:500,
        background: subTab===id?'#0f6e56':'#161C19',
        color: subTab===id?'white':'#8A9994',
        border: subTab===id?'none':'1px solid rgba(255,255,255,0.07)' }}>
      {label}
    </button>
  );

  return (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {btnSub('emergencia','🚨 Emergencia')}
        {puedeMensajes && btnSub('mensaje','💬 Mensaje')}
        {btnSub('historial','📋 Historial')}
      </div>

      {msg.text && (
        <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13,
          background: msg.ok?'#12382D':'#2D0F0F', color: msg.ok?'#4CAF50':'#EF4444',
          borderLeft:`4px solid ${msg.ok?'#4CAF50':'#EF4444'}` }}>
          {msg.text}
        </div>
      )}

      {subTab === 'emergencia' && (
        <div style={{ background:'#111714', borderRadius:12, border:'1px solid rgba(255,255,255,0.07)', padding:20 }}>
          {!ascensorSel ? (
            <div>
              <label style={{ display:'block', fontWeight:700, fontSize:14, color:'#F4F7F5', marginBottom:8 }}>¿Qué ascensor?</label>
              <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
                placeholder="🔍 Comunidad y/o N° (ej: aromos 2)" autoFocus
                onKeyDown={e => { if (e.key === 'Enter' && resultados.length === 1) seleccionarAscensor(resultados[0]); }}
                style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #0f6e56', borderRadius:8,
                  fontSize:14, boxSizing:'border-box', fontFamily:'inherit', outline:'none',
                  background:'#0D1311', color:'#F4F7F5', marginBottom:12 }} />
              {!busqueda.trim() && recientes.length > 0 && (
                <>
                  <div style={{ fontSize:11, fontWeight:700, color:'#6B7C74', marginBottom:6 }}>RECIENTES</div>
                  <div style={{ border:'1px solid rgba(255,255,255,0.07)', borderRadius:8 }}>
                    {recientes.map(a => FilaAsc(a, 'r'))}
                  </div>
                </>
              )}
              {!busqueda.trim() && recientes.length === 0 && (
                <div style={{ padding:16, textAlign:'center', color:'#6B7C74', fontSize:13 }}>
                  {ascensores.length ? 'Escribe la comunidad o el N° de ascensor' : 'Cargando ascensores…'}
                </div>
              )}
              {busqueda.trim() && (
                <div style={{ maxHeight:360, overflowY:'auto', border:'1px solid rgba(255,255,255,0.07)', borderRadius:8 }}>
                  {resultados.map(a => FilaAsc(a, 'b'))}
                  {resultados.length === 0 && (
                    <div style={{ padding:24, textAlign:'center', color:'#6B7C74', fontSize:13 }}>
                      Sin resultados para "{busqueda}"
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={{ background:'#1a1a2e', borderRadius:10, padding:12, marginBottom:14,
                display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ flex:1 }}>
                  <div style={{ color:'white', fontWeight:700, fontSize:15 }}>{ascensorSel.comunidad}</div>
                  <div style={{ color:'rgba(255,255,255,0.6)', fontSize:12 }}>
                    Ascensor N° {ascensorSel.n_ascensor}{ascensorSel.torre ? ` · Torre ${ascensorSel.torre}` : ''}
                  </div>
                </div>
                <span onClick={() => setAscensorSel(null)}
                  style={{ color:'#4CAF50', fontSize:13, fontWeight:600, cursor:'pointer' }}>Cambiar</span>
              </div>

              <div onClick={() => setPersonaEncerrada(v => !v)}
                style={{ display:'flex', alignItems:'center', gap:10, padding:12, borderRadius:10, cursor:'pointer',
                  marginBottom:14, border:`2px solid ${personaEncerrada ? '#c62828' : 'rgba(255,255,255,0.08)'}`,
                  background: personaEncerrada ? '#1A0808' : 'transparent' }}>
                <span style={{ width:22, height:22, borderRadius:6, display:'inline-flex', alignItems:'center',
                  justifyContent:'center', border:`2px solid ${personaEncerrada ? '#c62828' : '#2E4A40'}`,
                  background: personaEncerrada ? '#c62828' : 'transparent', color:'white', fontSize:13, fontWeight:700 }}>
                  {personaEncerrada ? '✓' : ''}
                </span>
                <span style={{ fontWeight:700, fontSize:14, color: personaEncerrada ? '#EF4444' : '#C9D3CE' }}>
                  🆘 Hay persona encerrada
                </span>
              </div>

              <label style={{ display:'block', fontWeight:700, fontSize:14, color:'#F4F7F5', marginBottom:8 }}>¿Qué pasa?</label>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:10 }}>
                {PROBLEMAS_EMERG.map(p => (
                  <button key={p} onClick={() => setProblemaTipo(prev => prev === p ? null : p)}
                    style={{ padding:'8px 14px', borderRadius:20, cursor:'pointer', fontSize:13,
                      border:`1px solid ${problemaTipo === p ? '#c62828' : 'rgba(255,255,255,0.12)'}`,
                      background: problemaTipo === p ? '#2D0F0F' : '#161C19',
                      color: problemaTipo === p ? '#EF4444' : '#C9D3CE',
                      fontWeight: problemaTipo === p ? 700 : 500 }}>
                    {p}
                  </button>
                ))}
              </div>
              <textarea value={detalle} onChange={e => setDetalle(e.target.value)}
                placeholder={problemaTipo || personaEncerrada ? 'Detalle (opcional)' : 'Otro problema…'}
                style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #2E4A40', borderRadius:8,
                  fontSize:14, minHeight:56, resize:'vertical', fontFamily:'inherit', boxSizing:'border-box', marginBottom:14,
                  background:'#0D1311', color:'#F4F7F5' }} />

              {listo && (
                <div style={{ background:'#1a1a2e', borderRadius:10, padding:14, marginBottom:14 }}>
                  <div style={{ color:'rgba(255,255,255,0.5)', fontSize:11, marginBottom:4 }}>SE ENVIARÁ</div>
                  <div style={{ color:'white', fontWeight:700 }}>
                    {personaEncerrada ? '🆘 EMERGENCIA — PERSONA ENCERRADA' : '🚨 EMERGENCIA'}
                  </div>
                  <div style={{ color:'rgba(255,255,255,0.85)', fontSize:13, marginTop:4 }}>
                    {ascensorSel.comunidad} · Ascensor {ascensorSel.n_ascensor} · {problemaFinal} · {hora()}
                  </div>
                </div>
              )}

              <button disabled={!listo || enviandoEmerg}
                onPointerDown={holdStart} onPointerUp={holdEnd} onPointerLeave={holdEnd} onPointerCancel={holdEnd}
                onContextMenu={e => e.preventDefault()}
                style={{ position:'relative', overflow:'hidden', width:'100%', padding:'14px', border:'none',
                  borderRadius:10, cursor: listo ? 'pointer' : 'not-allowed', fontSize:15, fontWeight:700,
                  color:'white', userSelect:'none', touchAction:'none',
                  background:'#c62828', opacity: (!listo || enviandoEmerg) ? 0.5 : 1 }}>
                <span style={{ position:'absolute', left:0, top:0, bottom:0, width:`${holdPct}%`,
                  background:'#7f1414' }} />
                <span style={{ position:'relative' }}>
                  {enviandoEmerg ? 'Enviando…' : listo ? '🚨 Mantén presionado para enviar' : 'Elige el problema'}
                </span>
              </button>
            </div>
          )}
        </div>
      )}

      {subTab === 'mensaje' && puedeMensajes && (
        <div style={{ background:'#111714', borderRadius:12, padding:20, border:'1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontWeight:600, fontSize:13, color:'#8A9994', marginBottom:6 }}>Destinatarios</label>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {[['todos','👥 Todos'],['tecnicos','🔧 Técnicos'],['admins','⚙ Admins'],['individual','👤 Individual']].map(([id,lbl]) => (
                <button key={id} onClick={() => { setDestinatario(id); setDestinatariosIds([]); }}
                  style={{ padding:'7px 12px', borderRadius:8, cursor:'pointer', fontSize:13,
                    border:`1.5px solid ${destinatario===id?'#4CAF50':'#2E4A40'}`,
                    background: destinatario===id?'#12382D':'#0D1311',
                    color: destinatario===id?'#4CAF50':'#8A9994', fontWeight: destinatario===id?700:400 }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          {destinatario === 'individual' && (
            <div style={{ marginBottom:14, maxHeight:180, overflowY:'auto',
              border:'1px solid rgba(255,255,255,0.07)', borderRadius:8, padding:8 }}>
              {tecnicos.filter(t => t.activo).map(t => (
                <div key={t.id} onClick={() => setDestinatariosIds(prev =>
                  prev.includes(t.id) ? prev.filter(x=>x!==t.id) : [...prev,t.id])}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 10px',
                    borderRadius:8, cursor:'pointer', marginBottom:4,
                    background: destinatariosIds.includes(t.id)?'#12382D':'transparent' }}>
                  <div style={{ width:18, height:18, borderRadius:4, border:'2px solid',
                    borderColor: destinatariosIds.includes(t.id)?'#4CAF50':'#2E4A40',
                    background: destinatariosIds.includes(t.id)?'#4CAF50':'#0D1311',
                    display:'flex', alignItems:'center', justifyContent:'center' }}>
                    {destinatariosIds.includes(t.id) && <span style={{ color:'white', fontSize:11 }}>✓</span>}
                  </div>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13, color:'#F4F7F5' }}>{t.nombre}</div>
                    <div style={{ fontSize:11, color:'#6B7C74' }}>{t.rol}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginBottom:12 }}>
            <label style={{ display:'block', fontWeight:600, fontSize:13, color:'#8A9994', marginBottom:6 }}>Título</label>
            <input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Título del mensaje"
              style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #2E4A40', borderRadius:8,
                fontSize:14, fontFamily:'inherit', boxSizing:'border-box',
                background:'#0D1311', color:'#F4F7F5' }} />
          </div>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontWeight:600, fontSize:13, color:'#8A9994', marginBottom:6 }}>Mensaje</label>
            <textarea value={cuerpo} onChange={e => setCuerpo(e.target.value)} placeholder="Escribe el mensaje..."
              style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #2E4A40', borderRadius:8,
                fontSize:14, minHeight:70, resize:'vertical', fontFamily:'inherit', boxSizing:'border-box',
                background:'#0D1311', color:'#F4F7F5' }} />
          </div>
          <button onClick={enviarMensaje} disabled={enviandoMsg}
            style={{ width:'100%', padding:'13px', border:'none', borderRadius:10, cursor:'pointer',
              fontSize:15, fontWeight:700, color:'white',
              background: enviandoMsg?'#a5d6c7':'#0f6e56' }}>
            {enviandoMsg ? 'Enviando...' : '💬 Enviar mensaje'}
          </button>
        </div>
      )}

      {subTab === 'historial' && (
        <div style={{ background:'#111714', borderRadius:12, border:'1px solid rgba(255,255,255,0.07)', overflow:'hidden' }}>
          {historial.length === 0
            ? <div style={{ padding:24, textAlign:'center', color:'#6B7C74', fontSize:13 }}>Sin notificaciones enviadas</div>
            : historial.map((n,i) => {
              const t = tipoNotif(n);
              const cfg = TIPO_NOTIF_CFG[t];
              return (
              <div key={n.id} style={{ padding:'12px 16px',
                borderBottom: i<historial.length-1?'1px solid rgba(255,255,255,0.05)':'none',
                background: i%2===0?'#161C19':'#111714' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:700,
                    background: cfg.bg, color: cfg.fg }}>
                    {cfg.label}
                  </span>
                  <span style={{ fontSize:11, color:'#6B7C74' }}>
                    {n.hora} · {String(n.fecha).slice(0,10).split('-').reverse().join('/')}
                  </span>
                  <span style={{ fontSize:11, color:'#6B7C74', marginLeft:'auto' }}>
                    Por {n.enviado_por_nombre || 'Sistema'}
                  </span>
                </div>
                {(t === 'emergencia' || t === 'respuesta') && (
                  <div style={{ fontWeight:600, fontSize:14, color:'#F4F7F5' }}>{n.comunidad}</div>
                )}
                <div style={{ fontSize:13, color:'#8A9994', marginTop:2, whiteSpace:'pre-line' }}>
                  {detalleNotif(n, t)}
                </div>
              </div>
              );
            })
          }
        </div>
      )}
    </div>
  );
}

// ── Tab Pendientes de aprobación ───────────────────────────────────────────────
const ESTADO_EQUIPO_OPTS = ['Operativo','Operativo en observación','Fuera de servicio'];
const ESTADO_ITEM_STYLE  = { OK:{ bg:'#12382D',color:'#4CAF50' }, RO:{ bg:'#1C1200',color:'#F59E0B' }, RAI:{ bg:'#2D0F0F',color:'#EF4444' }, NA:{ bg:'#1A1F1D',color:'#6B7C74' } };

function ModalDetallePendiente({ id, onClose, onAprobado, onRechazado }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [guardando,setGuardando]= useState(false);
  const [accion,   setAccion]   = useState(null);
  const [edits,    setEdits]    = useState({});

  useEffect(() => {
    api.get(`/formularios/${id}`)
      .then(r => {
        setData(r.data);
        setEdits({
          nombre_cliente:          r.data.nombre_cliente          || '',
          estado_final_equipo:     r.data.estado_final_equipo     || '',
          resumen_visita:          r.data.resumen_visita          || '',
          observaciones_generales: r.data.observaciones_generales || '',
          info_interna:            r.data.info_interna            || '',
        });
      })
      .catch(() => alert('Error al cargar el formulario'))
      .finally(() => setLoading(false));
  }, [id]);

  const set = (k, v) => setEdits(p => ({ ...p, [k]: v }));

  const guardarCambios = async () => {
    setGuardando(true);
    try {
      await api.patch(`/admin/formularios/${id}`, edits);
    } catch (e) {
      alert('Error al guardar: ' + (e.response?.data?.error || e.message));
    }
    setGuardando(false);
  };

  const aprobar = async () => {
    if (!window.confirm('¿Guardar cambios y aprobar? Se enviará el correo al cliente.')) return;
    setAccion('aprobar');
    try {
      await guardarCambios();
      await api.post(`/admin/aprobar/${id}`);
      onAprobado();
    } catch (e) {
      alert('Error al aprobar: ' + (e.response?.data?.error || e.message));
    }
    setAccion(null);
  };

  const rechazar = async () => {
    if (!window.confirm('¿Rechazar este formulario? No se enviará al cliente.')) return;
    setAccion('rechazar');
    try {
      await api.post(`/admin/rechazar/${id}`);
      onRechazado();
    } catch (e) {
      alert('Error al rechazar: ' + (e.response?.data?.error || e.message));
    }
    setAccion(null);
  };

  const inputSt = { width:'100%', padding:'9px 11px', border:'1.5px solid #2E4A40',
    borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box',
    background:'#0D1311', color:'#F4F7F5' };
  const labelSt = { display:'block', fontSize:11, fontWeight:700, color:'#8A9994',
    textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:5 };

  const secciones = {};
  (data?.checklist || []).forEach(item => {
    if (!secciones[item.seccion]) secciones[item.seccion] = [];
    secciones[item.seccion].push(item);
  });

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)',
                  zIndex:2000, overflowY:'auto', padding:'24px 16px' }}>
      <div style={{ background:'#111714', borderRadius:16, maxWidth:680,
                    margin:'0 auto', overflow:'hidden',
                    border:'1px solid rgba(255,255,255,0.07)' }}>

        {/* Header */}
        <div style={{ background:'#0f6e56', padding:'16px 20px',
                      display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ color:'white', fontWeight:800, fontSize:16 }}>
              {loading ? 'Cargando...' : data?.folio}
              {data && (
                <span style={{ marginLeft:10, fontSize:12, fontWeight:600,
                  background:'rgba(255,255,255,0.2)', borderRadius:6, padding:'2px 8px' }}>
                  {data.tipo === 'mantenimiento' ? '🔧 Mantenimiento' : '📋 Visita'}
                </span>
              )}
            </div>
            {data && (
              <div style={{ color:'rgba(255,255,255,0.8)', fontSize:12, marginTop:2 }}>
                {data.comunidad} · Asc. {data.n_ascensor}{data.torre ? ` · Torre ${data.torre}` : ''}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none',
            color:'white', fontSize:24, cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        {loading ? (
          <div style={{ padding:60, textAlign:'center', color:'#6B7C74' }}>Cargando...</div>
        ) : data && (
          <div style={{ padding:'20px' }}>

            {/* Datos de solo lectura */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10,
                          fontSize:13, marginBottom:20, padding:'12px 14px',
                          background:'#0D1311', borderRadius:10,
                          border:'1px solid rgba(255,255,255,0.06)' }}>
              <div><span style={{ color:'#6B7C74' }}>Técnico: </span><strong style={{ color:'#F4F7F5' }}>{data.tecnico_nombre}</strong></div>
              <div><span style={{ color:'#6B7C74' }}>Fecha visita: </span>
                <strong style={{ color:'#F4F7F5' }}>{data.fecha_visita ? String(data.fecha_visita).slice(0,10) : '—'}</strong></div>
              {data.hora_ingreso && (
                <div><span style={{ color:'#6B7C74' }}>Horario: </span>
                  <strong style={{ color:'#F4F7F5' }}>{data.hora_ingreso}{data.hora_salida ? ` - ${data.hora_salida}` : ''}</strong></div>
              )}
              {data.tipo_visita && (
                <div><span style={{ color:'#6B7C74' }}>Tipo visita: </span><strong style={{ color:'#F4F7F5' }}>{data.tipo_visita}</strong></div>
              )}
              {data.presupuesto_numero && (
                <div><span style={{ color:'#6B7C74' }}>Presupuesto: </span>
                  <strong style={{ color:'#4CAF50' }}>{data.presupuesto_numero}{data.presupuesto_nombre ? ` — ${data.presupuesto_nombre}` : ''}</strong></div>
              )}
            </div>

            {/* Campos editables */}
            <div style={{ marginBottom:16 }}>
              <label style={labelSt}>Nombre cliente</label>
              <input style={inputSt} value={edits.nombre_cliente}
                onChange={e => set('nombre_cliente', e.target.value)}
                placeholder="Nombre del cliente o administrador" />
            </div>

            <div style={{ marginBottom:16 }}>
              <label style={labelSt}>Estado del equipo</label>
              <select style={inputSt} value={edits.estado_final_equipo}
                onChange={e => set('estado_final_equipo', e.target.value)}>
                <option value="">— Sin especificar —</option>
                {ESTADO_EQUIPO_OPTS.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>

            <div style={{ marginBottom:16 }}>
              <label style={labelSt}>
                {data.tipo === 'mantenimiento' ? 'Observaciones generales' : 'Resumen de la visita'}
              </label>
              <textarea rows={4} style={{ ...inputSt, resize:'vertical' }}
                value={data.tipo === 'mantenimiento' ? edits.observaciones_generales : edits.resumen_visita}
                onChange={e => set(
                  data.tipo === 'mantenimiento' ? 'observaciones_generales' : 'resumen_visita',
                  e.target.value
                )}
                placeholder="Texto del informe..." />
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={labelSt}>Nota interna (no va en el PDF)</label>
              <textarea rows={2} style={{ ...inputSt, resize:'vertical' }}
                value={edits.info_interna}
                onChange={e => set('info_interna', e.target.value)}
                placeholder="Notas internas opcionales..." />
            </div>

            {/* Checklist (solo lectura) */}
            {data.tipo === 'mantenimiento' && Object.keys(secciones).length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#8A9994',
                  textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:10 }}>
                  Checklist
                </div>
                {Object.entries(secciones).map(([seccion, items]) => (
                  <div key={seccion} style={{ marginBottom:12 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:'#6B7C74',
                      borderBottom:'1px solid rgba(255,255,255,0.07)', paddingBottom:4, marginBottom:6 }}>
                      {seccion}
                    </div>
                    {items.map((item, i) => {
                      const est = ESTADO_ITEM_STYLE[item.estado] || ESTADO_ITEM_STYLE.NA;
                      return (
                        <div key={i}>
                          <div style={{ display:'flex', justifyContent:'space-between',
                            alignItems:'center', padding:'4px 0',
                            borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:12 }}>
                            <span style={{ color:'#C9D3CE', flex:1, marginRight:8 }}>{item.descripcion}</span>
                            <span style={{ background:est.bg, color:est.color,
                              borderRadius:6, padding:'1px 8px',
                              fontSize:11, fontWeight:800, whiteSpace:'nowrap' }}>
                              {item.estado || 'N/A'}
                            </span>
                          </div>
                          {item.observacion && (
                            <div style={{ fontSize:11, color:'#e65100', fontStyle:'italic',
                              paddingLeft:8, borderLeft:'2px solid #e65100', margin:'2px 0 4px' }}>
                              {item.observacion}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            {/* Firmas */}
            {(data.firma_tecnico || data.firma_cliente) && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#8A9994',
                  textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:10 }}>Firmas</div>
                <div style={{ display:'flex', gap:16 }}>
                  {data.firma_tecnico && (
                    <div style={{ flex:1, textAlign:'center' }}>
                      <img src={data.firma_tecnico} alt="Firma técnico"
                        style={{ width:'100%', maxHeight:100, objectFit:'contain',
                          border:'1px solid rgba(255,255,255,0.08)', borderRadius:8 }} />
                      <div style={{ fontSize:11, color:'#6B7C74', marginTop:4 }}>Técnico</div>
                    </div>
                  )}
                  {data.firma_cliente && (
                    <div style={{ flex:1, textAlign:'center' }}>
                      <img src={data.firma_cliente} alt="Firma cliente"
                        style={{ width:'100%', maxHeight:100, objectFit:'contain',
                          border:'1px solid rgba(255,255,255,0.08)', borderRadius:8 }} />
                      <div style={{ fontSize:11, color:'#6B7C74', marginTop:4 }}>Cliente</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Fotos */}
            {data.fotos && data.fotos.length > 0 && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#8A9994',
                  textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:10 }}>
                  Fotografías ({data.fotos.length})
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {data.fotos.map((foto, i) => (
                    <img key={i} src={foto.url || foto.dataURL} alt={`Foto ${i+1}`}
                      style={{ width:110, height:90, objectFit:'cover',
                        borderRadius:8, cursor:'pointer' }}
                      onClick={() => window.open(foto.url || foto.dataURL)} />
                  ))}
                </div>
              </div>
            )}

            {/* Botones de acción */}
            <div style={{ display:'flex', gap:10, paddingTop:16,
                          borderTop:'1px solid rgba(255,255,255,0.08)', flexWrap:'wrap' }}>
              <button onClick={guardarCambios} disabled={guardando}
                style={{ padding:'10px 18px', background:'#161C19', color:'#C9D3CE',
                  border:'1.5px solid rgba(255,255,255,0.12)', borderRadius:9, fontSize:13,
                  fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                {guardando ? 'Guardando...' : '💾 Guardar cambios'}
              </button>
              <div style={{ flex:1 }} />
              <button onClick={rechazar} disabled={!!accion}
                style={{ padding:'10px 20px', background:'#2D0F0F', color:'#EF4444',
                  border:'1.5px solid #EF4444', borderRadius:9, fontSize:13,
                  fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                {accion === 'rechazar' ? 'Rechazando...' : '✗ Rechazar'}
              </button>
              <button onClick={aprobar} disabled={!!accion}
                style={{ padding:'10px 20px', background:'#0f6e56', color:'white',
                  border:'none', borderRadius:9, fontSize:13,
                  fontWeight:700, cursor:'pointer' }}>
                {accion === 'aprobar' ? 'Aprobando...' : '✓ Aprobar y enviar'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TabPendientes() {
  const [items,         setItems]         = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [detalleId,     setDetalleId]     = useState(null);
  const [comunidades,   setComunidades]   = useState([]);
  const [verConfig,     setVerConfig]     = useState(false);
  const [toggling,      setToggling]      = useState(null); // nombre comunidad en proceso
  const [togglingTodas, setTogglingTodas] = useState(false);
  const [busqCom,       setBusqCom]       = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [pend, ascs] = await Promise.all([
        api.get('/admin/pendientes'),
        api.get('/admin/ascensores'),
      ]);
      setItems(pend.data.items || []);
      // Agrupar por comunidad — envio_directo viene del primer ascensor de cada comunidad
      const map = {};
      (ascs.data || []).forEach(a => {
        if (!map[a.comunidad]) map[a.comunidad] = { nombre: a.comunidad, envio_directo: a.envio_directo };
      });
      setComunidades(Object.values(map).sort((a,b) => a.nombre.localeCompare(b.nombre)));
    } catch (e) {
      console.error('Error cargando pendientes:', e.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const toggleTodasLasComunidades = async (envioDirecto) => {
    if (!window.confirm(
      envioDirecto
        ? '¿Activar envío directo para TODAS las comunidades? Los correos se enviarán sin revisión previa.'
        : '¿Activar revisión previa para TODAS las comunidades? Todos los formularios quedarán retenidos.'
    )) return;
    setTogglingTodas(true);
    try {
      await api.patch('/admin/ascensores/envio-directo-todas', { envio_directo: envioDirecto });
      setComunidades(prev => prev.map(c => ({ ...c, envio_directo: envioDirecto })));
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
    setTogglingTodas(false);
  };

  const toggleEnvioDirecto = async (comunidad, valor) => {
    setToggling(comunidad.nombre);
    try {
      await api.patch(
        `/admin/ascensores/comunidad/${encodeURIComponent(comunidad.nombre)}/envio-directo`,
        { envio_directo: valor }
      );
      setComunidades(prev => prev.map(c =>
        c.nombre === comunidad.nombre ? { ...c, envio_directo: valor } : c
      ));
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
    setToggling(null);
  };

  const TIPO_LABEL = { mantenimiento: 'Mantenimiento', visita: 'Visita' };

  const comFiltradas = busqCom
    ? comunidades.filter(c => c.nombre.toLowerCase().includes(busqCom.toLowerCase()))
    : comunidades;

  const sinEnvioDirecto = comunidades.filter(c => !c.envio_directo).length;

  return (
    <div>
      {detalleId && (
        <ModalDetallePendiente
          id={detalleId}
          onClose={() => setDetalleId(null)}
          onAprobado={() => { setDetalleId(null); cargar(); }}
          onRechazado={() => { setDetalleId(null); cargar(); }}
        />
      )}

      {/* ── Configuración de comunidades ── */}
      <div style={{ marginBottom:16 }}>
        <button onClick={() => setVerConfig(v => !v)} style={{
          display:'flex', alignItems:'center', gap:10, width:'100%',
          background:'#161C19', border:'1.5px solid rgba(255,255,255,0.08)',
          borderRadius:10, padding:'12px 16px', cursor:'pointer', textAlign:'left',
        }}>
          <span style={{ fontSize:16 }}>{verConfig ? '▼' : '▶'}</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:14, fontWeight:700, color:'#F4F7F5' }}>
              Configuración — ¿qué comunidades requieren revisión previa?
            </div>
            <div style={{ fontSize:12, color:'#8A9994', marginTop:2 }}>
              {sinEnvioDirecto > 0
                ? `${sinEnvioDirecto} comunidad${sinEnvioDirecto > 1 ? 'es' : ''} con revisión activada`
                : 'Ninguna comunidad requiere revisión — los correos se envían directo'}
            </div>
          </div>
          <span style={{
            background: sinEnvioDirecto > 0 ? '#1C1200' : '#12382D',
            color:      sinEnvioDirecto > 0 ? '#F59E0B' : '#4CAF50',
            borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:700, flexShrink:0,
          }}>
            {sinEnvioDirecto > 0 ? `${sinEnvioDirecto} en revisión` : 'Envío directo'}
          </span>
        </button>

        {verConfig && (
          <div style={{ background:'#0D1311', border:'1.5px solid rgba(255,255,255,0.07)',
                        borderTop:'none', borderRadius:'0 0 10px 10px', padding:14 }}>
            <p style={{ margin:'0 0 10px', fontSize:12, color:'#6B7C74', lineHeight:1.5 }}>
              Cuando <strong style={{ color:'#C9D3CE' }}>Revisión previa</strong> está activada, los formularios
              de esa comunidad quedan retenidos aquí hasta que los apruebes manualmente.
              Con <strong style={{ color:'#C9D3CE' }}>Envío directo</strong>, el correo se envía al cliente automáticamente.
            </p>
            <div style={{ display:'flex', gap:8, marginBottom:10 }}>
              <button
                disabled={togglingTodas}
                onClick={() => toggleTodasLasComunidades(false)}
                style={{
                  flex:1, padding:'8px 12px', borderRadius:8, fontSize:12, fontWeight:700,
                  cursor: togglingTodas ? 'default' : 'pointer',
                  background:'#1C1200', color:'#F59E0B',
                  border:'1.5px solid rgba(245,158,11,0.4)',
                  opacity: togglingTodas ? 0.6 : 1,
                }}
              >
                {togglingTodas ? '...' : '⏸ Revisión para todas'}
              </button>
              <button
                disabled={togglingTodas}
                onClick={() => toggleTodasLasComunidades(true)}
                style={{
                  flex:1, padding:'8px 12px', borderRadius:8, fontSize:12, fontWeight:700,
                  cursor: togglingTodas ? 'default' : 'pointer',
                  background:'#12382D', color:'#4CAF50',
                  border:'1.5px solid rgba(76,175,80,0.4)',
                  opacity: togglingTodas ? 0.6 : 1,
                }}
              >
                {togglingTodas ? '...' : '✅ Envío directo para todas'}
              </button>
            </div>
            <input
              value={busqCom}
              onChange={e => setBusqCom(e.target.value)}
              placeholder="Buscar comunidad..."
              style={{ width:'100%', padding:'8px 12px', border:'1.5px solid #2E4A40',
                       borderRadius:8, fontSize:13, marginBottom:10, boxSizing:'border-box',
                       background:'#0B0F0D', color:'#F4F7F5' }}
            />
            <div style={{ maxHeight:320, overflowY:'auto', display:'flex', flexDirection:'column', gap:6 }}>
              {comFiltradas.map(c => {
                const revisión = !c.envio_directo;
                const procesando = toggling === c.nombre;
                return (
                  <div key={c.nombre} style={{
                    display:'flex', alignItems:'center', gap:12,
                    padding:'10px 12px', borderRadius:8,
                    background: revisión ? '#1C1200' : '#111714',
                    border: `1px solid ${revisión ? 'rgba(245,158,11,0.3)' : 'rgba(255,255,255,0.05)'}`,
                  }}>
                    <div style={{ flex:1, fontSize:13, fontWeight:600, color:'#F4F7F5' }}>
                      {c.nombre}
                    </div>
                    <span style={{
                      fontSize:11, fontWeight:700,
                      color: revisión ? '#F59E0B' : '#4CAF50',
                      minWidth:90, textAlign:'right',
                    }}>
                      {revisión ? '⏸ Revisión previa' : '✅ Envío directo'}
                    </span>
                    <button
                      disabled={procesando}
                      onClick={() => toggleEnvioDirecto(c, revisión)}
                      style={{
                        padding:'5px 12px', borderRadius:7, fontSize:12, fontWeight:600,
                        cursor: procesando ? 'default' : 'pointer', flexShrink:0,
                        background: revisión ? '#0f6e56' : '#1C1200',
                        color:      revisión ? 'white'   : '#F59E0B',
                        border:     revisión ? 'none'    : '1px solid rgba(245,158,11,0.4)',
                        opacity:    procesando ? 0.6 : 1,
                      }}
                    >
                      {procesando ? '...' : revisión ? 'Activar envío directo' : 'Activar revisión'}
                    </button>
                  </div>
                );
              })}
              {comFiltradas.length === 0 && (
                <p style={{ color:'#6B7C74', fontSize:13, textAlign:'center', padding:16 }}>
                  Sin resultados
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Lista de formularios pendientes ── */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <p style={{ margin:0, color:'#8A9994', fontSize:13 }}>
          Formularios retenidos para revisión antes de enviarse al cliente.
        </p>
        <Btn small onClick={cargar}>{loading ? 'Cargando...' : '🔄 Actualizar'}</Btn>
      </div>

      {!loading && items.length === 0 && (
        <div style={{ textAlign:'center', padding:'40px 20px', background:'#161C19',
                      borderRadius:12, border:'2px dashed rgba(255,255,255,0.08)' }}>
          <p style={{ fontSize:24, marginBottom:8 }}>✅</p>
          <p style={{ color:'#6B7C74', fontSize:14 }}>Sin documentos pendientes de aprobación</p>
        </div>
      )}

      {items.length > 0 && (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#0f6e56', color:'white' }}>
                {['Folio','Tipo','Comunidad','Técnico','Fecha',''].map(h => (
                  <th key={h} style={{ padding:'10px 12px', textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={item.id} style={{ background: i%2===0 ? '#161C19' : '#111714' }}>
                  <td style={{ padding:'8px 12px', fontWeight:600, color:'#0f6e56' }}>{item.folio}</td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{
                      background: item.tipo === 'mantenimiento' ? '#0D1E2E' : '#1C1200',
                      color:      item.tipo === 'mantenimiento' ? '#38BDF8' : '#F59E0B',
                      padding:'2px 8px', borderRadius:6, fontSize:11, fontWeight:700,
                    }}>
                      {TIPO_LABEL[item.tipo] || item.tipo}
                    </span>
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    {item.comunidad}
                    {item.n_ascensor && <span style={{ color:'#6B7C74', marginLeft:6 }}>Asc. {item.n_ascensor}</span>}
                    {item.torre && <span style={{ color:'#6B7C74', marginLeft:4 }}>Torre {item.torre}</span>}
                  </td>
                  <td style={{ padding:'8px 12px' }}>{item.tecnico_nombre}</td>
                  <td style={{ padding:'8px 12px', color:'#6B7C74' }}>
                    {item.fecha_visita ? String(item.fecha_visita).slice(0,10) : '—'}
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    <Btn small onClick={() => setDetalleId(item.id)}>
                      👁 Revisar
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tab Rendiciones ────────────────────────────────────────────────────────────
const CATEGORIAS_GASTO = [
  'Combustible/Bencina','Peaje','Estacionamiento',
  'Materiales','Herramientas','Alimentación','Transporte','Otros',
];

const ESTADO_REND_COLOR = {
  borrador:  { bg:'#f5f5f5',  text:'#888' },
  enviada:   { bg:'#e3f2fd',  text:'#1565c0' },
  aprobada:  { bg:'#e8f5f1',  text:'#0f6e56' },
  rechazada: { bg:'#ffebee',  text:'#c62828' },
  pagada:    { bg:'#e8eaf6',  text:'#283593' },
};

function formatCLP(n) {
  return '$' + Number(n || 0).toLocaleString('es-CL');
}

function formatPeriodo(dateStr) {
  const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio',
                 'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const d = new Date(dateStr + 'T00:00:00');
  return `${meses[d.getMonth()]} ${d.getFullYear()}`;
}

function TabRendiciones({ soloLectura }) {
  const [rendiciones, setRendiciones] = useState([]);
  const [tecnicos,    setTecnicos]    = useState([]);
  const [filtros,     setFiltros]     = useState({ estado:'', tecnico_id:'', periodo:'' });
  const [detalle,     setDetalle]     = useState(null); // { rendicion, items }
  const [revisar,     setRevisar]     = useState(null); // { id, accion: 'aprobada'|'rechazada' }
  const [comentario,  setComentario]  = useState('');
  const [loading,     setLoading]     = useState(false);
  const [fotoVista,   setFotoVista]   = useState(null); // dataURL de boleta en visor

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.estado)     params.estado     = filtros.estado;
      if (filtros.tecnico_id) params.tecnico_id = filtros.tecnico_id;
      if (filtros.periodo)    params.periodo    = filtros.periodo;
      const r = await api.get('/rendiciones', { params });
      setRendiciones(r.data);
    } catch(e) {
      console.error('Error cargando rendiciones:', e.message);
    }
    setLoading(false);
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    api.get('/admin/tecnicos').then(r => setTecnicos(r.data)).catch(() => {});
  }, []);

  const verDetalle = async (rend) => {
    try {
      const r = await api.get(`/rendiciones/${rend.id}`);
      setDetalle(r.data);
    } catch(e) {
      alert('Error cargando detalle: ' + (e.response?.data?.error || e.message));
    }
  };

  const confirmarRevision = async () => {
    if (!revisar) return;
    if (revisar.accion === 'rechazada' && !comentario.trim()) {
      alert('Ingresa un comentario para indicar el motivo del rechazo'); return;
    }
    try {
      await api.post(`/rendiciones/${revisar.rendicion_id}/revisar`, {
        estado: revisar.accion,
        comentario: comentario.trim() || null,
      });
      setRevisar(null);
      setComentario('');
      setDetalle(null);
      cargar();
    } catch(e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const marcarPagada = async () => {
    if (!detalle) return;
    if (!window.confirm(`¿Marcar la rendición #${detalle.id} como pagada? Esta acción es final.`)) return;
    try {
      await api.post(`/rendiciones/${detalle.id}/pagar`);
      setDetalle(null);
      cargar();
    } catch(e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const setF = (k, v) => setFiltros(p => ({ ...p, [k]: v }));

  const selectSt = {
    padding:'9px 12px', border:'1.5px solid #2E4A40', borderRadius:8,
    fontSize:13, fontFamily:'inherit', background:'#0D1311', color:'#F4F7F5',
  };

  return (
    <div>
      {/* Filtros */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <select value={filtros.tecnico_id} onChange={e => setF('tecnico_id', e.target.value)}
          style={{ ...selectSt, minWidth:160 }}>
          <option value="">Todos los técnicos</option>
          {tecnicos.filter(t => t.activo).map(t => (
            <option key={t.id} value={t.id}>{t.nombre}</option>
          ))}
        </select>
        <select value={filtros.estado} onChange={e => setF('estado', e.target.value)}
          style={{ ...selectSt, minWidth:140 }}>
          <option value="">Todos los estados</option>
          <option value="borrador">Borrador</option>
          <option value="enviada">Enviada</option>
          <option value="aprobada">Aprobada</option>
          <option value="rechazada">Rechazada</option>
          <option value="pagada">Pagada</option>
        </select>
        <input type="month" value={filtros.periodo}
          onChange={e => setF('periodo', e.target.value)}
          style={{ ...selectSt, minWidth:140 }} />
        <Btn small onClick={cargar}>{loading ? 'Cargando...' : '🔄 Actualizar'}</Btn>
      </div>

      {/* Tabla */}
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'#0f6e56', color:'white' }}>
              {['ID','Técnico','Período','Ítems','Total','Estado','Enviada',''].map(h => (
                <th key={h} style={{ padding:'10px 12px', textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rendiciones.map((r, i) => {
              const ec = ESTADO_REND_COLOR[r.estado] || ESTADO_REND_COLOR.borrador;
              return (
                <tr key={r.id} style={{ background: i%2===0 ? '#161C19' : '#111714' }}>
                  <td style={{ padding:'8px 12px', color:'#aaa', fontSize:11 }}>#{r.id}</td>
                  <td style={{ padding:'8px 12px', fontWeight:600 }}>{r.tecnico_nombre}</td>
                  <td style={{ padding:'8px 12px' }}>{formatPeriodo(r.periodo)}</td>
                  <td style={{ padding:'8px 12px', textAlign:'center' }}>{r.n_items}</td>
                  <td style={{ padding:'8px 12px', fontWeight:700, color:'#0f6e56' }}>
                    {formatCLP(r.total_clp)}
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700,
                      background: ec.bg, color: ec.text }}>
                      {r.estado}
                    </span>
                  </td>
                  <td style={{ padding:'8px 12px', color:'#aaa', fontSize:11 }}>
                    {r.revisado_at
                      ? String(r.revisado_at).slice(0,10)
                      : r.estado === 'enviada' ? '⏳ Pendiente' : '—'}
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    <Btn small onClick={() => verDetalle(r)}>Ver →</Btn>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rendiciones.length === 0 && !loading && (
          <p style={{ textAlign:'center', color:'#aaa', padding:32 }}>
            Sin rendiciones para los filtros seleccionados
          </p>
        )}
      </div>

      {/* Modal detalle */}
      {detalle && (
        <Modal title={`Rendición #${detalle.id} — ${formatPeriodo(detalle.periodo)}`}
          onClose={() => setDetalle(null)}>
          {/* Info cabecera */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10,
                        marginBottom:16, fontSize:13 }}>
            <div>
              <span style={{ color:'#6B7C74' }}>Técnico: </span>
              <strong style={{ color:'#F4F7F5' }}>{detalle.tecnico_nombre}</strong>
            </div>
            <div>
              <span style={{ color:'#6B7C74' }}>Estado: </span>
              <span style={{ fontWeight:700,
                color: ESTADO_REND_COLOR[detalle.estado]?.text || '#8A9994' }}>
                {detalle.estado}
              </span>
            </div>
            <div>
              <span style={{ color:'#6B7C74' }}>Total: </span>
              <strong style={{ color:'#4CAF50' }}>{formatCLP(detalle.total_clp)}</strong>
            </div>
            {detalle.revisado_por_nombre && (
              <div>
                <span style={{ color:'#6B7C74' }}>Revisado por: </span>
                <strong style={{ color:'#F4F7F5' }}>{detalle.revisado_por_nombre}</strong>
              </div>
            )}
          </div>

          {detalle.descripcion && (
            <div style={{ marginBottom:12, padding:'8px 12px', background:'#0D1311',
                          borderRadius:8, fontSize:13, color:'#C9D3CE',
                          border:'1px solid rgba(255,255,255,0.06)' }}>
              {detalle.descripcion}
            </div>
          )}

          {/* Comentario del admin (si existe) */}
          {detalle.comentario && (
            <div style={{ marginBottom:16, padding:'10px 12px', borderRadius:8, fontSize:13,
              background: detalle.estado==='rechazada' ? '#2D0F0F' : '#12382D',
              color:      detalle.estado==='rechazada' ? '#EF4444' : '#4CAF50',
              borderLeft: `4px solid ${detalle.estado==='rechazada'?'#EF4444':'#4CAF50'}` }}>
              <strong>Comentario admin:</strong> {detalle.comentario}
            </div>
          )}

          {/* Ítems */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontWeight:700, fontSize:13, color:'#8A9994',
                          marginBottom:8, borderBottom:'1px solid rgba(255,255,255,0.08)', paddingBottom:6 }}>
              Gastos ({detalle.items?.length || 0})
            </div>
            {(!detalle.items || detalle.items.length === 0) ? (
              <p style={{ color:'#6B7C74', fontSize:12, textAlign:'center', padding:'12px 0' }}>
                Sin ítems
              </p>
            ) : detalle.items.map((item, idx) => (
              <div key={item.id} style={{ display:'flex', alignItems:'flex-start', gap:10,
                padding:'8px 0', borderBottom: idx < detalle.items.length-1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:13, color:'#F4F7F5' }}>
                    {item.descripcion}
                  </div>
                  <div style={{ fontSize:11, color:'#6B7C74', marginTop:2 }}>{item.categoria}</div>
                </div>
                {(item.fotos && item.fotos.length > 0) && (
                  <div style={{ display:'flex', gap:6, flexShrink:0, flexWrap:'wrap',
                                maxWidth:170, justifyContent:'flex-end' }}>
                    {item.fotos.map((f, idx) => (
                      <div key={f.id || idx} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
                        <img src={f.foto} alt="boleta"
                          onClick={() => setFotoVista(f.foto)}
                          style={{ width:44, height:44, objectFit:'cover', borderRadius:6,
                                   border:'1px solid rgba(255,255,255,0.12)', cursor:'pointer' }} />
                        {f.drive_url && (
                          <a href={f.drive_url} target="_blank" rel="noreferrer"
                            style={{ fontSize:10, color:'#60A5FA' }}>Drive</a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ fontWeight:700, color:'#4CAF50', whiteSpace:'nowrap', fontSize:13 }}>
                  {formatCLP(item.monto_clp)}
                </div>
              </div>
            ))}
          </div>

          {/* Total */}
          <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16,
                        padding:'10px 0', borderTop:'2px solid #0f6e56' }}>
            <strong style={{ fontSize:15 }}>Total: {formatCLP(detalle.total_clp)}</strong>
          </div>

          {/* Acciones de revisión (solo si está enviada) */}
          {detalle.estado === 'enviada' && !soloLectura && (
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <Btn outline color='#c62828'
                onClick={() => { setRevisar({ rendicion_id: detalle.id, accion:'rechazada' }); setComentario(''); }}>
                ✗ Rechazar
              </Btn>
              <Btn color='#0f6e56'
                onClick={() => { setRevisar({ rendicion_id: detalle.id, accion:'aprobada' }); setComentario(''); }}>
                ✓ Aprobar
              </Btn>
            </div>
          )}

          {/* Marcar como pagada (solo si está aprobada) */}
          {detalle.estado === 'aprobada' && !soloLectura && (
            <div style={{ marginTop:8 }}>
              <Btn color='#283593' onClick={marcarPagada}>
                💳 Marcar como pagada
              </Btn>
            </div>
          )}

          {detalle.estado === 'pagada' && (
            <div style={{ marginTop:8, padding:'10px 12px', background:'#0D1E2E',
                          borderRadius:8, fontSize:13, color:'#60A5FA', fontWeight:600,
                          border:'1px solid rgba(96,165,250,0.2)' }}>
              ✅ Rendición pagada
            </div>
          )}

          <div style={{ display:'flex', justifyContent:'flex-end', marginTop:8 }}>
            <Btn small outline color='#888' onClick={() => setDetalle(null)}>Cerrar</Btn>
          </div>
        </Modal>
      )}

      {/* Modal confirmar revisión */}
      {revisar && (
        <Modal
          title={revisar.accion === 'aprobada' ? '✓ Aprobar rendición' : '✗ Rechazar rendición'}
          onClose={() => setRevisar(null)}>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:600,
                            color:'#8A9994', marginBottom:6 }}>
              Comentario {revisar.accion === 'rechazada' ? '(obligatorio)' : '(opcional)'}
            </label>
            <textarea value={comentario} onChange={e => setComentario(e.target.value)}
              placeholder={revisar.accion === 'rechazada'
                ? 'Indica el motivo del rechazo...'
                : 'Observaciones opcionales...'}
              style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #2E4A40',
                       borderRadius:8, fontSize:13, minHeight:80, resize:'vertical',
                       fontFamily:'inherit', boxSizing:'border-box',
                       background:'#0D1311', color:'#F4F7F5' }} />
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <Btn outline color='#888' onClick={() => setRevisar(null)}>Cancelar</Btn>
            <Btn onClick={confirmarRevision}
              color={revisar.accion === 'aprobada' ? '#0f6e56' : '#c62828'}>
              {revisar.accion === 'aprobada' ? '✓ Confirmar aprobación' : '✗ Confirmar rechazo'}
            </Btn>
          </div>
        </Modal>
      )}

      {/* Visor de boleta (imagen inline, no navega a data: URL) */}
      {fotoVista && (
        <div onClick={() => setFotoVista(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1000,
                   display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <img src={fotoVista} alt="boleta"
            style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain', borderRadius:8 }} />
          <button onClick={() => setFotoVista(null)}
            style={{ position:'absolute', top:16, right:20, background:'rgba(0,0,0,0.6)',
                     color:'#fff', border:'none', borderRadius:20, width:40, height:40,
                     fontSize:22, cursor:'pointer', lineHeight:1 }}>
            ×
          </button>
        </div>
      )}
    </div>
  );
}

// ── Helper fecha legible ───────────────────────────────────────────────────────
function formatFechaLabel(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const dias  = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
  const meses = ['enero','febrero','marzo','abril','mayo','junio',
                 'julio','agosto','septiembre','octubre','noviembre','diciembre'];
  return `${dias[d.getDay()]} ${d.getDate()} de ${meses[d.getMonth()]}`;
}

// ── Tab Planificación ──────────────────────────────────────────────────────────
// La planificación vive en su propia página (/planificacion), con el calendario
// mensual clicable. Mantener aquí una segunda implementación fue justamente lo
// que dejó el botón "Publicar" en solo una de las dos vistas.
function TabPlanificacion() {
  const navigate = useNavigate();
  return (
    <div style={{ padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>🗓</div>
      <p style={{ color: '#C9D3CE', fontSize: 14, margin: '0 0 6px', fontWeight: 700 }}>
        La planificación se edita en su propia pantalla
      </p>
      <p style={{ color: '#8A9994', fontSize: 12, margin: '0 0 18px', lineHeight: 1.6 }}>
        Calendario mensual clicable, asignación por comunidad y publicación a los técnicos.
      </p>
      <button
        onClick={() => navigate('/planificacion')}
        style={{
          background: '#0f6e56', border: 'none', borderRadius: 10,
          padding: '10px 22px', color: 'white', fontWeight: 700,
          fontSize: 14, cursor: 'pointer',
        }}
      >
        Abrir planificación →
      </button>
    </div>
  );
}

// ── Tab Presupuestos ───────────────────────────────────────────────────────────
function TabPresupuestos() {
  return (
    <div style={{ padding: 20 }}>
      <ListaPresupuestos />
    </div>
  );
}

// ── Tab Webhooks — pruebas de integración n8n (solo superadmin) ───────────────
const EVENTOS_WEBHOOK = [
  { id: 'test_conexion',             label: 'Test conexión',              desc: 'Verifica que n8n recibe el webhook correctamente' },
  { id: 'visita_tecnica_completada', label: 'Visita técnica completada',  desc: 'Simula el registro de una visita técnica' },
  { id: 'mantenimiento_registrado',  label: 'Mantenimiento registrado',   desc: 'Simula el envío de un formulario de mantenimiento' },
  { id: 'emergencia_activada',       label: 'Emergencia activada',        desc: 'Simula el disparo de una alerta de emergencia' },
  { id: 'emergencia_resuelta',       label: 'Emergencia resuelta',        desc: 'Simula que un técnico toma la emergencia' },
  { id: 'rendicion_enviada',         label: 'Rendición enviada',          desc: 'Simula el envío de una rendición de gastos' },
  { id: 'presupuesto_enviado',       label: 'Presupuesto enviado',        desc: 'Simula el envío de un presupuesto al cliente (dispara integración contable)' },
  { id: 'comprobante_generado',      label: 'Comprobante generado',       desc: 'Simula la generación de un PDF de comprobante' },
];

// ── Tab Importar Históricos ────────────────────────────────────────────────────
function TabImportarHistorico() {
  const fileRef                     = React.useRef();
  const [preview,   setPreview]     = useState(null);
  const [loading,   setLoading]     = useState(false);
  const [resultado, setResultado]   = useState(null);

  const descargarPlantilla = async () => {
    try {
      // Obtener token de corta duración — evita el problema de blob URL en Safari
      const tokenRes = await api.get('/admin/formularios/plantilla/token');
      const dlToken  = tokenRes.data.token;
      const baseUrl  = 'https://solvert-app-production.up.railway.app/api';
      const url      = `${baseUrl}/admin/formularios/plantilla?token=${encodeURIComponent(dlToken)}`;
      // Navegar a URL directa — funciona en todos los browsers incluido Safari/iOS
      const a = document.createElement('a');
      a.href     = url;
      a.download = 'plantilla_historicos.xlsx';
      a.target   = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      alert('Error al descargar la plantilla: ' + (e.response?.data?.error || e.message));
    }
  };

  const seleccionarArchivo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResultado(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      fd.append('preview', 'true');
      const r = await api.post('/admin/formularios/importar', fd);
      setPreview({ ...r.data, file });
    } catch (err) {
      alert('Error al leer el archivo: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const confirmar = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('archivo', preview.file);
      const r = await api.post('/admin/formularios/importar', fd);
      setResultado(r.data);
      setPreview(null);
    } catch (err) {
      alert('Error al importar: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 style={{ fontSize: 16, marginBottom: 6, color: '#F4F7F5' }}>
        Importar registros históricos
      </h3>
      <p style={{ fontSize: 13, color: '#8A9994', marginBottom: 20 }}>
        Carga formularios de mantenimiento o visita técnica anteriores al sistema
        desde un archivo Excel. Los registros importados quedan visibles en bitácoras
        e historial igual que los ingresados desde la app.
      </p>

      {/* Acciones principales */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
        <Btn onClick={descargarPlantilla} outline color='#0f6e56'>
          📄 Descargar plantilla Excel
        </Btn>
        <input type="file" accept=".xlsx,.xls" ref={fileRef}
          onChange={seleccionarArchivo} style={{ display: 'none' }} />
        <Btn onClick={() => { setPreview(null); setResultado(null); fileRef.current.click(); }}
          disabled={loading}>
          {loading ? 'Procesando…' : '📥 Seleccionar archivo'}
        </Btn>
      </div>

      {/* Instrucciones rápidas */}
      {!preview && !resultado && (
        <div style={{ background: '#0D1E2E', border: '1px solid rgba(56,189,248,0.2)',
                      borderRadius: 12, padding: 16, fontSize: 13, color: '#C9D3CE' }}>
          <p style={{ fontWeight: 700, marginBottom: 10, color: '#38BDF8' }}>
            Columnas del archivo Excel (en orden):
          </p>
          <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 2 }}>
            <li><strong>Código ascensor</strong> — ej: ABA01 &nbsp;<span style={{ color:'#EF4444' }}>*obligatorio</span></li>
            <li><strong>Tipo</strong> — <code>mantenimiento</code> o <code>visita</code> &nbsp;<span style={{ color:'#EF4444' }}>*obligatorio</span></li>
            <li><strong>Fecha visita</strong> — formato DD/MM/AAAA &nbsp;<span style={{ color:'#EF4444' }}>*obligatorio</span></li>
            <li><strong>Nombre técnico</strong> — nombre exacto en el sistema &nbsp;<span style={{ color:'#EF4444' }}>*obligatorio</span></li>
            <li>Hora ingreso — HH:MM</li>
            <li>Hora salida — HH:MM</li>
            <li>Nombre cliente</li>
            <li>Estado equipo — Operativo / Operativo en observación / Fuera de servicio</li>
            <li>Observaciones generales</li>
            <li>Resumen de visita</li>
            <li>Tipo de visita — Emergencia / Reparación / Inspección / Otro</li>
            <li>Folio original — dejar vacío para auto-generar</li>
          </ol>
        </div>
      )}

      {/* Preview */}
      {preview && (
        <div style={{ background: '#12382D', border: '1.5px solid rgba(76,175,80,0.3)',
                      borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 12, color: '#4CAF50' }}>
            Vista previa de importación
          </p>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#4CAF50' }}>
                {preview.validas}
              </div>
              <div style={{ fontSize: 12, color: '#8A9994' }}>registros válidos</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800,
                color: preview.omitidas > 0 ? '#EF4444' : '#6B7C74' }}>
                {preview.omitidas}
              </div>
              <div style={{ fontSize: 12, color: '#8A9994' }}>omitidos</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#F4F7F5' }}>
                {preview.total}
              </div>
              <div style={{ fontSize: 12, color: '#8A9994' }}>total en archivo</div>
            </div>
          </div>

          {preview.detalle_omitidas?.length > 0 && (
            <div style={{ background: '#2D0F0F', borderRadius: 8, padding: 12,
                          marginBottom: 16, maxHeight: 180, overflowY: 'auto' }}>
              <p style={{ fontWeight: 600, fontSize: 12, color: '#EF4444', marginBottom: 8 }}>
                Filas omitidas (se muestran hasta 20):
              </p>
              {preview.detalle_omitidas.map((o, i) => (
                <div key={i} style={{ fontSize: 12, color: '#8A9994', marginBottom: 4 }}>
                  Fila {o.fila} — <strong style={{ color: '#F4F7F5' }}>{o.codigo}</strong>: {o.motivo}
                </div>
              ))}
            </div>
          )}

          {preview.validas === 0 ? (
            <p style={{ color: '#EF4444', fontWeight: 600, fontSize: 13 }}>
              No hay registros válidos para importar. Revisa el archivo y los datos.
            </p>
          ) : (
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn onClick={confirmar} disabled={loading}>
                {loading ? 'Importando…' : `✅ Confirmar importación (${preview.validas} registros)`}
              </Btn>
              <Btn outline color='#888' onClick={() => setPreview(null)}>
                Cancelar
              </Btn>
            </div>
          )}
        </div>
      )}

      {/* Resultado */}
      {resultado && (
        <div style={{ background: '#12382D', border: '1.5px solid rgba(76,175,80,0.3)',
                      borderRadius: 12, padding: 20 }}>
          <p style={{ fontWeight: 700, fontSize: 16, color: '#4CAF50', marginBottom: 8 }}>
            ✅ Importación completada
          </p>
          <p style={{ fontSize: 14, color: '#C9D3CE' }}>
            <strong style={{ color: '#F4F7F5' }}>{resultado.insertados}</strong> registro{resultado.insertados !== 1 ? 's' : ''} importado{resultado.insertados !== 1 ? 's' : ''} correctamente.
            {resultado.omitidas > 0 && (
              <span style={{ color: '#6B7C74' }}> ({resultado.omitidas} omitidos por errores o folio duplicado)</span>
            )}
          </p>
          <Btn outline color='#0f6e56' onClick={() => setResultado(null)}
            style={{ marginTop: 12 }}>
            Importar otro archivo
          </Btn>
        </div>
      )}
    </div>
  );
}

const GRUPOS_LABEL = { n8n: 'Integración n8n', email: 'Correo electrónico', google: 'Google Drive', emergencias: 'Emergencias' };

function TabWebhooks() {
  const [config,     setConfig]     = React.useState(null);
  const [settings,   setSettings]   = React.useState([]);
  const [editando,   setEditando]   = React.useState({});
  const [guardando,  setGuardando]  = React.useState({});
  const [msgCfg,     setMsgCfg]     = React.useState({});
  const [showPass,   setShowPass]   = React.useState({});
  const [resultados, setResultados] = React.useState({});
  const [cargando,   setCargando]   = React.useState({});

  const cargarSettings = () =>
    api.get('/webhooks/settings').then(r => setSettings(r.data)).catch(() => {});

  React.useEffect(() => {
    api.get('/webhooks/config')
      .then(r => setConfig(r.data))
      .catch(() => setConfig({ error: true }));
    cargarSettings();
  }, []);

  const iniciarEdicion = (clave, valor) =>
    setEditando(p => ({ ...p, [clave]: valor ?? '' }));

  const cancelar = (clave) =>
    setEditando(p => { const n = { ...p }; delete n[clave]; return n; });

  const guardarSetting = async (clave) => {
    setGuardando(p => ({ ...p, [clave]: true }));
    setMsgCfg(p => ({ ...p, [clave]: '' }));
    try {
      await api.put(`/webhooks/settings/${clave}`, { valor: editando[clave] });
      setMsgCfg(p => ({ ...p, [clave]: 'ok' }));
      cancelar(clave);
      cargarSettings();
      // Refresca estado de conexión
      api.get('/webhooks/config').then(r => setConfig(r.data)).catch(() => {});
    } catch (e) {
      setMsgCfg(p => ({ ...p, [clave]: e.response?.data?.error || 'Error al guardar' }));
    } finally {
      setGuardando(p => ({ ...p, [clave]: false }));
    }
  };

  const toggleBoolean = async (clave, valorActual) => {
    const nuevoValor = valorActual === 'true' ? 'false' : 'true';
    setGuardando(p => ({ ...p, [clave]: true }));
    try {
      await api.put(`/webhooks/settings/${clave}`, { valor: nuevoValor });
      cargarSettings();
      api.get('/webhooks/config').then(r => setConfig(r.data)).catch(() => {});
    } finally {
      setGuardando(p => ({ ...p, [clave]: false }));
    }
  };

  // Agrupar settings por grupo
  const grupos = settings.reduce((acc, s) => {
    if (!acc[s.grupo]) acc[s.grupo] = [];
    acc[s.grupo].push(s);
    return acc;
  }, {});

  const disparar = async (evento) => {
    setCargando(p => ({ ...p, [evento]: true }));
    setResultados(p => ({ ...p, [evento]: null }));
    try {
      const r = await api.post(`/webhooks/test/${evento}`);
      setResultados(p => ({ ...p, [evento]: { ok: r.data.ok, msg: r.data.mensaje, payload: r.data.payload } }));
    } catch (e) {
      setResultados(p => ({ ...p, [evento]: { ok: false, msg: e.response?.data?.error || 'Error de red' } }));
    } finally {
      setCargando(p => ({ ...p, [evento]: false }));
    }
  };

  const inpSt = {
    flex: 1, padding: '8px 10px', border: '1.5px solid #0f6e56',
    borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
  };

  const n8nEnabledSetting = settings.find(s => s.clave === 'n8n_enabled');
  const n8nEnabledVal    = n8nEnabledSetting?.valor ?? 'false';
  const webhooksActivos  = n8nEnabledVal === 'true' && config?.url_configurada;

  const toggleN8nEnabled = async () => {
    if (!n8nEnabledSetting) return;
    const nuevo = n8nEnabledVal === 'true' ? 'false' : 'true';
    setGuardando(p => ({ ...p, n8n_enabled: true }));
    try {
      await api.put('/webhooks/settings/n8n_enabled', { valor: nuevo });
      cargarSettings();
      api.get('/webhooks/config').then(r => setConfig(r.data)).catch(() => {});
    } finally {
      setGuardando(p => ({ ...p, n8n_enabled: false }));
    }
  };

  return (
    <div style={{ padding: 20 }}>

      {/* ── Toggle principal ───────────────────────────────────────── */}
      <div style={{
        background: '#161C19', border: `1.5px solid ${webhooksActivos ? 'rgba(76,175,80,0.35)' : 'rgba(255,255,255,0.08)'}`,
        borderRadius: 16, padding: '18px 18px', marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Ícono de estado */}
          <div style={{
            width: 48, height: 48, borderRadius: 14, flexShrink: 0,
            background: webhooksActivos ? '#12382D' : '#1C1C1C',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 22 }}>{webhooksActivos ? '🔗' : '🔌'}</span>
          </div>

          {/* Texto */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: '#F4F7F5' }}>
              Envío de webhooks
            </p>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: webhooksActivos ? '#4CAF50' : '#8A9994' }}>
              {!config
                ? 'Cargando…'
                : !config.url_configurada
                ? 'URL no configurada — revisa la sección n8n abajo'
                : webhooksActivos
                ? 'Activo — los eventos se envían a n8n'
                : 'Pausado — los eventos no se envían'}
            </p>
          </div>

          {/* Switch toggle */}
          {n8nEnabledSetting && (
            <button
              onClick={toggleN8nEnabled}
              disabled={guardando.n8n_enabled}
              title={n8nEnabledVal === 'true' ? 'Desactivar webhooks' : 'Activar webhooks'}
              style={{
                position: 'relative', width: 52, height: 28, borderRadius: 14,
                border: 'none', cursor: guardando.n8n_enabled ? 'wait' : 'pointer',
                background: n8nEnabledVal === 'true' ? '#0f6e56' : '#2E3830',
                transition: 'background 0.2s', flexShrink: 0, padding: 0,
                opacity: guardando.n8n_enabled ? 0.6 : 1,
              }}>
              <span style={{
                position: 'absolute', top: 3, borderRadius: '50%',
                width: 22, height: 22, background: 'white',
                transition: 'left 0.2s',
                left: n8nEnabledVal === 'true' ? 27 : 3,
                boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
              }} />
            </button>
          )}
        </div>
      </div>

      {/* ── Variables configurables agrupadas ─────────────────────── */}
      <p style={{ fontSize: 13, fontWeight: 700, color: '#6B7C74', textTransform: 'uppercase',
                  letterSpacing: '0.5px', marginBottom: 14 }}>
        Configuración
      </p>

      {settings.length === 0 && (
        <div style={{ background: '#1C1200', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10,
                      padding: '12px 16px', marginBottom: 20, fontSize: 13, color: '#F59E0B' }}>
          Ejecuta la migración <code>migrate_config_app.js</code> en Railway para activar esta sección.
        </div>
      )}

      {Object.entries(grupos).map(([grupo, items]) => (
        <div key={grupo} style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: '#4CAF50', textTransform: 'uppercase',
                      letterSpacing: '0.6px', margin: '0 0 8px',
                      borderBottom: '1px solid rgba(76,175,80,0.2)', paddingBottom: 6 }}>
            {GRUPOS_LABEL[grupo] || grupo}
          </p>

          {items.filter(item => item.clave !== 'n8n_enabled').map(item => {
            const enEdicion = editando[item.clave] !== undefined;
            const msg       = msgCfg[item.clave];
            const esBoolean = item.tipo === 'boolean';
            const esPassword = item.tipo === 'password';
            const valorActual = item.valor ?? '';

            return (
              <div key={item.clave} style={{
                background: '#161C19', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)',
                padding: '12px 14px', marginBottom: 8,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: 13, color: '#F4F7F5' }}>
                      {item.label}
                    </p>
                    {item.descripcion && (
                      <p style={{ margin: '0 0 6px', fontSize: 12, color: '#6B7C74' }}>{item.descripcion}</p>
                    )}

                    {esBoolean ? (
                      /* Toggle para booleanos */
                      <button
                        onClick={() => toggleBoolean(item.clave, valorActual)}
                        disabled={guardando[item.clave]}
                        style={{
                          padding: '5px 14px', borderRadius: 20, border: 'none',
                          background: valorActual === 'true' ? '#0f6e56' : '#1A1F1D',
                          color: valorActual === 'true' ? 'white' : '#8A9994',
                          fontWeight: 700, fontSize: 13, cursor: 'pointer',
                        }}>
                        {guardando[item.clave] ? '…' : valorActual === 'true' ? 'Activado' : 'Desactivado'}
                      </button>
                    ) : enEdicion ? (
                      /* Modo edición */
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                          type={esPassword && !showPass[item.clave] ? 'password' : 'text'}
                          value={editando[item.clave]}
                          onChange={e => setEditando(p => ({ ...p, [item.clave]: e.target.value }))}
                          onKeyDown={e => {
                            if (e.key === 'Enter') guardarSetting(item.clave);
                            if (e.key === 'Escape') cancelar(item.clave);
                          }}
                          autoFocus
                          style={inpSt}
                          placeholder={item.tipo === 'url' ? 'https://…' : ''}
                        />
                        {esPassword && (
                          <button type="button"
                            onClick={() => setShowPass(p => ({ ...p, [item.clave]: !p[item.clave] }))}
                            style={{ padding: '7px 10px', border: '1px solid #2E4A40', borderRadius: 8,
                                     background: '#0D1311', cursor: 'pointer', fontSize: 12,
                                     fontWeight: 600, color: '#8A9994' }}>
                            {showPass[item.clave] ? 'Ocultar' : 'Mostrar'}
                          </button>
                        )}
                        <button onClick={() => guardarSetting(item.clave)}
                          disabled={guardando[item.clave]}
                          style={{ padding: '7px 14px', background: '#0f6e56', color: 'white',
                                   border: 'none', borderRadius: 8, cursor: 'pointer',
                                   fontWeight: 600, fontSize: 13 }}>
                          {guardando[item.clave] ? 'Guardando…' : 'Guardar'}
                        </button>
                        <button onClick={() => cancelar(item.clave)}
                          style={{ padding: '7px 12px', background: 'none', border: '1px solid #2E4A40',
                                   borderRadius: 8, cursor: 'pointer', fontSize: 13, color: '#8A9994' }}>
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      /* Modo lectura */
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 14, color: valorActual ? '#C9D3CE' : '#6B7C74',
                                       fontFamily: esPassword ? 'monospace' : 'inherit' }}>
                          {esPassword
                            ? (valorActual ? '••••••••' : 'No configurado')
                            : (valorActual || 'No configurado')}
                        </span>
                        <button onClick={() => iniciarEdicion(item.clave, valorActual)}
                          style={{ padding: '4px 12px', border: '1px solid rgba(76,175,80,0.3)',
                                   borderRadius: 6, background: '#12382D', color: '#4CAF50',
                                   cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          Editar
                        </button>
                      </div>
                    )}

                    {msg && msg !== 'ok' && (
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: '#EF4444' }}>{msg}</p>
                    )}
                    {msg === 'ok' && (
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: '#4CAF50' }}>✓ Guardado</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* ── Pruebas de eventos ─────────────────────────────────────── */}
      <p style={{ fontSize: 13, fontWeight: 700, color: '#6B7C74', textTransform: 'uppercase',
                  letterSpacing: '0.5px', margin: '24px 0 12px' }}>
        Pruebas de eventos
      </p>

      {EVENTOS_WEBHOOK.map(ev => {
        const res  = resultados[ev.id];
        const busy = cargando[ev.id];
        return (
          <div key={ev.id} style={{
            background: '#161C19', borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.07)', padding: '14px 16px',
            marginBottom: 10,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <p style={{ margin: '0 0 3px', fontWeight: 700, fontSize: 14, color: '#F4F7F5' }}>
                  {ev.label}
                </p>
                <p style={{ margin: 0, fontSize: 12, color: '#6B7C74' }}>{ev.desc}</p>
                <code style={{ fontSize: 11, color: '#4CAF50', background: '#12382D',
                                padding: '1px 6px', borderRadius: 4, marginTop: 4, display: 'inline-block' }}>
                  {ev.id}
                </code>
              </div>
              <button
                onClick={() => disparar(ev.id)}
                disabled={busy || !config?.enabled || !config?.url_configurada}
                style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: busy ? '#0a4433' : '#0f6e56',
                  color: 'white', fontWeight: 600, fontSize: 13,
                  cursor: busy || !config?.enabled ? 'not-allowed' : 'pointer',
                  flexShrink: 0, opacity: !config?.enabled || !config?.url_configurada ? 0.5 : 1,
                }}>
                {busy ? 'Enviando…' : '▶ Disparar'}
              </button>
            </div>

            {res && (
              <div style={{
                marginTop: 10, padding: '10px 12px', borderRadius: 8,
                background: res.ok ? '#12382D' : '#2D0F0F',
                border: `1px solid ${res.ok ? 'rgba(76,175,80,0.3)' : 'rgba(239,68,68,0.3)'}`,
              }}>
                <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600,
                            color: res.ok ? '#4CAF50' : '#EF4444' }}>
                  {res.ok ? '✓ Disparado correctamente' : '✗ ' + res.msg}
                </p>
                {res.ok && res.payload && (
                  <pre style={{
                    margin: 0, fontSize: 11, color: '#8A9994', whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all', maxHeight: 160, overflowY: 'auto',
                    background: '#0B0F0D', padding: '8px', borderRadius: 6,
                  }}>
                    {JSON.stringify(res.payload, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Tab Trabajos por Equipo ────────────────────────────────────────────────────
const ESTADOS_TRABAJO = [
  { value: 'pendiente',  label: 'Pendiente',  color: '#e67e22' },
  { value: 'realizado',  label: 'Realizado',  color: '#27ae60' },
  { value: 'no_aplica',  label: 'No aplica',  color: '#95a5a6' },
];

const TIPOS_ITEM = [
  { value: 'generico',  label: '🌐 Genérico',   color: '#2c3e50' },
  { value: 'modelo',    label: '🏭 Del modelo',  color: '#0f6e56' },
  { value: 'exclusivo', label: '📍 Exclusivo',   color: '#c0392b' },
];

function EstadoBadge({ estado }) {
  const e = ESTADOS_TRABAJO.find(x => x.value === estado) || ESTADOS_TRABAJO[0];
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 20,
      background: e.color + '22', color: e.color,
      fontSize: 12, fontWeight: 700,
    }}>{e.label}</span>
  );
}

function FilaTrabajoDetalle({ row, ascensorId, formularios, onGuardado, onCerrar }) {
  const [estado,       setEstado]       = useState(row.estado || 'pendiente');
  const [fecha,        setFecha]        = useState(row.fecha_realizacion?.slice(0,10) || '');
  const [obs,          setObs]          = useState(row.observaciones || '');
  const [formularioId, setFormularioId] = useState(row.formulario_id ? String(row.formulario_id) : '');
  const [guardando,    setGuardando]    = useState(false);

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.post('/trabajos-equipo/registros', {
        ascensor_id:   ascensorId,
        item_id:       row.item_id,
        estado,
        fecha_realizacion: fecha || null,
        observaciones: obs,
        formulario_id: formularioId ? Number(formularioId) : null,
      });
      onGuardado();
      onCerrar();
    } catch (e) {
      alert('Error al guardar: ' + (e.response?.data?.error || e.message));
    }
    setGuardando(false);
  };

  const inputSt = {
    width: '100%', padding: '8px 10px', border: '1.5px solid #2E4A40',
    borderRadius: 7, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box',
    background: '#0D1311', color: '#F4F7F5',
  };

  const formSeleccionado = formularios.find(f => String(f.id) === formularioId);

  return (
    <tr>
      <td colSpan={5} style={{ padding: 0 }}>
        <div style={{
          background: '#12382D', borderLeft: '4px solid #0f6e56',
          padding: '16px 20px', display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr', gap: '12px 16px',
          alignItems: 'start',
        }}>
          {/* Estado */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#4CAF50', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Estado</label>
            <select value={estado} onChange={e => setEstado(e.target.value)} style={inputSt}>
              {ESTADOS_TRABAJO.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </div>

          {/* Fecha de realización */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#4CAF50', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Fecha de realización</label>
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={inputSt} />
          </div>

          {/* Comprobante vinculado */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#4CAF50', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Comprobante</label>
            <select value={formularioId} onChange={e => setFormularioId(e.target.value)} style={inputSt}>
              <option value="">— Sin comprobante —</option>
              {formularios.map(f => (
                <option key={f.id} value={f.id}>
                  {f.folio} · {f.tipo === 'mantenimiento' ? 'Mant.' : 'Visita'} · {f.fecha_visita?.slice(0,10)}
                </option>
              ))}
            </select>
            {formSeleccionado?.pdf_url && (
              <a href={formSeleccionado.pdf_url} target="_blank" rel="noreferrer"
                style={{ display: 'inline-block', marginTop: 4, fontSize: 11, color: '#4CAF50', fontWeight: 600 }}>
                📄 Ver comprobante {formSeleccionado.folio}
              </a>
            )}
          </div>

          {/* Observaciones — ocupa las 3 columnas */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#4CAF50', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Observaciones</label>
            <textarea value={obs} onChange={e => setObs(e.target.value)} rows={2}
              style={{ ...inputSt, resize: 'vertical' }} placeholder="Observaciones opcionales…" />
          </div>

          {/* Acciones + auditoría */}
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            {row.creado_por_nombre && (
              <span style={{ fontSize: 11, color: '#888' }}>
                Registrado por {row.creado_por_nombre}
                {row.actualizado_por_nombre && row.actualizado_por_nombre !== row.creado_por_nombre
                  ? ` · Actualizado por ${row.actualizado_por_nombre}` : ''}
              </span>
            )}
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <Btn onClick={onCerrar} outline color="#888" small>Cancelar</Btn>
              <Btn onClick={guardar} color="#0f6e56" small>{guardando ? 'Guardando…' : 'Guardar'}</Btn>
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
}

function ModalGestionItems({ onClose }) {
  const FORM_VACIO = { nombre: '', descripcion: '', tipo: 'generico', marca: '', modelo: '', comunidad_item: '' };
  const [items,        setItems]        = useState([]);
  const [editando,     setEditando]     = useState(null);
  const [nuevo,        setNuevo]        = useState(false);
  const [form,         setForm]         = useState(FORM_VACIO);
  const [guardando,    setGuardando]    = useState(false);
  const [marcasMod,    setMarcasMod]    = useState([]);

  const cargar = useCallback(async () => {
    const r = await api.get('/trabajos-equipo/items');
    setItems(r.data);
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    api.get('/trabajos-equipo/marcas-modelos').then(r => setMarcasMod(r.data)).catch(() => {});
  }, []);

  const abrirNuevo = () => { setForm(FORM_VACIO); setEditando(null); setNuevo(true); };

  const abrirEditar = (item) => {
    setForm({
      nombre: item.nombre,
      descripcion: item.descripcion || '',
      tipo: item.tipo || 'generico',
      marca: item.marca || '',
      modelo: item.modelo || '',
      comunidad_item: item.comunidad || '',
    });
    setEditando(item); setNuevo(false);
  };

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const guardar = async () => {
    if (!form.nombre.trim()) { alert('El nombre es requerido'); return; }
    if (form.tipo === 'modelo' && (!form.marca.trim() || !form.modelo.trim())) {
      alert('Para ítems de modelo se requiere marca y modelo'); return;
    }
    if (form.tipo === 'exclusivo' && !form.comunidad_item.trim()) {
      alert('Para ítems exclusivos se requiere la comunidad'); return;
    }
    setGuardando(true);
    try {
      const payload = {
        nombre:      form.nombre.trim(),
        descripcion: form.descripcion.trim() || null,
        tipo:        form.tipo,
        marca:       form.tipo === 'modelo'    ? form.marca.trim()         : null,
        modelo:      form.tipo === 'modelo'    ? form.modelo.trim()        : null,
        comunidad:   form.tipo === 'exclusivo' ? form.comunidad_item.trim(): null,
        ...(editando ? { activo: editando.activo } : {}),
      };
      if (editando) {
        await api.put(`/trabajos-equipo/items/${editando.id}`, payload);
      } else {
        await api.post('/trabajos-equipo/items', payload);
      }
      setEditando(null); setNuevo(false); cargar();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
    setGuardando(false);
  };

  const toggleActivo = async (item) => {
    try {
      await api.put(`/trabajos-equipo/items/${item.id}`, {
        nombre: item.nombre, descripcion: item.descripcion,
        tipo: item.tipo, marca: item.marca, modelo: item.modelo,
        comunidad: item.comunidad, activo: !item.activo,
      });
      cargar();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
  };

  const inputSt = {
    width: '100%', padding: '9px 12px', border: '1.5px solid #2E4A40',
    borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box',
    background: '#0D1311', color: '#F4F7F5',
  };

  return (
    <Modal title="⚙ Gestionar ítems del checklist" onClose={onClose} maxWidth={640}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Btn onClick={abrirNuevo} color="#0f6e56">+ Nuevo ítem</Btn>
      </div>

      {(nuevo || editando) && (
        <div style={{ background: '#111714', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <h4 style={{ margin: '0 0 14px', fontSize: 14 }}>{editando ? 'Editar ítem' : 'Nuevo ítem'}</h4>

          {/* Selector de tipo */}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#8A9994', display: 'block', marginBottom: 6 }}>Tipo de ítem</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {TIPOS_ITEM.map(t => (
                <button key={t.value} onClick={() => setF('tipo', t.value)} style={{
                  padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                  border: `2px solid ${form.tipo === t.value ? t.color : 'rgba(255,255,255,0.1)'}`,
                  background: form.tipo === t.value ? t.color + '22' : '#161C19',
                  color: form.tipo === t.value ? t.color : '#8A9994', fontWeight: 600,
                }}>{t.label}</button>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div style={{ gridColumn: form.tipo === 'generico' ? '1 / -1' : '1' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#8A9994', display: 'block', marginBottom: 4 }}>Nombre *</label>
              <input value={form.nombre} onChange={e => setF('nombre', e.target.value)} style={inputSt} placeholder="Nombre del trabajo…" />
            </div>

            {/* Campos condicionales según tipo */}
            {form.tipo === 'modelo' && (() => {
              const marcasGI = [...new Set(marcasMod.map(mm => mm.marca).filter(Boolean))];
              const modelosGI = marcasMod.filter(mm => !form.marca || mm.marca === form.marca).map(mm => mm.modelo).filter(Boolean);
              return (
                <>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#0f6e56', display: 'block', marginBottom: 4 }}>Marca *</label>
                    <input value={form.marca} onChange={e => setF('marca', e.target.value)}
                      list="dl-gi-marca" style={{ ...inputSt, borderColor: '#0f6e5644' }} placeholder="Ej: Schindler" />
                    <datalist id="dl-gi-marca">
                      {marcasGI.map(m => <option key={m} value={m} />)}
                    </datalist>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#0f6e56', display: 'block', marginBottom: 4 }}>Modelo *</label>
                    <input value={form.modelo} onChange={e => setF('modelo', e.target.value)}
                      list="dl-gi-modelo" style={{ ...inputSt, borderColor: '#0f6e5644' }} placeholder="Ej: 3300" />
                    <datalist id="dl-gi-modelo">
                      {modelosGI.map(m => <option key={m} value={m} />)}
                    </datalist>
                  </div>
                </>
              );
            })()}
            {form.tipo === 'exclusivo' && (
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#c0392b', display: 'block', marginBottom: 4 }}>Comunidad *</label>
                <input value={form.comunidad_item} onChange={e => setF('comunidad_item', e.target.value)} style={{ ...inputSt, borderColor: '#c0392b44' }} placeholder="Nombre exacto de la comunidad" />
              </div>
            )}

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#8A9994', display: 'block', marginBottom: 4 }}>Descripción</label>
              <input value={form.descripcion} onChange={e => setF('descripcion', e.target.value)} style={inputSt} placeholder="Descripción opcional…" />
            </div>
          </div>

          {/* Aviso de alcance */}
          <div style={{ marginTop: 10, padding: '7px 12px', borderRadius: 7, fontSize: 12, color: '#C9D3CE',
            background: form.tipo === 'generico' ? '#12382D' : form.tipo === 'modelo' ? '#0D1F1A' : '#2D0F0F',
          }}>
            {form.tipo === 'generico'  && '🌐 Este ítem aparecerá en el checklist de TODOS los equipos.'}
            {form.tipo === 'modelo'    && `🏭 Aparecerá en todos los equipos con marca "${form.marca || '…'}" y modelo "${form.modelo || '…'}".`}
            {form.tipo === 'exclusivo' && `📍 Aparecerá solo en los equipos de la comunidad "${form.comunidad_item || '…'}".`}
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
            <Btn onClick={() => { setNuevo(false); setEditando(null); }} outline color="#888">Cancelar</Btn>
            <Btn onClick={guardar} color="#0f6e56">{guardando ? 'Guardando…' : 'Guardar'}</Btn>
          </div>
        </div>
      )}

      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#0f6e56' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Nombre</th>
              <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Tipo / Alcance</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600 }}>Estado</th>
              <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => {
              const tipoInfo = TIPOS_ITEM.find(t => t.value === item.tipo) || TIPOS_ITEM[0];
              const alcance = item.tipo === 'modelo'
                ? `${item.marca} ${item.modelo}`
                : item.tipo === 'exclusivo' ? item.comunidad : 'Todos los equipos';
              return (
                <tr key={item.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', opacity: item.activo ? 1 : 0.45 }}>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ fontWeight: 500, color: '#F4F7F5' }}>{item.nombre}</div>
                    {item.descripcion && <div style={{ fontSize: 11, color: '#6B7C74', marginTop: 2 }}>{item.descripcion}</div>}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: tipoInfo.color }}>{tipoInfo.label}</span>
                    <div style={{ fontSize: 11, color: '#6B7C74', marginTop: 1 }}>{alcance}</div>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: item.activo ? '#4CAF50' : '#6B7C74' }}>
                      {item.activo ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button onClick={() => abrirEditar(item)} style={{
                      background: '#0D1311', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
                      padding: '3px 10px', fontSize: 12, cursor: 'pointer', marginRight: 6,
                    }}>Editar</button>
                    <button onClick={() => toggleActivo(item)} style={{
                      background: 'none',
                      border: `1px solid ${item.activo ? '#EF4444' : '#4CAF50'}`,
                      borderRadius: 6, padding: '3px 10px', fontSize: 12, cursor: 'pointer',
                      color: item.activo ? '#EF4444' : '#4CAF50',
                    }}>{item.activo ? 'Desactivar' : 'Activar'}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

function FilaSeccion({ grupo, expandido, toggleExpand, formularios, ascensorId, cargarRegistros, setExpandido }) {
  return grupo.map(row => {
    const abierto = expandido === row.item_id;
    const formVinculado = formularios.find(f => f.id === row.formulario_id);
    return (
      <React.Fragment key={row.item_id}>
        <tr
          onClick={() => toggleExpand(row.item_id)}
          style={{ borderBottom: abierto ? 'none' : '1px solid #24312B', cursor: 'pointer', background: abierto ? '#12382D' : '#111714' }}
          onMouseEnter={e => { if (!abierto) e.currentTarget.style.background = '#1A2420'; }}
          onMouseLeave={e => { if (!abierto) e.currentTarget.style.background = '#111714'; }}
        >
          <td style={{ padding: '11px 16px', color: '#4D6358', fontSize: 12, userSelect: 'none', width: 28 }}>
            {abierto ? '▲' : '▼'}
          </td>
          <td style={{ padding: '11px 16px', fontWeight: 500, color: '#F4F7F5' }}>
            {row.item_nombre}
            {row.item_descripcion && <div style={{ fontSize: 11, color: '#6B7C74', fontWeight: 400, marginTop: 2 }}>{row.item_descripcion}</div>}
          </td>
          <td style={{ padding: '11px 16px', textAlign: 'center', width: 120 }}>
            <EstadoBadge estado={row.estado || 'pendiente'} />
          </td>
          <td style={{ padding: '11px 16px', color: '#8A9994', fontSize: 13, width: 110 }}>
            {row.fecha_realizacion ? row.fecha_realizacion.slice(0,10) : '—'}
          </td>
          <td style={{ padding: '11px 16px', fontSize: 13, width: 150 }}>
            {formVinculado
              ? formVinculado.pdf_url
                ? <a href={formVinculado.pdf_url} target="_blank" rel="noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{ color: '#4CAF50', fontWeight: 600 }}>{formVinculado.folio}</a>
                : <span style={{ fontWeight: 500, color: '#F4F7F5' }}>{formVinculado.folio}</span>
              : <span style={{ color: '#4D6358' }}>—</span>
            }
          </td>
        </tr>
        {abierto && (
          <FilaTrabajoDetalle
            row={row}
            ascensorId={ascensorId}
            formularios={formularios}
            onGuardado={() => cargarRegistros(ascensorId)}
            onCerrar={() => setExpandido(null)}
          />
        )}
      </React.Fragment>
    );
  });
}

// ── Tab Órdenes de Trabajo (presupuestos aprobados) ───────────────────────────
function TabFacturable() {
  const [rows,      setRows]      = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [desde,     setDesde]     = useState('');
  const [hasta,     setHasta]     = useState('');
  const [comunidad, setComunidad] = useState('');
  const [facturado, setFacturado] = useState('false'); // '' | 'false' | 'true'
  const [expandido, setExpandido] = useState(null);
  const [error,     setError]     = useState('');
  const [facturar,  setFacturar]  = useState(null);     // fila a facturar
  const [factForm,  setFactForm]  = useState({ numero:'', fecha:'' });
  const [guardando, setGuardando] = useState(false);

  const CLP = (n) => Number(n || 0).toLocaleString('es-CL', { style:'currency', currency:'CLP', maximumFractionDigits:0 });

  const TIPO_LABEL = { reparacion:'Reparación', modernizacion:'Modernización', normativo:'Normativo' };

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (desde && hasta) { params.desde = desde; params.hasta = hasta; }
      if (comunidad) params.comunidad = comunidad;
      if (facturado) params.facturado = facturado;
      const r = await api.get('/reportes/facturable', { params });
      setRows(r.data || []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [desde, hasta, comunidad, facturado]);

  useEffect(() => { cargar(); }, [cargar]);

  const nPorFacturar   = rows.filter(r => !r.facturado).length;
  const nFacturados    = rows.filter(r => r.facturado).length;
  const totalPorFact   = rows.filter(r => !r.facturado).reduce((a, r) => a + Number(r.total || 0), 0);
  const totalFacturado = rows.filter(r =>  r.facturado).reduce((a, r) => a + Number(r.total || 0), 0);

  const inp = { padding:'8px 10px', border:'1.5px solid #2E4A40', borderRadius:8,
                fontSize:13, background:'#0D1311', color:'#F4F7F5', fontFamily:'inherit' };
  const th = { padding:'10px 12px', textAlign:'left', fontSize:11, fontWeight:700,
               color:'#8A9994', textTransform:'uppercase', letterSpacing:'0.5px',
               borderBottom:'1px solid #1C2922', whiteSpace:'nowrap' };

  const abrirFacturar = (r) => {
    setFactForm({ numero: r.factura_numero || '', fecha: r.factura_fecha ? String(r.factura_fecha).slice(0,10) : hoyChile() });
    setError('');
    setFacturar(r);
  };

  const confirmarFacturar = async () => {
    if (!facturar) return;
    if (!factForm.numero.trim()) { setError('Ingresa el número de factura'); return; }
    if (!factForm.fecha)         { setError('Ingresa la fecha de facturación'); return; }
    setGuardando(true); setError('');
    try {
      await api.put(`/reportes/facturable/${facturar.id}/facturar`, {
        factura_numero: factForm.numero.trim(),
        factura_fecha:  factForm.fecha,
      });
      setFacturar(null);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo marcar como facturado');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div>
      {/* Filtros */}
      <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={inp} title="Desde" />
        <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={inp} title="Hasta" />
        <input placeholder="Comunidad…" value={comunidad}
          onChange={e => setComunidad(e.target.value)} style={{ ...inp, minWidth:160 }} />
        <select value={facturado} onChange={e => setFacturado(e.target.value)} style={inp}>
          <option value="false">Por facturar</option>
          <option value="true">Facturados</option>
          <option value="">Todos</option>
        </select>
        <button onClick={cargar}
          style={{ padding:'8px 16px', background:'#0f6e56', color:'white', border:'none',
                   borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13 }}>
          Actualizar
        </button>
      </div>

      {/* Resumen */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <div style={{ background:'#0D1311', border:'1px solid #2A1F0A', borderRadius:10, padding:'10px 14px', minWidth:120 }}>
          <div style={{ fontSize:22, fontWeight:800, color:'#F59E0B' }}>{nPorFacturar}</div>
          <div style={{ fontSize:12, color:'#8A9994' }}>Por facturar</div>
        </div>
        <div style={{ background:'#0D1311', border:'1px solid #12382D', borderRadius:10, padding:'10px 14px', minWidth:120 }}>
          <div style={{ fontSize:22, fontWeight:800, color:'#4CAF50' }}>{nFacturados}</div>
          <div style={{ fontSize:12, color:'#8A9994' }}>Facturados</div>
        </div>
        <div style={{ background:'#0D1311', border:'1px solid #2A1F0A', borderRadius:10, padding:'10px 14px', minWidth:160, marginLeft:'auto' }}>
          <div style={{ fontSize:22, fontWeight:800, color:'#F59E0B' }}>{CLP(totalPorFact)}</div>
          <div style={{ fontSize:12, color:'#8A9994' }}>Monto por facturar</div>
        </div>
        <div style={{ background:'#0D1311', border:'1px solid #12382D', borderRadius:10, padding:'10px 14px', minWidth:160 }}>
          <div style={{ fontSize:22, fontWeight:800, color:'#4CAF50' }}>{CLP(totalFacturado)}</div>
          <div style={{ fontSize:12, color:'#8A9994' }}>Monto facturado</div>
        </div>
      </div>

      {error && !facturar && <p style={{ color:'#EF4444', fontSize:13 }}>{error}</p>}
      {loading ? (
        <p style={{ color:'#6B7C74', padding:20 }}>Cargando…</p>
      ) : rows.length === 0 ? (
        <p style={{ color:'#6B7C74', padding:20 }}>No hay órdenes completadas para facturar en el período.</p>
      ) : (
        <div style={{ overflowX:'auto', border:'1px solid #1C2922', borderRadius:12 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#0D1311' }}>
                <th style={th}></th>
                <th style={th}>Presupuesto</th>
                <th style={th}>Comunidad</th>
                <th style={th}>Completado</th>
                <th style={{ ...th, textAlign:'right' }}>Total</th>
                <th style={th}>Facturación</th>
                <th style={{ ...th, textAlign:'center' }}>Comprob.</th>
                <th style={{ ...th, textAlign:'right' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const abierto = expandido === r.id;
                const comps = Array.isArray(r.comprobantes) ? r.comprobantes : [];
                return (
                  <React.Fragment key={r.id}>
                    <tr onClick={() => setExpandido(abierto ? null : r.id)}
                        style={{ cursor:'pointer', background: abierto ? '#12382D' : 'transparent',
                                 borderBottom:'1px solid #1C2922' }}>
                      <td style={{ padding:'10px 12px', color:'#4D6358', width:28 }}>{abierto ? '▲' : '▼'}</td>
                      <td style={{ padding:'10px 12px', color:'#F4F7F5' }}>
                        <strong>{r.numero}</strong>
                        <div style={{ fontSize:11, color:'#6B7C74' }}>
                          {r.nombre || TIPO_LABEL[r.tipo] || r.tipo}
                        </div>
                      </td>
                      <td style={{ padding:'10px 12px', color:'#C9D3CE' }}>{r.comunidad || '—'}</td>
                      <td style={{ padding:'10px 12px', color:'#C9D3CE', whiteSpace:'nowrap' }}>
                        {r.orden_completado_at ? fechaCL(r.orden_completado_at) : '—'}
                      </td>
                      <td style={{ padding:'10px 12px', color:'#4CAF50', fontWeight:700, textAlign:'right', whiteSpace:'nowrap' }}>{CLP(r.total)}</td>
                      <td style={{ padding:'10px 12px' }}>
                        {r.facturado ? (
                          <span style={{ background:'#12382D', color:'#4CAF50', borderRadius:4,
                                         padding:'3px 9px', fontSize:12, fontWeight:600, whiteSpace:'nowrap' }}>
                            Facturado · {r.factura_numero}
                          </span>
                        ) : (
                          <span style={{ background:'#2A1F0A', color:'#F59E0B', borderRadius:4,
                                         padding:'3px 9px', fontSize:12, fontWeight:600, whiteSpace:'nowrap' }}>
                            Por facturar
                          </span>
                        )}
                      </td>
                      <td style={{ padding:'10px 12px', textAlign:'center', color:'#C9D3CE' }}>{r.n_comprobantes}</td>
                      <td style={{ padding:'8px 12px', textAlign:'right', whiteSpace:'nowrap' }}>
                        {r.facturado ? (
                          <button onClick={(e) => { e.stopPropagation(); abrirFacturar(r); }}
                            style={{ padding:'5px 11px', background:'transparent', color:'#8A9994',
                                     border:'1px solid #2E4A40', borderRadius:7, cursor:'pointer', fontSize:12 }}>
                            Editar factura
                          </button>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); abrirFacturar(r); }}
                            style={{ padding:'6px 12px', background:'#0f6e56', color:'white',
                                     border:'none', borderRadius:7, cursor:'pointer', fontWeight:600, fontSize:12 }}>
                            Marcar facturado
                          </button>
                        )}
                      </td>
                    </tr>
                    {abierto && (() => {
                      const items = Array.isArray(r.items) ? r.items : [];
                      return (
                      <tr style={{ background:'#0D1311' }}>
                        <td colSpan={8} style={{ padding:'12px 16px' }}>
                          {r.orden_completado_at && (
                            <div style={{ fontSize:12, color:'#4CAF50', marginBottom:8 }}>
                              Orden completada el {fechaCL(r.orden_completado_at)}
                              {r.completado_por_nombre ? ` por ${r.completado_por_nombre}` : ''}
                            </div>
                          )}
                          {items.length > 0 && (
                            <div style={{ marginBottom:10 }}>
                              <span style={{ fontSize:11, color:'#8A9994', fontWeight:700, textTransform:'uppercase' }}>
                                Desglose del presupuesto
                              </span>
                              <div style={{ display:'flex', flexDirection:'column', gap:4, marginTop:6 }}>
                                {items.map((it, idx) => (
                                  <div key={idx} style={{ borderTop:'1px solid #1C2922', padding:'6px 0' }}>
                                    <div style={{ display:'flex', gap:8, alignItems:'baseline' }}>
                                      <span style={{ color: it.hecho ? '#4CAF50' : '#6B7C74', fontSize:13, whiteSpace:'nowrap' }}>
                                        {it.hecho ? '✓' : '○'}
                                      </span>
                                      <span style={{ color:'#F4F7F5', fontSize:13, flex:1 }}>{it.descripcion}</span>
                                      {it.cantidad != null && (
                                        <span style={{ color:'#8A9994', fontSize:12, whiteSpace:'nowrap' }}>
                                          x{Number(it.cantidad)}
                                        </span>
                                      )}
                                    </div>
                                    {it.comentario && (
                                      <div style={{ color:'#9FAAA5', fontSize:12, marginLeft:20, marginTop:2, whiteSpace:'pre-wrap' }}>
                                        {it.comentario}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {r.facturado && (
                            <div style={{ fontSize:12, color:'#C9D3CE', marginBottom:8, background:'#12382D',
                                          borderRadius:8, padding:'8px 12px' }}>
                              🧾 Factura <strong>{r.factura_numero}</strong>
                              {r.factura_fecha ? ` · ${fechaCL(r.factura_fecha)}` : ''}
                              {r.facturado_por_nombre ? ` · registrada por ${r.facturado_por_nombre}` : ''}
                            </div>
                          )}
                          {r.orden_notas && (
                            <div style={{ marginBottom:10 }}>
                              <span style={{ fontSize:11, color:'#8A9994', fontWeight:700, textTransform:'uppercase' }}>
                                Nota de cierre
                              </span>
                              <div style={{ fontSize:13, color:'#C9D3CE', marginTop:4, whiteSpace:'pre-wrap' }}>{r.orden_notas}</div>
                            </div>
                          )}
                          {comps.length === 0 ? (
                            <span style={{ fontSize:13, color:'#6B7C74' }}>
                              {r.orden_notas ? 'Cierre por nota (sin comprobantes de terreno).' : 'Sin comprobantes de terreno asociados.'}
                            </span>
                          ) : (
                            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                              <span style={{ fontSize:11, color:'#8A9994', fontWeight:700, textTransform:'uppercase' }}>
                                Comprobantes que lo ejecutaron
                              </span>
                              {comps.map(c => (
                                <div key={c.id} style={{ display:'flex', justifyContent:'space-between',
                                                         alignItems:'center', gap:10, fontSize:13,
                                                         borderTop:'1px solid #1C2922', padding:'6px 0' }}>
                                  <span style={{ color:'#C9D3CE' }}>
                                    {c.folio} · {c.tipo_visita || (c.tipo === 'mantenimiento' ? 'Mantenimiento' : 'Visita')}
                                    {' · '}{fechaCL(c.fecha_visita)}
                                  </span>
                                  {c.pdf_url
                                    ? <a href={c.pdf_url} target="_blank" rel="noreferrer"
                                         style={{ color:'#38BDF8', fontSize:12, whiteSpace:'nowrap' }}>Ver PDF ↗</a>
                                    : <span style={{ color:'#555', fontSize:12 }}>sin PDF</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                      );
                    })()}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal marcar facturado */}
      {facturar && (
        <Modal title={facturar.facturado ? 'Editar factura' : 'Marcar como facturado'}
               onClose={() => setFacturar(null)} maxWidth={420}>
          <p style={{ margin:'0 0 16px', fontSize:13, color:'#C9D3CE', lineHeight:1.5 }}>
            Presupuesto <strong>{facturar.numero}</strong>
            {facturar.comunidad ? <> · {facturar.comunidad}</> : null} · <strong style={{ color:'#4CAF50' }}>{CLP(facturar.total)}</strong>
          </p>
          <div style={{ marginBottom:14 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>
              Número de factura *
            </label>
            <input value={factForm.numero} autoFocus
              onChange={e => setFactForm(f => ({ ...f, numero: e.target.value }))}
              placeholder="Ej: F-001234"
              style={{ ...inp, width:'100%', boxSizing:'border-box' }} />
          </div>
          <div style={{ marginBottom:18 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>
              Fecha de facturación *
            </label>
            <input type="date" value={factForm.fecha}
              onChange={e => setFactForm(f => ({ ...f, fecha: e.target.value }))}
              style={{ ...inp, width:'100%', boxSizing:'border-box' }} />
          </div>
          {error && <div style={{ color:'#EF4444', fontSize:13, marginBottom:12 }}>{error}</div>}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={() => setFacturar(null)} disabled={guardando}
              style={{ padding:'9px 18px', border:'1px solid #2E4A40', borderRadius:8,
                       background:'none', color:'#8A9994', cursor:'pointer', fontSize:14 }}>
              Cancelar
            </button>
            <button onClick={confirmarFacturar} disabled={guardando}
              style={{ padding:'9px 20px', background: guardando ? '#0a4a38' : '#0f6e56',
                       color:'white', border:'none', borderRadius:8, fontWeight:600,
                       cursor: guardando ? 'wait' : 'pointer', fontSize:14 }}>
              {guardando ? 'Guardando…' : 'Confirmar facturación'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Materiales (insumos) retirados para una orden + costo. Se carga al expandir.
function MaterialesOrden({ trabajoId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let vivo = true;
    api.get(`/trabajos/${trabajoId}/materiales`)
      .then(r => { if (vivo) setData(r.data); })
      .catch(() => { if (vivo) setData({ materiales: [], libres: [], total_costo: 0 }); });
    return () => { vivo = false; };
  }, [trabajoId]);
  if (!data) return null;
  const hay = (data.materiales?.length || 0) + (data.libres?.length || 0) > 0;
  return (
    <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #1C2922' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#8A9994', marginBottom: hay ? 6 : 0 }}>
        🧰 Materiales retirados
        {data.total_costo > 0 && <span style={{ color: '#4CAF50' }}> · costo {CLP0(data.total_costo)}</span>}
      </div>
      {!hay ? (
        <span style={{ fontSize: 12, color: '#6B7C74' }}>Sin materiales asociados a esta orden.</span>
      ) : (<>
        {data.materiales.map((m, i) => (
          <div key={i} style={{ fontSize: 12, color: '#C9D3CE', display: 'flex', justifyContent: 'space-between', gap: 10, padding: '2px 0' }}>
            <span>{m.insumo} <span style={{ color: '#6B7C74' }}>× {Number(m.cantidad)} {m.unidad_medida || ''}</span></span>
            <span style={{ color: '#4CAF50', whiteSpace: 'nowrap' }}>{CLP0(m.costo)}</span>
          </div>
        ))}
        {data.libres.map((l, i) => (
          <div key={'l' + i} style={{ fontSize: 12, color: '#9FAAA5', padding: '2px 0' }}>
            {l.nombre} <span style={{ color: '#6B7C74' }}>× {Number(l.cantidad)}</span> <span style={{ color: '#6B7C74', fontSize: 11 }}>(no catalogado)</span>
          </div>
        ))}
      </>)}
    </div>
  );
}

function TabOrdenesTrabajo() {
  const { tecnico: usuarioActual } = useAuth();
  const esAdmin = ['admin', 'superadmin'].includes(usuarioActual?.rol);
  const [trabajos,  setTrabajos]  = useState([]);
  const [tecnicos,  setTecnicos]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('');
  const [editando,  setEditando]  = useState(null); // trabajo seleccionado
  const [form,      setForm]      = useState({});
  const [guardando, setGuardando] = useState(false);
  const [error,     setError]     = useState('');
  const [devolviendo,   setDevolviendo]   = useState(false); // panel motivo devolución
  const [motivoDevol,   setMotivoDevol]   = useState('');
  const [guardandoDevol, setGuardandoDevol] = useState(false);
  const [confirmarCompletar, setConfirmarCompletar] = useState(null); // { id, nombre } al terminar todos los ítems
  const [completandoOrden,   setCompletandoOrden]   = useState(false);
  const [confirmarGuardar,   setConfirmarGuardar]   = useState(false); // confirmación al marcar Completado desde Gestionar
  const [subiendoPdf,        setSubiendoPdf]        = useState(false); // subida manual del PDF a Drive
  const [comentarioCierre,   setComentarioCierre]   = useState('');    // comentario obligatorio al completar sin comprobante (flujo ítems)
  const [requiereComentario, setRequiereComentario] = useState(false); // el backend exigió comentario de cierre
  const [creando,   setCreando]   = useState(false); // modal nueva orden manual
  const [nuevaForm, setNuevaForm] = useState({ descripcion:'', comunidad:'', fecha_programada:'', prioridad:'media' });
  const [guardandoNueva, setGuardandoNueva] = useState(false);
  const [comunidades, setComunidades] = useState([]);
  const [comprobantes, setComprobantes] = useState([]); // evidencia de la orden abierta
  const [expandido,   setExpandido]   = useState(null); // id de orden con ítems desplegados
  const [itemsCache,  setItemsCache]  = useState({});    // { [ordenId]: { loading, items, error, manual, descripcion } }

  const toggleExpandirOrden = async (t) => {
    if (expandido === t.id) { setExpandido(null); return; }
    setExpandido(t.id);
    if (itemsCache[t.id]) return;
    // Orden manual (sin presupuesto): mostrar su descripción, sin llamada al backend
    if (!t.presupuesto_id) {
      setItemsCache(prev => ({ ...prev, [t.id]: { loading: false, items: null, error: '', manual: true, descripcion: t.descripcion || '' } }));
      return;
    }
    setItemsCache(prev => ({ ...prev, [t.id]: { loading: true, items: null, error: '' } }));
    try {
      const r = await api.get(`/trabajos/${t.id}/items`);
      setItemsCache(prev => ({ ...prev, [t.id]: { loading: false, items: r.data || [], error: '' } }));
    } catch (e) {
      setItemsCache(prev => ({ ...prev, [t.id]: { loading: false, items: null, error: e.response?.data?.error || 'Error al cargar ítems' } }));
    }
  };

  // Marca/desmarca un ítem de la orden como hecho (optimista; revierte si falla)
  const toggleItemHecho = async (trabajo, item) => {
    const trabajoId = trabajo.id;
    const nuevo = !item.hecho;
    setItemsCache(prev => {
      const info = prev[trabajoId];
      if (!info?.items) return prev;
      return { ...prev, [trabajoId]: { ...info,
        items: info.items.map(it => it.id === item.id ? { ...it, hecho: nuevo } : it) } };
    });
    try {
      await api.put(`/trabajos/${trabajoId}/items/${item.id}`, { hecho: nuevo });
      // Si con este marcado quedaron TODOS los ítems hechos y la orden aún no está
      // completada, ofrecer cambiar el estado a "Completado".
      if (nuevo && trabajo.estado !== 'completado') {
        const info = itemsCache[trabajoId];
        const total = info?.items?.length || 0;
        const todosHechos = total > 0 &&
          info.items.every(it => it.id === item.id ? true : it.hecho);
        if (todosHechos) {
          setRequiereComentario(false);
          setComentarioCierre('');
          setConfirmarCompletar({ id: trabajoId, nombre: trabajo.nombre || trabajo.numero_presupuesto });
        }
      }
    } catch (e) {
      // Revertir
      setItemsCache(prev => {
        const info = prev[trabajoId];
        if (!info?.items) return prev;
        return { ...prev, [trabajoId]: { ...info,
          items: info.items.map(it => it.id === item.id ? { ...it, hecho: !nuevo } : it) } };
      });
      setError(e.response?.data?.error || 'No se pudo actualizar el ítem');
    }
  };

  // Confirma el cambio de estado de la orden a "Completado" desde el modal.
  // Si el backend exige evidencia (sin comprobante ni comentario), pide un
  // comentario de cierre en el mismo modal y reintenta con él.
  const ejecutarCompletar = async () => {
    if (!confirmarCompletar) return;
    if (requiereComentario && comentarioCierre.trim().length === 0) return;
    setCompletandoOrden(true);
    try {
      const body = { estado: 'completado' };
      if (requiereComentario) body.notas = comentarioCierre.trim();
      await api.put(`/trabajos/${confirmarCompletar.id}`, body);
      setConfirmarCompletar(null);
      setRequiereComentario(false);
      setComentarioCierre('');
      cargar();
    } catch (e) {
      if (e.response?.data?.code === 'CIERRE_SIN_EVIDENCIA') {
        // No hay comprobante asociado: exigir comentario y dejar el modal abierto.
        setRequiereComentario(true);
      } else {
        setError(e.response?.data?.error || 'No se pudo completar la orden');
        setConfirmarCompletar(null);
      }
    } finally {
      setCompletandoOrden(false);
    }
  };

  const cerrarConfirmarCompletar = () => {
    setConfirmarCompletar(null);
    setRequiereComentario(false);
    setComentarioCierre('');
  };

  const ESTADO_CFG = {
    pendiente:   { label: 'Pendiente',   bg: '#1C2922', color: '#8A9994' },
    programado:  { label: 'Programado',  bg: '#0D1E2E', color: '#38BDF8' },
    en_proceso:  { label: 'En proceso',  bg: '#2A1F0A', color: '#F59E0B' },
    completado:  { label: 'Completado',  bg: '#12382D', color: '#4CAF50' },
    cancelado:   { label: 'Cancelado',   bg: '#2D0F0F', color: '#EF4444' },
  };

  const PRIORIDAD_CFG = {
    alta:  { label: 'Alta',  bg: '#2D0F0F', color: '#EF4444' },
    media: { label: 'Media', bg: '#2A1F0A', color: '#F59E0B' },
    baja:  { label: 'Baja',  bg: '#1C2922', color: '#8A9994' },
  };

  const TIPO_LABEL = { reparacion: 'Reparación', modernizacion: 'Modernización', normativo: 'Normativo', menor: 'Menor' };

  const CLP = (n) => Number(n || 0).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = filtroEstado ? { estado: filtroEstado } : {};
      const r = await api.get('/trabajos', { params });
      setTrabajos(r.data);
    } catch { setError('Error al cargar órdenes'); }
    finally { setLoading(false); }
  }, [filtroEstado]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    api.get('/admin/tecnicos').then(r => setTecnicos(r.data.filter(t => t.activo))).catch(() => {});
    api.get('/admin/ascensores').then(r => {
      const nombres = [...new Set((r.data || []).map(a => a.comunidad).filter(Boolean))].sort();
      setComunidades(nombres);
    }).catch(() => {});
  }, []);

  const crearOrden = async () => {
    if (!nuevaForm.descripcion.trim()) { setError('La descripción es requerida'); return; }
    setGuardandoNueva(true);
    try {
      await api.post('/trabajos', {
        descripcion:      nuevaForm.descripcion.trim(),
        comunidad:        nuevaForm.comunidad.trim() || null,
        fecha_programada: nuevaForm.fecha_programada || null,
        prioridad:        nuevaForm.prioridad,
      });
      setCreando(false);
      setNuevaForm({ descripcion:'', comunidad:'', fecha_programada:'', prioridad:'media' });
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al crear la orden');
    } finally {
      setGuardandoNueva(false);
    }
  };

  const abrirEditar = (t) => {
    setEditando(t);
    setForm({
      estado:          t.estado,
      tecnico_id:      t.tecnico_id || '',
      fecha_programada: t.fecha_programada ? t.fecha_programada.slice(0, 10) : '',
      notas:           t.notas || '',
      prioridad:       t.prioridad || 'media',
    });
    setError('');
    setDevolviendo(false);
    setMotivoDevol('');
    setComprobantes([]);
    api.get(`/trabajos/${t.id}`)
      .then(r => {
        setComprobantes(r.data.comprobantes || []);
        setEditando(prev => prev ? { ...prev, ...r.data } : prev);
      })
      .catch(() => setComprobantes([]));
  };

  const guardar = async () => {
    // Confirmación automática al marcar la orden como Completada.
    if (form.estado === 'completado' && editando?.estado !== 'completado') {
      setConfirmarGuardar(true);
      return;
    }
    await ejecutarGuardar();
  };

  const ejecutarGuardar = async () => {
    setGuardando(true);
    try {
      await api.put(`/trabajos/${editando.id}`, {
        estado:          form.estado || null,
        tecnico_id:      form.tecnico_id || null,
        fecha_programada: form.fecha_programada || null,
        notas:           form.notas || null,
        prioridad:       form.prioridad || null,
      });
      setConfirmarGuardar(false);
      setEditando(null);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
      setConfirmarGuardar(false);
    } finally {
      setGuardando(false);
    }
  };

  // Sube (o reutiliza) el PDF del presupuesto de una orden ya completada a Drive.
  const subirPdfDrive = async () => {
    if (!editando?.id) return;
    setSubiendoPdf(true);
    setError('');
    try {
      const r = await api.post(`/trabajos/${editando.id}/subir-pdf`);
      setEditando(prev => prev ? { ...prev, pdf_completado_url: r.data.url, pdf_completado_drive_id: 'ok' } : prev);
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudo subir el PDF a Drive');
    } finally {
      setSubiendoPdf(false);
    }
  };

  // Devuelve el presupuesto (aceptado por error) a borrador y elimina esta orden.
  // Solo admin/superadmin; motivo obligatorio (queda en auditoría).
  const devolverAPresupuesto = async () => {
    if (!editando?.presupuesto_id) return;
    if (motivoDevol.trim().length < 5) {
      setError('El motivo es obligatorio (mínimo 5 caracteres)');
      return;
    }
    setGuardandoDevol(true);
    setError('');
    try {
      await api.post(`/presupuestos/${editando.presupuesto_id}/devolver-borrador`, {
        motivo: motivoDevol.trim(),
      });
      setEditando(null);
      setDevolviendo(false);
      setMotivoDevol('');
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al devolver a presupuesto');
    } finally {
      setGuardandoDevol(false);
    }
  };

  const inp = { width:'100%', padding:'9px 11px', border:'1.5px solid #2E4A40',
                borderRadius:8, fontSize:14, background:'#0D1311', color:'#F4F7F5',
                fontFamily:'inherit', boxSizing:'border-box' };

  return (
    <div>
      {/* Filtros */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
          style={{ ...inp, width:'auto', padding:'8px 10px' }}>
          <option value="">Todos los estados</option>
          {Object.entries(ESTADO_CFG).map(([k,v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <button onClick={cargar}
          style={{ padding:'8px 16px', background:'#0f6e56', color:'white',
                   border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600 }}>
          Actualizar
        </button>
        <button onClick={() => { setError(''); setNuevaForm({ descripcion:'', comunidad:'', fecha_programada:'', prioridad:'media' }); setCreando(true); }}
          style={{ padding:'8px 16px', background:'#0D1E2E', color:'#38BDF8',
                   border:'1px solid #38BDF8', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600 }}>
          ＋ Nueva orden
        </button>
        <span style={{ fontSize:12, color:'#8A9994', marginLeft:'auto' }}>
          {trabajos.length} orden{trabajos.length !== 1 ? 'es' : ''}
        </span>
      </div>

      {error && (
        <div style={{ background:'#2D0F0F', color:'#EF4444', borderRadius:8,
                      padding:'10px 14px', marginBottom:14, fontSize:14 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color:'#8A9994', textAlign:'center', padding:32 }}>Cargando…</p>
      ) : trabajos.length === 0 ? (
        <div style={{ textAlign:'center', padding:'48px 0', color:'#8A9994' }}>
          <p style={{ fontSize:32, marginBottom:8 }}>📋</p>
          <p>Sin órdenes de trabajo {filtroEstado ? `en estado "${ESTADO_CFG[filtroEstado]?.label}"` : ''}</p>
        </div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#0D1311' }}>
                {['Orden','Comunidad','Tipo','Total','Estado','Prioridad','Técnico','F. Programada',''].map(h => (
                  <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontWeight:600,
                                       color:'#8A9994', borderBottom:'1px solid #1C2922',
                                       whiteSpace:'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trabajos.map(t => {
                const cfg = ESTADO_CFG[t.estado] || ESTADO_CFG.pendiente;
                const info = itemsCache[t.id];
                return (
                  <React.Fragment key={t.id}>
                  <tr style={{ borderBottom:'1px solid #111714' }}>
                    <td style={{ padding:'10px 12px' }}>
                      <button onClick={() => toggleExpandirOrden(t)} title="Ver ítems / detalle"
                        style={{ background:'none', border:'none', cursor:'pointer', padding:0,
                                 display:'flex', alignItems:'center', gap:5, textAlign:'left' }}>
                        <span style={{ color:'#6B7C74', fontSize:10, width:8 }}>
                          {expandido === t.id ? '▾' : '▸'}
                        </span>
                        <span style={{ fontWeight:600, color:'#F4F7F5', fontSize:13 }}>
                          {t.nombre || t.numero_presupuesto}
                        </span>
                      </button>
                      {t.numero_presupuesto ? (
                        <div style={{ fontSize:11, color:'#8A9994', fontFamily:'monospace', marginLeft:13 }}>
                          {t.numero_presupuesto}
                        </div>
                      ) : (
                        <span style={{ fontSize:10, color:'#38BDF8', background:'#0D1E2E',
                                       borderRadius:4, padding:'1px 6px', fontWeight:600, marginLeft:13 }}>
                          Manual
                        </span>
                      )}
                    </td>
                    <td style={{ padding:'10px 12px', color:'#C9D3CE', maxWidth:140,
                                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                      {t.comunidad || '—'}
                    </td>
                    <td style={{ padding:'10px 12px', color:'#C9D3CE' }}>
                      {TIPO_LABEL[t.tipo] || t.tipo || '—'}
                    </td>
                    <td style={{ padding:'10px 12px', color:'#4CAF50', fontWeight:700, whiteSpace:'nowrap' }}>
                      {CLP(t.total)}
                    </td>
                    <td style={{ padding:'10px 12px' }}>
                      <span style={{ background:cfg.bg, color:cfg.color, borderRadius:4,
                                     padding:'3px 9px', fontSize:12, fontWeight:600 }}>
                        {cfg.label}
                      </span>
                    </td>
                    <td style={{ padding:'10px 12px' }}>
                      {(() => {
                        const pc = PRIORIDAD_CFG[t.prioridad] || PRIORIDAD_CFG.media;
                        return (
                          <span style={{ background:pc.bg, color:pc.color, borderRadius:4,
                                         padding:'3px 9px', fontSize:12, fontWeight:600 }}>
                            {pc.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ padding:'10px 12px', color:'#C9D3CE' }}>
                      {t.tecnico_nombre || <span style={{ color:'#555' }}>Sin asignar</span>}
                    </td>
                    <td style={{ padding:'10px 12px', color:'#C9D3CE', whiteSpace:'nowrap' }}>
                      {t.fecha_programada
                        ? fechaCL(t.fecha_programada)
                        : <span style={{ color:'#555' }}>—</span>}
                    </td>
                    <td style={{ padding:'10px 12px' }}>
                      <button onClick={() => abrirEditar(t)}
                        style={{ padding:'4px 12px', background:'#0D1E2E', color:'#38BDF8',
                                 border:'1px solid #38BDF8', borderRadius:6,
                                 cursor:'pointer', fontSize:12, fontWeight:600 }}>
                        Gestionar
                      </button>
                    </td>
                  </tr>
                  {expandido === t.id && (
                    <tr style={{ background:'#0B0F0D' }}>
                      <td colSpan={9} style={{ padding:'0 12px 12px 33px' }}>
                        {info?.loading ? (
                          <p style={{ color:'#8A9994', fontSize:12, margin:'6px 0' }}>Cargando…</p>
                        ) : info?.error ? (
                          <p style={{ color:'#EF4444', fontSize:12, margin:'6px 0' }}>{info.error}</p>
                        ) : info?.manual ? (
                          <p style={{ color:'#C9D3CE', fontSize:13, margin:'6px 0',
                                      borderLeft:'2px solid #2E4A40', paddingLeft:12 }}>
                            {info.descripcion || 'Orden manual sin descripción.'}
                          </p>
                        ) : info?.items && info.items.length > 0 ? (
                          (() => {
                            const hechos = info.items.filter(it => it.hecho).length;
                            const total  = info.items.length;
                            const pct    = Math.round((hechos / total) * 100);
                            return (
                          <div style={{ borderLeft:'2px solid #2E4A40', paddingLeft:12 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:10, margin:'6px 0 8px' }}>
                              <div style={{ flex:1, maxWidth:220, height:6, background:'#1C2922',
                                            borderRadius:3, overflow:'hidden' }}>
                                <div style={{ width:`${pct}%`, height:'100%',
                                              background: hechos === total ? '#4CAF50' : '#38BDF8',
                                              transition:'width .2s' }} />
                              </div>
                              <span style={{ fontSize:12, fontWeight:600,
                                             color: hechos === total ? '#4CAF50' : '#8A9994' }}>
                                {hechos}/{total} realizados
                              </span>
                            </div>
                            {info.items.map((it, idx) => (
                              <div key={it.id || idx} style={{ padding:'4px 0' }}>
                                <label title={it.hecho && it.hecho_por_nombre
                                        ? `Marcado por ${it.hecho_por_nombre}` : undefined}
                                  style={{ display:'flex', gap:8, alignItems:'flex-start',
                                      fontSize:13, cursor:'pointer',
                                      color: it.hecho ? '#6B7C74' : '#C9D3CE' }}>
                                  <input type="checkbox" checked={!!it.hecho}
                                    onChange={() => toggleItemHecho(t, it)}
                                    style={{ marginTop:2, accentColor:'#4CAF50', cursor:'pointer' }} />
                                  <span style={{ flex:1, textDecoration: it.hecho ? 'line-through' : 'none' }}>
                                    {it.descripcion}
                                  </span>
                                  <span style={{ color:'#8A9994', whiteSpace:'nowrap',
                                                 textDecoration: it.hecho ? 'line-through' : 'none' }}>
                                    × {it.cantidad}
                                  </span>
                                </label>
                                {it.comentario && (
                                  <div style={{ color:'#9FAAA5', fontSize:12, marginLeft:24, marginTop:2,
                                                whiteSpace:'pre-wrap' }}>
                                    {it.comentario}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                            );
                          })()
                        ) : (
                          <p style={{ color:'#6B7C74', fontSize:12, margin:'6px 0' }}>Sin ítems</p>
                        )}
                        <MaterialesOrden trabajoId={t.id} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal gestión */}
      {editando && (
        <Modal title={`Orden — ${editando.nombre || editando.numero_presupuesto}`}
               onClose={() => setEditando(null)} maxWidth={480}>
          <div style={{ marginBottom:14 }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14,
                          background:'#0D1311', borderRadius:8, padding:'12px 14px' }}>
              <div>
                <span style={{ fontSize:11, color:'#8A9994', display:'block', marginBottom:2 }}>COMUNIDAD</span>
                <span style={{ fontSize:14, color:'#F4F7F5' }}>{editando.comunidad || '—'}</span>
              </div>
              <div>
                <span style={{ fontSize:11, color:'#8A9994', display:'block', marginBottom:2 }}>TOTAL</span>
                <span style={{ fontSize:14, color:'#4CAF50', fontWeight:700 }}>{CLP(editando.total)}</span>
              </div>
            </div>

            {comprobantes.length > 0 && (
              <div style={{ marginBottom:14, background:'#0D1311', borderRadius:8, padding:'10px 14px' }}>
                <span style={{ fontSize:11, color:'#8A9994', display:'block', marginBottom:6 }}>
                  COMPROBANTES ASOCIADOS ({comprobantes.length})
                </span>
                {comprobantes.map(c => (
                  <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                                           padding:'6px 0', borderTop:'1px solid #1C2922', fontSize:13, gap:8 }}>
                    <span style={{ color:'#C9D3CE' }}>
                      {c.folio} · {c.tipo_visita || (c.tipo === 'mantenimiento' ? 'Mantenimiento' : 'Visita')} · Asc. {c.n_ascensor}
                    </span>
                    <span style={{ color:'#8A9994', whiteSpace:'nowrap' }}>{fechaCL(c.fecha_visita)}</span>
                  </div>
                ))}
              </div>
            )}

            {editando.completado_at && (
              <div style={{ marginBottom:14, fontSize:12, color:'#4CAF50' }}>
                Completado el {fechaCL(editando.completado_at)}
                {editando.completado_por_nombre ? ` por ${editando.completado_por_nombre}` : ''}
              </div>
            )}

            {/* PDF del presupuesto en Drive (órdenes completadas con presupuesto) */}
            {editando.estado === 'completado' && editando.presupuesto_id && (
              <div style={{ marginBottom:14, background:'#0D1311', borderRadius:8, padding:'10px 14px',
                            display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, flexWrap:'wrap' }}>
                <span style={{ fontSize:12, color:'#8A9994' }}>PDF del presupuesto en Drive</span>
                {editando.pdf_completado_url ? (
                  <a href={editando.pdf_completado_url} target="_blank" rel="noreferrer"
                     style={{ fontSize:13, color:'#4CAF50', fontWeight:600 }}>
                    📄 Ver en Drive
                  </a>
                ) : (
                  <button onClick={subirPdfDrive} disabled={subiendoPdf}
                    style={{ padding:'7px 14px', background: subiendoPdf ? '#0a4a38' : '#0f6e56',
                             color:'white', border:'none', borderRadius:7, fontWeight:600,
                             fontSize:13, cursor: subiendoPdf ? 'wait' : 'pointer' }}>
                    {subiendoPdf ? 'Subiendo…' : '☁️ Subir PDF a Drive'}
                  </button>
                )}
              </div>
            )}

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>
                Estado
              </label>
              <select value={form.estado} onChange={e => setForm(p => ({ ...p, estado: e.target.value }))}
                style={inp}>
                {Object.entries(ESTADO_CFG).map(([k,v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>
                Técnico asignado
              </label>
              <select value={form.tecnico_id} onChange={e => setForm(p => ({ ...p, tecnico_id: e.target.value }))}
                style={inp}>
                <option value="">Sin asignar</option>
                {tecnicos.map(t => (
                  <option key={t.id} value={t.id}>{t.nombre}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>
                Fecha programada
              </label>
              <input type="date" value={form.fecha_programada}
                onChange={e => setForm(p => ({ ...p, fecha_programada: e.target.value }))}
                style={inp} />
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>
                Prioridad
              </label>
              <select value={form.prioridad} onChange={e => setForm(p => ({ ...p, prioridad: e.target.value }))}
                style={inp}>
                {Object.entries(PRIORIDAD_CFG).map(([k,v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom:6 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>
                Notas internas
              </label>
              <textarea value={form.notas} rows={3}
                onChange={e => setForm(p => ({ ...p, notas: e.target.value }))}
                placeholder="Observaciones, instrucciones especiales…"
                style={{ ...inp, resize:'vertical' }} />
            </div>

            {error && (
              <p style={{ color:'#EF4444', fontSize:13, margin:'8px 0' }}>{error}</p>
            )}

            {/* Devolver a presupuesto (corrección de marcaje) — solo admin/superadmin */}
            {esAdmin && editando.presupuesto_id && (
              <div style={{ marginTop:14, paddingTop:14, borderTop:'1px solid #1C2922' }}>
                {!devolviendo ? (
                  <button onClick={() => { setError(''); setMotivoDevol(''); setDevolviendo(true); }}
                    style={{ padding:'7px 14px', border:'1px solid #3D2B00', borderRadius:8,
                             background:'#1C1200', color:'#F59E0B', cursor:'pointer',
                             fontSize:13, fontWeight:600 }}>
                    ↩ Devolver a presupuesto
                  </button>
                ) : (
                  <div style={{ background:'#1A1408', border:'1px solid #3D2B00',
                                borderRadius:8, padding:'12px 14px' }}>
                    <p style={{ margin:'0 0 10px', fontSize:13, color:'#C9D3CE', lineHeight:1.5 }}>
                      El presupuesto <strong>{editando.numero_presupuesto}</strong> volverá a
                      <strong> Borrador</strong> para editarlo y esta orden se eliminará.
                      Indica el motivo (queda registrado en la auditoría).
                    </p>
                    <textarea autoFocus value={motivoDevol} rows={3}
                      onChange={e => setMotivoDevol(e.target.value)}
                      placeholder="Ej: Se aceptó por error / faltaba ajustar valores antes de aprobar…"
                      style={{ ...inp, resize:'vertical', border:'1.5px solid #3D2B00' }} />
                    <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:10 }}>
                      <button onClick={() => { setDevolviendo(false); setError(''); }}
                        style={{ padding:'8px 14px', border:'1px solid #2E4A40', borderRadius:8,
                                 background:'none', color:'#8A9994', cursor:'pointer', fontSize:13 }}>
                        Cancelar
                      </button>
                      <button onClick={devolverAPresupuesto} disabled={guardandoDevol}
                        style={{ padding:'8px 16px', background: guardandoDevol ? '#7a5a10' : '#B45309',
                                 color:'white', border:'none', borderRadius:8, fontWeight:600,
                                 cursor: guardandoDevol ? 'wait' : 'pointer', fontSize:13 }}>
                        {guardandoDevol ? 'Devolviendo…' : 'Confirmar devolución'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={() => setEditando(null)}
              style={{ padding:'9px 18px', border:'1px solid #2E4A40', borderRadius:8,
                       background:'none', color:'#8A9994', cursor:'pointer', fontSize:14 }}>
              Cancelar
            </button>
            <button onClick={guardar} disabled={guardando}
              style={{ padding:'9px 20px', background: guardando ? '#0a4a38' : '#0f6e56',
                       color:'white', border:'none', borderRadius:8, fontWeight:600,
                       cursor: guardando ? 'wait' : 'pointer', fontSize:14 }}>
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal nueva orden manual */}
      {creando && (
        <Modal title="Nueva orden de trabajo" onClose={() => setCreando(false)} maxWidth={480}>
          <div style={{ marginBottom:14 }}>
            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>
                Descripción del trabajo *
              </label>
              <textarea value={nuevaForm.descripcion} rows={3} autoFocus
                onChange={e => setNuevaForm(p => ({ ...p, descripcion: e.target.value }))}
                placeholder="Ej: Cambiar botonera piso 3, ajustar puerta de cabina…"
                style={{ ...inp, resize:'vertical' }} />
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>
                Comunidad
              </label>
              <input list="comunidades-orden" value={nuevaForm.comunidad}
                onChange={e => setNuevaForm(p => ({ ...p, comunidad: e.target.value }))}
                placeholder="Escribe o selecciona (opcional)"
                style={inp} />
              <datalist id="comunidades-orden">
                {comunidades.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:6 }}>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>
                  Fecha límite
                </label>
                <input type="date" value={nuevaForm.fecha_programada}
                  onChange={e => setNuevaForm(p => ({ ...p, fecha_programada: e.target.value }))}
                  style={inp} />
              </div>
              <div>
                <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>
                  Prioridad
                </label>
                <select value={nuevaForm.prioridad}
                  onChange={e => setNuevaForm(p => ({ ...p, prioridad: e.target.value }))}
                  style={inp}>
                  {Object.entries(PRIORIDAD_CFG).map(([k,v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <p style={{ color:'#EF4444', fontSize:13, margin:'10px 0 0' }}>{error}</p>
            )}
          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={() => setCreando(false)}
              style={{ padding:'9px 18px', border:'1px solid #2E4A40', borderRadius:8,
                       background:'none', color:'#8A9994', cursor:'pointer', fontSize:14 }}>
              Cancelar
            </button>
            <button onClick={crearOrden} disabled={guardandoNueva}
              style={{ padding:'9px 20px', background: guardandoNueva ? '#0a4a38' : '#0f6e56',
                       color:'white', border:'none', borderRadius:8, fontWeight:600,
                       cursor: guardandoNueva ? 'wait' : 'pointer', fontSize:14 }}>
              {guardandoNueva ? 'Creando…' : 'Crear orden'}
            </button>
          </div>
        </Modal>
      )}

      {/* Modal confirmar completar orden (al marcar todos los ítems) */}
      {confirmarCompletar && (() => {
        const puedeCompletar = !requiereComentario || comentarioCierre.trim().length > 0;
        return (
        <Modal title="¿Completar la orden?" onClose={cerrarConfirmarCompletar} maxWidth={420}>
          <p style={{ margin:'0 0 14px', fontSize:14, color:'#C9D3CE', lineHeight:1.5 }}>
            Marcaste <strong>todos los ítems</strong> de la orden
            {confirmarCompletar.nombre ? <> <strong>{confirmarCompletar.nombre}</strong></> : null} como realizados.
            ¿Quieres cambiar su estado a <strong style={{ color:'#4CAF50' }}>Completado</strong>?
          </p>
          {requiereComentario && (
            <div style={{ marginBottom:18 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#F59E0B', marginBottom:5 }}>
                Comentario de cierre (obligatorio — no hay comprobante asociado)
              </label>
              <textarea value={comentarioCierre} onChange={e => setComentarioCierre(e.target.value)}
                rows={3} autoFocus placeholder="Describe el trabajo realizado o la evidencia del cierre…"
                style={{ ...inp, width:'100%', resize:'vertical', boxSizing:'border-box' }} />
            </div>
          )}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={cerrarConfirmarCompletar}
              style={{ padding:'9px 18px', border:'1px solid #2E4A40', borderRadius:8,
                       background:'none', color:'#8A9994', cursor:'pointer', fontSize:14 }}>
              Ahora no
            </button>
            <button onClick={ejecutarCompletar} disabled={completandoOrden || !puedeCompletar}
              style={{ padding:'9px 20px',
                       background: (completandoOrden || !puedeCompletar) ? '#0a4a38' : '#0f6e56',
                       color:'white', border:'none', borderRadius:8, fontWeight:600,
                       cursor: completandoOrden ? 'wait' : (puedeCompletar ? 'pointer' : 'not-allowed'),
                       opacity: puedeCompletar ? 1 : 0.7, fontSize:14 }}>
              {completandoOrden ? 'Completando…' : 'Sí, completar'}
            </button>
          </div>
        </Modal>
        );
      })()}

      {/* Modal confirmar completar orden (al cambiar el estado desde Gestionar) */}
      {confirmarGuardar && (() => {
        const tieneComprobante = comprobantes.length > 0;
        const comentarioOk = tieneComprobante || (form.notas || '').trim().length > 0;
        return (
        <Modal title="¿Marcar la orden como Completada?" onClose={() => setConfirmarGuardar(false)} maxWidth={440}>
          <p style={{ margin:'0 0 14px', fontSize:14, color:'#C9D3CE', lineHeight:1.5 }}>
            Vas a cambiar el estado de esta orden a <strong style={{ color:'#4CAF50' }}>Completado</strong>.
            {editando?.presupuesto_id
              ? <> Se generará el PDF del presupuesto asociado y se archivará automáticamente en la carpeta de Drive de trabajos completados.</>
              : <> Esta orden es manual (sin presupuesto), por lo que no se subirá ningún PDF a Drive.</>}
          </p>
          {tieneComprobante ? (
            <p style={{ margin:'0 0 18px', fontSize:13, color:'#4CAF50' }}>
              ✓ La orden tiene {comprobantes.length} comprobante(s) asociado(s) como evidencia.
            </p>
          ) : (
            <div style={{ marginBottom:18 }}>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#F59E0B', marginBottom:5 }}>
                Comentario de cierre (obligatorio — no hay comprobante asociado)
              </label>
              <textarea value={form.notas || ''} onChange={e => setForm(p => ({ ...p, notas: e.target.value }))}
                rows={3} autoFocus placeholder="Describe el trabajo realizado o la evidencia del cierre…"
                style={{ ...inp, width:'100%', resize:'vertical', boxSizing:'border-box' }} />
            </div>
          )}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={() => setConfirmarGuardar(false)} disabled={guardando}
              style={{ padding:'9px 18px', border:'1px solid #2E4A40', borderRadius:8,
                       background:'none', color:'#8A9994', cursor:'pointer', fontSize:14 }}>
              Cancelar
            </button>
            <button onClick={ejecutarGuardar} disabled={guardando || !comentarioOk}
              style={{ padding:'9px 20px',
                       background: (guardando || !comentarioOk) ? '#0a4a38' : '#0f6e56',
                       color:'white', border:'none', borderRadius:8, fontWeight:600,
                       cursor: guardando ? 'wait' : (comentarioOk ? 'pointer' : 'not-allowed'),
                       opacity: comentarioOk ? 1 : 0.7, fontSize:14 }}>
              {guardando ? 'Completando…' : 'Sí, completar'}
            </button>
          </div>
        </Modal>
        );
      })()}
    </div>
  );
}

function TabRecordatorios() {
  const [items,     setItems]     = useState([]);
  const [tecnicos,  setTecnicos]  = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [verHechos, setVerHechos] = useState(false);
  const [nuevo,     setNuevo]     = useState('');
  const [prioridad, setPrioridad] = useState('media');
  const [creando,   setCreando]   = useState(false);
  const [error,     setError]     = useState('');

  const PRIORIDAD_CFG = {
    alta:  { label: 'Alta',  bg: '#2D0F0F', color: '#EF4444' },
    media: { label: 'Media', bg: '#2A1F0A', color: '#F59E0B' },
    baja:  { label: 'Baja',  bg: '#1C2922', color: '#8A9994' },
  };

  const inp = { padding:'9px 11px', border:'1.5px solid #2E4A40', borderRadius:8,
                fontSize:14, background:'#0D1311', color:'#F4F7F5',
                fontFamily:'inherit', boxSizing:'border-box' };

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = verHechos ? {} : { hecho: 'false' };
      const r = await api.get('/recordatorios', { params });
      setItems(r.data);
    } catch { setError('Error al cargar pendientes'); }
    finally { setLoading(false); }
  }, [verHechos]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    api.get('/admin/tecnicos').then(r => setTecnicos(r.data.filter(t => t.activo))).catch(() => {});
  }, []);

  const agregar = async () => {
    if (!nuevo.trim()) return;
    setCreando(true);
    setError('');
    try {
      await api.post('/recordatorios', { texto: nuevo.trim(), prioridad });
      setNuevo('');
      setPrioridad('media');
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al agregar');
    } finally {
      setCreando(false);
    }
  };

  const actualizar = async (id, cambios) => {
    try {
      await api.put(`/recordatorios/${id}`, cambios);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al actualizar');
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este pendiente?')) return;
    try {
      await api.delete(`/recordatorios/${id}`);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al eliminar');
    }
  };

  const pendientesCount = items.filter(i => !i.hecho).length;

  return (
    <div>
      {/* Captura rápida */}
      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <input
          value={nuevo}
          onChange={e => setNuevo(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') agregar(); }}
          placeholder="Escribe un pendiente y presiona Enter…"
          style={{ ...inp, flex:1, minWidth:220 }}
        />
        <select value={prioridad} onChange={e => setPrioridad(e.target.value)}
          style={{ ...inp, width:'auto' }}>
          {Object.entries(PRIORIDAD_CFG).map(([k,v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <button onClick={agregar} disabled={creando || !nuevo.trim()}
          style={{ padding:'9px 18px', background: (creando || !nuevo.trim()) ? '#0a4a38' : '#0f6e56',
                   color:'white', border:'none', borderRadius:8, fontWeight:600,
                   cursor: (creando || !nuevo.trim()) ? 'default' : 'pointer', fontSize:14 }}>
          {creando ? 'Agregando…' : 'Agregar'}
        </button>
      </div>

      <div style={{ display:'flex', gap:12, marginBottom:14, alignItems:'center' }}>
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'#8A9994', cursor:'pointer' }}>
          <input type="checkbox" checked={verHechos} onChange={e => setVerHechos(e.target.checked)} />
          Mostrar completados
        </label>
        <span style={{ fontSize:12, color:'#8A9994', marginLeft:'auto' }}>
          {pendientesCount} pendiente{pendientesCount !== 1 ? 's' : ''}
        </span>
      </div>

      {error && (
        <div style={{ background:'#2D0F0F', color:'#EF4444', borderRadius:8,
                      padding:'10px 14px', marginBottom:14, fontSize:14 }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color:'#8A9994', textAlign:'center', padding:32 }}>Cargando…</p>
      ) : items.length === 0 ? (
        <div style={{ textAlign:'center', padding:'48px 0', color:'#8A9994' }}>
          <p style={{ fontSize:32, marginBottom:8 }}>✅</p>
          <p>Sin pendientes {verHechos ? '' : 'por hacer'}</p>
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {items.map(it => {
            const pc = PRIORIDAD_CFG[it.prioridad] || PRIORIDAD_CFG.media;
            const vencido = it.fecha_limite && !it.hecho &&
              String(it.fecha_limite).slice(0,10) < hoyChile();
            return (
              <div key={it.id} style={{
                display:'flex', alignItems:'flex-start', gap:10,
                background:'#161C19', borderRadius:10, border:'1px solid rgba(255,255,255,0.07)',
                padding:'12px 14px', opacity: it.hecho ? 0.55 : 1,
              }}>
                <input type="checkbox" checked={it.hecho}
                  onChange={e => actualizar(it.id, { hecho: e.target.checked })}
                  style={{ marginTop:3, cursor:'pointer', width:18, height:18, flexShrink:0 }} />

                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ margin:'0 0 6px', fontSize:14, color:'#F4F7F5',
                              textDecoration: it.hecho ? 'line-through' : 'none' }}>
                    {it.texto}
                  </p>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                    <span style={{ background:pc.bg, color:pc.color, borderRadius:4,
                                   padding:'2px 8px', fontSize:11, fontWeight:600 }}>
                      {pc.label}
                    </span>

                    <select value={it.responsable_id || ''}
                      onChange={e => actualizar(it.id, { responsable_id: e.target.value || null })}
                      style={{ ...inp, padding:'3px 8px', fontSize:12, width:'auto' }}>
                      <option value="">Sin asignar</option>
                      {tecnicos.map(t => (
                        <option key={t.id} value={t.id}>{t.nombre}</option>
                      ))}
                    </select>

                    <input type="date" value={it.fecha_limite ? it.fecha_limite.slice(0,10) : ''}
                      onChange={e => actualizar(it.id, { fecha_limite: e.target.value || null })}
                      style={{ ...inp, padding:'3px 8px', fontSize:12, width:'auto',
                               color: vencido ? '#EF4444' : '#C9D3CE',
                               borderColor: vencido ? '#EF4444' : '#2E4A40' }} />

                    {it.creado_por_nombre && (
                      <span style={{ fontSize:11, color:'#6B7C74' }}>
                        por {it.creado_por_nombre}
                      </span>
                    )}
                  </div>
                </div>

                <button onClick={() => eliminar(it.id)}
                  title="Eliminar"
                  style={{ background:'none', border:'none', color:'#6B7C74',
                           cursor:'pointer', fontSize:18, lineHeight:1, flexShrink:0 }}>
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Ventana unificada de Trabajos por equipo: Checklist estándar + Historial de reparaciones.
function TabTrabajos() {
  const [sub, setSub] = useState('checklist'); // 'checklist' | 'historial'
  const tab = (k, label) => (
    <button key={k} onClick={() => setSub(k)}
      style={{ padding: '8px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
               border: '1px solid #2E4A40',
               background: sub === k ? '#0f6e56' : 'transparent',
               color: sub === k ? '#fff' : '#8A9994' }}>{label}</button>
  );
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {tab('checklist', '🔧 Checklist por equipo')}
        {tab('historial', '🗂 Historial de reparaciones')}
      </div>
      {sub === 'checklist' ? <TabTrabajosEquipo /> : <TabReparaciones />}
    </div>
  );
}

function TabTrabajosEquipo() {
  const [comunidades,     setComunidades]     = useState([]); // [{comunidad, marca, modelo}]
  const [ascensores,      setAscensores]      = useState([]);
  const [comunidad,       setComunidad]       = useState('');
  const [ascensorId,      setAscensorId]      = useState('');
  const [registros,       setRegistros]       = useState([]);
  const [formularios,     setFormularios]     = useState([]);
  const [expandido,       setExpandido]       = useState(null);
  const [gestionItems,    setGestionItems]    = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [editandoModelo,  setEditandoModelo]  = useState(false);
  const [marcaEdit,       setMarcaEdit]       = useState('');
  const [modeloEdit,      setModeloEdit]      = useState('');
  const [guardandoModelo, setGuardandoModelo] = useState(false);
  const [agregarItem,    setAgregarItem]    = useState(false);
  const [nuevoTipo,      setNuevoTipo]      = useState('generico');
  const [nuevoNombre,    setNuevoNombre]    = useState('');
  const [nuevoDesc,      setNuevoDesc]      = useState('');
  const [guardandoNuevo, setGuardandoNuevo] = useState(false);
  const [marcasModelos,  setMarcasModelos]  = useState([]);
  const [verCola,        setVerCola]        = useState(false);
  const [colaCount,      setColaCount]      = useState(0);

  const cargarColaCount = useCallback(() => {
    api.get('/trabajos-equipo/por-asociar/count').then(r => setColaCount(r.data.n || 0)).catch(() => {});
  }, []);
  useEffect(() => { cargarColaCount(); }, [cargarColaCount]);

  const cargarComunidades = useCallback(async () => {
    const r = await api.get('/trabajos-equipo/comunidades');
    setComunidades(r.data);
  }, []);

  useEffect(() => { cargarComunidades(); }, [cargarComunidades]);

  useEffect(() => {
    api.get('/trabajos-equipo/marcas-modelos').then(r => setMarcasModelos(r.data)).catch(() => {});
  }, []);

  const comunidadInfo = comunidades.find(c => c.comunidad === comunidad);
  const marcaMod = comunidadInfo ? [comunidadInfo.marca, comunidadInfo.modelo].filter(Boolean).join(' ') : '';

  useEffect(() => {
    if (!comunidad) { setAscensores([]); setAscensorId(''); setEditandoModelo(false); return; }
    api.get('/trabajos-equipo/ascensores', { params: { comunidad } })
      .then(r => { setAscensores(r.data); setAscensorId(''); setRegistros([]); setExpandido(null); })
      .catch(() => {});
  }, [comunidad]);

  const abrirEditarModelo = () => {
    setMarcaEdit(comunidadInfo?.marca || '');
    setModeloEdit(comunidadInfo?.modelo || '');
    setEditandoModelo(true);
  };

  const guardarModelo = async () => {
    setGuardandoModelo(true);
    try {
      await api.put('/trabajos-equipo/comunidad-modelo', { comunidad, marca: marcaEdit, modelo: modeloEdit });
      await cargarComunidades();
      setEditandoModelo(false);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
    setGuardandoModelo(false);
  };

  const cargarRegistros = useCallback(async (id) => {
    setLoading(true);
    setExpandido(null);
    try {
      const [rReg, rForm] = await Promise.all([
        api.get('/trabajos-equipo/registros',  { params: { ascensor_id: id } }),
        api.get('/trabajos-equipo/formularios', { params: { ascensor_id: id } }),
      ]);
      setRegistros(rReg.data);
      setFormularios(rForm.data);
    } catch (e) {
      alert('Error cargando datos: ' + (e.response?.data?.error || e.message));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!ascensorId) { setRegistros([]); setFormularios([]); return; }
    cargarRegistros(ascensorId);
  }, [ascensorId, cargarRegistros]);

  const guardarNuevoItem = async () => {
    if (!nuevoNombre.trim()) { alert('El nombre es requerido'); return; }
    if (nuevoTipo === 'modelo' && !marcaMod) {
      alert('Define primero la marca y modelo de esta comunidad'); return;
    }
    setGuardandoNuevo(true);
    try {
      await api.post('/trabajos-equipo/items', {
        nombre:      nuevoNombre.trim(),
        descripcion: nuevoDesc.trim() || null,
        tipo:        nuevoTipo,
        marca:       nuevoTipo === 'modelo'    ? comunidadInfo?.marca  : null,
        modelo:      nuevoTipo === 'modelo'    ? comunidadInfo?.modelo : null,
        comunidad:   nuevoTipo === 'exclusivo' ? comunidad             : null,
      });
      setAgregarItem(false); setNuevoNombre(''); setNuevoDesc(''); setNuevoTipo('generico');
      if (ascensorId) cargarRegistros(ascensorId);
    } catch (e) {
      alert('Error: ' + (e.response?.data?.error || e.message));
    }
    setGuardandoNuevo(false);
  };

  const toggleExpand = (itemId) => setExpandido(p => p === itemId ? null : itemId);

  const genericos  = registros.filter(r => r.item_tipo === 'generico');
  const deModelo   = registros.filter(r => r.item_tipo === 'modelo');
  const exclusivos = registros.filter(r => r.item_tipo === 'exclusivo');
  const realizados = registros.filter(r => r.estado === 'realizado').length;
  const pendientes = registros.filter(r => !r.estado || r.estado === 'pendiente').length;
  const noAplica   = registros.filter(r => r.estado === 'no_aplica').length;

  const selectSt = { padding: '10px 12px', border: '1.5px solid #2E4A40', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', background: '#0D1311', color: '#F4F7F5', minWidth: 220 };
  const inputSt  = { padding: '8px 10px', border: '1.5px solid #2E4A40', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', background: '#0D1311', color: '#F4F7F5' };

  const marcasDistintas    = [...new Set(marcasModelos.map(mm => mm.marca).filter(Boolean))];
  const modelosPorMarcaEdit = marcasModelos.filter(mm => !marcaEdit || mm.marca === marcaEdit).map(mm => mm.modelo).filter(Boolean);
  const ascensorActual = ascensores.find(a => String(a.id) === String(ascensorId));
  const secciones = [
    { grupo: genericos,  label: '🔧 Ítems genéricos',                          color: '#2c3e50', desc: 'Aplican a todos los equipos' },
    { grupo: deModelo,   label: `🏭 Del modelo${marcaMod ? `: ${marcaMod}` : ''}`, color: '#0f6e56', desc: 'Específicos de este modelo' },
    { grupo: exclusivos, label: `📍 Exclusivos de ${comunidad}`,               color: '#c0392b', desc: 'Solo esta comunidad' },
  ];

  return (
    <div>
      {/* Cabecera */}
      <div style={{ background: '#161C19', borderRadius: 12, padding: '20px 24px', marginBottom: 20, border: '1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#8A9994', marginBottom: 5 }}>Comunidad</label>
            <select value={comunidad} onChange={e => { setComunidad(e.target.value); setEditandoModelo(false); }} style={selectSt}>
              <option value="">— Selecciona comunidad —</option>
              {comunidades.map(c => <option key={c.comunidad} value={c.comunidad}>{c.comunidad}</option>)}
            </select>
          </div>
          {comunidad && (
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#8A9994', marginBottom: 5 }}>Equipo / Ascensor</label>
              <select value={ascensorId} onChange={e => setAscensorId(e.target.value)} style={selectSt}>
                <option value="">— Selecciona equipo —</option>
                {ascensores.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.n_ascensor ? `#${a.n_ascensor}` : ''}{a.torre ? ` Torre ${a.torre}` : ''} — {a.label || a.codigo}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button onClick={() => setVerCola(true)} style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
              border: '1px solid ' + (colaCount ? '#5A4A1E' : '#2E4A40'),
              background: colaCount ? '#2A1F0A' : 'transparent',
              color: colaCount ? '#F59E0B' : '#8A9994',
            }}>🔗 Por asociar{colaCount ? ` (${colaCount})` : ''}</button>
            <Btn onClick={() => setGestionItems(true)} outline color="#0f6e56" small>⚙ Gestionar ítems</Btn>
          </div>
        </div>

        {/* Widget marca/modelo */}
        {comunidad && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {!editandoModelo ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#6B7C74' }}>Marca / Modelo:</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: marcaMod ? '#4CAF50' : '#2E4A40' }}>
                  {marcaMod || 'Sin definir'}
                </span>
                <button onClick={abrirEditarModelo} style={{
                  background: '#0D1311', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
                  padding: '3px 10px', fontSize: 12, cursor: 'pointer', color: '#555',
                }}>✎ Editar</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#6B7C74' }}>Marca / Modelo:</span>
                <input value={marcaEdit} onChange={e => setMarcaEdit(e.target.value)}
                  list="dl-cm-marca" placeholder="Marca (ej: Schindler)"
                  style={{ ...inputSt, width: 160 }} />
                <datalist id="dl-cm-marca">
                  {marcasDistintas.map(m => <option key={m} value={m} />)}
                </datalist>
                <input value={modeloEdit} onChange={e => setModeloEdit(e.target.value)}
                  list="dl-cm-modelo" placeholder="Modelo (ej: 3300)"
                  style={{ ...inputSt, width: 110 }} />
                <datalist id="dl-cm-modelo">
                  {modelosPorMarcaEdit.map(m => <option key={m} value={m} />)}
                </datalist>
                <Btn onClick={guardarModelo} color="#0f6e56" small>{guardandoModelo ? 'Guardando…' : 'Guardar'}</Btn>
                <Btn onClick={() => setEditandoModelo(false)} outline color="#888" small>Cancelar</Btn>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Contadores */}
      {ascensorId && registros.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {[
            { label: 'Realizados', count: realizados, color: '#27ae60' },
            { label: 'Pendientes', count: pendientes, color: '#e67e22' },
            { label: 'No aplica',  count: noAplica,   color: '#95a5a6' },
          ].map(s => (
            <div key={s.label} style={{
              background: '#161C19', borderRadius: 10, padding: '12px 20px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textAlign: 'center',
              borderLeft: `4px solid ${s.color}`,
            }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.count}</div>
              <div style={{ fontSize: 12, color: '#8A9994' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Checklist */}
      {ascensorId ? (
        loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#6B7C74' }}>Cargando…</div>
        ) : (
          <>
            {/* Panel agregar ítem */}
            {agregarItem ? (
              <div style={{ background: '#12382D', borderRadius: 12, padding: 20, marginBottom: 16, borderLeft: '4px solid #0f6e56', border: '1px solid rgba(76,175,80,0.2)' }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14, color: '#F4F7F5' }}>+ Nuevo ítem de checklist</div>
                <div style={{ marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    { value: 'generico',  label: '🌐 Genérico',  color: '#2c3e50', desc: 'Todos los equipos' },
                    { value: 'modelo',    label: '🏭 Del modelo', color: '#0f6e56', desc: marcaMod || '⚠ sin modelo definido' },
                    { value: 'exclusivo', label: '📍 Exclusivo',  color: '#c0392b', desc: `Solo ${comunidad}` },
                  ].map(t => (
                    <button key={t.value} onClick={() => setNuevoTipo(t.value)} style={{
                      padding: '8px 14px', borderRadius: 8, cursor: 'pointer',
                      border: `2px solid ${nuevoTipo === t.value ? t.color : 'rgba(255,255,255,0.1)'}`,
                      background: nuevoTipo === t.value ? t.color + '22' : '#161C19',
                      fontSize: 13, textAlign: 'left',
                    }}>
                      <div style={{ fontWeight: 700, color: nuevoTipo === t.value ? t.color : '#F4F7F5' }}>{t.label}</div>
                      <div style={{ fontSize: 11, color: '#6B7C74', marginTop: 2 }}>{t.desc}</div>
                    </button>
                  ))}
                </div>
                {nuevoTipo === 'modelo' && !marcaMod && (
                  <div style={{ background: '#1C1200', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#F59E0B' }}>
                    ⚠ Define primero la marca y modelo usando el botón "✎ Editar" en la cabecera.
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#8A9994', marginBottom: 4 }}>Nombre *</label>
                    <input value={nuevoNombre} onChange={e => setNuevoNombre(e.target.value)}
                      style={{ ...inputSt, width: '100%', boxSizing: 'border-box', padding: '9px 12px' }}
                      placeholder="Nombre del trabajo…" />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#8A9994', marginBottom: 4 }}>Descripción</label>
                    <input value={nuevoDesc} onChange={e => setNuevoDesc(e.target.value)}
                      style={{ ...inputSt, width: '100%', boxSizing: 'border-box', padding: '9px 12px' }}
                      placeholder="Opcional…" />
                  </div>
                </div>
                <div style={{ padding: '7px 12px', borderRadius: 7, fontSize: 12, marginBottom: 14,
                  background: nuevoTipo === 'generico' ? '#12382D' : nuevoTipo === 'modelo' ? '#0D1F1A' : '#2D0F0F', color: '#C9D3CE',
                }}>
                  {nuevoTipo === 'generico'  && '🌐 Este ítem aparecerá en el checklist de TODOS los equipos.'}
                  {nuevoTipo === 'modelo'    && `🏭 Se agregará a todos los equipos con modelo "${marcaMod || '—'}".`}
                  {nuevoTipo === 'exclusivo' && `📍 Solo aparecerá en los equipos de "${comunidad}".`}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn onClick={() => { setAgregarItem(false); setNuevoNombre(''); setNuevoDesc(''); }} outline color="#888" small>Cancelar</Btn>
                  <Btn onClick={guardarNuevoItem} color="#0f6e56" small>{guardandoNuevo ? 'Guardando…' : 'Agregar ítem'}</Btn>
                </div>
              </div>
            ) : (
              <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <Btn onClick={() => setAgregarItem(true)} color="#0f6e56" small>+ Agregar ítem</Btn>
              </div>
            )}

            {/* Checklist con 3 secciones */}
            <div style={{ background: '#161C19', borderRadius: 12, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', fontWeight: 700, fontSize: 15, color: '#F4F7F5' }}>
                {ascensorActual
                  ? `${comunidad} — Equipo ${ascensorActual.n_ascensor ? `#${ascensorActual.n_ascensor}` : ''}${ascensorActual.torre ? ` Torre ${ascensorActual.torre}` : ''} (${ascensorActual.label || ascensorActual.codigo})`
                  : 'Checklist'}
              </div>
              {secciones.every(s => s.grupo.length === 0) && (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: '#6B7C74', fontSize: 14 }}>
                  No hay ítems para este equipo. Usa "+ Agregar ítem" para comenzar.
                </div>
              )}
              {secciones.map(({ grupo, label, color, desc }) => grupo.length === 0 ? null : (
                <div key={label}>
                  <div style={{ background: color + '0d', borderTop: '1px solid ' + color + '33', padding: '7px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color }}>{label}</span>
                    <span style={{ fontSize: 11, color: '#6B7C74' }}>{desc}</span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#fff', background: color, borderRadius: 20, padding: '1px 8px' }}>
                      {grupo.length}
                    </span>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <tbody>
                      <FilaSeccion
                        grupo={grupo}
                        expandido={expandido}
                        toggleExpand={toggleExpand}
                        formularios={formularios}
                        ascensorId={ascensorId}
                        cargarRegistros={cargarRegistros}
                        setExpandido={setExpandido}
                      />
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </>
        )
      ) : (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6B7C74', background: '#161C19', borderRadius: 12, border: '1px solid rgba(255,255,255,0.07)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔧</div>
          <div style={{ fontSize: 15 }}>Selecciona una comunidad y un equipo para ver el checklist</div>
        </div>
      )}

      {gestionItems && (
        <ModalGestionItems onClose={() => { setGestionItems(false); if (ascensorId) cargarRegistros(ascensorId); }} />
      )}

      {verCola && (
        <ColaAsociarModal
          onClose={() => { setVerCola(false); cargarColaCount(); if (ascensorId) cargarRegistros(ascensorId); }}
          onChange={cargarColaCount}
        />
      )}
    </div>
  );
}

// Cola "Por asociar": reparaciones que el cruce automático no pudo asociar.
function ColaAsociarModal({ onClose, onChange }) {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [aviso, setAviso]       = useState('');
  const [abierto, setAbierto]   = useState(null);       // formulario_id expandido
  const [items, setItems]       = useState([]);          // ítems aplicables del equipo abierto
  const [sel, setSel]           = useState({});          // { item_id: true }
  const [cargandoItems, setCargandoItems] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [nuevoItem, setNuevoItem] = useState('');
  const [filtroItem, setFiltroItem] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/trabajos-equipo/por-asociar'); setRows(r.data || []); }
    catch { setAviso('Error al cargar la cola'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const procesarHistorico = async () => {
    setProcesando(true); setAviso('');
    try {
      const r = await api.post('/trabajos-equipo/procesar-reparaciones');
      setAviso(`Procesados ${r.data.procesados}: ${r.data.auto} auto-asociados, ${r.data.pendientes} por asociar${r.data.errores ? `, ${r.data.errores} con error` : ''}.`);
      cargar(); onChange && onChange();
    } catch (e) { setAviso(e.response?.data?.error || 'No se pudo procesar'); }
    finally { setProcesando(false); }
  };

  const abrir = async (row) => {
    if (abierto === row.formulario_id) { setAbierto(null); return; }
    setAbierto(row.formulario_id); setSel({}); setItems([]); setFiltroItem(''); setCargandoItems(true);
    try {
      const r = await api.get('/trabajos-equipo/registros', { params: { ascensor_id: row.ascensor_id } });
      setItems(r.data || []);
    } catch { setItems([]); }
    finally { setCargandoItems(false); }
  };

  const asociar = async (row) => {
    const ids = Object.keys(sel).filter(k => sel[k]).map(Number);
    if (!ids.length) { setAviso('Selecciona al menos un ítem'); return; }
    setGuardando(true); setAviso('');
    try {
      await api.post(`/trabajos-equipo/por-asociar/${row.formulario_id}/asociar`, { item_ids: ids });
      setAbierto(null); cargar(); onChange && onChange();
    } catch (e) { setAviso(e.response?.data?.error || 'No se pudo asociar'); }
    finally { setGuardando(false); }
  };

  const descartar = async (row) => {
    if (!window.confirm('¿Descartar esta reparación de la cola? (no marcará ningún ítem)')) return;
    try { await api.post(`/trabajos-equipo/por-asociar/${row.formulario_id}/descartar`); cargar(); onChange && onChange(); }
    catch (e) { setAviso(e.response?.data?.error || 'No se pudo descartar'); }
  };

  // Mejora: crear un ítem genérico nuevo y asociarlo de inmediato a este comprobante.
  const crearYAsociar = async (row) => {
    const nombre = nuevoItem.trim();
    if (!nombre) return;
    setGuardando(true); setAviso('');
    try {
      const r = await api.post('/trabajos-equipo/items', { nombre, tipo: 'generico' });
      const nuevoId = r.data?.id;
      if (nuevoId) {
        await api.post(`/trabajos-equipo/por-asociar/${row.formulario_id}/asociar`, { item_ids: [nuevoId] });
      }
      setNuevoItem('');
      cargar(); onChange && onChange();
    } catch (e) { setAviso(e.response?.data?.error || 'No se pudo crear/asociar el ítem'); }
    finally { setGuardando(false); }
  };

  const chip = { fontSize: 12, color: '#C9D3CE', background: '#0D1311', border: '1px solid #2E4A40', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', display: 'flex', gap: 6, alignItems: 'center' };

  return (
    <Modal title="Reparaciones por asociar" onClose={onClose} maxWidth={720}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: 13, color: '#8A9994', flex: 1, minWidth: 240 }}>
          Reparaciones cuyo texto no calzó automáticamente con un ítem del checklist. Ábrelas para
          asociarlas a uno o más ítems, o descártalas. ¿Falta un ítem? Créalo en “Gestionar ítems”.
        </p>
        <button onClick={procesarHistorico} disabled={procesando} style={{
          padding: '8px 14px', background: procesando ? '#0a4a38' : '#0f6e56', color: 'white',
          border: 'none', borderRadius: 8, fontWeight: 600, cursor: procesando ? 'wait' : 'pointer', fontSize: 13 }}>
          {procesando ? 'Procesando…' : '↻ Procesar histórico'}
        </button>
      </div>

      {aviso && <div style={{ background: '#12382D', color: '#4CAF50', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 13 }}>{aviso}</div>}

      {loading ? (
        <p style={{ color: '#6B7C74' }}>Cargando…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: '#6B7C74', padding: 12 }}>No hay reparaciones pendientes de asociar. 🎉</p>
      ) : (
        <div style={{ maxHeight: 420, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map(row => (
            <div key={row.formulario_id} style={{ border: '1px solid #1C2922', borderRadius: 8 }}>
              <div onClick={() => abrir(row)} style={{ padding: '10px 12px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
                  <span style={{ color: '#F4F7F5', fontSize: 13, fontWeight: 600 }}>
                    {row.comunidad} · Asc. {row.n_ascensor}{row.torre ? ` T${row.torre}` : ''}
                  </span>
                  <span style={{ color: '#8A9994', fontSize: 12, whiteSpace: 'nowrap' }}>
                    {row.fecha_visita ? fechaCL(row.fecha_visita) : ''} · {row.folio || ''}
                  </span>
                </div>
                <div style={{ color: '#C9D3CE', fontSize: 12, marginTop: 4, whiteSpace: 'pre-wrap' }}>{row.resumen_visita || '(sin resumen)'}</div>
              </div>
              {abierto === row.formulario_id && (
                <div style={{ borderTop: '1px solid #1C2922', padding: '10px 12px', background: '#0B0F0D' }}>
                  {cargandoItems ? <p style={{ color: '#6B7C74', fontSize: 12 }}>Cargando ítems…</p> : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: '#8A9994', fontWeight: 700, textTransform: 'uppercase' }}>Marca los ítems realizados</span>
                        <input value={filtroItem} onChange={e => setFiltroItem(e.target.value)}
                          placeholder="Buscar ítem…"
                          style={{ marginLeft: 'auto', padding: '5px 8px', border: '1px solid #2E4A40', borderRadius: 6, background: '#0D1311', color: '#F4F7F5', fontSize: 12, minWidth: 160 }} />
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10, maxHeight: 180, overflowY: 'auto' }}>
                        {items
                          .filter(it => !filtroItem.trim() || (it.item_nombre || '').toLowerCase().includes(filtroItem.trim().toLowerCase()))
                          .map(it => (
                          <label key={it.item_id} style={{ ...chip, opacity: it.estado === 'realizado' ? 0.6 : 1 }}>
                            <input type="checkbox" checked={!!sel[it.item_id]} onChange={e => setSel(s => ({ ...s, [it.item_id]: e.target.checked }))} />
                            {it.item_nombre}{it.estado === 'realizado' ? ' ✓' : ''}
                          </label>
                        ))}
                        {items.length === 0 && <span style={{ color: '#6B7C74', fontSize: 12 }}>Sin ítems aplicables. Crea uno abajo o en “Gestionar ítems”.</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
                        <input value={nuevoItem} onChange={e => setNuevoItem(e.target.value)}
                          placeholder="Crear ítem nuevo (ej: Citofonía)…"
                          style={{ flex: 1, padding: '6px 8px', border: '1px solid #2E4A40', borderRadius: 6, background: '#0D1311', color: '#F4F7F5', fontSize: 12 }} />
                        <button onClick={() => crearYAsociar(row)} disabled={guardando || !nuevoItem.trim()}
                          style={{ padding: '6px 10px', background: '#0D1E2E', color: '#38BDF8', border: '1px solid #1E3A5F', borderRadius: 6, cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap' }}>
                          + Crear y asociar
                        </button>
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
                        {row.pdf_url && <a href={row.pdf_url} target="_blank" rel="noreferrer" style={{ color: '#38BDF8', fontSize: 12, marginRight: 'auto' }}>Ver comprobante ↗</a>}
                        <button onClick={() => descartar(row)} style={{ padding: '6px 12px', background: 'transparent', color: '#EF4444', border: '1px solid #5A1E1E', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>Descartar</button>
                        <button onClick={() => asociar(row)} disabled={guardando} style={{ padding: '6px 14px', background: '#0f6e56', color: 'white', border: 'none', borderRadius: 7, fontWeight: 600, cursor: 'pointer', fontSize: 12 }}>{guardando ? 'Guardando…' : 'Asociar'}</button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
// ── Tab Catálogo (incluye acceso a configuración del sistema) ──────────────────
function TabCatalogo() {
  const [verConfig, setVerConfig] = useState(false);
  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setVerConfig(true)} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', background: '#0f6e56',
          border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
          fontSize: 13, fontWeight: 600, color: '#555', cursor: 'pointer',
        }}>⚙ Parámetros del sistema</button>
      </div>

      <CatalogoItems />

      {verConfig && (
        <Modal title="⚙ Parámetros del sistema" onClose={() => setVerConfig(false)}>
          <ConfiguracionSistema />
        </Modal>
      )}
    </div>
  );
}

// ── Tab Almacenamiento (uso de Google Drive + tamaño de la base de datos) ──────
function formatBytes(bytes) {
  if (bytes == null) return '—';
  if (bytes === 0) return '0 B';
  const unidades = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), unidades.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${unidades[i]}`;
}

function BarraUso({ pct, alerta }) {
  return (
    <div style={{
      width: '100%', height: 10, borderRadius: 6,
      background: 'rgba(255,255,255,0.08)', overflow: 'hidden',
    }}>
      <div style={{
        width: `${Math.min(pct, 100)}%`, height: '100%',
        background: alerta ? '#E5533D' : pct >= 65 ? '#E5A93D' : '#4CAF50',
        transition: 'width 0.3s',
      }} />
    </div>
  );
}

function TabAlmacenamiento() {
  const [datos,     setDatos]     = useState(null);
  const [cargando,  setCargando]  = useState(true);
  const [error,     setError]     = useState('');

  const cargar = () => {
    setCargando(true);
    setError('');
    api.get('/admin/almacenamiento')
      .then(r => setDatos(r.data))
      .catch(e => setError(e.response?.data?.error || 'Error al consultar almacenamiento'))
      .finally(() => setCargando(false));
  };

  useEffect(() => { cargar(); }, []);

  const cardSt = {
    background: '#161C19', border: '1.5px solid rgba(255,255,255,0.08)',
    borderRadius: 16, padding: 20, marginBottom: 16,
  };

  const drive       = datos?.drive;
  const drivePct    = drive?.limiteBytes ? (drive.usadoBytes / drive.limiteBytes) * 100 : null;
  const driveAlerta = drivePct != null && drivePct >= 85;

  return (
    <div style={{ padding: 20, maxWidth: 640 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ margin: 0, fontSize: 13, color: '#8A9A93' }}>
          Uso actual de almacenamiento. Revísalo periódicamente para anticipar si se acerca al límite.
        </p>
        <button onClick={cargar} disabled={cargando} style={{
          background: 'none', border: '1px solid rgba(255,255,255,0.2)',
          color: '#C9D3CE', borderRadius: 8, padding: '6px 12px',
          fontSize: 13, cursor: cargando ? 'default' : 'pointer', flexShrink: 0, marginLeft: 12,
        }}>{cargando ? 'Actualizando…' : '↻ Actualizar'}</button>
      </div>

      {error && (
        <div style={{ ...cardSt, borderColor: 'rgba(229,83,61,0.4)', color: '#E5533D', fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* ── Google Drive ── */}
      <div style={cardSt}>
        <p style={{ margin: '0 0 12px', fontWeight: 700, fontSize: 15, color: '#F4F7F5' }}>
          📁 Google Drive (fotos y PDFs)
        </p>
        {cargando && !datos ? (
          <p style={{ color: '#8A9A93', fontSize: 14 }}>Cargando…</p>
        ) : datos?.driveError ? (
          <p style={{ color: '#E5A93D', fontSize: 14, margin: 0 }}>
            No se pudo consultar Drive: {datos.driveError}
          </p>
        ) : drive ? (
          <>
            {drive.cuenta && (
              <p style={{ margin: '0 0 10px', fontSize: 13, color: '#8A9A93' }}>
                Cuenta: {drive.cuenta}
              </p>
            )}
            {drive.limiteBytes != null ? (
              <>
                <BarraUso pct={drivePct} alerta={driveAlerta} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                  <span style={{ fontSize: 13, color: '#C9D3CE' }}>
                    {formatBytes(drive.usadoBytes)} usados de {formatBytes(drive.limiteBytes)}
                  </span>
                  <span style={{
                    fontSize: 13, fontWeight: 700,
                    color: driveAlerta ? '#E5533D' : '#C9D3CE',
                  }}>{drivePct.toFixed(1)}%</span>
                </div>
                {driveAlerta && (
                  <p style={{ margin: '10px 0 0', fontSize: 13, color: '#E5533D' }}>
                    ⚠ Uso superior al 85% — considera liberar espacio o ampliar el plan de Google.
                  </p>
                )}
              </>
            ) : (
              <p style={{ margin: 0, fontSize: 14, color: '#C9D3CE' }}>
                {formatBytes(drive.usadoBytes)} usados — cuenta con almacenamiento ilimitado (Workspace).
              </p>
            )}
          </>
        ) : null}
      </div>

      {/* ── Base de datos ── */}
      <div style={cardSt}>
        <p style={{ margin: '0 0 12px', fontWeight: 700, fontSize: 15, color: '#F4F7F5' }}>
          🗄 Base de datos (Railway PostgreSQL)
        </p>
        {cargando && !datos ? (
          <p style={{ color: '#8A9A93', fontSize: 14 }}>Cargando…</p>
        ) : datos?.dbError ? (
          <p style={{ color: '#E5A93D', fontSize: 14, margin: 0 }}>
            No se pudo consultar la base de datos: {datos.dbError}
          </p>
        ) : datos?.baseDatos ? (
          <p style={{ margin: 0, fontSize: 14, color: '#C9D3CE' }}>
            Tamaño actual: <strong>{datos.baseDatos.legible}</strong>
          </p>
        ) : null}
        <p style={{ margin: '10px 0 0', fontSize: 12, color: '#6B7C74' }}>
          El límite del plan de Railway no es consultable por API — compáralo con lo mostrado
          en el dashboard de Railway (proyecto → servicio Postgres → pestaña Metrics).
        </p>
      </div>
    </div>
  );
}

// ── Tab Mantenciones ─────────────────────────────────────────────────────────
function TabMantenciones() {
  const [datos,   setDatos]   = useState(null);   // { resumen, comunidades }
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [soloPendientes, setSoloPendientes] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/admin/estado-mantenciones');
      setDatos(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar el estado de mantenciones');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const diasDesde = (fecha) => {
    if (!fecha) return null;
    const f   = new Date(String(fecha).slice(0, 10) + 'T12:00:00');
    const hoy = new Date(hoyChile() + 'T12:00:00');
    return Math.round((hoy - f) / 86400000);
  };

  const todas      = datos?.comunidades || [];
  const pendientes = todas.filter(c => c.estado === 'pendiente');
  const lista = (soloPendientes ? pendientes : todas)
    .slice()
    .sort((a, b) => (a.estado === b.estado)
      ? a.comunidad.localeCompare(b.comunidad)
      : (a.estado === 'pendiente' ? -1 : 1));

  const mesActual = (() => {
    const [y, m] = hoyChile().split('-');
    const meses = ['enero','febrero','marzo','abril','mayo','junio','julio',
                   'agosto','septiembre','octubre','noviembre','diciembre'];
    return `${meses[parseInt(m) - 1]} ${y}`;
  })();

  const chip = (label, valor, color) => (
    <div style={{ background:'#161C19', border:'1px solid rgba(255,255,255,0.08)',
                  borderRadius:12, padding:'12px 16px', minWidth:110 }}>
      <div style={{ fontSize:22, fontWeight:800, color }}>{valor}</div>
      <div style={{ fontSize:12, color:'#8A9994' }}>{label}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                    marginBottom:14, flexWrap:'wrap', gap:10 }}>
        <p style={{ margin:0, fontSize:13, color:'#8A9994' }}>
          Estado de la mantención mensual por comunidad — mes en curso: <strong style={{ color:'#C9D3CE' }}>{mesActual}</strong>
        </p>
        <button onClick={cargar} disabled={loading}
          style={{ background:'none', border:'1px solid rgba(255,255,255,0.2)', color:'#C9D3CE',
                   borderRadius:8, padding:'6px 12px', fontSize:13,
                   cursor: loading ? 'default' : 'pointer' }}>
          {loading ? 'Actualizando…' : '↻ Actualizar'}
        </button>
      </div>

      {error && (
        <div style={{ background:'#2D0F0F', color:'#EF4444', borderRadius:8,
                      padding:'10px 14px', marginBottom:14, fontSize:14 }}>{error}</div>
      )}

      {datos && (
        <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
          {chip('Comunidades', datos.resumen.total, '#F4F7F5')}
          {chip('Al día',      datos.resumen.al_dia, '#4CAF50')}
          {chip('Pendientes',  datos.resumen.pendientes, datos.resumen.pendientes > 0 ? '#EF4444' : '#4CAF50')}
        </div>
      )}

      {pendientes.length > 0 && (
        <div style={{ background:'#2D0F0F', border:'1px solid rgba(229,83,61,0.4)',
                      borderRadius:12, padding:'14px 16px', marginBottom:16 }}>
          <p style={{ margin:'0 0 6px', fontWeight:700, fontSize:14, color:'#EF4444' }}>
            ⚠ {pendientes.length} comunidad{pendientes.length !== 1 ? 'es' : ''} pendiente{pendientes.length !== 1 ? 's' : ''} de mantención este mes
          </p>
          <p style={{ margin:0, fontSize:13, color:'#C9A' }}>
            {pendientes.map(c => c.comunidad).join('  ·  ')}
          </p>
        </div>
      )}

      <label style={{ display:'inline-flex', alignItems:'center', gap:6, fontSize:13,
                      color:'#8A9994', cursor:'pointer', marginBottom:12 }}>
        <input type="checkbox" checked={soloPendientes}
          onChange={e => setSoloPendientes(e.target.checked)} />
        Mostrar solo pendientes
      </label>

      {loading && !datos ? (
        <p style={{ color:'#8A9994', textAlign:'center', padding:32 }}>Cargando…</p>
      ) : lista.length === 0 ? (
        <div style={{ textAlign:'center', padding:'40px 0', color:'#8A9994' }}>
          <p style={{ fontSize:32, marginBottom:8 }}>✅</p>
          <p>{soloPendientes ? 'Sin comunidades pendientes' : 'Sin comunidades'}</p>
        </div>
      ) : (
        <div style={{ overflowX:'auto', borderRadius:12, border:'1px solid rgba(255,255,255,0.07)' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#161C19' }}>
                {['Comunidad','Ascensores','Último mantenimiento','Estado'].map(h => (
                  <th key={h} style={{ padding:'10px 12px', textAlign:'left', fontWeight:600,
                                       color:'#9FAAA5', borderBottom:'1px solid rgba(255,255,255,0.07)' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lista.map((c, i) => {
                const dias = diasDesde(c.ultimo_mant);
                const esPend = c.estado === 'pendiente';
                return (
                  <tr key={c.comunidad} style={{ background: i % 2 === 0 ? '#111714' : '#0D1311',
                                                 borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding:'9px 12px', color:'#F4F7F5', fontWeight:600 }}>
                      {c.comunidad}
                    </td>
                    <td style={{ padding:'9px 12px', color:'#C9D3CE' }}>
                      {c.n_ascensores}
                      {c.sin_registro > 0 && (
                        <span style={{ marginLeft:6, fontSize:11, color:'#E5A93D' }}>
                          ({c.sin_registro} sin registro)
                        </span>
                      )}
                    </td>
                    <td style={{ padding:'9px 12px', color:'#C9D3CE', whiteSpace:'nowrap' }}>
                      {c.ultimo_mant
                        ? <>{fechaCL(c.ultimo_mant)} <span style={{ color:'#6B7C74' }}>· hace {dias} día{dias !== 1 ? 's' : ''}</span></>
                        : <span style={{ color:'#E5A93D' }}>Sin registro</span>}
                    </td>
                    <td style={{ padding:'9px 12px' }}>
                      <span style={{ background: esPend ? '#2D0F0F' : '#12382D',
                                     color: esPend ? '#EF4444' : '#4CAF50',
                                     borderRadius:4, padding:'2px 9px', fontSize:12, fontWeight:600 }}>
                        {esPend ? 'Pendiente' : 'Al día'}
                      </span>
                      {esPend && c.pendientes < c.n_ascensores && (
                        <span style={{ marginLeft:6, fontSize:11, color:'#8A9994' }}>
                          {c.pendientes}/{c.n_ascensores} asc. sin mantención
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tab Auditoría (solo superadmin) ───────────────────────────────────────────
const CRIT_CFG = {
  urgente: { label: 'URGENTE (RAI)', bg: '#2D0F0F', color: '#EF4444' },
  alta:    { label: 'Alta',  bg: '#2D1410', color: '#E5533D' },
  media:   { label: 'Media', bg: '#2A1F0A', color: '#E5A93D' },
  baja:    { label: 'Baja',  bg: '#0D1E2E', color: '#38BDF8' },
};
const CLP0 = (n) => Number(n || 0).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
// Estado de envío/aprobación del presupuesto Solvert vinculado (A2)
const PRESUP_CFG = {
  aceptado:  { label: 'Aprobado',           bg: '#12382D', color: '#4CAF50' },
  enviado:   { label: 'Enviado s/aprobar',  bg: '#2A1F0A', color: '#F59E0B' },
  borrador:  { label: 'No enviado',         bg: '#2D1410', color: '#E5533D' },
  rechazado: { label: 'Rechazado',          bg: '#2D0F0F', color: '#EF4444' },
};

// Selector con predicción (typeahead) de ítems de catálogo para una línea.
function ItemPicker({ linea, onAsignar }) {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState('');
  const [res, setRes] = useState([]);
  const buscar = async (texto) => {
    setQ(texto);
    if (texto.trim().length < 2) { setRes([]); return; }
    try {
      const r = await api.get('/catalogo', { params: { q: texto.trim(), activo: 'true' } });
      setRes((r.data || []).slice(0, 12));
    } catch { setRes([]); }
  };
  if (!abierto) {
    return (
      <button onClick={() => { setAbierto(true); setQ(''); setRes([]); }}
        style={{ textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer',
                 color: linea.catalogo_nombre ? '#F4F7F5' : '#F59E0B', fontSize: 13, padding: 0 }}>
        {linea.catalogo_nombre || '— asignar ítem —'}
        {linea.catalogo_codigo ? <span style={{ color: '#6B7C74', fontSize: 11 }}> · {linea.catalogo_codigo}</span> : null}
      </button>
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <input autoFocus value={q} onChange={e => buscar(e.target.value)}
        placeholder="Escribe para buscar…" onBlur={() => setTimeout(() => setAbierto(false), 180)}
        style={{ width: '100%', padding: '6px 8px', border: '1.5px solid #0f6e56', borderRadius: 6,
                 background: '#0D1311', color: '#F4F7F5', fontSize: 13, boxSizing: 'border-box' }} />
      {res.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 20, top: '100%', left: 0, right: 0, background: '#111714',
                      border: '1px solid #2E4A40', borderRadius: 6, maxHeight: 220, overflowY: 'auto', marginTop: 2 }}>
          {res.map(it => (
            <div key={it.id} onMouseDown={() => { onAsignar(it); setAbierto(false); }}
              style={{ padding: '7px 9px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid #1C2922', color: '#C9D3CE' }}>
              <span style={{ color: '#F4F7F5' }}>{it.nombre}</span>
              <span style={{ color: '#6B7C74' }}> · {it.codigo} · {CLP0(it.precio_ref)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TabControlPresupuestos() {
  const [vista, setVista]       = useState('carpetas'); // 'carpetas' | 'detalle'
  const [comunidades, setComunidades] = useState([]);
  const [comSel, setComSel]     = useState(null);
  const [lineas, setLineas]     = useState([]);
  const [ascTab, setAscTab]     = useState('general'); // 'general' | n_ascensor
  const [estadoTab, setEstadoTab] = useState('pendiente'); // pendiente | transitorio
  const [busq, setBusq]         = useState('');
  const [sel, setSel]           = useState({});
  const [loading, setLoading]   = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [error, setError]       = useState('');
  const [aviso, setAviso]       = useState('');
  const [orden, setOrden]       = useState('crit');  // orden de la tabla de líneas
  const [ordenDir, setOrdenDir] = useState('desc');  // 'asc' | 'desc'
  const [busqCom, setBusqCom]   = useState('');      // buscador de la grilla de comunidades

  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#8A9994',
               textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #1C2922', whiteSpace: 'nowrap' };

  const cargarComunidades = useCallback(async () => {
    setLoading(true); setError('');
    try { const r = await api.get('/control-presupuestos/comunidades'); setComunidades(r.data || []); }
    catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (vista === 'carpetas') cargarComunidades(); }, [vista, cargarComunidades]);

  const cargarLineas = useCallback(async () => {
    if (!comSel) return;
    setLoading(true); setError('');
    try {
      const r = await api.get('/control-presupuestos/lineas', { params: { comunidad: comSel, estado: estadoTab } });
      setLineas(r.data || []); setSel({});
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [comSel, estadoTab]);
  useEffect(() => { if (vista === 'detalle') cargarLineas(); }, [vista, cargarLineas]);

  const procesar = async () => {
    setProcesando(true); setAviso(''); setError('');
    try {
      const r = await api.post('/control-presupuestos/procesar');
      setAviso(`Generadas ${r.data.creadas} observaciones (${r.data.con_item} con ítem sugerido, ${r.data.sin_item} para revisión).`);
      cargarComunidades();
    } catch (e) { setError(e.response?.data?.error || 'No se pudo procesar'); }
    finally { setProcesando(false); }
  };

  const abrirComunidad = (c) => { setComSel(c.comunidad); setAscTab('general'); setEstadoTab('pendiente'); setVista('detalle'); };

  const total = (l) => (Number(l.precio_neto) || 0) * (1 + (Number(l.porcentaje_utilidad) || 0) / 100);
  const bq = busq.trim().toLowerCase();
  const visibles = lineas.filter(l =>
    (ascTab === 'general' || String(l.n_ascensor) === String(ascTab)) &&
    (!bq || `${l.observacion || ''} ${l.catalogo_nombre || ''} ${l.presupuesto_numero || ''}`.toLowerCase().includes(bq))
  );
  const ascensores = [...new Set(lineas.map(l => l.n_ascensor).filter(v => v != null))].sort((a, b) => a - b);
  const idsSel = Object.keys(sel).filter(k => sel[k]).map(Number);
  const totalSel = lineas.filter(l => idsSel.includes(l.id)).reduce((a, l) => a + total(l), 0);

  // Ordenamiento de la tabla (client-side). 'crit' = orden del servidor (relevancia).
  const cambiarOrden = (k) => { setOrden(k); setOrdenDir(['observacion', 'catalogo'].includes(k) ? 'asc' : 'desc'); };
  const sorted = (() => {
    if (orden === 'crit') return visibles;
    const dir = ordenDir === 'asc' ? 1 : -1;
    const val = (l) => {
      switch (orden) {
        case 'fecha':       return l.fecha || '';
        case 'updated':     return l.updated_at || '';
        case 'observacion': return (l.observacion || '').toLowerCase();
        case 'catalogo':    return (l.catalogo_nombre || '').toLowerCase();
        case 'monto':       return total(l);
        default:            return 0;
      }
    };
    return [...visibles].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  })();

  const asignarItem = async (linea, it) => {
    try {
      await api.put(`/control-presupuestos/lineas/${linea.id}`, { catalogo_id: it.id });
      cargarLineas();
    } catch (e) { setError(e.response?.data?.error || 'No se pudo asignar'); }
  };
  const editarUtilidad = async (linea, val) => {
    const util = parseFloat(val);
    if (Number.isNaN(util)) return;
    try { await api.put(`/control-presupuestos/lineas/${linea.id}`, { porcentaje_utilidad: util }); cargarLineas(); }
    catch (e) { setError(e.response?.data?.error || 'No se pudo actualizar'); }
  };
  const duplicar = async (linea) => {
    try { await api.post(`/control-presupuestos/lineas/${linea.id}/duplicar`); cargarLineas(); }
    catch (e) { setError(e.response?.data?.error || 'No se pudo agregar ítem'); }
  };
  const mover = async (estado) => {
    if (!idsSel.length) return;
    try { await api.post('/control-presupuestos/mover', { ids: idsSel, estado }); cargarLineas(); cargarComunidades(); }
    catch (e) { setError(e.response?.data?.error || 'No se pudo mover'); }
  };
  const selPorPrioridad = (crit) => {
    const next = {};
    visibles.forEach(l => { if (l.criticidad === crit) next[l.id] = true; });
    setSel(next);
  };

  const [dash, setDash] = useState(null);
  const cargarDash = useCallback(async () => {
    try { const r = await api.get('/control-presupuestos/dashboard'); setDash(r.data); } catch { /* noop */ }
  }, []);
  useEffect(() => { if (vista === 'dashboard') cargarDash(); }, [vista, cargarDash]);

  const presupuestar = async () => {
    if (!idsSel.length) return;
    if (!window.confirm(`¿Generar presupuesto con ${idsSel.length} línea(s)? Se crea(n) como borrador en Presupuestos (una por comunidad).`)) return;
    setError(''); setAviso('');
    try {
      const r = await api.post('/control-presupuestos/presupuestar', { ids: idsSel });
      const nums = (r.data.creados || []).map(c => `${c.numero} (${c.comunidad})`).join(', ');
      setAviso(`Presupuesto(s) creado(s): ${nums}`);
      cargarLineas(); cargarComunidades();
    } catch (e) { setError(e.response?.data?.error || 'No se pudo presupuestar'); }
  };

  const exportar = async () => {
    try {
      const params = {};
      if (vista === 'detalle' && comSel) params.comunidad = comSel;
      const r = await api.get('/control-presupuestos/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `control_presupuestos_${hoyChile()}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { setError('No se pudo exportar'); }
  };

  const [ia, setIa] = useState(null);
  const iaPollRef = useRef(null);
  const consultarIA = useCallback(async () => {
    try {
      const r = await api.get('/control-presupuestos/sugerir-ia/progreso');
      setIa(r.data);
      if (!r.data.running && iaPollRef.current) { clearInterval(iaPollRef.current); iaPollRef.current = null; cargarLineas(); }
    } catch { /* noop */ }
  }, [cargarLineas]);
  useEffect(() => () => { if (iaPollRef.current) clearInterval(iaPollRef.current); }, []);
  const sugerirIA = async () => {
    setError(''); setAviso('');
    try {
      await api.post('/control-presupuestos/sugerir-ia', comSel ? { comunidad: comSel } : {});
      if (iaPollRef.current) clearInterval(iaPollRef.current);
      consultarIA();
      iaPollRef.current = setInterval(consultarIA, 2500);
    } catch (e) { setError(e.response?.data?.error || 'No se pudo iniciar la IA'); }
  };

  const roLinea = estadoTab === 'presupuestado';

  // ── Vista dashboard ──
  if (vista === 'dashboard') {
    const card = (n, lbl, color) => (
      <div style={{ background: '#0D1311', border: '1px solid #1C2922', borderRadius: 10, padding: '12px 16px', minWidth: 130 }}>
        <div style={{ fontSize: 24, fontWeight: 800, color }}>{n ?? '—'}</div>
        <div style={{ fontSize: 12, color: '#8A9994' }}>{lbl}</div>
      </div>
    );
    return (
      <div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <button onClick={() => setVista('carpetas')} style={{ background: 'none', border: '1px solid #2E4A40', borderRadius: 8, color: '#8A9994', padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>← Comunidades</button>
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {card(dash?.pendientes, 'Pendientes', '#F59E0B')}
          {card(dash?.transitorio, 'Transitorio', '#38BDF8')}
          {card(dash?.presupuestado, 'Presupuestado', '#4CAF50')}
          {card(dash?.comunidades, 'Comunidades activas', '#F4F7F5')}
          {card(dash?.urgentes, 'Urgentes (RAI)', '#EF4444')}
          {card(CLP0(dash?.monto_transitorio), 'Monto transitorio s/IVA', '#4CAF50')}
        </div>
        <div style={{ background: '#111714', border: '1px solid #1C2922', borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#F4F7F5', marginBottom: 10 }}>
            ⏰ Presupuestos enviados sin aprobar (+7 días)
          </div>
          {(!dash?.recordatorios?.length) ? (
            <p style={{ color: '#6B7C74', fontSize: 13, margin: 0 }}>Sin recordatorios pendientes. 🎉</p>
          ) : dash.recordatorios.map(r => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '7px 0', borderTop: '1px solid #1C2922', fontSize: 13 }}>
              <span style={{ color: '#F4F7F5' }}>{r.numero} <span style={{ color: '#8A9994' }}>· {r.comunidad || '—'}</span></span>
              <span style={{ color: '#F59E0B', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.dias} días sin aprobar</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Vista carpetas ──
  if (vista === 'carpetas') {
    const bqc = busqCom.trim().toLowerCase();
    const comVisibles = comunidades.filter(c => !bqc || (c.comunidad || '').toLowerCase().includes(bqc));
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
          <p style={{ margin: 0, fontSize: 13, color: '#8A9994', flex: 1, minWidth: 260 }}>
            Observaciones de comprobantes (RO, RAI y visitas de reparación) listas para presupuestar.
            Flujo: <b>Pendientes → Transitorio → Presupuestado</b>.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setVista('dashboard')}
              style={{ padding: '8px 14px', background: 'transparent', color: '#38BDF8', border: '1px solid #1E3A5F', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>📊 Dashboard</button>
            <button onClick={exportar}
              style={{ padding: '8px 14px', background: 'transparent', color: '#4CAF50', border: '1.5px solid rgba(79,175,80,0.4)', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>⬇ Excel</button>
            <button onClick={procesar} disabled={procesando}
              style={{ padding: '8px 16px', background: procesando ? '#0a4a38' : '#0f6e56', color: 'white',
                       border: 'none', borderRadius: 8, fontWeight: 700, cursor: procesando ? 'wait' : 'pointer', fontSize: 13 }}>
              {procesando ? 'Procesando…' : '↻ Procesar observaciones'}
            </button>
          </div>
        </div>
        {aviso && <p style={{ color: '#4CAF50', fontSize: 13 }}>{aviso}</p>}
        {error && <p style={{ color: '#EF4444', fontSize: 13 }}>{error}</p>}
        {loading ? <p style={{ color: '#6B7C74', padding: 20 }}>Cargando…</p>
          : comunidades.length === 0 ? <p style={{ color: '#6B7C74', padding: 20 }}>Sin observaciones. Pulsa “Procesar observaciones”.</p>
          : (
          <>
            <input value={busqCom} onChange={e => setBusqCom(e.target.value)} placeholder="🔍 Buscar comunidad…"
              style={{ width: '100%', maxWidth: 360, padding: '8px 12px', border: '1.5px solid #2E4A40', borderRadius: 8,
                       fontSize: 13, background: '#0D1311', color: '#F4F7F5', marginBottom: 12 }} />
            {comVisibles.length === 0 ? (
              <p style={{ color: '#6B7C74', padding: 20 }}>Sin comunidades que coincidan con «{busqCom}».</p>
            ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 12 }}>
              {comVisibles.map(c => (
                <div key={c.comunidad} onClick={() => abrirComunidad(c)}
                  style={{ background: '#111714', border: '1px solid ' + (c.urgentes ? '#5A1E1E' : '#1C2922'),
                           borderRadius: 12, padding: '14px 16px', cursor: 'pointer' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#F4F7F5', marginBottom: 8 }}>📁 {c.comunidad}</div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12 }}>
                    <span style={{ color: '#F59E0B' }}>Pendientes: <b>{c.pendientes}</b></span>
                    <span style={{ color: '#38BDF8' }}>Transitorio: <b>{c.transitorio}</b></span>
                    {c.urgentes > 0 && <span style={{ color: '#EF4444' }}>⚠ {c.urgentes} urgente(s)</span>}
                  </div>
                </div>
              ))}
            </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ── Vista detalle (dentro de una comunidad) ──
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={() => setVista('carpetas')} style={{ background: 'none', border: '1px solid #2E4A40', borderRadius: 8, color: '#8A9994', padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>← Comunidades</button>
        <strong style={{ color: '#F4F7F5', fontSize: 15 }}>📁 {comSel}</strong>
      </div>

      {/* Estado + ascensor */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {[['pendiente', 'Pendientes'], ['transitorio', 'Transitorio'], ['presupuestado', 'Presupuestado']].map(([k, lbl]) => (
          <button key={k} onClick={() => setEstadoTab(k)}
            style={{ padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid #2E4A40',
                     background: estadoTab === k ? '#0f6e56' : 'transparent', color: estadoTab === k ? '#fff' : '#8A9994' }}>{lbl}</button>
        ))}
        <input value={busq} onChange={e => setBusq(e.target.value)}
          placeholder={estadoTab === 'presupuestado' ? 'Buscar ítem o N° presupuesto…' : 'Buscar observación o ítem…'}
          style={{ marginLeft: 'auto', padding: '7px 10px', border: '1.5px solid #2E4A40', borderRadius: 8, fontSize: 13, background: '#0D1311', color: '#F4F7F5', minWidth: 200 }} />
        <select value={orden} onChange={e => cambiarOrden(e.target.value)} title="Ordenar por"
          style={{ padding: '7px 10px', border: '1px solid #2E4A40', borderRadius: 8, fontSize: 13, background: '#0D1311', color: '#F4F7F5', cursor: 'pointer' }}>
          <option value="crit">Orden: Relevancia</option>
          <option value="fecha">Fecha</option>
          <option value="observacion">Nombre (observación)</option>
          <option value="catalogo">Ítem catálogo</option>
          <option value="monto">Monto</option>
          <option value="updated">Última modificación</option>
        </select>
        <button onClick={() => setOrdenDir(d => d === 'asc' ? 'desc' : 'asc')} disabled={orden === 'crit'}
          title={ordenDir === 'asc' ? 'Ascendente — clic para invertir' : 'Descendente — clic para invertir'}
          style={{ padding: '7px 11px', border: '1px solid #2E4A40', borderRadius: 8, fontSize: 13, fontWeight: 700,
                   background: '#0D1311', color: orden === 'crit' ? '#4D6358' : '#C9D3CE', cursor: orden === 'crit' ? 'default' : 'pointer' }}>
          {ordenDir === 'asc' ? '↑' : '↓'}
        </button>
        <button onClick={sugerirIA} disabled={ia?.running}
          style={{ padding: '7px 12px', background: 'transparent', color: '#A855F7', border: '1px solid #3B2A5A', borderRadius: 8, fontWeight: 600, cursor: ia?.running ? 'wait' : 'pointer', fontSize: 13 }}>
          {ia?.running ? `IA… ${ia.procesados}/${ia.total}` : '✨ Sugerir ítems (IA)'}
        </button>
        <button onClick={exportar} style={{ padding: '7px 12px', background: 'transparent', color: '#4CAF50', border: '1.5px solid rgba(79,175,80,0.4)', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>⬇ Excel</button>
        <span style={{ width: 1, height: 22, background: '#2E4A40', margin: '0 4px' }} />
        {['general', ...ascensores].map(a => (
          <button key={a} onClick={() => setAscTab(a)}
            style={{ padding: '6px 11px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid #2E4A40',
                     background: String(ascTab) === String(a) ? '#0D1E2E' : 'transparent', color: String(ascTab) === String(a) ? '#38BDF8' : '#8A9994' }}>
            {a === 'general' ? 'General' : `Asc. ${a}`}</button>
        ))}
      </div>

      {/* Barra de acciones (no aplica en Presupuestado) */}
      {!roLinea && (
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center', background: '#0D1311', border: '1px solid #1C2922', borderRadius: 10, padding: '8px 12px' }}>
        <span style={{ fontSize: 13, color: '#C9D3CE' }}>{idsSel.length} seleccionada(s) · <b style={{ color: '#4CAF50' }}>{CLP0(totalSel)}</b> sin IVA</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button onClick={() => setSel(Object.fromEntries(visibles.map(l => [l.id, true])))} style={btnMini('#2E4A40', '#C9D3CE')}>Todas</button>
          <button onClick={() => selPorPrioridad('urgente')} style={btnMini('#5A1E1E', '#EF4444')}>Urgentes</button>
          <button onClick={() => selPorPrioridad('alta')} style={btnMini('#3A2218', '#E5533D')}>Alta</button>
          <button onClick={() => setSel({})} style={btnMini('#2E4A40', '#8A9994')}>Limpiar</button>
          {estadoTab === 'pendiente' ? (
            <button onClick={() => mover('transitorio')} disabled={!idsSel.length} style={btnMini('#0f6e56', '#fff', true)}>Enviar a Transitorio →</button>
          ) : (
            <>
              <button onClick={() => mover('pendiente')} disabled={!idsSel.length} style={btnMini('#38BDF8', '#0B0F0D', true)}>← Devolver a Pendientes</button>
              <button onClick={presupuestar} disabled={!idsSel.length} style={btnMini('#0f6e56', '#fff', true)}>Presupuestar →</button>
            </>
          )}
          <button onClick={() => mover('descartado')} disabled={!idsSel.length} style={btnMini('#5A1E1E', '#EF4444')}>Descartar</button>
        </div>
      </div>
      )}

      {ia && (ia.running || ia.done) && (
        <p style={{ fontSize: 12, color: ia.running ? '#A855F7' : '#8A9994', margin: '0 0 8px' }}>
          {ia.running ? 'IA sugiriendo ítems…' : 'IA finalizada.'} {ia.procesados}/{ia.total} · <span style={{ color: '#4CAF50' }}>{ia.asignados} asignados</span> · {ia.sin_match} sin match{ia.errores ? ` · ${ia.errores} error(es)` : ''}
          {ia.primer_error ? ` · ${ia.primer_error}` : ''}
        </p>
      )}
      {error && <p style={{ color: '#EF4444', fontSize: 13 }}>{error}</p>}
      {loading ? <p style={{ color: '#6B7C74', padding: 20 }}>Cargando…</p>
        : visibles.length === 0 ? <p style={{ color: '#6B7C74', padding: 20 }}>Sin líneas en {{ pendiente: 'Pendientes', transitorio: 'Transitorio', presupuestado: 'Presupuestado' }[estadoTab]}.</p>
        : (
        <div style={{ overflowX: 'auto', border: '1px solid #1C2922', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#0D1311' }}>
              <th style={th}></th>
              <th style={th}>Crit.</th>
              <th style={{ ...th, minWidth: 220 }}>Observación</th>
              <th style={{ ...th, minWidth: 180 }}>Ítem catálogo</th>
              <th style={{ ...th, textAlign: 'right' }}>Neto</th>
              <th style={{ ...th, textAlign: 'center' }}>Util. %</th>
              <th style={{ ...th, textAlign: 'right' }}>Total s/IVA</th>
              <th style={th}></th>
            </tr></thead>
            <tbody>
              {sorted.map(l => {
                const cc = CRIT_CFG[l.criticidad] || CRIT_CFG.media;
                return (
                  <tr key={l.id} style={{ borderBottom: '1px solid #1C2922', background: l.criticidad === 'urgente' ? '#1A0D0D' : 'transparent' }}>
                    <td style={{ padding: '6px 10px' }}>
                      {!roLinea && <input type="checkbox" checked={!!sel[l.id]} onChange={e => setSel(s => ({ ...s, [l.id]: e.target.checked }))} />}
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <span style={{ background: cc.bg, color: cc.color, borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{cc.label}</span>
                    </td>
                    <td style={{ padding: '6px 10px', color: '#E6ECE9', whiteSpace: 'pre-wrap', textDecoration: roLinea ? 'line-through' : 'none', opacity: roLinea ? 0.7 : 1 }}>
                      {l.observacion || '—'}
                      <div style={{ fontSize: 11, color: '#6B7C74', textDecoration: 'none' }}>
                        {l.origen === 'visita' ? 'Visita' : l.origen === 'rai' ? 'RAI' : 'Mantención'}
                        {l.folio ? ` · ${l.folio}` : ''}{l.fecha ? ` · ${fechaCL(l.fecha)}` : ''}
                        {l.pdf_url ? <> · <a href={l.pdf_url} target="_blank" rel="noreferrer" style={{ color: '#38BDF8' }}>PDF ↗</a></> : ''}
                      </div>
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      {roLinea ? <span style={{ color: '#C9D3CE' }}>{l.catalogo_nombre || '—'}</span>
                               : <ItemPicker linea={l} onAsignar={(it) => asignarItem(l, it)} />}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: '#C9D3CE', whiteSpace: 'nowrap' }}>{CLP0(l.precio_neto)}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                      {roLinea ? <span style={{ color: '#C9D3CE' }}>{Number(l.porcentaje_utilidad)}%</span> : (
                        <input type="number" min="0" step="5" defaultValue={Number(l.porcentaje_utilidad)}
                          onBlur={e => { if (Number(e.target.value) !== Number(l.porcentaje_utilidad)) editarUtilidad(l, e.target.value); }}
                          style={{ width: 56, padding: '4px 6px', border: '1px solid #2E4A40', borderRadius: 6, background: '#0D1311', color: '#F4F7F5', fontSize: 12, textAlign: 'right' }} />
                      )}
                    </td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: '#4CAF50', fontWeight: 700, whiteSpace: 'nowrap' }}>{CLP0(total(l))}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {roLinea ? (() => {
                        const pc = PRESUP_CFG[l.presupuesto_estado] || PRESUP_CFG.borrador;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                            <span style={{ fontSize: 11, color: '#C9D3CE', fontWeight: 600 }}>{l.presupuesto_numero || '—'}</span>
                            <span style={{ background: pc.bg, color: pc.color, borderRadius: 4, padding: '1px 7px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' }}>{pc.label}</span>
                          </div>
                        );
                      })()
                        : <button onClick={() => duplicar(l)} title="Agregar otro ítem a esta observación"
                            style={{ padding: '3px 9px', background: '#12382D', color: '#4CAF50', border: '1px solid rgba(79,175,80,0.3)', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>+</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function btnMini(bg, color, solid) {
  return { padding: '6px 11px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
           border: solid ? 'none' : `1px solid ${bg}`, background: solid ? bg : 'transparent', color };
}

function TabReparaciones() {
  const [vista, setVista]         = useState('dashboard'); // 'dashboard' | 'detalle'
  const [ascensores, setAscensores] = useState([]);
  const [dash, setDash]           = useState(null);
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [descargando, setDescargando] = useState(false);
  const [filtros, setFiltros]     = useState({ comunidad:'', ascensor_id:'', desde:'', hasta:'', tecnico:'', q:'', origen:'' });
  const [modal, setModal]         = useState(null); // { id?, ascensor_id, fecha, trabajo, tecnico_nombre, observaciones, formulario_id }
  const [guardando, setGuardando] = useState(false);
  const [compResp, setCompResp]   = useState([]); // comprobantes del equipo para vincular como respaldo

  const inp = { padding:'8px 10px', border:'1.5px solid #2E4A40', borderRadius:8, fontSize:13,
                background:'#0D1311', color:'#F4F7F5', fontFamily:'inherit' };
  const th = { padding:'10px 12px', textAlign:'left', fontSize:11, fontWeight:700, color:'#8A9994',
               textTransform:'uppercase', letterSpacing:'0.5px', borderBottom:'1px solid #1C2922', whiteSpace:'nowrap' };

  useEffect(() => { api.get('/admin/ascensores').then(r => setAscensores(r.data || [])).catch(() => {}); }, []);

  const comunidades = [...new Set(ascensores.map(a => a.comunidad).filter(Boolean))].sort();
  const ascDeComunidad = filtros.comunidad
    ? ascensores.filter(a => a.comunidad === filtros.comunidad)
    : ascensores;

  const params = () => {
    const p = {};
    Object.entries(filtros).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  };

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const p = {}; Object.entries(filtros).forEach(([k, v]) => { if (v) p[k] = v; });
      if (vista === 'dashboard') {
        const r = await api.get('/reparaciones/dashboard', { params: p });
        setDash(r.data);
      } else {
        const r = await api.get('/reparaciones', { params: p });
        setRows(r.data || []);
      }
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [vista, filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  // Carga los comprobantes del equipo seleccionado en el modal (para vincular respaldo)
  useEffect(() => {
    if (modal?.ascensor_id) {
      api.get('/reparaciones/comprobantes', { params: { ascensor_id: modal.ascensor_id } })
        .then(r => setCompResp(r.data || [])).catch(() => setCompResp([]));
    } else { setCompResp([]); }
  }, [modal?.ascensor_id]);

  const exportar = async () => {
    setDescargando(true);
    try {
      const r = await api.get('/reparaciones/export', { params: params(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `reparaciones_${hoyChile()}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { setError('No se pudo exportar'); }
    finally { setDescargando(false); }
  };

  const abrirNuevo = () => setModal({ ascensor_id: filtros.ascensor_id || '', fecha: hoyChile(), trabajo:'', tecnico_nombre:'', observaciones:'', formulario_id:'' });
  const abrirEditar = (r) => setModal({ id: r.id, ascensor_id: r.ascensor_id, fecha: String(r.fecha).slice(0,10), trabajo: r.trabajo || '', tecnico_nombre: r.tecnico_nombre || '', observaciones: r.observaciones || '', formulario_id: r.formulario_id || '' });

  const guardarManual = async () => {
    if (!modal.ascensor_id || !modal.fecha || !modal.trabajo.trim()) { setError('Equipo, fecha y trabajo son obligatorios'); return; }
    setGuardando(true); setError('');
    try {
      if (modal.id) await api.put(`/reparaciones/manual/${modal.id}`, modal);
      else          await api.post('/reparaciones/manual', modal);
      setModal(null); cargar();
    } catch (e) { setError(e.response?.data?.error || 'No se pudo guardar'); }
    finally { setGuardando(false); }
  };

  const eliminarManual = async (r) => {
    if (!window.confirm('¿Eliminar esta entrada manual?')) return;
    try { await api.delete(`/reparaciones/manual/${r.id}`); cargar(); }
    catch (e) { setError(e.response?.data?.error || 'No se pudo eliminar'); }
  };

  const maxN = dash ? Math.max(1, ...dash.comunidades.map(c => c.n)) : 1;

  return (
    <div>
      <p style={{ margin:'0 0 12px', fontSize:13, color:'#8A9994' }}>
        Historial de reparaciones por equipo. Se alimenta <b>automáticamente</b> de los comprobantes de
        <b> visita técnica con tipo “Reparación”</b>, más las entradas manuales que agregues. Todo filtrable y exportable.
      </p>

      {/* Sub-vistas */}
      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        {[['dashboard','📊 Dashboard'], ['detalle','📋 Detalle']].map(([k, lbl]) => (
          <button key={k} onClick={() => setVista(k)}
            style={{ padding:'7px 14px', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
                     border:'1px solid #2E4A40',
                     background: vista===k ? '#0f6e56' : 'transparent',
                     color: vista===k ? '#fff' : '#8A9994' }}>{lbl}</button>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <select value={filtros.comunidad} onChange={e => setFiltros(f => ({ ...f, comunidad:e.target.value, ascensor_id:'' }))} style={inp}>
          <option value="">Todas las comunidades</option>
          {comunidades.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {vista === 'detalle' && (
          <select value={filtros.ascensor_id} onChange={e => setFiltros(f => ({ ...f, ascensor_id:e.target.value }))} style={inp}>
            <option value="">Todos los equipos</option>
            {ascDeComunidad.map(a => <option key={a.id} value={a.id}>Asc. {a.n_ascensor}{a.torre ? ` T${a.torre}` : ''} — {a.comunidad}</option>)}
          </select>
        )}
        <input type="date" value={filtros.desde} onChange={e => setFiltros(f => ({ ...f, desde:e.target.value }))} style={inp} title="Desde" />
        <input type="date" value={filtros.hasta} onChange={e => setFiltros(f => ({ ...f, hasta:e.target.value }))} style={inp} title="Hasta" />
        <input placeholder="Técnico…" value={filtros.tecnico} onChange={e => setFiltros(f => ({ ...f, tecnico:e.target.value }))} style={{ ...inp, width:120 }} />
        {vista === 'detalle' && (
          <>
            <input placeholder="Buscar trabajo…" value={filtros.q} onChange={e => setFiltros(f => ({ ...f, q:e.target.value }))} style={{ ...inp, minWidth:150 }} />
            <select value={filtros.origen} onChange={e => setFiltros(f => ({ ...f, origen:e.target.value }))} style={inp}>
              <option value="">Auto + manual</option>
              <option value="comprobante">Solo comprobantes</option>
              <option value="manual">Solo manuales</option>
            </select>
          </>
        )}
        <button onClick={cargar} style={{ padding:'8px 16px', background:'#0f6e56', color:'white', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13 }}>Actualizar</button>
        <button onClick={exportar} disabled={descargando} style={{ padding:'8px 14px', background:'transparent', color:'#4CAF50', border:'1.5px solid rgba(79,175,80,0.4)', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13 }}>{descargando ? 'Generando…' : '⬇ Excel'}</button>
        <button onClick={abrirNuevo} style={{ padding:'8px 14px', background:'#0D1E2E', color:'#38BDF8', border:'1px solid #1E3A5F', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13, marginLeft:'auto' }}>+ Entrada manual</button>
      </div>

      {error && <p style={{ color:'#EF4444', fontSize:13 }}>{error}</p>}
      {loading ? (
        <p style={{ color:'#6B7C74', padding:20 }}>Cargando…</p>
      ) : vista === 'dashboard' ? (
        <div>
          <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
            <div style={{ background:'#0D1311', border:'1px solid #12382D', borderRadius:10, padding:'10px 16px' }}>
              <div style={{ fontSize:24, fontWeight:800, color:'#4CAF50' }}>{dash?.total || 0}</div>
              <div style={{ fontSize:12, color:'#8A9994' }}>Reparaciones (total filtrado)</div>
            </div>
            <div style={{ background:'#0D1311', border:'1px solid #1C2922', borderRadius:10, padding:'10px 16px' }}>
              <div style={{ fontSize:24, fontWeight:800, color:'#F4F7F5' }}>{dash?.comunidades?.length || 0}</div>
              <div style={{ fontSize:12, color:'#8A9994' }}>Comunidades con reparaciones</div>
            </div>
          </div>
          <div style={{ overflowX:'auto', border:'1px solid #1C2922', borderRadius:12 }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead><tr style={{ background:'#0D1311' }}>
                <th style={th}>Comunidad</th>
                <th style={{ ...th, width:'40%' }}>Reparaciones</th>
                <th style={{ ...th, textAlign:'center' }}>Equipos</th>
                <th style={{ ...th, textAlign:'center' }}>Manuales</th>
                <th style={th}>Última</th>
              </tr></thead>
              <tbody>
                {(!dash?.comunidades?.length) && <tr><td colSpan={5} style={{ padding:20, color:'#6B7C74', textAlign:'center' }}>Sin reparaciones para el filtro.</td></tr>}
                {dash?.comunidades?.map((c, i) => (
                  <tr key={i} style={{ borderBottom:'1px solid #1C2922', cursor:'pointer' }}
                      onClick={() => { setFiltros(f => ({ ...f, comunidad:c.comunidad })); setVista('detalle'); }}>
                    <td style={{ padding:'9px 12px', color:'#F4F7F5' }}>{c.comunidad}</td>
                    <td style={{ padding:'9px 12px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ flex:1, height:8, background:'#1C2922', borderRadius:4, overflow:'hidden', maxWidth:240 }}>
                          <div style={{ width:`${(c.n/maxN)*100}%`, height:'100%', background:'#4CAF50' }} />
                        </div>
                        <span style={{ color:'#4CAF50', fontWeight:700 }}>{c.n}</span>
                      </div>
                    </td>
                    <td style={{ padding:'9px 12px', textAlign:'center', color:'#C9D3CE' }}>{c.n_equipos}</td>
                    <td style={{ padding:'9px 12px', textAlign:'center', color:'#8A9994' }}>{c.n_manual || 0}</td>
                    <td style={{ padding:'9px 12px', color:'#C9D3CE', whiteSpace:'nowrap' }}>{c.ultima ? fechaCL(c.ultima) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize:11, color:'#6B7C74', marginTop:8 }}>Toca una comunidad para ver su detalle.</p>
        </div>
      ) : (
        <div style={{ overflowX:'auto', border:'1px solid #1C2922', borderRadius:12 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead><tr style={{ background:'#0D1311' }}>
              <th style={th}>Fecha</th>
              <th style={th}>Comunidad · Equipo</th>
              <th style={th}>Trabajo realizado</th>
              <th style={th}>Técnico</th>
              <th style={th}>Origen</th>
              <th style={{ ...th, textAlign:'right' }}></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} style={{ padding:20, color:'#6B7C74', textAlign:'center' }}>Sin reparaciones para el filtro.</td></tr>}
              {rows.map((r, i) => (
                <tr key={i} style={{ borderBottom:'1px solid #1C2922' }}>
                  <td style={{ padding:'9px 12px', color:'#C9D3CE', whiteSpace:'nowrap' }}>{r.fecha ? fechaCL(r.fecha) : '—'}</td>
                  <td style={{ padding:'9px 12px', color:'#C9D3CE' }}>{r.comunidad || '—'}<div style={{ fontSize:11, color:'#6B7C74' }}>Asc. {r.n_ascensor}{r.torre ? ` T${r.torre}` : ''}</div></td>
                  <td style={{ padding:'9px 12px', color:'#E6ECE9', maxWidth:420, whiteSpace:'pre-wrap' }}>{r.trabajo || '—'}</td>
                  <td style={{ padding:'9px 12px', color:'#C9D3CE' }}>{r.tecnico_nombre || '—'}</td>
                  <td style={{ padding:'9px 12px' }}>
                    <span style={{ fontSize:11, fontWeight:600, borderRadius:4, padding:'2px 8px',
                                   background: r.origen==='manual' ? '#0D1E2E' : '#12382D',
                                   color: r.origen==='manual' ? '#38BDF8' : '#4CAF50' }}>
                      {r.origen === 'manual' ? 'Manual' : 'Comprobante'}
                    </span>
                  </td>
                  <td style={{ padding:'7px 12px', textAlign:'right', whiteSpace:'nowrap' }}>
                    {r.origen === 'comprobante' && r.pdf_url && (
                      <a href={r.pdf_url} target="_blank" rel="noreferrer" style={{ color:'#38BDF8', fontSize:12 }}>PDF ↗</a>
                    )}
                    {r.origen === 'manual' && (
                      <>
                        <button onClick={() => abrirEditar(r)} style={{ padding:'4px 9px', background:'#12382D', color:'#4CAF50', border:'1px solid rgba(79,175,80,0.3)', borderRadius:6, cursor:'pointer', fontSize:12, marginRight:6 }}>Editar</button>
                        <button onClick={() => eliminarManual(r)} style={{ padding:'4px 9px', background:'#2D0F0F', color:'#EF4444', border:'1px solid rgba(198,40,40,0.3)', borderRadius:6, cursor:'pointer', fontSize:12 }}>×</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <Modal title={modal.id ? 'Editar reparación manual' : 'Nueva reparación manual'} onClose={() => setModal(null)} maxWidth={480}>
          <div style={{ marginBottom:12 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>Equipo *</label>
            <select value={modal.ascensor_id} onChange={e => setModal(m => ({ ...m, ascensor_id:e.target.value }))} style={{ ...inp, width:'100%', boxSizing:'border-box' }}>
              <option value="">— Selecciona —</option>
              {ascensores.map(a => <option key={a.id} value={a.id}>{a.comunidad} — Asc. {a.n_ascensor}{a.torre ? ` T${a.torre}` : ''}</option>)}
            </select>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
            <div>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>Fecha *</label>
              <input type="date" value={modal.fecha} onChange={e => setModal(m => ({ ...m, fecha:e.target.value }))} style={{ ...inp, width:'100%', boxSizing:'border-box' }} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>Técnico</label>
              <input value={modal.tecnico_nombre} onChange={e => setModal(m => ({ ...m, tecnico_nombre:e.target.value }))} placeholder="Nombre" style={{ ...inp, width:'100%', boxSizing:'border-box' }} />
            </div>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>Trabajo realizado *</label>
            <textarea value={modal.trabajo} onChange={e => setModal(m => ({ ...m, trabajo:e.target.value }))} rows={3} placeholder="Describe la reparación…" style={{ ...inp, width:'100%', boxSizing:'border-box', resize:'vertical' }} />
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>Comprobante de respaldo (opcional)</label>
            <select value={modal.formulario_id || ''} onChange={e => setModal(m => ({ ...m, formulario_id:e.target.value }))}
              disabled={!modal.ascensor_id} style={{ ...inp, width:'100%', boxSizing:'border-box' }}>
              <option value="">— Ninguno —</option>
              {compResp.map(c => (
                <option key={c.id} value={c.id}>
                  {c.folio} · {c.tipo_visita || (c.tipo === 'mantenimiento' ? 'Mantención' : 'Visita')} · {c.fecha_visita ? fechaCL(c.fecha_visita) : ''}
                </option>
              ))}
            </select>
            {!modal.ascensor_id
              ? <small style={{ color:'#6B7C74' }}>Selecciona primero el equipo.</small>
              : compResp.length === 0 && <small style={{ color:'#6B7C74' }}>Este equipo no tiene comprobantes en el sistema.</small>}
          </div>
          {error && <div style={{ color:'#EF4444', fontSize:13, marginBottom:10 }}>{error}</div>}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button onClick={() => setModal(null)} disabled={guardando} style={{ padding:'9px 18px', border:'1px solid #2E4A40', borderRadius:8, background:'none', color:'#8A9994', cursor:'pointer', fontSize:14 }}>Cancelar</button>
            <button onClick={guardarManual} disabled={guardando} style={{ padding:'9px 20px', background: guardando ? '#0a4a38' : '#0f6e56', color:'white', border:'none', borderRadius:8, fontWeight:600, cursor: guardando ? 'wait' : 'pointer', fontSize:14 }}>{guardando ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function ImportarVisitasModal({ onClose }) {
  const [archivos, setArchivos] = useState([]);
  const [preview, setPreview]   = useState(null);
  const [analizando, setAnalizando] = useState(false);
  const [job, setJob]           = useState(null);
  const [err, setErr]           = useState('');
  const fileRef = useRef(null);
  const tablaRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const analizar = async (files, tabla = false) => {
    setErr(''); setPreview(null); setJob(null);
    const fd = new FormData();
    [...files].forEach(f => fd.append('archivos', f));
    setAnalizando(true);
    try {
      const ep = tabla ? '/reportes/bitacora/importar-visitas/preview-tabla' : '/reportes/bitacora/importar-visitas/preview';
      const r = await api.post(ep, fd);
      setPreview(r.data);
    } catch (e) { setErr(e.response?.data?.error || 'Error al analizar el archivo'); }
    finally { setAnalizando(false); }
  };

  const consultarJob = async () => {
    try {
      const r = await api.get('/reportes/bitacora/importar-visitas/progreso');
      setJob(r.data);
      if (!r.data.running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    } catch { /* ignora */ }
  };

  const confirmar = async () => {
    const items = (preview?.items || [])
      .filter(x => x.estado_import === 'ok')
      .map(x => ({ sheets_id: x.sheets_id, fecha: x.fecha, tipo: x.tipo, resumen: x.resumen, folio: x.folio, estado: x.estado, tecnico: x.tecnico }));
    if (!items.length) { setErr('No hay filas válidas para importar'); return; }
    setErr('');
    try {
      await api.post('/reportes/bitacora/importar-visitas/confirmar', { items });
      if (pollRef.current) clearInterval(pollRef.current);
      consultarJob();
      pollRef.current = setInterval(consultarJob, 2500);
    } catch (e) { setErr(e.response?.data?.error || 'No se pudo iniciar la importación'); }
  };

  const descargarSinCoincidencia = () => {
    const sc = (preview?.items || []).filter(x => x.estado_import === 'sin_coincidencia' || x.estado_import === 'error');
    if (!sc.length) return;
    const cols = ['comunidad', 'torre', 'ascensor', 'codigo', 'sheets_id', 'fecha', 'folio', 'tecnico', 'estado', 'resumen', 'archivo', 'motivo'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [cols.join(',')].concat(sc.map(r => cols.map(c => esc(r[c])).join(','))).join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'visitas_sin_coincidencia.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const okN = preview?.ok || 0;
  const colorEstado = { ok:'#4CAF50', sin_coincidencia:'#F59E0B', error:'#EF4444' };
  const labelEstado = { ok:'Se agregará', sin_coincidencia:'Sin coincidencia', error:'Error' };

  return (
    <Modal title="Importar comprobantes de visita (Excel antiguos)" onClose={onClose} maxWidth={720}>
      {!job && (
        <p style={{ margin:'0 0 12px', fontSize:13, color:'#8A9994' }}>
          <b>Archivos de visita:</b> los <b>.xlsx</b> antiguos (uno por comprobante). Se extraen fecha, resumen,
          folio, técnico y estado, y se ubican en la bitácora por <b>comunidad + N° + torre</b>.<br/>
          <b>Tabla corregida:</b> re-sube el <b>CSV/Excel</b> de “sin coincidencia” después de arreglar la comunidad/ascensor
          (o completando la columna <code>codigo</code> o <code>sheets_id</code>). Una fila = un comprobante.<br/>
          En ambos casos no se duplica lo ya cargado (dedupe por fecha + resumen); lo que no calce queda en el listado aparte.
        </p>
      )}

      {!job && (
        <div style={{ marginBottom:14, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <button onClick={() => fileRef.current?.click()} style={{
            padding:'9px 18px', background:'#0f6e56', color:'white', border:'none',
            borderRadius:8, fontWeight:600, cursor:'pointer', fontSize:14 }}>
            ⬆ Archivos de visita (.xlsx)
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" multiple style={{ display:'none' }}
            onChange={e => { const fs = e.target.files; if (fs?.length) { setArchivos([...fs]); analizar(fs, false); } }} />

          <button onClick={() => tablaRef.current?.click()} style={{
            padding:'9px 18px', background:'transparent', color:'#38BDF8', border:'1px solid #1E3A5F',
            borderRadius:8, fontWeight:600, cursor:'pointer', fontSize:14 }}>
            ⬆ Tabla corregida (CSV/Excel)
          </button>
          <input ref={tablaRef} type="file" accept=".csv,.xlsx,.xls" style={{ display:'none' }}
            onChange={e => { const fs = e.target.files; if (fs?.length) { setArchivos([...fs]); analizar(fs, true); } }} />

          {archivos.length > 0 && <span style={{ color:'#C9D3CE', fontSize:13 }}>{archivos.length} archivo(s)</span>}
        </div>
      )}

      {err && <div style={{ background:'#2D0F0F', color:'#EF4444', borderRadius:8, padding:'10px 14px', marginBottom:12, fontSize:13 }}>{err}</div>}
      {analizando && <p style={{ color:'#8A9994' }}>Analizando archivos…</p>}

      {preview && !job && (
        <div>
          <p style={{ margin:'0 0 10px', fontSize:14, color:'#F4F7F5' }}>
            {preview.total} archivo(s): <b style={{ color:'#4CAF50' }}>{preview.ok} se agregarán</b>,{' '}
            <b style={{ color:'#F59E0B' }}>{preview.sin_coincidencia} sin coincidencia</b>
            {preview.errores ? <>, <b style={{ color:'#EF4444' }}>{preview.errores} con error</b></> : null}.
          </p>
          {(preview.sin_coincidencia > 0 || preview.errores > 0) && (
            <button onClick={descargarSinCoincidencia}
              style={{ marginBottom:10, padding:'6px 12px', background:'transparent', color:'#F59E0B',
                       border:'1px solid #5A4A1E', borderRadius:7, cursor:'pointer', fontSize:12 }}>
              ⬇ Descargar sin coincidencia (CSV)
            </button>
          )}
          <div style={{ maxHeight:300, overflowY:'auto', border:'1px solid #1C2922', borderRadius:8, fontSize:12 }}>
            {preview.items.map((it, i) => (
              <div key={i} style={{ display:'flex', gap:8, padding:'6px 10px', borderBottom:'1px solid #1C2922', alignItems:'baseline' }}>
                <span style={{ color:colorEstado[it.estado_import], fontWeight:700, minWidth:120 }}>{labelEstado[it.estado_import]}</span>
                <span style={{ color:'#8A9994', minWidth:80 }}>{it.fecha || '—'}</span>
                <span style={{ color:'#C9D3CE', flex:1 }}>
                  {it.comunidad || it.archivo}{it.ascensor ? ` · Asc.${it.ascensor}` : ''}{it.torre ? `/${it.torre}` : ''}
                  {it.motivo ? <span style={{ color:'#F59E0B' }}> — {it.motivo}</span> : ''}
                </span>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
            <button onClick={onClose} style={{ padding:'9px 18px', border:'1px solid #2E4A40', borderRadius:8, background:'none', color:'#8A9994', cursor:'pointer', fontSize:14 }}>Cerrar</button>
            <button onClick={confirmar} disabled={!okN}
              style={{ padding:'9px 20px', background: okN ? '#0f6e56' : '#0a4a38', color:'white', border:'none',
                       borderRadius:8, fontWeight:600, cursor: okN ? 'pointer' : 'not-allowed', fontSize:14, opacity: okN ? 1 : 0.6 }}>
              Importar {okN} a las bitácoras
            </button>
          </div>
        </div>
      )}

      {job && (
        <div style={{ marginTop:6 }}>
          <p style={{ fontSize:14, color:'#F4F7F5', margin:'0 0 8px' }}>
            {job.running ? 'Importando…' : 'Importación finalizada.'}{' '}
            <span style={{ color:'#8A9994', fontWeight:400 }}>
              {job.procesados}/{job.total} · <span style={{ color:'#4CAF50' }}>{job.escritos} escritos</span>
              {job.duplicados ? <> · <span style={{ color:'#8A9994' }}>{job.duplicados} ya existían</span></> : null}
              {job.errores ? <> · <span style={{ color:'#EF4444' }}>{job.errores} con error</span></> : null}
            </span>
          </p>
          <div style={{ height:8, background:'#1C2922', borderRadius:4, overflow:'hidden' }}>
            <div style={{ width:`${job.total ? Math.round((job.procesados/job.total)*100) : 0}%`, height:'100%', background:'#4CAF50', transition:'width .3s' }} />
          </div>
          {job.primer_error && <p style={{ margin:'8px 0 0', fontSize:12, color:'#F59E0B' }}>Primer error: {job.primer_error}</p>}
          {!job.running && (
            <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}>
              <button onClick={onClose} style={{ padding:'9px 20px', background:'#0f6e56', color:'white', border:'none', borderRadius:8, fontWeight:600, cursor:'pointer', fontSize:14 }}>Listo</button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function TabBitacoras() {
  const [resumen, setResumen] = useState(null);
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [desde,   setDesde]   = useState('');
  const [hasta,   setHasta]   = useState('');
  const [comunidad, setComunidad] = useState('');
  const [estado,  setEstado]  = useState('pendientes'); // '' | cargadas | pendientes | sin_sheets
  const [reintentando, setReintentando] = useState(null); // id en curso
  const [aviso, setAviso] = useState('');
  const [job, setJob] = useState(null); // progreso de la carga masiva
  const pollRef = useRef(null);
  const [dup, setDup] = useState(null); // progreso/resultados del escaneo de duplicados
  const dupPollRef = useRef(null);
  const [limpia, setLimpia] = useState(null); // progreso de la limpieza de duplicados
  const limpiaPollRef = useRef(null);
  const [importarVisitas, setImportarVisitas] = useState(false);

  const inp = { padding:'8px 10px', border:'1.5px solid #2E4A40', borderRadius:8,
                fontSize:13, background:'#0D1311', color:'#F4F7F5', fontFamily:'inherit' };
  const th = { padding:'10px 12px', textAlign:'left', fontSize:11, fontWeight:700,
               color:'#8A9994', textTransform:'uppercase', letterSpacing:'0.5px',
               borderBottom:'1px solid #1C2922', whiteSpace:'nowrap' };

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (desde && hasta) { params.desde = desde; params.hasta = hasta; }
      if (comunidad) params.comunidad = comunidad;
      if (estado) params.estado = estado;
      const r = await api.get('/reportes/bitacora-estado', { params });
      setResumen(r.data.resumen);
      setRows(r.data.rows || []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [desde, hasta, comunidad, estado]);

  useEffect(() => { cargar(); }, [cargar]);

  const reintentar = async (id) => {
    setReintentando(id); setAviso(''); setError('');
    try {
      await api.post(`/reportes/bitacora/${id}/reintentar`);
      setAviso('Bitácora actualizada correctamente.');
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'No se pudo actualizar'); }
    finally { setReintentando(null); }
  };

  // Consulta el progreso del job en segundo plano y arranca/detiene el polling.
  const consultarProgreso = useCallback(async () => {
    try {
      const r = await api.get('/reportes/bitacora/progreso');
      setJob(r.data);
      if (!r.data.running) {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (r.data.done) cargar(); // refresca conteos al terminar
      }
    } catch { /* ignora fallos puntuales de polling */ }
  }, [cargar]);

  const iniciarPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    consultarProgreso();
    pollRef.current = setInterval(consultarProgreso, 2500);
  }, [consultarProgreso]);

  // Al montar: si ya hay un job corriendo (p.ej. se recargó la página), retómalo.
  useEffect(() => {
    consultarProgreso();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [consultarProgreso]);

  const procesarTodo = async () => {
    if (!window.confirm(`¿Cargar todas las bitácoras pendientes del filtro actual? El proceso corre en el servidor; puedes seguir usando la app y ver el avance aquí.`)) return;
    setAviso(''); setError('');
    try {
      const body = {};
      if (desde && hasta) { body.desde = desde; body.hasta = hasta; }
      if (comunidad) body.comunidad = comunidad;
      await api.post('/reportes/bitacora/procesar-todo', body);
      iniciarPolling();
    } catch (e) { setError(e.response?.data?.error || 'No se pudo iniciar la carga'); }
  };

  const cancelarProceso = async () => {
    try { await api.post('/reportes/bitacora/cancelar'); consultarProgreso(); } catch { /* noop */ }
  };

  // ── Escaneo de duplicados en las planillas ──
  const consultarDup = useCallback(async () => {
    try {
      const r = await api.get('/reportes/bitacora/duplicados/progreso');
      setDup(r.data);
      if (!r.data.running && dupPollRef.current) { clearInterval(dupPollRef.current); dupPollRef.current = null; }
    } catch { /* ignora */ }
  }, []);

  useEffect(() => {
    consultarDup();
    return () => { if (dupPollRef.current) clearInterval(dupPollRef.current); };
  }, [consultarDup]);

  const escanearDuplicados = async () => {
    if (!window.confirm('¿Revisar todas las planillas de bitácora en busca de filas repetidas (misma fecha y resumen)? Corre en el servidor (~1-3 min).')) return;
    setError('');
    try {
      await api.post('/reportes/bitacora/duplicados/scan');
      if (dupPollRef.current) clearInterval(dupPollRef.current);
      consultarDup();
      dupPollRef.current = setInterval(consultarDup, 2500);
    } catch (e) { setError(e.response?.data?.error || 'No se pudo iniciar el escaneo'); }
  };

  // ── Limpieza de duplicados (destructivo) ──
  const consultarLimpia = useCallback(async () => {
    try {
      const r = await api.get('/reportes/bitacora/duplicados/limpiar/progreso');
      setLimpia(r.data);
      if (!r.data.running && limpiaPollRef.current) { clearInterval(limpiaPollRef.current); limpiaPollRef.current = null; }
    } catch { /* ignora */ }
  }, []);

  useEffect(() => {
    consultarLimpia();
    return () => { if (limpiaPollRef.current) clearInterval(limpiaPollRef.current); };
  }, [consultarLimpia]);

  const limpiarDuplicados = async () => {
    const c1 = window.confirm('Se ELIMINARÁN las filas repetidas (misma fecha y resumen) en las planillas de bitácora, conservando la primera de cada grupo. Esta acción modifica tus Google Sheets y no se puede deshacer. ¿Continuar?');
    if (!c1) return;
    const c2 = window.prompt('Para confirmar, escribe: ELIMINAR');
    if ((c2 || '').trim().toUpperCase() !== 'ELIMINAR') { setError('Limpieza cancelada.'); return; }
    setError('');
    try {
      await api.post('/reportes/bitacora/duplicados/limpiar');
      if (limpiaPollRef.current) clearInterval(limpiaPollRef.current);
      consultarLimpia();
      limpiaPollRef.current = setInterval(consultarLimpia, 2500);
    } catch (e) { setError(e.response?.data?.error || 'No se pudo iniciar la limpieza'); }
  };

  const Card = ({ n, label, color }) => (
    <div style={{ background:'#0D1311', border:'1px solid #1C2922', borderRadius:10, padding:'10px 14px', minWidth:110 }}>
      <div style={{ fontSize:22, fontWeight:800, color }}>{n ?? '—'}</div>
      <div style={{ fontSize:12, color:'#8A9994' }}>{label}</div>
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, flexWrap:'wrap' }}>
        <p style={{ margin:'0 0 12px', fontSize:13, color:'#8A9994', flex:1, minWidth:260 }}>
          Estado de registro de cada comprobante en su bitácora (Google Sheets del ascensor).
          Los <strong style={{ color:'#F59E0B' }}>pendientes</strong> se pueden reintentar; los
          <strong style={{ color:'#EF4444' }}> sin bitácora</strong> son ascensores sin <code>sheets_id</code> configurado.
        </p>
        <button onClick={() => setImportarVisitas(true)}
          style={{ padding:'8px 14px', background:'#0D1E2E', color:'#38BDF8', border:'1px solid #1E3A5F',
                   borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13, whiteSpace:'nowrap' }}>
          ⬆ Importar visitas antiguas (Excel)
        </button>
      </div>
      {importarVisitas && <ImportarVisitasModal onClose={() => setImportarVisitas(false)} />}

      {/* Filtros */}
      <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
        <input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={inp} title="Desde" />
        <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={inp} title="Hasta" />
        <input placeholder="Comunidad…" value={comunidad} onChange={e => setComunidad(e.target.value)} style={{ ...inp, minWidth:160 }} />
        <select value={estado} onChange={e => setEstado(e.target.value)} style={inp}>
          <option value="pendientes">Pendientes</option>
          <option value="cargadas">Cargadas</option>
          <option value="sin_sheets">Sin bitácora</option>
          <option value="">Todas</option>
        </select>
        <button onClick={cargar}
          style={{ padding:'8px 16px', background:'#0f6e56', color:'white', border:'none', borderRadius:8, cursor:'pointer', fontWeight:600, fontSize:13 }}>
          Actualizar
        </button>
        <button onClick={procesarTodo} disabled={job?.running || !(resumen?.pendientes)}
          style={{ padding:'8px 16px', background: (job?.running || !(resumen?.pendientes)) ? '#2A1F0A' : '#F59E0B',
                   color: (job?.running || !(resumen?.pendientes)) ? '#8A6D1A' : '#0B0F0D',
                   border:'none', borderRadius:8, cursor: (job?.running || !(resumen?.pendientes)) ? 'not-allowed' : 'pointer',
                   fontWeight:700, fontSize:13, marginLeft:'auto' }}>
          {job?.running ? 'Cargando…' : `↻ Cargar todas las pendientes${resumen?.pendientes ? ` (${resumen.pendientes})` : ''}`}
        </button>
      </div>

      {/* Progreso de la carga masiva (job en segundo plano) */}
      {job && (job.running || job.done) && (job.total > 0) && (() => {
        const pct = job.total ? Math.round((job.procesados / job.total) * 100) : 0;
        return (
          <div style={{ background:'#0D1311', border:'1px solid #1C2922', borderRadius:10, padding:'12px 14px', marginBottom:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, flexWrap:'wrap', gap:8 }}>
              <span style={{ fontSize:13, color:'#F4F7F5', fontWeight:600 }}>
                {job.running ? 'Cargando bitácoras…' : 'Carga finalizada'}
                {'  '}
                <span style={{ color:'#8A9994', fontWeight:400 }}>
                  {job.procesados}/{job.total} · <span style={{ color:'#4CAF50' }}>{job.ok} ok</span>
                  {job.fallidos ? <> · <span style={{ color:'#EF4444' }}>{job.fallidos} con error</span></> : null}
                </span>
              </span>
              {job.running && (
                <button onClick={cancelarProceso}
                  style={{ padding:'5px 12px', background:'transparent', color:'#EF4444', border:'1px solid #5A1E1E', borderRadius:7, cursor:'pointer', fontSize:12 }}>
                  Detener
                </button>
              )}
            </div>
            <div style={{ height:8, background:'#1C2922', borderRadius:4, overflow:'hidden' }}>
              <div style={{ width:`${pct}%`, height:'100%', background: job.fallidos && !job.ok ? '#EF4444' : '#4CAF50', transition:'width .3s' }} />
            </div>
            {job.primer_error && (
              <p style={{ margin:'8px 0 0', fontSize:12, color:'#F59E0B' }}>
                Primer error: <span style={{ fontFamily:'monospace' }}>{job.primer_error}</span>
              </p>
            )}
          </div>
        );
      })()}

      {/* Resumen */}
      <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
        <Card n={resumen?.total} label="Total" color="#F4F7F5" />
        <Card n={resumen?.cargadas} label="Cargadas" color="#4CAF50" />
        <Card n={resumen?.pendientes} label="Pendientes" color="#F59E0B" />
        <Card n={resumen?.sin_sheets} label="Sin bitácora" color="#EF4444" />
      </div>

      {/* Revisión de duplicados en las planillas */}
      <div style={{ background:'#0D1311', border:'1px solid #1C2922', borderRadius:10, padding:'12px 14px', marginBottom:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:10, flexWrap:'wrap' }}>
          <span style={{ fontSize:13, color:'#C9D3CE' }}>
            Revisar folios repetidos dentro de las planillas de Google Sheets.
          </span>
          <button onClick={escanearDuplicados} disabled={dup?.running}
            style={{ padding:'7px 14px', background: dup?.running ? '#1C2922' : '#0D1E2E',
                     color: dup?.running ? '#6B7C74' : '#38BDF8', border:'1px solid #1E3A5F',
                     borderRadius:8, cursor: dup?.running ? 'default' : 'pointer', fontWeight:600, fontSize:13 }}>
            {dup?.running ? `Escaneando… ${dup.escaneados}/${dup.total}` : '🔍 Revisar duplicados'}
          </button>
        </div>

        {dup && (dup.running || dup.done) && (
          <div style={{ marginTop:10 }}>
            <div style={{ fontSize:12, color:'#8A9994', marginBottom:8 }}>
              {dup.running ? 'Escaneando planillas…' : 'Escaneo finalizado.'}{' '}
              Planillas: {dup.escaneados}/{dup.total} ·{' '}
              {dup.con_duplicados > 0
                ? <span style={{ color:'#EF4444' }}>{dup.con_duplicados} con duplicados · {dup.total_dups} filas repetidas</span>
                : (!dup.running ? <span style={{ color:'#4CAF50' }}>Sin duplicados 🎉</span> : 'sin duplicados hasta ahora')}
              {dup.primer_error ? <span style={{ color:'#F59E0B' }}> · error: {dup.primer_error}</span> : null}
            </div>
            {(dup.resultados || []).length > 0 && (
              <div style={{ maxHeight:220, overflowY:'auto', border:'1px solid #1C2922', borderRadius:8 }}>
                {dup.resultados.map((h, idx) => (
                  <div key={idx} style={{ padding:'8px 12px', borderBottom:'1px solid #1C2922' }}>
                    <div style={{ fontSize:13, color:'#F4F7F5', fontWeight:600, marginBottom:4 }}>
                      {h.comunidad || '(sin comunidad)'}
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      {h.dups.map((d, j) => (
                        <div key={j} style={{ fontSize:12, color:'#C9D3CE', background:'#2A1F0A',
                                               borderRadius:4, padding:'4px 8px' }}>
                          <span style={{ color:'#8A9994' }}>{d.fecha}</span>
                          {d.tipo ? <span style={{ color:'#6B7C74' }}> · {d.tipo}</span> : null}
                          {' '}<span style={{ color:'#F59E0B' }}>×{d.veces}</span>
                          <div style={{ color:'#E6ECE9' }}>{d.resumen || '(sin resumen)'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Botón de limpieza (solo si el escaneo terminó y encontró duplicados) */}
            {dup.done && dup.con_duplicados > 0 && (
              <div style={{ marginTop:12, borderTop:'1px solid #1C2922', paddingTop:12 }}>
                <button onClick={limpiarDuplicados} disabled={limpia?.running}
                  style={{ padding:'8px 14px', background: limpia?.running ? '#2D0F0F' : '#7A1E1E',
                           color:'#fff', border:'none', borderRadius:8,
                           cursor: limpia?.running ? 'default' : 'pointer', fontWeight:700, fontSize:13 }}>
                  {limpia?.running ? `Eliminando… ${limpia.procesados}/${limpia.total}` : `🗑 Eliminar duplicados (dejar 1) · ${dup.total_dups} filas`}
                </button>
                <span style={{ fontSize:11, color:'#6B7C74', marginLeft:10 }}>
                  Conserva la primera fila de cada grupo (misma fecha + resumen). Modifica las planillas.
                </span>
              </div>
            )}

            {limpia && (limpia.running || limpia.done) && (
              <div style={{ marginTop:10, fontSize:12, color:'#8A9994' }}>
                {limpia.running ? 'Eliminando duplicados…' : 'Limpieza finalizada.'}{' '}
                Planillas: {limpia.procesados}/{limpia.total} ·{' '}
                <span style={{ color:'#4CAF50' }}>{limpia.filas_eliminadas} filas eliminadas</span>
                {' '}en {limpia.hojas_limpiadas} planillas
                {limpia.primer_error ? <span style={{ color:'#F59E0B' }}> · error: {limpia.primer_error}</span> : null}
                {limpia.done ? <span style={{ color:'#6B7C74' }}> — vuelve a "Revisar duplicados" para confirmar que quedaron limpias.</span> : null}
              </div>
            )}
          </div>
        )}
      </div>

      {aviso && <p style={{ color:'#4CAF50', fontSize:13 }}>{aviso}</p>}
      {error && <p style={{ color:'#EF4444', fontSize:13 }}>{error}</p>}

      {loading ? (
        <p style={{ color:'#6B7C74', padding:20 }}>Cargando…</p>
      ) : rows.length === 0 ? (
        <p style={{ color:'#6B7C74', padding:20 }}>Sin comprobantes para este filtro.</p>
      ) : (
        <div style={{ overflowX:'auto', border:'1px solid #1C2922', borderRadius:12 }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#0D1311' }}>
                <th style={th}>Folio</th>
                <th style={th}>Fecha</th>
                <th style={th}>Comunidad · Asc.</th>
                <th style={th}>Técnico</th>
                <th style={th}>Bitácora</th>
                <th style={{ ...th, textAlign:'right' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const sinSheets = !r.sheets_id;
                return (
                  <tr key={r.id} style={{ borderBottom:'1px solid #1C2922' }}>
                    <td style={{ padding:'9px 12px', color:'#F4F7F5', fontWeight:600, whiteSpace:'nowrap' }}>
                      {r.folio || `#${r.id}`}
                      <div style={{ fontSize:11, color:'#6B7C74' }}>
                        {r.tipo_visita || (r.tipo === 'mantenimiento' ? 'Mantenimiento' : 'Visita')}
                      </div>
                    </td>
                    <td style={{ padding:'9px 12px', color:'#C9D3CE', whiteSpace:'nowrap' }}>{fechaCL(r.fecha_visita)}</td>
                    <td style={{ padding:'9px 12px', color:'#C9D3CE' }}>
                      {r.comunidad || '—'}<span style={{ color:'#6B7C74' }}> · Asc. {r.n_ascensor}{r.torre ? ` T${r.torre}` : ''}</span>
                    </td>
                    <td style={{ padding:'9px 12px', color:'#C9D3CE' }}>{r.tecnico_nombre || '—'}</td>
                    <td style={{ padding:'9px 12px' }}>
                      {r.bitacora_actualizada ? (
                        <span style={{ background:'#12382D', color:'#4CAF50', borderRadius:4, padding:'3px 9px', fontSize:12, fontWeight:600 }}>✓ Cargada</span>
                      ) : sinSheets ? (
                        <span style={{ background:'#2D0F0F', color:'#EF4444', borderRadius:4, padding:'3px 9px', fontSize:12, fontWeight:600 }}>Sin bitácora</span>
                      ) : (
                        <span style={{ background:'#2A1F0A', color:'#F59E0B', borderRadius:4, padding:'3px 9px', fontSize:12, fontWeight:600 }}>Pendiente</span>
                      )}
                    </td>
                    <td style={{ padding:'7px 12px', textAlign:'right', whiteSpace:'nowrap' }}>
                      {!r.bitacora_actualizada && !sinSheets && (
                        <button onClick={() => reintentar(r.id)} disabled={reintentando === r.id}
                          style={{ padding:'6px 12px', background: reintentando === r.id ? '#0a4a38' : '#0f6e56',
                                   color:'white', border:'none', borderRadius:7, cursor: reintentando === r.id ? 'wait' : 'pointer', fontWeight:600, fontSize:12 }}>
                          {reintentando === r.id ? 'Enviando…' : 'Reintentar'}
                        </button>
                      )}
                      {r.pdf_url && (
                        <a href={r.pdf_url} target="_blank" rel="noreferrer"
                          style={{ color:'#38BDF8', fontSize:12, marginLeft:10, whiteSpace:'nowrap' }}>PDF ↗</a>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TabAuditoria() {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [opciones, setOpciones] = useState({ entidades: [], actores: [] });
  const [f, setF]               = useState({ actor_id:'', entidad:'', accion:'', desde:'', hasta:'', q:'' });
  const [offset, setOffset]     = useState(0);
  const [descargando, setDescargando] = useState(false);
  const [expandido, setExpandido] = useState(null); // id de registro con detalle abierto
  const LIMIT = 100;

  useEffect(() => {
    api.get('/auditoria/opciones').then(r => setOpciones(r.data || { entidades:[], actores:[] })).catch(() => {});
  }, []);

  const descargar = async () => {
    setDescargando(true);
    try {
      const params = {};
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get('/auditoria/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : 'auditoria.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Error al descargar la auditoría'); }
    finally { setDescargando(false); }
  };

  const cargar = async (off = 0) => {
    setLoading(true); setError('');
    try {
      const params = { limit: LIMIT, offset: off };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get('/auditoria', { params });
      setTotal(r.data.total);
      setOffset(off);
      setItems(off === 0 ? r.data.items : prev => [...prev, ...r.data.items]);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al cargar la auditoría');
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(0); /* eslint-disable-next-line */ }, []);

  const ACCION_CFG = {
    crear:     { label: 'Creó',      bg: '#12382D', color: '#4CAF50' },
    editar:    { label: 'Editó',     bg: '#2A1F0A', color: '#F59E0B' },
    eliminar:  { label: 'Eliminó',   bg: '#2D0F0F', color: '#EF4444' },
    consultar: { label: 'Consultó',  bg: '#0D1E2E', color: '#38BDF8' },
  };
  const fmt = (ts) => { try { return new Date(ts).toLocaleString('es-CL', { timeZone: 'America/Santiago' }); } catch { return String(ts); } };
  const inp = { padding:'8px 10px', border:'1.5px solid #2E4A40', borderRadius:8, fontSize:13,
                background:'#0D1311', color:'#F4F7F5', fontFamily:'inherit' };

  return (
    <div>
      <p style={{ margin:'0 0 12px', fontSize:13, color:'#8A9994' }}>
        Registro de todas las acciones (envíos y modificaciones) con autor, fecha y hora. Solo superadmin.
      </p>

      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'flex-end', marginBottom:14 }}>
        <select value={f.actor_id} onChange={e => setF(p => ({ ...p, actor_id:e.target.value }))} style={{ ...inp, width:'auto' }}>
          <option value="">Todos los usuarios</option>
          {opciones.actores.map(a => <option key={a.actor_id} value={a.actor_id}>{a.actor_nombre || `#${a.actor_id}`}</option>)}
        </select>
        <select value={f.entidad} onChange={e => setF(p => ({ ...p, entidad:e.target.value }))} style={{ ...inp, width:'auto' }}>
          <option value="">Toda entidad</option>
          {opciones.entidades.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        <select value={f.accion} onChange={e => setF(p => ({ ...p, accion:e.target.value }))} style={{ ...inp, width:'auto' }}>
          <option value="">Toda acción</option>
          <option value="crear">Creó</option>
          <option value="editar">Editó</option>
          <option value="eliminar">Eliminó</option>
          <option value="consultar">Consultó</option>
        </select>
        <input type="date" value={f.desde} onChange={e => setF(p => ({ ...p, desde:e.target.value }))} style={inp} />
        <input type="date" value={f.hasta} onChange={e => setF(p => ({ ...p, hasta:e.target.value }))} style={inp} />
        <input value={f.q} onChange={e => setF(p => ({ ...p, q:e.target.value }))} placeholder="Buscar ruta/usuario…" style={{ ...inp, minWidth:160 }} />
        <button onClick={() => cargar(0)} disabled={loading}
          style={{ padding:'9px 18px', background:'#0f6e56', color:'white', border:'none',
                   borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer' }}>
          {loading ? 'Cargando…' : 'Filtrar'}
        </button>
        <button onClick={descargar} disabled={descargando}
          style={{ padding:'9px 18px', background:'#0D1E2E', color:'#60A5FA',
                   border:'1px solid #1E3A5F', borderRadius:8, fontWeight:700, fontSize:13,
                   cursor: descargando ? 'default' : 'pointer' }}>
          {descargando ? 'Descargando…' : '⬇ Excel'}
        </button>
      </div>

      {error && (
        <div style={{ background:'#2D0F0F', color:'#EF4444', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:14 }}>{error}</div>
      )}

      <div style={{ fontSize:12, color:'#8A9994', marginBottom:8 }}>{total} registros</div>

      <div style={{ overflowX:'auto', borderRadius:10, border:'1px solid rgba(255,255,255,0.07)' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
          <thead>
            <tr style={{ background:'#161C19' }}>
              {['Fecha y hora','Usuario','Acción','Entidad','Ruta','Estado'].map(h => (
                <th key={h} style={{ padding:'9px 12px', textAlign:'left', color:'#9FAAA5', fontWeight:600,
                                     borderBottom:'1px solid rgba(255,255,255,0.07)', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={6} style={{ padding:20, textAlign:'center', color:'#6B7C74', background:'#111714' }}>Sin registros</td></tr>
            )}
            {items.map((r, i) => {
              const ac = ACCION_CFG[r.accion] || { label:r.accion, bg:'#1A1F1D', color:'#8A9994' };
              const stColor = r.status_code >= 500 ? '#EF4444' : r.status_code >= 400 ? '#F59E0B' : '#4CAF50';
              const abierto = expandido === r.id;
              const rawLine = `${new Date(r.created_at).toISOString()}  ${r.metodo || ''} ${r.ruta || ''}  ${r.status_code ?? ''}  — ${r.actor_nombre || '—'}${r.actor_rol ? ` (${r.actor_rol})` : ''}  [${r.ip || 's/IP'}]`;
              const Campo = ({ k, v, mono }) => (
                <div style={{ display:'flex', gap:8, padding:'3px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                  <span style={{ minWidth:110, color:'#6B7C74', fontSize:12 }}>{k}</span>
                  <span style={{ color:'#E6ECE9', fontSize:12, fontFamily: mono ? 'monospace' : 'inherit', wordBreak:'break-all' }}>{v ?? '—'}</span>
                </div>
              );
              return (
                <React.Fragment key={r.id || i}>
                <tr onClick={() => setExpandido(abierto ? null : r.id)}
                    style={{ background: abierto ? '#12382D' : (i%2===0?'#111714':'#0D1311'),
                             borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:'pointer' }}>
                  <td style={{ padding:'8px 12px', color:'#C9D3CE', whiteSpace:'nowrap' }}>
                    <span style={{ color:'#4D6358', fontSize:10, marginRight:6 }}>{abierto ? '▾' : '▸'}</span>
                    {fmt(r.created_at)}
                  </td>
                  <td style={{ padding:'8px 12px', color:'#F4F7F5' }}>
                    {r.actor_nombre || <span style={{ color:'#6B7C74' }}>—</span>}
                    {r.actor_rol && <span style={{ fontSize:11, color:'#6B7C74' }}> · {r.actor_rol}</span>}
                  </td>
                  <td style={{ padding:'8px 12px' }}>
                    <span style={{ background:ac.bg, color:ac.color, borderRadius:4, padding:'2px 8px', fontSize:12, fontWeight:600 }}>{ac.label}</span>
                  </td>
                  <td style={{ padding:'8px 12px', color:'#C9D3CE' }}>{r.entidad}</td>
                  <td style={{ padding:'8px 12px', color:'#8A9994', fontFamily:'monospace', fontSize:12 }}>{r.metodo} {r.ruta}</td>
                  <td style={{ padding:'8px 12px', color:stColor, fontWeight:600 }}>{r.status_code}</td>
                </tr>
                {abierto && (
                  <tr style={{ background:'#0B0F0D' }}>
                    <td colSpan={6} style={{ padding:'12px 16px' }}>
                      {/* Línea cruda estilo log */}
                      <div style={{ background:'#050706', border:'1px solid #1C2922', borderRadius:8,
                                    padding:'10px 12px', marginBottom:12, fontFamily:'monospace', fontSize:12,
                                    color:'#9FE7C8', whiteSpace:'pre-wrap', wordBreak:'break-all' }}>
                        {rawLine}
                      </div>
                      {/* Detalle campo por campo */}
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 24px' }}>
                        <Campo k="ID registro" v={r.id} mono />
                        <Campo k="Fecha y hora" v={fmt(r.created_at)} />
                        <Campo k="Usuario" v={`${r.actor_nombre || '—'}${r.actor_id ? ` (#${r.actor_id})` : ''}`} />
                        <Campo k="Rol" v={r.actor_rol} />
                        <Campo k="Acción" v={ac.label} />
                        <Campo k="Entidad" v={r.entidad} />
                        <Campo k="Método" v={r.metodo} mono />
                        <Campo k="Estado HTTP" v={r.status_code} mono />
                        <Campo k="IP" v={r.ip} mono />
                        <Campo k="Ruta" v={r.ruta} mono />
                      </div>
                      <p style={{ margin:'10px 0 0', fontSize:11, color:'#6B7C74' }}>
                        El registro es a nivel de endpoint (quién, qué ruta, cuándo, resultado). Por seguridad no se guarda el contenido enviado (evita contraseñas y fotos).
                      </p>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {items.length < total && (
        <div style={{ textAlign:'center', marginTop:14 }}>
          <button onClick={() => cargar(offset + LIMIT)} disabled={loading}
            style={{ padding:'9px 20px', background:'#161C19', color:'#C9D3CE',
                     border:'1px solid rgba(255,255,255,0.15)', borderRadius:8, cursor:'pointer', fontSize:13 }}>
            {loading ? 'Cargando…' : `Cargar más (${items.length}/${total})`}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Reportes de asistencia (GeoVictoria) — período + Excel ────────────────────
function ReportesAsistencia() {
  const hoy = hoyChile();
  const diasAtras = (n) => { const d = new Date(hoy + 'T12:00:00'); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
  const [desde, setDesde]   = useState(diasAtras(30));
  const [hasta, setHasta]   = useState(hoy);
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError]   = useState('');

  const consultar = async () => {
    setLoading(true); setError('');
    try { const r = await api.get('/geovictoria/reportes/resumen', { params: { desde, hasta } }); setData(r.data); }
    catch (e) { setError(e.response?.data?.error || 'No se pudo cargar'); setData(null); }
    finally { setLoading(false); }
  };
  const exportar = async () => {
    setExporting(true); setError('');
    try {
      const r = await api.get('/geovictoria/reportes/export', { params: { desde, hasta }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `asistencia_${desde}_a_${hasta}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { setError('No se pudo exportar'); }
    finally { setExporting(false); }
  };

  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#8A9994',
               textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #1C2922', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', color: '#E6ECE9', borderBottom: '1px solid #141A17', fontSize: 13, whiteSpace: 'nowrap' };
  const inp = { padding: '7px 10px', border: '1.5px solid #2E4A40', borderRadius: 8, fontSize: 13, background: '#0D1311', color: '#F4F7F5' };
  const minToHHMM = (m) => `${String(Math.floor((m || 0) / 60)).padStart(2, '0')}:${String((m || 0) % 60).padStart(2, '0')}`;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: '#8A9994' }}>Desde<br /><input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={inp} /></label>
        <label style={{ fontSize: 12, color: '#8A9994' }}>Hasta<br /><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={inp} /></label>
        <button onClick={consultar} disabled={loading}
          style={{ padding: '8px 16px', background: '#0f6e56', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: loading ? 'wait' : 'pointer', fontSize: 13 }}>
          {loading ? 'Consultando…' : 'Consultar'}
        </button>
        <button onClick={exportar} disabled={exporting}
          style={{ padding: '8px 14px', background: 'transparent', color: '#4CAF50', border: '1.5px solid rgba(79,175,80,0.4)', borderRadius: 8, fontWeight: 600, cursor: exporting ? 'wait' : 'pointer', fontSize: 13 }}>
          {exporting ? 'Generando…' : '⬇ Excel'}
        </button>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6B7C74' }}>
        Resumen de asistencia por técnico en el período (según turnos de GeoVictoria): días con turno, presentes, atrasos y ausencias. El Excel trae además el detalle día a día.
      </p>

      {error && <p style={{ color: '#EF4444', fontSize: 13 }}>{error}</p>}
      {loading && !data ? <p style={{ color: '#6B7C74', padding: 20 }}>Cargando…</p> : data && (
        data.resumen.length === 0 ? <p style={{ color: '#6B7C74', padding: 20 }}>Sin datos en el período.</p> : (
        <div style={{ overflowX: 'auto', border: '1px solid #1C2922', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#0D1311' }}>
              <th style={th}>Técnico</th><th style={{ ...th, textAlign: 'center' }}>Días turno</th>
              <th style={{ ...th, textAlign: 'center' }}>Presentes</th><th style={{ ...th, textAlign: 'center' }}>Atrasados</th>
              <th style={{ ...th, textAlign: 'center' }}>Atraso acum.</th><th style={{ ...th, textAlign: 'center' }}>Sin marca</th>
              <th style={{ ...th, textAlign: 'center' }}>Ausentes</th><th style={{ ...th, textAlign: 'center' }}>Permisos</th>
            </tr></thead>
            <tbody>
              {data.resumen.map((r, i) => (
                <tr key={r.identifier || i}>
                  <td style={td}>{r.tecnico || '—'}</td>
                  <td style={{ ...td, textAlign: 'center', color: '#8A9994' }}>{r.dias_turno}</td>
                  <td style={{ ...td, textAlign: 'center', color: '#4CAF50' }}>{r.presentes}</td>
                  <td style={{ ...td, textAlign: 'center', color: r.atrasados ? '#F59E0B' : '#8A9994' }}>{r.atrasados}</td>
                  <td style={{ ...td, textAlign: 'center', color: r.atraso_min ? '#F59E0B' : '#8A9994' }}>{minToHHMM(r.atraso_min)}</td>
                  <td style={{ ...td, textAlign: 'center', color: r.sin_marca ? '#E5533D' : '#8A9994' }}>{r.sin_marca}</td>
                  <td style={{ ...td, textAlign: 'center', color: r.ausentes ? '#EF4444' : '#8A9994' }}>{r.ausentes}</td>
                  <td style={{ ...td, textAlign: 'center', color: '#38BDF8' }}>{r.permisos}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      <div style={{ height: 16 }} />
    </div>
  );
}

// ── Jornada de hoy (GeoVictoria) — supervisión diaria ─────────────────────────
const JORNADA_CFG = {
  presente:  { label: 'Presente',  bg: '#12382D', color: '#4CAF50' },
  atrasado:  { label: 'Atrasado',  bg: '#1C1200', color: '#F59E0B' },
  sin_marca: { label: 'Sin marca', bg: '#2D1410', color: '#E5533D' },
  ausente:   { label: 'Ausente',   bg: '#2D0F0F', color: '#EF4444' },
  permiso:   { label: 'Permiso',   bg: '#0D1E2E', color: '#38BDF8' },
};
function JornadaHoy() {
  const [fecha, setFecha]     = useState(hoyChile());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const cargar = useCallback(async (f) => {
    setLoading(true); setError('');
    try { const r = await api.get('/geovictoria/jornada', { params: { fecha: f } }); setData(r.data); }
    catch (e) { setError(e.response?.data?.error || 'No se pudo cargar'); setData(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(fecha); }, [fecha, cargar]);

  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#8A9994',
               textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #1C2922', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', color: '#E6ECE9', borderBottom: '1px solid #141A17', fontSize: 13, whiteSpace: 'nowrap' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: '#8A9994' }}>Fecha<br />
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
            style={{ padding: '7px 10px', border: '1.5px solid #2E4A40', borderRadius: 8, fontSize: 13, background: '#0D1311', color: '#F4F7F5' }} /></label>
        <button onClick={() => cargar(fecha)} disabled={loading}
          style={{ padding: '8px 14px', background: '#0f6e56', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: loading ? 'wait' : 'pointer', fontSize: 13 }}>
          {loading ? 'Cargando…' : '↻ Actualizar'}
        </button>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6B7C74' }}>
        Técnicos con turno ese día según GeoVictoria, con su entrada/salida, atraso y estado. Se actualiza con las marcas reales.
      </p>

      {error && <p style={{ color: '#EF4444', fontSize: 13 }}>{error}</p>}
      {loading && !data ? <p style={{ color: '#6B7C74', padding: 20 }}>Cargando…</p> : data && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {Object.entries(JORNADA_CFG).map(([k, cfg]) => (
              <span key={k} style={{ background: cfg.bg, color: cfg.color, borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600 }}>
                {cfg.label}: <b>{data.resumen[k] || 0}</b>
              </span>
            ))}
          </div>
          {data.jornada.length === 0 ? <p style={{ color: '#6B7C74', padding: 20 }}>Nadie con turno ese día (o GeoVictoria no devolvió turnos).</p> : (
          <div style={{ overflowX: 'auto', border: '1px solid #1C2922', borderRadius: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#0D1311' }}>
                <th style={th}>Técnico</th><th style={th}>Turno</th><th style={th}>Entrada</th><th style={th}>Salida</th>
                <th style={th}>Atraso</th><th style={th}>Horas</th><th style={th}>Estado</th>
              </tr></thead>
              <tbody>
                {data.jornada.map((j, i) => {
                  const cfg = JORNADA_CFG[j.estado] || JORNADA_CFG.sin_marca;
                  return (
                    <tr key={j.tecnico_id || j.identifier || i}>
                      <td style={td}>{j.tecnico || j.nombre_gv || '—'}</td>
                      <td style={{ ...td, color: '#8A9994' }}>{j.turno || '—'}</td>
                      <td style={td}>{j.entrada || '—'}</td>
                      <td style={td}>{j.salida || '—'}</td>
                      <td style={{ ...td, color: j.atraso ? '#F59E0B' : '#6B7C74' }}>{j.atraso || '—'}</td>
                      <td style={{ ...td, color: '#8A9994' }}>{j.trabajado || '—'}</td>
                      <td style={td}><span style={{ background: cfg.bg, color: cfg.color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{cfg.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
          <div style={{ height: 16 }} />
        </>
      )}
    </div>
  );
}

// ── Cruce de marcas GeoVictoria con mantenciones programadas ──────────────────
const CRUCE_CFG = {
  con_marca:   { label: 'Con marca',          bg: '#12382D', color: '#4CAF50' },
  sin_marca:   { label: 'Sin marca',          bg: '#2D0F0F', color: '#EF4444' },
  marca_lejos: { label: 'Lejos del edificio', bg: '#2D1410', color: '#E5533D' },
  sin_gps:     { label: 'Sin GPS',            bg: '#0D1E2E', color: '#38BDF8' },
  sin_tecnico: { label: 'Sin técnico',        bg: '#1A1E1C', color: '#8A9994' },
};
function horaCL(ts) {
  if (!ts) return '—';
  try {
    return new Intl.DateTimeFormat('es-CL', { timeZone: 'America/Santiago', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(ts));
  } catch { return '—'; }
}
function CruceVisitas() {
  const hoy = hoyChile();
  const diasAtras = (n) => { const d = new Date(hoy + 'T12:00:00'); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
  const [desde, setDesde]   = useState(diasAtras(14));
  const [hasta, setHasta]   = useState(hoy);
  const [umbral, setUmbral] = useState(300);
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const consultar = async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/geovictoria/cruce', { params: { desde, hasta, umbral } });
      setData(r.data);
    } catch (e) { setError(e.response?.data?.error || 'No se pudo cargar el cruce'); setData(null); }
    finally { setLoading(false); }
  };

  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#8A9994',
               textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #1C2922', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', color: '#E6ECE9', borderBottom: '1px solid #141A17', fontSize: 13, whiteSpace: 'nowrap' };
  const inp = { padding: '7px 10px', border: '1.5px solid #2E4A40', borderRadius: 8, fontSize: 13, background: '#0D1311', color: '#F4F7F5' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
        <label style={{ fontSize: 12, color: '#8A9994' }}>Desde<br /><input type="date" value={desde} onChange={e => setDesde(e.target.value)} style={inp} /></label>
        <label style={{ fontSize: 12, color: '#8A9994' }}>Hasta<br /><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} style={inp} /></label>
        <label style={{ fontSize: 12, color: '#8A9994' }}>Umbral (m)<br /><input type="number" min="10" step="50" value={umbral} onChange={e => setUmbral(e.target.value)} style={{ ...inp, width: 90 }} /></label>
        <button onClick={consultar} disabled={loading}
          style={{ padding: '8px 16px', background: '#0f6e56', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: loading ? 'wait' : 'pointer', fontSize: 13 }}>
          {loading ? 'Consultando…' : 'Consultar'}
        </button>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6B7C74' }}>
        Cruza las mantenciones programadas con las marcas del técnico ese día: entrada/salida, distancia GPS de la marca más cercana al edificio y estado por visita.
      </p>

      {error && <p style={{ color: '#EF4444', fontSize: 13 }}>{error}</p>}
      {loading && !data ? <p style={{ color: '#6B7C74', padding: 20 }}>Cargando…</p> : data && (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {Object.entries(CRUCE_CFG).map(([k, cfg]) => (
              <span key={k} style={{ background: cfg.bg, color: cfg.color, borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600 }}>
                {cfg.label}: <b>{data.resumen[k] || 0}</b>
              </span>
            ))}
          </div>
          {data.visitas.length === 0 ? <p style={{ color: '#6B7C74', padding: 20 }}>Sin mantenciones programadas en el rango.</p> : (
          <div style={{ overflowX: 'auto', border: '1px solid #1C2922', borderRadius: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#0D1311' }}>
                <th style={th}>Fecha</th><th style={th}>Comunidad</th><th style={th}>Asc.</th><th style={th}>Técnico</th>
                <th style={th}>Entrada</th><th style={th}>Salida</th><th style={{ ...th, textAlign: 'right' }}>Dist. (m)</th><th style={th}>Estado</th>
              </tr></thead>
              <tbody>
                {data.visitas.map(v => {
                  const cfg = CRUCE_CFG[v.estado] || CRUCE_CFG.sin_tecnico;
                  return (
                    <tr key={v.id}>
                      <td style={td}>{fechaCL(v.fecha)}</td>
                      <td style={{ ...td, whiteSpace: 'normal' }}>{v.comunidad}</td>
                      <td style={td}>{v.n_ascensor}{v.torre ? ` T${v.torre}` : ''}</td>
                      <td style={td}>{v.tecnico || '—'}</td>
                      <td style={td}>{horaCL(v.entrada)}</td>
                      <td style={td}>{horaCL(v.salida)}</td>
                      <td style={{ ...td, textAlign: 'right', color: v.estado === 'marca_lejos' ? '#E5533D' : '#8A9994' }}>
                        {v.dist_min != null ? v.dist_min : (v.edificio_con_coords ? '—' : 's/coord')}
                      </td>
                      <td style={td}><span style={{ background: cfg.bg, color: cfg.color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{cfg.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
          <div style={{ height: 16 }} />
        </>
      )}
    </div>
  );
}

// ── Recordatorios de marca (asistencia) — estado del día ──────────────────────
function RecordatoriosMarca() {
  const [fecha, setFecha]     = useState(hoyChile());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [motivoInputs, setMot] = useState({});
  const [diag, setDiag]       = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const cargar = useCallback(async (f) => {
    setLoading(true); setError('');
    try { const r = await api.get('/geovictoria/recordatorios', { params: { fecha: f } }); setData(r.data || []); }
    catch (e) { setError(e.response?.data?.error || 'No se pudo cargar'); setData(null); }
    finally { setLoading(false); }
  }, []);

  const verDiagnostico = async () => {
    setDiagLoading(true);
    try { const r = await api.get('/geovictoria/attendance-raw', { params: { fecha, limit: 3 } }); setDiag(r.data); }
    catch (e) { setDiag({ error: e.response?.data?.error || 'Error al consultar GeoVictoria' }); }
    finally { setDiagLoading(false); }
  };
  useEffect(() => { cargar(fecha); }, [fecha, cargar]);

  const guardarMotivo = async (id) => {
    const motivo = (motivoInputs[id] || '').trim();
    if (!motivo) return;
    try {
      await api.post(`/geovictoria/recordatorios/${id}/motivo`, { motivo });
      setMot(s => { const n = { ...s }; delete n[id]; return n; });
      cargar(fecha);
    } catch (e) { setError(e.response?.data?.error || 'No se pudo guardar el motivo'); }
  };

  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#8A9994',
               textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #1C2922', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', color: '#E6ECE9', borderBottom: '1px solid #141A17', fontSize: 13 };
  const estadoBadge = (r) => {
    if (r.marcado)       return { label: 'Marcó', bg: '#12382D', color: '#4CAF50' };
    if (r.admin_avisado) return { label: 'Avisado a admin', bg: '#2D0F0F', color: '#EF4444' };
    return { label: 'Recordando', bg: '#1C1200', color: '#F59E0B' };
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: '#8A9994' }}>Fecha<br />
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)}
            style={{ padding: '7px 10px', border: '1.5px solid #2E4A40', borderRadius: 8, fontSize: 13, background: '#0D1311', color: '#F4F7F5' }} /></label>
        <button onClick={() => cargar(fecha)} disabled={loading}
          style={{ padding: '8px 14px', background: '#0f6e56', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: loading ? 'wait' : 'pointer', fontSize: 13 }}>
          {loading ? 'Cargando…' : '↻ Actualizar'}
        </button>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6B7C74' }}>
        Recordatorios de marca enviados hoy (Lun–Vie hábiles). Entrada 08–13 h, salida 18–22 h, cada 30 min.
        Tras 3 recordatorios sin marcar se avisa al admin para registrar el motivo.
      </p>

      {error && <p style={{ color: '#EF4444', fontSize: 13 }}>{error}</p>}
      {loading && !data ? <p style={{ color: '#6B7C74', padding: 20 }}>Cargando…</p> :
        data && data.length === 0 ? <p style={{ color: '#6B7C74', padding: 20 }}>Sin recordatorios en esa fecha.</p> :
        data && (
        <div style={{ overflowX: 'auto', border: '1px solid #1C2922', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#0D1311' }}>
              <th style={th}>Técnico</th><th style={th}>Marca</th><th style={{ ...th, textAlign: 'center' }}>Avisos</th>
              <th style={th}>Estado</th><th style={{ ...th, minWidth: 220 }}>Motivo</th>
            </tr></thead>
            <tbody>
              {data.map(r => {
                const b = estadoBadge(r);
                return (
                  <tr key={r.id}>
                    <td style={td}>{r.tecnico}</td>
                    <td style={td}>{r.tipo === 'ingreso' ? 'Entrada' : 'Salida'}</td>
                    <td style={{ ...td, textAlign: 'center' }}>{r.enviados}</td>
                    <td style={td}><span style={{ background: b.bg, color: b.color, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{b.label}</span></td>
                    <td style={td}>
                      {r.motivo ? (
                        <span>{r.motivo} {r.motivo_por_nombre && <span style={{ color: '#6B7C74', fontSize: 11 }}>· {r.motivo_por_nombre}</span>}</span>
                      ) : r.admin_avisado && !r.marcado ? (
                        <span style={{ display: 'flex', gap: 6 }}>
                          <input value={motivoInputs[r.id] || ''} onChange={e => setMot(s => ({ ...s, [r.id]: e.target.value }))}
                            placeholder="Motivo de la ausencia de marca…"
                            style={{ flex: 1, padding: '6px 10px', border: '1.5px solid #2E4A40', borderRadius: 8, fontSize: 13, background: '#0D1311', color: '#F4F7F5' }} />
                          <button onClick={() => guardarMotivo(r.id)} disabled={!(motivoInputs[r.id] || '').trim()}
                            style={{ padding: '6px 12px', background: '#0f6e56', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Guardar</button>
                        </span>
                      ) : <span style={{ color: '#6B7C74' }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Diagnóstico: ver lo que devuelve GeoVictoria para validar los turnos */}
      <div style={{ marginTop: 22, borderTop: '1px solid #1C2922', paddingTop: 14 }}>
        <button onClick={verDiagnostico} disabled={diagLoading}
          style={{ padding: '8px 14px', background: 'transparent', color: '#A855F7', border: '1px solid #3B2A5A', borderRadius: 8, fontWeight: 600, cursor: diagLoading ? 'wait' : 'pointer', fontSize: 13 }}>
          {diagLoading ? 'Consultando GeoVictoria…' : '🔍 Diagnóstico GeoVictoria (turnos del día seleccionado)'}
        </button>
        <p style={{ fontSize: 12, color: '#6B7C74', margin: '6px 0 0' }}>
          Muestra el libro de asistencia crudo de GeoVictoria para 3 técnicos y qué RUTs detecta <b>con turno</b> ese día. Sirve para validar que los recordatorios se gatillen bien.
        </p>
        {diag && (
          <>
            <p style={{ fontSize: 13, color: '#C9D3CE', marginTop: 10 }}>
              Con turno detectado: <b style={{ color: '#4CAF50' }}>{(diag.con_turno || []).join(', ') || '— (ninguno)'}</b>
            </p>
            <pre style={{ background: '#0D1311', border: '1px solid #1C2922', borderRadius: 8, padding: 12, fontSize: 11, color: '#C9D3CE', overflow: 'auto', maxHeight: 340, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(diag, null, 2)}
            </pre>
          </>
        )}
      </div>
      <div style={{ height: 16 }} />
    </div>
  );
}

// ── Permisos de técnicos (vacaciones/licencia) — excluyen de recordatorios ─────
const TIPO_PERMISO = [
  { v: 'vacaciones', l: 'Vacaciones' },
  { v: 'licencia',   l: 'Licencia médica' },
  { v: 'permiso',    l: 'Permiso' },
  { v: 'otro',       l: 'Otro' },
];
function PermisosTecnicos() {
  const [tecnicos, setTecnicos] = useState([]);
  const [permisos, setPermisos] = useState(null);
  const [error, setError]   = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ tecnico_id: '', tipo: 'vacaciones', fecha_desde: hoyChile(), fecha_hasta: hoyChile(), nota: '' });

  const cargar = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([
        api.get('/geovictoria/tecnicos'),
        api.get('/geovictoria/permisos'),
      ]);
      setTecnicos(t.data || []); setPermisos(p.data || []);
    } catch (e) { setError(e.response?.data?.error || 'No se pudo cargar'); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const crear = async () => {
    if (!form.tecnico_id) { setError('Selecciona un técnico'); return; }
    setSaving(true); setError('');
    try {
      await api.post('/geovictoria/permisos', {
        tecnico_id: Number(form.tecnico_id), tipo: form.tipo,
        fecha_desde: form.fecha_desde, fecha_hasta: form.fecha_hasta || form.fecha_desde,
        nota: form.nota,
      });
      setForm(f => ({ ...f, nota: '' }));
      cargar();
    } catch (e) { setError(e.response?.data?.error || 'No se pudo guardar'); }
    finally { setSaving(false); }
  };
  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar este permiso?')) return;
    try { await api.delete(`/geovictoria/permisos/${id}`); cargar(); }
    catch (e) { setError(e.response?.data?.error || 'No se pudo eliminar'); }
  };

  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#8A9994',
               textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #1C2922', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', color: '#E6ECE9', borderBottom: '1px solid #141A17', fontSize: 13 };
  const inp = { padding: '7px 10px', border: '1.5px solid #2E4A40', borderRadius: 8, fontSize: 13, background: '#0D1311', color: '#F4F7F5' };

  return (
    <div>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6B7C74' }}>
        Registra permisos (vacaciones, licencia) por un día o un rango. Durante esos días el técnico <b>no recibe recordatorios</b> de marca.
      </p>
      {/* Formulario */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', background: '#0D1311', border: '1px solid #1C2922', borderRadius: 12, padding: '12px 14px', marginBottom: 16 }}>
        <label style={{ fontSize: 12, color: '#8A9994' }}>Técnico<br />
          <select value={form.tecnico_id} onChange={e => setForm(f => ({ ...f, tecnico_id: e.target.value }))} style={{ ...inp, minWidth: 180 }}>
            <option value="">— Selecciona —</option>
            {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select></label>
        <label style={{ fontSize: 12, color: '#8A9994' }}>Tipo<br />
          <select value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} style={inp}>
            {TIPO_PERMISO.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select></label>
        <label style={{ fontSize: 12, color: '#8A9994' }}>Desde<br /><input type="date" value={form.fecha_desde} onChange={e => setForm(f => ({ ...f, fecha_desde: e.target.value }))} style={inp} /></label>
        <label style={{ fontSize: 12, color: '#8A9994' }}>Hasta<br /><input type="date" value={form.fecha_hasta} onChange={e => setForm(f => ({ ...f, fecha_hasta: e.target.value }))} style={inp} /></label>
        <label style={{ fontSize: 12, color: '#8A9994', flex: 1, minWidth: 160 }}>Nota (opcional)<br /><input value={form.nota} onChange={e => setForm(f => ({ ...f, nota: e.target.value }))} placeholder="Detalle…" style={{ ...inp, width: '100%' }} /></label>
        <button onClick={crear} disabled={saving}
          style={{ padding: '8px 16px', background: '#0f6e56', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'wait' : 'pointer', fontSize: 13 }}>
          {saving ? 'Guardando…' : 'Agregar permiso'}
        </button>
      </div>
      {error && <p style={{ color: '#EF4444', fontSize: 13 }}>{error}</p>}

      <h3 style={{ fontSize: 14, color: '#F4F7F5', margin: '0 0 8px' }}>Permisos vigentes y futuros</h3>
      {!permisos ? <p style={{ color: '#6B7C74', padding: 20 }}>Cargando…</p> :
        permisos.length === 0 ? <p style={{ color: '#6B7C74', fontSize: 13 }}>Sin permisos registrados.</p> : (
        <div style={{ overflowX: 'auto', border: '1px solid #1C2922', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#0D1311' }}>
              <th style={th}>Técnico</th><th style={th}>Tipo</th><th style={th}>Desde</th><th style={th}>Hasta</th><th style={th}>Nota</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {permisos.map(p => (
                <tr key={p.id}>
                  <td style={td}>{p.tecnico}</td>
                  <td style={td}>{(TIPO_PERMISO.find(x => x.v === p.tipo) || {}).l || p.tipo}</td>
                  <td style={td}>{fechaCL(p.fecha_desde)}</td>
                  <td style={td}>{fechaCL(p.fecha_hasta)}</td>
                  <td style={{ ...td, color: '#8A9994' }}>{p.nota || '—'}</td>
                  <td style={td}>
                    <button onClick={() => eliminar(p.id)}
                      style={{ padding: '4px 10px', background: '#2D0F0F', color: '#EF4444', border: '1px solid #3D1515', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ height: 16 }} />
    </div>
  );
}

// ── Tab Asistencia (GeoVictoria) — vínculo técnico ↔ usuario GV ────────────────
function TabAsistencia() {
  const [config, setConfig]   = useState(null);   // { configurado }
  const [data, setData]       = useState(null);   // respuesta de /match
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [rutInputs, setRutInputs] = useState({}); // { tecnicoId: rut }
  const [conteo, setConteo]       = useState(null);
  const [sincronizando, setSync]  = useState(false);
  const [syncMsg, setSyncMsg]     = useState('');
  const [subvista, setSubvista]   = useState('match'); // 'match' | 'cruce'

  const diasAtras = (n) => {
    const d = new Date(); d.setDate(d.getDate() - n);
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Santiago' }).format(d);
  };

  const cargarConfig = useCallback(async () => {
    try { const r = await api.get('/geovictoria/config'); setConfig(r.data); }
    catch { setConfig({ configurado: false }); }
  }, []);

  const cargarMatch = useCallback(async () => {
    setLoading(true); setError('');
    try { const r = await api.get('/geovictoria/match'); setData(r.data); }
    catch (e) { setError(e.response?.data?.error || 'No se pudo cargar el match'); setData(null); }
    finally { setLoading(false); }
  }, []);

  const cargarConteo = useCallback(async () => {
    try { const r = await api.get('/geovictoria/conteo'); setConteo(r.data || []); }
    catch { /* noop */ }
  }, []);

  useEffect(() => { cargarConfig(); }, [cargarConfig]);
  useEffect(() => { if (config?.configurado) { cargarMatch(); cargarConteo(); } }, [config, cargarMatch, cargarConteo]);

  const guardarRut = async (tecnicoId) => {
    const rut = rutInputs[tecnicoId] ?? '';
    setError('');
    try {
      await api.put(`/geovictoria/tecnicos/${tecnicoId}/rut`, { rut });
      setRutInputs(s => { const n = { ...s }; delete n[tecnicoId]; return n; });
      cargarMatch();
    } catch (e) { setError(e.response?.data?.error || 'No se pudo guardar el RUT'); }
  };

  const sincronizar7 = async () => {
    setSync(true); setSyncMsg(''); setError('');
    try {
      const r = await api.post('/geovictoria/backfill', { desde: diasAtras(7), hasta: hoyChile() });
      setSyncMsg(`Sincronización lista: ${r.data.marcas} marca(s) de los últimos 7 días.`);
      cargarConteo(); cargarMatch();
    } catch (e) { setError(e.response?.data?.error || 'No se pudo sincronizar'); }
    finally { setSync(false); }
  };

  const Card = ({ color, n, label }) => (
    <div style={{ background: '#0D1311', border: '1px solid #1C2922', borderRadius: 10, padding: '12px 16px', minWidth: 120 }}>
      <div style={{ fontSize: 24, fontWeight: 800, color }}>{n ?? '—'}</div>
      <div style={{ fontSize: 12, color: '#8A9994' }}>{label}</div>
    </div>
  );
  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#8A9994',
               textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #1C2922', whiteSpace: 'nowrap' };
  const td = { padding: '8px 10px', color: '#E6ECE9', borderBottom: '1px solid #141A17', fontSize: 13 };

  if (config && !config.configurado) {
    return (
      <div style={{ background: '#1C1200', border: '1px solid #3D2B00', borderRadius: 12, padding: 20, color: '#F59E0B', fontSize: 14 }}>
        <b>GeoVictoria no está configurado.</b>
        <p style={{ margin: '8px 0 0', color: '#C9D3CE', fontSize: 13, lineHeight: 1.6 }}>
          Carga en Railway las variables <code>GEOVICTORIA_BASE_URL</code>, <code>GEOVICTORIA_API_KEY</code> y
          <code> GEOVICTORIA_API_SECRET</code>. Luego recarga esta pantalla para ver el match de técnicos.
        </p>
      </div>
    );
  }

  const r = data?.resumen;
  const subTab = (id, lbl) => (
    <button onClick={() => setSubvista(id)}
      style={{ padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid #2E4A40',
               background: subvista === id ? '#0f6e56' : 'transparent', color: subvista === id ? '#fff' : '#8A9994' }}>{lbl}</button>
  );
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {subTab('match', 'Vínculo técnicos')}
        {subTab('jornada', 'Jornada de hoy')}
        {subTab('cruce', 'Cruce con visitas')}
        {subTab('recordatorios', 'Recordatorios')}
        {subTab('permisos', 'Permisos')}
        {subTab('reportes', 'Reportes')}
      </div>
      {subvista === 'jornada' ? <JornadaHoy /> :
       subvista === 'cruce' ? <CruceVisitas /> :
       subvista === 'recordatorios' ? <RecordatoriosMarca /> :
       subvista === 'permisos' ? <PermisosTecnicos /> :
       subvista === 'reportes' ? <ReportesAsistencia /> : (
      <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: 13, color: '#8A9994', flex: 1, minWidth: 260 }}>
          Vínculo de técnicos de Solvert con usuarios de GeoVictoria (por RUT). Asigna el RUT a los técnicos
          que falten para poder cruzar sus marcas de asistencia.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={sincronizar7} disabled={sincronizando}
            style={{ padding: '8px 14px', background: 'transparent', color: '#38BDF8', border: '1px solid #1E3A5F', borderRadius: 8, fontWeight: 600, cursor: sincronizando ? 'wait' : 'pointer', fontSize: 13 }}>
            {sincronizando ? 'Sincronizando…' : '⟳ Sincronizar últimos 7 días'}
          </button>
          <button onClick={cargarMatch} disabled={loading}
            style={{ padding: '8px 14px', background: '#0f6e56', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, cursor: loading ? 'wait' : 'pointer', fontSize: 13 }}>
            {loading ? 'Cargando…' : '↻ Actualizar'}
          </button>
        </div>
      </div>

      {error && <p style={{ color: '#EF4444', fontSize: 13 }}>{error}</p>}
      {syncMsg && <p style={{ color: '#4CAF50', fontSize: 13 }}>{syncMsg}</p>}
      {loading && !data ? <p style={{ color: '#6B7C74', padding: 20 }}>Cargando…</p> : data && (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
            <Card color="#4CAF50" n={r.emparejados}        label="Emparejados" />
            <Card color="#F59E0B" n={r.tecnicos_sin_rut}    label="Técnicos sin RUT" />
            <Card color="#E5533D" n={r.tecnicos_sin_match}  label="RUT sin usuario GV" />
            <Card color="#38BDF8" n={r.gv_sin_tecnico}      label="Usuarios GV sin técnico" />
          </div>

          {/* Conteo de marcas por técnico (últimos 7 días) */}
          {conteo && conteo.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 14, color: '#F4F7F5', margin: '0 0 8px' }}>Marcas por técnico — últimos 7 días</h3>
              <div style={{ overflowX: 'auto', border: '1px solid #1C2922', borderRadius: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#0D1311' }}>
                    <th style={th}>Técnico</th><th style={th}>RUT (GV)</th>
                    <th style={{ ...th, textAlign: 'right' }}>Marcas</th><th style={th}>Primera</th><th style={th}>Última</th>
                  </tr></thead>
                  <tbody>
                    {conteo.map((c, idx) => (
                      <tr key={idx}>
                        <td style={td}>{c.tecnico_nombre || <span style={{ color: '#E5533D' }}>Sin técnico</span>}</td>
                        <td style={{ ...td, fontFamily: 'monospace', color: '#8A9994' }}>{c.user_identifier || '—'}</td>
                        <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: '#4CAF50' }}>{c.marcas}</td>
                        <td style={{ ...td, color: '#8A9994', whiteSpace: 'nowrap' }}>{c.primera ? fechaCL(c.primera) : '—'}</td>
                        <td style={{ ...td, color: '#8A9994', whiteSpace: 'nowrap' }}>{c.ultima ? fechaCL(c.ultima) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Técnicos sin RUT → asignar */}
          <h3 style={{ fontSize: 14, color: '#F4F7F5', margin: '0 0 8px' }}>Técnicos sin RUT ({data.tecnicos_sin_rut.length})</h3>
          {data.tecnicos_sin_rut.length === 0 ? <p style={{ color: '#6B7C74', fontSize: 13 }}>Todos los técnicos tienen RUT. 🎉</p> : (
            <div style={{ overflowX: 'auto', border: '1px solid #1C2922', borderRadius: 12, marginBottom: 20 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#0D1311' }}><th style={th}>Técnico</th><th style={th}>Email</th><th style={th}>RUT</th><th style={th}></th></tr></thead>
                <tbody>
                  {data.tecnicos_sin_rut.map(t => (
                    <tr key={t.id}>
                      <td style={td}>{t.nombre}</td>
                      <td style={{ ...td, color: '#8A9994' }}>{t.email || '—'}</td>
                      <td style={td}>
                        <input value={rutInputs[t.id] ?? ''} onChange={e => setRutInputs(s => ({ ...s, [t.id]: e.target.value }))}
                          placeholder="12345678-9"
                          style={{ padding: '6px 10px', border: '1.5px solid #2E4A40', borderRadius: 8, fontSize: 13, background: '#0D1311', color: '#F4F7F5', width: 140 }} />
                      </td>
                      <td style={td}>
                        <button onClick={() => guardarRut(t.id)} disabled={!(rutInputs[t.id] || '').trim()}
                          style={{ padding: '6px 12px', background: '#0f6e56', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Guardar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* RUT sin match en GV */}
          {data.tecnicos_sin_match.length > 0 && (
            <>
              <h3 style={{ fontSize: 14, color: '#F4F7F5', margin: '0 0 8px' }}>Técnicos con RUT que no existe en GeoVictoria ({data.tecnicos_sin_match.length})</h3>
              <div style={{ background: '#0D1311', border: '1px solid #1C2922', borderRadius: 12, padding: '6px 14px', marginBottom: 20 }}>
                {data.tecnicos_sin_match.map(t => (
                  <div key={t.id} style={{ padding: '7px 0', borderBottom: '1px solid #141A17', fontSize: 13, color: '#E6ECE9', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <span>{t.nombre}</span><span style={{ color: '#E5533D', fontFamily: 'monospace' }}>{t.rut}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Usuarios GV sin técnico */}
          <h3 style={{ fontSize: 14, color: '#F4F7F5', margin: '0 0 8px' }}>Usuarios de GeoVictoria sin técnico en Solvert ({data.gv_sin_tecnico.length})</h3>
          {data.gv_sin_tecnico.length === 0 ? <p style={{ color: '#6B7C74', fontSize: 13 }}>Ninguno.</p> : (
            <div style={{ overflowX: 'auto', border: '1px solid #1C2922', borderRadius: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#0D1311' }}><th style={th}>Nombre (GV)</th><th style={th}>RUT</th><th style={th}>Cargo</th><th style={th}>Activo</th></tr></thead>
                <tbody>
                  {data.gv_sin_tecnico.map(u => (
                    <tr key={u.identifier}>
                      <td style={td}>{u.nombre || '—'}</td>
                      <td style={{ ...td, fontFamily: 'monospace', color: '#8A9994' }}>{u.identifier}</td>
                      <td style={{ ...td, color: '#8A9994' }}>{u.cargo || '—'}</td>
                      <td style={td}>{u.enabled ? <span style={{ color: '#4CAF50' }}>Sí</span> : <span style={{ color: '#6B7C74' }}>No</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ height: 20 }} />
        </>
      )}
      </>
      )}
    </div>
  );
}

// ── Panel Principal ────────────────────────────────────────────────────────────
// ── Tab Inventario ───────────────────────────────────────────────────────────
const CATEGORIAS_INV = ['INSUMO_MANTENIMIENTO', 'REPUESTO', 'EPP', 'HERRAMIENTA', 'OTRO'];
const UNIDADES_INV   = ['unidad', 'par', 'litro', 'ml', 'kg', 'gramo', 'metro', 'rollo', 'caja'];
const INV_VACIO = {
  sku: '', nombre: '', categoria: 'INSUMO_MANTENIMIENTO', unidad_medida: 'unidad',
  costo_unitario: '', stock_minimo: '', stock_critico: '', ubicacion: '',
};
const invCLP  = (n) => '$' + Number(n || 0).toLocaleString('es-CL');
const invCant = (n) => Number(n || 0).toLocaleString('es-CL', { maximumFractionDigits: 2 });
const COLOR_STOCK = { critico: '#d9534f', bajo: '#E0A415', ok: '#4CAF50', proyeccion: '#E0A415' };
const LABEL_ALERTA = { critico: 'Stock crítico', bajo: 'Bajo mínimo', proyeccion: 'Se agota <7 días' };

function BadgeStock({ estado }) {
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
      color: COLOR_STOCK[estado] || '#8A9994',
      background: `${COLOR_STOCK[estado] || '#8A9994'}22`,
      whiteSpace: 'nowrap',
    }}>
      {estado === 'ok' ? 'OK' : (LABEL_ALERTA[estado] || estado)}
    </span>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <label style={{ display:'block', fontSize:13, fontWeight:600, color:'#8A9994', marginBottom:5 }}>{label}</label>}
      <select value={value} onChange={e => onChange(e.target.value)}
        style={{ width:'100%', padding:'10px 12px', border:'1.5px solid #2E4A40', borderRadius:8,
                 fontSize:14, fontFamily:'inherit', background:'#0D1311', color:'#F4F7F5' }}>
        {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    </div>
  );
}

// Pedidos de insumos NO catalogados (texto libre) que hicieron los técnicos.
function InvNoCatalogados({ onCambio }) {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(false);
  const [verTodos, setVerTodos] = useState(false);
  const [error, setError]   = useState('');

  const cargar = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/solicitudes-insumos/no-catalogados', { params: verTodos ? { pendientes: 'false' } : {} });
      setRows(r.data || []);
    } catch (e) { setError(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [verTodos]);
  useEffect(() => { cargar(); }, [cargar]);

  const gestionar = async (id) => {
    try { await api.post(`/solicitudes-insumos/no-catalogados/${id}/gestionar`); cargar(); onCambio && onCambio(); }
    catch (e) { setError(e.response?.data?.error || 'No se pudo marcar'); }
  };

  const th = { padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#8A9994',
               textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #1C2922', whiteSpace: 'nowrap' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <p style={{ margin: 0, fontSize: 13, color: '#8A9994', flex: 1, minWidth: 240 }}>
          Insumos que los técnicos pidieron y <b>no existen en bodega</b>. Consíguelos o créalos en el catálogo, y marca “Gestionado”.
        </p>
        <label style={{ fontSize: 12, color: '#C9D3CE', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={verTodos} onChange={e => setVerTodos(e.target.checked)} /> Ver gestionados
        </label>
      </div>
      {error && <p style={{ color: '#EF4444', fontSize: 13 }}>{error}</p>}
      {loading ? <p style={{ color: '#6B7C74', padding: 16 }}>Cargando…</p>
        : rows.length === 0 ? <p style={{ color: '#6B7C74', padding: 16 }}>No hay pedidos no catalogados pendientes. 🎉</p>
        : (
        <div style={{ overflowX: 'auto', border: '1px solid #1C2922', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#0D1311' }}>
              <th style={th}>Fecha</th>
              <th style={th}>Insumo solicitado</th>
              <th style={{ ...th, textAlign: 'right' }}>Cant.</th>
              <th style={th}>Técnico</th>
              <th style={th}>Comunidad · Equipo</th>
              <th style={th}>Nota</th>
              <th style={{ ...th, textAlign: 'right' }}>Acción</th>
            </tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid #1C2922', opacity: r.gestionado_at ? 0.55 : 1 }}>
                  <td style={{ padding: '8px 12px', color: '#C9D3CE', whiteSpace: 'nowrap' }}>{fechaCL(r.created_at)}</td>
                  <td style={{ padding: '8px 12px', color: '#F4F7F5', fontWeight: 600 }}>{r.nombre_libre}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'right', color: '#C9D3CE' }}>{Number(r.cantidad)}</td>
                  <td style={{ padding: '8px 12px', color: '#C9D3CE' }}>{r.tecnico_nombre || '—'}</td>
                  <td style={{ padding: '8px 12px', color: '#C9D3CE' }}>{r.comunidad || '—'}{r.n_ascensor ? ` · Asc. ${r.n_ascensor}${r.torre ? ` T${r.torre}` : ''}` : ''}</td>
                  <td style={{ padding: '8px 12px', color: '#8A9994', maxWidth: 220, whiteSpace: 'pre-wrap' }}>{r.nota || '—'}</td>
                  <td style={{ padding: '7px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {r.gestionado_at
                      ? <span style={{ fontSize: 12, color: '#4CAF50' }}>✓ {fechaCL(r.gestionado_at)}</span>
                      : <button onClick={() => gestionar(r.id)} style={{ padding: '5px 11px', background: '#0f6e56', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Marcar gestionado</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TabInventario() {
  const [vista, setVista]       = useState('catalogo'); // catalogo | revisar | solicitudes | alertas | reportes | importar
  const [insumos, setInsumos]   = useState([]);
  const [q, setQ]               = useState('');
  const [catFiltro, setCatFiltro] = useState('');
  const [loading, setLoading]   = useState(false);
  const [msg, setMsg]           = useState('');

  const [editando, setEditando] = useState(null);   // objeto insumo (o INV_VACIO) para el modal
  const [movInsumo, setMovInsumo] = useState(null);  // insumo para registrar movimiento
  const [kardexInsumo, setKardexInsumo] = useState(null);

  const cargarInsumos = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/inventario/insumos', { params: { q: q || undefined, categoria: catFiltro || undefined } });
      setInsumos(r.data);
    } catch (e) { setMsg(e.response?.data?.error || 'Error al cargar'); }
    finally { setLoading(false); }
  }, [q, catFiltro]);

  useEffect(() => { if (vista === 'catalogo') cargarInsumos(); }, [vista, cargarInsumos]);

  const [nNoCat, setNNoCat] = useState(0);
  useEffect(() => {
    api.get('/solicitudes-insumos/no-catalogados/count').then(r => setNNoCat(r.data.n || 0)).catch(() => {});
  }, [vista]);

  const SUBTABS = [
    { id: 'catalogo', label: '📦 Catálogo' },
    { id: 'revisar',  label: '🔎 Por revisar' },
    { id: 'solicitudes', label: `📥 Pedidos no catalogados${nNoCat ? ` (${nNoCat})` : ''}` },
    { id: 'alertas',  label: '⚠️ Alertas' },
    { id: 'reportes', label: '📊 Reportes' },
    { id: 'importar', label: '⬆️ Importar' },
  ];

  return (
    <div>
      <h2 style={{ margin: '0 0 4px', color: '#F4F7F5', fontSize: 20 }}>Inventario de bodega</h2>
      <p style={{ margin: '0 0 18px', color: '#8A9994', fontSize: 13 }}>
        Insumos y repuestos. El stock se descuenta con cada retiro y queda registrado en el kardex.
      </p>

      {/* Sub-navegación interna */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:20 }}>
        {SUBTABS.map(s => (
          <button key={s.id} onClick={() => setVista(s.id)} style={{
            padding:'7px 14px', borderRadius:20, cursor:'pointer', fontSize:13, fontWeight:600,
            border:`1.5px solid ${vista === s.id ? '#0f6e56' : 'rgba(255,255,255,0.12)'}`,
            background: vista === s.id ? '#0f6e56' : '#161C19',
            color: vista === s.id ? 'white' : '#C9D3CE', fontFamily:'inherit',
          }}>{s.label}</button>
        ))}
      </div>

      {msg && <div style={{ background:'#12382D', color:'#4CAF50', padding:'10px 14px', borderRadius:8, marginBottom:16, fontSize:13 }}>{msg}</div>}

      {vista === 'catalogo' && (
        <>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16, alignItems:'center' }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre o SKU…"
              style={{ flex:'1 1 240px', padding:'9px 12px', border:'1.5px solid #2E4A40', borderRadius:8,
                       background:'#0D1311', color:'#F4F7F5', fontSize:14 }} />
            <select value={catFiltro} onChange={e => setCatFiltro(e.target.value)}
              style={{ padding:'9px 12px', border:'1.5px solid #2E4A40', borderRadius:8, background:'#0D1311', color:'#F4F7F5', fontSize:14 }}>
              <option value="">Todas las categorías</option>
              {CATEGORIAS_INV.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <Btn onClick={() => setEditando({ ...INV_VACIO })}>+ Nuevo insumo</Btn>
          </div>

          {loading ? <p style={{ color:'#8A9994' }}>Cargando…</p> : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13, minWidth:760 }}>
                <thead>
                  <tr style={{ color:'#8A9994', textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                    <th style={{ padding:'8px 10px' }}>Insumo</th>
                    <th style={{ padding:'8px 10px' }}>Categoría</th>
                    <th style={{ padding:'8px 10px', textAlign:'right' }}>Stock</th>
                    <th style={{ padding:'8px 10px', textAlign:'right' }}>Costo unit.</th>
                    <th style={{ padding:'8px 10px' }}>Estado</th>
                    <th style={{ padding:'8px 10px', textAlign:'right' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {insumos.length === 0 && (
                    <tr><td colSpan={6} style={{ padding:'20px 10px', color:'#6B7C74', textAlign:'center' }}>Sin insumos. Crea uno o importa desde Excel.</td></tr>
                  )}
                  {insumos.map(it => (
                    <tr key={it.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.05)', color:'#E6ECE9' }}>
                      <td style={{ padding:'9px 10px' }}>
                        <div style={{ fontWeight:600 }}>{it.nombre}</div>
                        <div style={{ fontSize:11, color:'#6B7C74' }}>{it.sku || '—'}{it.ubicacion ? ` · ${it.ubicacion}` : ''}</div>
                      </td>
                      <td style={{ padding:'9px 10px', color:'#8A9994' }}>{it.categoria}</td>
                      <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:700,
                                   color: Number(it.stock_actual) < 0 ? '#d9534f' : '#F4F7F5' }}>
                        {invCant(it.stock_actual)} <span style={{ color:'#6B7C74', fontWeight:400 }}>{it.unidad_medida}</span>
                      </td>
                      <td style={{ padding:'9px 10px', textAlign:'right', color:'#8A9994' }}>{invCLP(it.costo_unitario)}</td>
                      <td style={{ padding:'9px 10px' }}><BadgeStock estado={it.estado_stock} /></td>
                      <td style={{ padding:'9px 10px', textAlign:'right', whiteSpace:'nowrap' }}>
                        <Btn small color="#0f6e56" onClick={() => setMovInsumo(it)}>Movimiento</Btn>{' '}
                        <Btn small outline onClick={() => setKardexInsumo(it)}>Kardex</Btn>{' '}
                        <Btn small outline onClick={() => setEditando(it)}>Editar</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {vista === 'revisar'  && <InvPorRevisar />}
      {vista === 'solicitudes' && <InvNoCatalogados onCambio={() => api.get('/solicitudes-insumos/no-catalogados/count').then(r => setNNoCat(r.data.n || 0)).catch(() => {})} />}
      {vista === 'alertas'  && <InvAlertas />}
      {vista === 'reportes' && <InvReportes />}
      {vista === 'importar' && <InvImportar onDone={() => { setVista('catalogo'); }} />}

      {editando && (
        <InvInsumoModal insumo={editando} onClose={() => setEditando(null)}
          onSaved={() => { setEditando(null); cargarInsumos(); }} />
      )}
      {movInsumo && (
        <InvMovimientoModal insumo={movInsumo} onClose={() => setMovInsumo(null)}
          onSaved={() => { setMovInsumo(null); cargarInsumos(); }} />
      )}
      {kardexInsumo && (
        <InvKardexModal insumo={kardexInsumo} onClose={() => setKardexInsumo(null)} />
      )}
    </div>
  );
}

function InvInsumoModal({ insumo, onClose, onSaved }) {
  const esNuevo = !insumo.id;
  const [f, setF] = useState({ ...INV_VACIO, ...insumo });
  const [err, setErr] = useState('');
  const set = (k) => (v) => setF(prev => ({ ...prev, [k]: v }));

  const guardar = async () => {
    if (!f.nombre?.trim()) { setErr('El nombre es requerido'); return; }
    const payload = {
      sku: f.sku, nombre: f.nombre, categoria: f.categoria, unidad_medida: f.unidad_medida,
      costo_unitario: Number(f.costo_unitario) || 0,
      stock_minimo: Number(f.stock_minimo) || 0,
      stock_critico: Number(f.stock_critico) || 0,
      ubicacion: f.ubicacion,
    };
    try {
      if (esNuevo) await api.post('/inventario/insumos', payload);
      else         await api.put(`/inventario/insumos/${insumo.id}`, payload);
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Error al guardar'); }
  };

  return (
    <Modal title={esNuevo ? 'Nuevo insumo' : 'Editar insumo'} onClose={onClose}>
      {err && <div style={{ color:'#d9534f', marginBottom:12, fontSize:13 }}>{err}</div>}
      <Input label="Nombre" value={f.nombre} onChange={set('nombre')} placeholder="Aceite rieles" />
      <Input label="SKU (opcional)" value={f.sku || ''} onChange={set('sku')} placeholder="ACE-001" />
      <Select label="Categoría" value={f.categoria} onChange={set('categoria')} options={CATEGORIAS_INV} />
      <Select label="Unidad de medida" value={f.unidad_medida} onChange={set('unidad_medida')} options={UNIDADES_INV} />
      <Input label="Costo unitario (CLP)" type="number" value={f.costo_unitario} onChange={set('costo_unitario')} />
      <div style={{ display:'flex', gap:12 }}>
        <div style={{ flex:1 }}><Input label="Stock mínimo" type="number" value={f.stock_minimo} onChange={set('stock_minimo')} /></div>
        <div style={{ flex:1 }}><Input label="Stock crítico" type="number" value={f.stock_critico} onChange={set('stock_critico')} /></div>
      </div>
      <Input label="Ubicación en bodega" value={f.ubicacion || ''} onChange={set('ubicacion')} placeholder="Estante A1" />
      {esNuevo && <p style={{ fontSize:12, color:'#8A9994', marginTop:4 }}>El stock inicial se carga después con un movimiento de <b>Entrada</b>.</p>}
      <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
        <Btn outline color="#888" onClick={onClose}>Cancelar</Btn>
        <Btn onClick={guardar}>{esNuevo ? 'Crear' : 'Guardar'}</Btn>
      </div>
    </Modal>
  );
}

function InvMovimientoModal({ insumo, onClose, onSaved }) {
  const [tipo, setTipo]   = useState('entrada');
  const [cantidad, setCantidad] = useState('');
  const [nuevoStock, setNuevoStock] = useState(String(insumo.stock_actual ?? ''));
  const [costo, setCosto] = useState(String(insumo.costo_unitario ?? ''));
  const [motivo, setMotivo] = useState('');
  const [err, setErr] = useState('');

  const guardar = async () => {
    try {
      const payload = { insumo_id: insumo.id, tipo, motivo };
      if (tipo === 'ajuste') payload.nuevo_stock = Number(nuevoStock);
      else {
        payload.cantidad = Number(cantidad);
        if (!(payload.cantidad > 0)) { setErr('La cantidad debe ser mayor a 0'); return; }
        if (tipo === 'entrada' && costo !== '') payload.costo_unitario = Number(costo);
      }
      await api.post('/inventario/movimientos', payload);
      onSaved();
    } catch (e) { setErr(e.response?.data?.error || 'Error al registrar'); }
  };

  return (
    <Modal title={`Movimiento — ${insumo.nombre}`} onClose={onClose}>
      {err && <div style={{ color:'#d9534f', marginBottom:12, fontSize:13 }}>{err}</div>}
      <p style={{ fontSize:13, color:'#8A9994', marginTop:0 }}>
        Stock actual: <b style={{ color:'#F4F7F5' }}>{invCant(insumo.stock_actual)} {insumo.unidad_medida}</b>
      </p>
      <Select label="Tipo de movimiento" value={tipo} onChange={setTipo}
        options={[
          { value:'entrada', label:'Entrada (compra / reposición)' },
          { value:'salida',  label:'Salida (consumo / merma)' },
          { value:'ajuste',  label:'Ajuste (corregir conteo)' },
        ]} />
      {tipo === 'ajuste' ? (
        <Input label="Nuevo stock real (conteo físico)" type="number" value={nuevoStock} onChange={setNuevoStock} />
      ) : (
        <Input label={`Cantidad (${insumo.unidad_medida})`} type="number" value={cantidad} onChange={setCantidad} />
      )}
      {tipo === 'entrada' && <Input label="Costo unitario de esta compra (opcional)" type="number" value={costo} onChange={setCosto} />}
      <Input label="Motivo / nota (opcional)" value={motivo} onChange={setMotivo} />
      <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
        <Btn outline color="#888" onClick={onClose}>Cancelar</Btn>
        <Btn onClick={guardar}>Registrar</Btn>
      </div>
    </Modal>
  );
}

function InvKardexModal({ insumo, onClose }) {
  const [movs, setMovs] = useState(null);
  useEffect(() => {
    api.get(`/inventario/insumos/${insumo.id}/kardex`)
      .then(r => setMovs(r.data)).catch(() => setMovs([]));
  }, [insumo.id]);
  const SIGNO = { entrada:'+', salida:'', ajuste:'' };
  const COLORT = { entrada:'#4CAF50', salida:'#d9534f', ajuste:'#E0A415' };
  return (
    <Modal title={`Kardex — ${insumo.nombre}`} onClose={onClose} maxWidth={640}>
      {movs === null ? <p style={{ color:'#8A9994' }}>Cargando…</p> :
       movs.length === 0 ? <p style={{ color:'#6B7C74' }}>Sin movimientos.</p> : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
            <thead><tr style={{ color:'#8A9994', textAlign:'left' }}>
              <th style={{ padding:'6px 8px' }}>Fecha</th>
              <th style={{ padding:'6px 8px' }}>Tipo</th>
              <th style={{ padding:'6px 8px', textAlign:'right' }}>Cantidad</th>
              <th style={{ padding:'6px 8px' }}>Detalle</th>
            </tr></thead>
            <tbody>
              {movs.map(m => (
                <tr key={m.id} style={{ borderTop:'1px solid rgba(255,255,255,0.06)', color:'#E6ECE9' }}>
                  <td style={{ padding:'6px 8px', color:'#8A9994', whiteSpace:'nowrap' }}>{String(m.created_at).slice(0,10)}</td>
                  <td style={{ padding:'6px 8px', color:COLORT[m.tipo], fontWeight:600 }}>
                    {m.tipo}{m.estado === 'por_revisar' ? ' *' : ''}
                  </td>
                  <td style={{ padding:'6px 8px', textAlign:'right', fontWeight:700, color:COLORT[m.tipo] }}>
                    {SIGNO[m.tipo]}{invCant(m.cantidad)}
                  </td>
                  <td style={{ padding:'6px 8px', color:'#8A9994' }}>
                    {m.tecnico_nombre || '—'}{m.comunidad ? ` · ${m.comunidad}` : ''}{m.motivo ? ` · ${m.motivo}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ fontSize:11, color:'#6B7C74', marginTop:8 }}>* = retiro pendiente de revisión</p>
        </div>
      )}
    </Modal>
  );
}

function InvPorRevisar() {
  const [items, setItems] = useState(null);
  const cargar = useCallback(() => {
    api.get('/inventario/movimientos/por-revisar').then(r => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const confirmar = async (id) => { await api.put(`/inventario/movimientos/${id}/revisar`, { accion:'confirmar' }); cargar(); };
  const corregir  = async (id) => {
    const val = window.prompt('Cantidad real retirada:');
    if (val == null) return;
    await api.put(`/inventario/movimientos/${id}/revisar`, { accion:'corregir', cantidad: Number(val) });
    cargar();
  };

  if (items === null) return <p style={{ color:'#8A9994' }}>Cargando…</p>;
  if (items.length === 0) return <p style={{ color:'#6B7C74' }}>No hay retiros pendientes de revisión. 👍</p>;
  return (
    <div style={{ overflowX:'auto' }}>
      <p style={{ color:'#8A9994', fontSize:13, marginTop:0 }}>Retiros de técnicos ya descontados del stock, pendientes de validar.</p>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13, minWidth:640 }}>
        <thead><tr style={{ color:'#8A9994', textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
          <th style={{ padding:'8px 10px' }}>Fecha</th>
          <th style={{ padding:'8px 10px' }}>Insumo</th>
          <th style={{ padding:'8px 10px', textAlign:'right' }}>Cantidad</th>
          <th style={{ padding:'8px 10px' }}>Técnico / Comunidad</th>
          <th style={{ padding:'8px 10px', textAlign:'right' }}>Acciones</th>
        </tr></thead>
        <tbody>
          {items.map(m => (
            <tr key={m.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.05)', color:'#E6ECE9' }}>
              <td style={{ padding:'9px 10px', color:'#8A9994', whiteSpace:'nowrap' }}>{String(m.created_at).slice(0,10)}</td>
              <td style={{ padding:'9px 10px', fontWeight:600 }}>{m.insumo_nombre}</td>
              <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:700, color:'#d9534f' }}>{invCant(m.cantidad)} {m.unidad_medida}</td>
              <td style={{ padding:'9px 10px', color:'#8A9994' }}>{m.tecnico_nombre || '—'}{m.comunidad ? ` · ${m.comunidad}` : ''}</td>
              <td style={{ padding:'9px 10px', textAlign:'right', whiteSpace:'nowrap' }}>
                <Btn small onClick={() => confirmar(m.id)}>Confirmar</Btn>{' '}
                <Btn small outline onClick={() => corregir(m.id)}>Corregir</Btn>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvAlertas() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get('/inventario/alertas').then(r => setItems(r.data)).catch(() => setItems([])); }, []);
  if (items === null) return <p style={{ color:'#8A9994' }}>Cargando…</p>;
  if (items.length === 0) return <p style={{ color:'#6B7C74' }}>Sin alertas. Todo el stock está sobre el mínimo. ✅</p>;
  return (
    <div style={{ overflowX:'auto' }}>
      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13, minWidth:680 }}>
        <thead><tr style={{ color:'#8A9994', textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
          <th style={{ padding:'8px 10px' }}>Insumo</th>
          <th style={{ padding:'8px 10px', textAlign:'right' }}>Stock</th>
          <th style={{ padding:'8px 10px', textAlign:'right' }}>Consumo/día</th>
          <th style={{ padding:'8px 10px', textAlign:'right' }}>Días restantes</th>
          <th style={{ padding:'8px 10px' }}>Motivo</th>
        </tr></thead>
        <tbody>
          {items.map(it => (
            <tr key={it.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.05)', color:'#E6ECE9' }}>
              <td style={{ padding:'9px 10px', fontWeight:600 }}>{it.nombre}</td>
              <td style={{ padding:'9px 10px', textAlign:'right', color: Number(it.stock_actual) <= 0 ? '#d9534f' : '#F4F7F5' }}>{invCant(it.stock_actual)} {it.unidad_medida}</td>
              <td style={{ padding:'9px 10px', textAlign:'right', color:'#8A9994' }}>{invCant(it.consumo_diario)}</td>
              <td style={{ padding:'9px 10px', textAlign:'right', fontWeight:700, color:'#E0A415' }}>{it.dias_restantes != null ? `${it.dias_restantes} d` : '—'}</td>
              <td style={{ padding:'9px 10px' }}><BadgeStock estado={it.motivo} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvReportes() {
  const hoy = hoyChile(); // YYYY-MM-DD
  const [anio, setAnio] = useState(Number(hoy.slice(0,4)));
  const [mes,  setMes]  = useState(Number(hoy.slice(5,7)));
  const [uso, setUso]   = useState([]);
  const [porCom, setPorCom] = useState([]);
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([
        api.get('/inventario/reportes/uso-mensual', { params: { anio, mes } }),
        api.get('/inventario/reportes/por-comunidad', { params: { anio, mes } }),
      ]);
      setUso(u.data); setPorCom(c.data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [anio, mes]);
  useEffect(() => { cargar(); }, [cargar]);

  const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  const totalUso = uso.reduce((a, r) => a + Number(r.costo_total || 0), 0);

  return (
    <div>
      <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:18, flexWrap:'wrap' }}>
        <select value={mes} onChange={e => setMes(Number(e.target.value))}
          style={{ padding:'8px 12px', border:'1.5px solid #2E4A40', borderRadius:8, background:'#0D1311', color:'#F4F7F5' }}>
          {MESES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
        </select>
        <input type="number" value={anio} onChange={e => setAnio(Number(e.target.value))}
          style={{ width:90, padding:'8px 12px', border:'1.5px solid #2E4A40', borderRadius:8, background:'#0D1311', color:'#F4F7F5' }} />
        <span style={{ color:'#8A9994', fontSize:13 }}>Total consumido: <b style={{ color:'#4CAF50' }}>{invCLP(totalUso)}</b></span>
      </div>

      {loading ? <p style={{ color:'#8A9994' }}>Cargando…</p> : (
        <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:24 }}>
          <div>
            <h3 style={{ color:'#F4F7F5', fontSize:15, margin:'0 0 10px' }}>Uso por insumo</h3>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13, minWidth:520 }}>
                <thead><tr style={{ color:'#8A9994', textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                  <th style={{ padding:'8px 10px' }}>Insumo</th>
                  <th style={{ padding:'8px 10px', textAlign:'right' }}>Cantidad</th>
                  <th style={{ padding:'8px 10px', textAlign:'right' }}>Costo</th>
                </tr></thead>
                <tbody>
                  {uso.length === 0 && <tr><td colSpan={3} style={{ padding:'16px 10px', color:'#6B7C74', textAlign:'center' }}>Sin consumo en el período.</td></tr>}
                  {uso.map(r => (
                    <tr key={r.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.05)', color:'#E6ECE9' }}>
                      <td style={{ padding:'8px 10px' }}>{r.nombre}</td>
                      <td style={{ padding:'8px 10px', textAlign:'right' }}>{invCant(r.cantidad_usada)} {r.unidad_medida}</td>
                      <td style={{ padding:'8px 10px', textAlign:'right', color:'#4CAF50' }}>{invCLP(r.costo_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h3 style={{ color:'#F4F7F5', fontSize:15, margin:'0 0 10px' }}>Consumo por comunidad</h3>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13, minWidth:520 }}>
                <thead><tr style={{ color:'#8A9994', textAlign:'left', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                  <th style={{ padding:'8px 10px' }}>Comunidad</th>
                  <th style={{ padding:'8px 10px' }}>Ascensor</th>
                  <th style={{ padding:'8px 10px', textAlign:'right' }}>Costo</th>
                </tr></thead>
                <tbody>
                  {porCom.length === 0 && <tr><td colSpan={3} style={{ padding:'16px 10px', color:'#6B7C74', textAlign:'center' }}>Sin consumo asociado a comunidades.</td></tr>}
                  {porCom.map((r, i) => (
                    <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.05)', color:'#E6ECE9' }}>
                      <td style={{ padding:'8px 10px' }}>{r.comunidad}</td>
                      <td style={{ padding:'8px 10px', color:'#8A9994' }}>{r.n_ascensor || '—'}</td>
                      <td style={{ padding:'8px 10px', textAlign:'right', color:'#4CAF50' }}>{invCLP(r.costo_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InvImportar({ onDone }) {
  const [archivo, setArchivo]   = useState(null);
  const [preview, setPreview]   = useState(null);
  const [resultado, setResultado] = useState(null);
  const [err, setErr]           = useState('');
  const [cargando, setCargando] = useState(false);
  const fileRef = useRef(null);

  const descargarPlantilla = async () => {
    try {
      const r = await api.get('/inventario/insumos/plantilla', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'plantilla_inventario.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } catch { setErr('No se pudo descargar la plantilla'); }
  };

  const analizar = async (file) => {
    setErr(''); setResultado(null); setPreview(null);
    const fd = new FormData(); fd.append('archivo', file);
    setCargando(true);
    try {
      const r = await api.post('/inventario/insumos/importar/preview', fd);
      setPreview(r.data);
    } catch (e) { setErr(e.response?.data?.error || 'Error al analizar el archivo'); }
    finally { setCargando(false); }
  };

  const confirmar = async () => {
    if (!archivo) return;
    const fd = new FormData(); fd.append('archivo', archivo);
    setCargando(true);
    try {
      const r = await api.post('/inventario/insumos/importar/confirmar', fd);
      setResultado(r.data);
    } catch (e) { setErr(e.response?.data?.error || 'Error al importar'); }
    finally { setCargando(false); }
  };

  return (
    <div style={{ maxWidth:640 }}>
      <p style={{ color:'#8A9994', fontSize:13, marginTop:0 }}>
        Descarga la plantilla, rellénala con tus insumos y súbela. Los insumos nuevos con
        <b> stock_inicial</b> generan una entrada al kardex; en los existentes solo se actualizan sus datos.
      </p>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:16 }}>
        <Btn outline onClick={descargarPlantilla}>⬇ Descargar plantilla Excel</Btn>
        <Btn onClick={() => fileRef.current?.click()}>⬆ Seleccionar archivo</Btn>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display:'none' }}
          onChange={e => { const file = e.target.files?.[0]; if (file) { setArchivo(file); analizar(file); } }} />
      </div>

      {archivo && <p style={{ color:'#C9D3CE', fontSize:13 }}>Archivo: <b>{archivo.name}</b></p>}
      {err && <div style={{ color:'#d9534f', marginBottom:12, fontSize:13 }}>{err}</div>}
      {cargando && <p style={{ color:'#8A9994' }}>Procesando…</p>}

      {preview && !resultado && (
        <div style={{ background:'#111714', border:'1px solid rgba(255,255,255,0.08)', borderRadius:12, padding:16, marginBottom:16 }}>
          <p style={{ margin:'0 0 8px', color:'#F4F7F5' }}>
            Se detectaron <b>{preview.total}</b> filas: <b style={{ color:'#4CAF50' }}>{preview.nuevos} nuevos</b>, <b style={{ color:'#E0A415' }}>{preview.actualizados} a actualizar</b>.
          </p>
          <div style={{ maxHeight:200, overflowY:'auto', fontSize:13 }}>
            {preview.detalle?.map((d, i) => (
              <div key={i} style={{ color:'#C9D3CE', padding:'2px 0' }}>
                <span style={{ color: d.accion === 'crear' ? '#4CAF50' : '#E0A415' }}>{d.accion === 'crear' ? '+ Crear' : '↻ Actualizar'}</span> {d.nombre}
                {d.accion === 'crear' && d.stock_inicial > 0 ? ` (stock inicial ${d.stock_inicial})` : ''}
              </div>
            ))}
          </div>
          <div style={{ marginTop:14 }}><Btn onClick={confirmar}>Confirmar importación</Btn></div>
        </div>
      )}

      {resultado && (
        <div style={{ background:'#12382D', color:'#4CAF50', padding:'14px 16px', borderRadius:12, marginBottom:12 }}>
          {resultado.message}
          <div style={{ marginTop:12 }}><Btn onClick={onDone}>Ver catálogo</Btn></div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const { tecnico, logout } = useAuth();
  const navigate = useNavigate();
  const TABS = ALL_TABS.filter(t => t.roles.includes(tecnico?.rol));
  const [searchParams] = useSearchParams();

  // Default por rol; el ?tab= solo se respeta si el rol tiene acceso a esa pestaña.
  const defaultTab =
    tecnico?.rol === 'central'      ? 'notificaciones' :
    tecnico?.rol === 'presupuestos' ? 'presupuestos'   :
    tecnico?.rol === 'facturacion'  ? 'facturable'     : 'stats';
  const tabPedido = searchParams.get('tab');
  const tabInicial = (tabPedido && TABS.some(t => t.id === tabPedido)) ? tabPedido : defaultTab;

  const grupoDeTab = (tabId) =>
    TAB_GROUPS.find(g => g.tabs.includes(tabId))?.id || TAB_GROUPS[0].id;

  const [tab,          setTab]          = useState(tabInicial);
  const [grupo,        setGrupo]        = useState(grupoDeTab(tabInicial));
  const [menuAbierto,  setMenuAbierto]  = useState(false);

  // Banner global (A3): presupuestos enviados +7 días sin aprobar
  const puedePresup = ['admin', 'superadmin', 'riesgos'].includes(tecnico?.rol);
  const [recsPresup, setRecsPresup]     = useState(null);
  const [recsCerrado, setRecsCerrado]   = useState(false);
  useEffect(() => {
    if (!puedePresup) return;
    api.get('/control-presupuestos/recordatorios').then(r => setRecsPresup(r.data)).catch(() => {});
  }, [puedePresup]);
  const irAControlPresup = () => { setGrupo(grupoDeTab('control-presupuestos')); setTab('control-presupuestos'); };

  useEffect(() => {
    if (tecnico && !['admin','superadmin','central','presupuestos','facturacion','riesgos'].includes(tecnico.rol)) navigate('/');
  }, [tecnico, navigate]);

  const cambiarGrupo = (grupoId) => {
    setGrupo(grupoId);
    const g = TAB_GROUPS.find(g => g.id === grupoId);
    const primerTab = TABS.find(t => g.tabs.includes(t.id));
    if (primerTab) setTab(primerTab.id);
  };

  const gruposVisibles = TAB_GROUPS.filter(g =>
    g.tabs.some(tabId => TABS.some(t => t.id === tabId))
  );

  const tabsDelGrupo = TABS.filter(t =>
    TAB_GROUPS.find(g => g.id === grupo)?.tabs.includes(t.id)
  );

  // riesgos: escritura en Órdenes, Notificaciones, Presupuestos, Catálogo;
  // lectura en Comunidades, RAI, Estadísticas y Rendiciones.
  const esRiesgos = tecnico?.rol === 'riesgos';

  const CONTENIDO = {
    ascensores:     <TabAscensores soloLectura={esRiesgos} />,
    tecnicos:       <TabTecnicos />,
    historial:      <TabHistorial />,
    rai:            <TabRAI soloLectura={esRiesgos} />,
    pendientes:     <TabPendientes />,
    notificaciones: <TabNotificaciones rol={tecnico?.rol} />,
    mapa:           <TabMapa />,
    asistencia:     <TabAsistencia />,
    stats:          <TabStats rol={tecnico?.rol} />,
    planificacion:  <TabPlanificacion />,
    rendiciones:    <TabRendiciones soloLectura={esRiesgos} />,
    presupuestos:   <TabPresupuestos />,
    catalogo:       <TabCatalogo />,
    facturable:     <TabFacturable />,
    historicos:     <TabImportarHistorico />,
    reportes:       <TabReportes />,
    trabajos:       <TabTrabajos />,
    ordenes:        <TabOrdenesTrabajo />,
    recordatorios:  <TabRecordatorios />,
    mantenciones:   <TabMantenciones />,
    inventario:     <TabInventario />,
    webhooks:       <TabWebhooks />,
    almacenamiento: <TabAlmacenamiento />,
    auditoria:      <TabAuditoria />,
    bitacoras:      <TabBitacoras />,
    'control-presupuestos': <TabControlPresupuestos />,
  };

  const GRUPO_ICON = {
    operaciones: '⚡',
    equipo:      '👥',
    finanzas:    '💰',
    sistema:     '⚙',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#080C0A' }}>

      {/* ── Header ── */}
      <div className="adm-header" style={{
        padding: '13px 20px',
        display: 'flex', alignItems: 'center', gap: 12,
        position: 'relative',
      }}>

        {/* Hamburguesa */}
        <button
          onClick={() => setMenuAbierto(v => !v)}
          style={{
            background: menuAbierto ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.12)',
            border: 'none', cursor: 'pointer', padding: '7px 8px',
            borderRadius: 7, display: 'flex', flexDirection: 'column',
            gap: 4, flexShrink: 0,
          }}>
          <span style={{ display:'block', width:18, height:2, background:'white', borderRadius:1 }} />
          <span style={{ display:'block', width:18, height:2, background:'white', borderRadius:1 }} />
          <span style={{ display:'block', width:18, height:2, background:'white', borderRadius:1 }} />
        </button>

        <span style={{ color: 'white', fontSize: 16, fontWeight: 700, flex: 1 }}>
          Solvert Admin
        </span>
        {/* Engranaje — acceso directo a Sistema */}
        <button
          onClick={() => { cambiarGrupo('sistema'); setMenuAbierto(false); }}
          title="Sistema"
          style={{
            background: grupo === 'sistema' ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.12)',
            border: 'none', cursor: 'pointer', padding: '7px 8px',
            borderRadius: 7, fontSize: 18, lineHeight: 1, flexShrink: 0,
            color: grupo === 'sistema' ? '#4CAF50' : 'white',
          }}>⚙</button>

        <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>{tecnico?.nombre}</span>
        <button onClick={() => navigate('/')} className="adm-chip"
          style={{ padding: '5px 13px', fontSize: 13, flexShrink: 0 }}>← App</button>
        <button onClick={logout} className="adm-chip"
          style={{ padding: '5px 13px', fontSize: 13, flexShrink: 0 }}>Salir</button>

        {/* Menú desplegable de grupos */}
        {menuAbierto && (
          <>
            <div
              onClick={() => setMenuAbierto(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 50 }}
            />
            <div style={{
              position: 'absolute', top: '100%', left: 0,
              background: '#111714', zIndex: 51,
              borderRadius: '0 0 12px 12px',
              border: '1px solid rgba(255,255,255,0.08)',
              borderTop: 'none', minWidth: 220,
              boxShadow: '0 8px 28px rgba(0,0,0,0.5)',
              overflow: 'hidden',
            }}>
              {gruposVisibles.filter(g => g.id !== 'sistema').map((g, i, arr) => (
                <button key={g.id}
                  onClick={() => { cambiarGrupo(g.id); setMenuAbierto(false); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    width: '100%', textAlign: 'left',
                    padding: '14px 20px', border: 'none',
                    background: grupo === g.id ? '#12382D' : 'transparent',
                    cursor: 'pointer', fontSize: 14, fontWeight: 600,
                    color: grupo === g.id ? '#4CAF50' : '#C9D3CE',
                    borderBottom: i < arr.length - 1
                      ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    fontFamily: 'inherit',
                  }}>
                  <span style={{ fontSize: 16, width: 22, textAlign: 'center' }}>
                    {GRUPO_ICON[g.id]}
                  </span>
                  {g.label}
                  {grupo === g.id && (
                    <span style={{ marginLeft: 'auto', fontSize: 10,
                                   color: '#4CAF50', fontWeight: 700 }}>
                      ●
                    </span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Sub-tabs del grupo activo ── */}
      {tabsDelGrupo.length > 1 && (
        <div className="ptabs">
          {tabsDelGrupo.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={'ptab' + (tab === t.id ? ' ptab--active' : '')}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Banner global: presupuestos enviados +7 días sin aprobar (A3) ── */}
      {puedePresup && recsPresup?.n > 0 && !recsCerrado && (
        <div style={{ maxWidth: 1200, margin: '12px auto 0', padding: '0 24px' }}>
          <div style={{ background: '#2A1F0A', border: '1px solid #5A4A1E', borderRadius: 10,
                        padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#F59E0B', fontWeight: 600 }}>
              ⏰ {recsPresup.n} presupuesto{recsPresup.n > 1 ? 's' : ''} enviado{recsPresup.n > 1 ? 's' : ''} hace +7 días sin aprobar.
            </span>
            <button onClick={irAControlPresup}
              style={{ padding: '5px 12px', background: '#F59E0B', color: '#0B0F0D', border: 'none', borderRadius: 7, fontWeight: 700, cursor: 'pointer', fontSize: 12 }}>
              Ver en Control Presup.
            </button>
            <button onClick={() => setRecsCerrado(true)}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#8A6D1A', cursor: 'pointer', fontSize: 16 }} title="Ocultar por ahora">×</button>
          </div>
        </div>
      )}

      {/* ── Contenido ── */}
      <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
        {CONTENIDO[tab]}
      </div>
    </div>
  );
}
